/**
 * CERTUS Analytics Computation Engine
 *
 * Pure function that takes raw address data + pricing history and computes
 * every analytic the Heston dashboard needs. No side effects, no API calls.
 *
 * Usage:
 *   import { computeAnalytics } from './analyticsEngine';
 *   const analytics = computeAnalytics(addresses, historyByAddress);
 *
 * @param {Array} addresses - Address objects from DynamoDB (current snapshot)
 * @param {Object} historyByAddress - { addressId: [{ timeOfScrape, pricing: {...} }, ...] }
 * @returns {Object} All computed analytics sections
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRICE_FIELDS = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit', 'mosquitoTickControl'];
const SUBSCRIPTION_FIELDS = ['pest365', 'pest365Plus']; // monthly recurring
const COORDINATED_THRESHOLD = 0.5; // 50% of addresses must change for "coordinated"
const OUTLIER_Z_THRESHOLD = 2.0;
// Statistical promo detection (no hardcoded values)
const PROMO_MIN_COUNT = 3;          // at least 3 addresses must converge to the same price
const PROMO_CONCENTRATION = 0.25;   // ≥25% of changed addresses converge to same value
const PROMO_DISCOUNT = 0.7;         // promo price must be ≤70% of the non-promo average

// ---------------------------------------------------------------------------
// Helpers: Parsing & Math
// ---------------------------------------------------------------------------

/**
 * Parse any price value to a numeric float.
 * Handles numbers, dollar-strings, "not_available", "N/A", null, undefined, empty strings.
 * Returns null for non-numeric / unavailable values.
 */
function parsePrice(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return isNaN(value) ? null : value;
  if (typeof value === 'object') {
    // DynamoDB Decimal objects
    const n = parseFloat(value.toString());
    return isNaN(n) ? null : n;
  }
  if (typeof value === 'string') {
    const lower = value.toLowerCase().trim();
    if (lower === 'not_available' || lower === 'not available' || lower === 'n/a' || lower === '') {
      return null;
    }
    const cleaned = value.replace(/[$,]/g, '').trim();
    const n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
  }
  return null;
}

/**
 * Returns true if the raw value represents "not_available" (the MTC sentinel).
 */
function isNotAvailable(value) {
  if (value == null) return false;
  if (typeof value === 'string') {
    const lower = value.toLowerCase().trim();
    return lower === 'not_available' || lower === 'not available';
  }
  return false;
}

/** Arithmetic mean, ignoring nulls. Returns null if no valid values. */
function mean(values) {
  const valid = values.filter(v => v != null && !isNaN(v));
  if (valid.length === 0) return null;
  return valid.reduce((s, v) => s + v, 0) / valid.length;
}

/** Population standard deviation, ignoring nulls. */
function stdDev(values) {
  const valid = values.filter(v => v != null && !isNaN(v));
  if (valid.length < 2) return 0;
  const avg = valid.reduce((s, v) => s + v, 0) / valid.length;
  const variance = valid.reduce((s, v) => s + (v - avg) ** 2, 0) / valid.length;
  return Math.sqrt(variance);
}

/** Human-readable field labels. */
const FIELD_LABELS = {
  pest365: 'Pest365',
  pest365Plus: 'Pest365+',
  oneTime: 'One-Time',
  initialServiceVisit: 'Initial Service Visit',
  mosquitoTickControl: 'Mosquito & Tick Control',
};
function fieldLabel(f) { return FIELD_LABELS[f] || f; }

/**
 * Statistically detect promotional prices across all price fields.
 * A price is promotional if:
 *  1. ≥ PROMO_MIN_COUNT addresses share the exact same value
 *  2. That cluster is ≥ PROMO_CONCENTRATION of all non-null values for the field
 *  3. The cluster price is ≤ PROMO_DISCOUNT × the average of non-cluster values
 *
 * Returns Map<field, { promoPrice, count, normalAvg }>
 */
function detectPromoPrices(addresses) {
  const promos = new Map();
  for (const field of PRICE_FIELDS) {
    const values = addresses.map(a => parsePrice(a[field])).filter(v => v != null);
    if (values.length < PROMO_MIN_COUNT) continue;

    // Count frequency of each price (keyed by 2-decimal string to avoid float drift)
    const freq = {};
    for (const v of values) {
      const key = v.toFixed(2);
      freq[key] = (freq[key] || 0) + 1;
    }

    // Check each unique price as a candidate promo
    for (const [priceStr, count] of Object.entries(freq)) {
      const price = parseFloat(priceStr);
      if (count < PROMO_MIN_COUNT) continue;
      if (count / values.length < PROMO_CONCENTRATION) continue;

      // Must be a discount relative to the rest
      const otherValues = values.filter(v => Math.abs(v - price) > 0.01);
      if (otherValues.length === 0) continue; // all same price = standard price, not promo
      const otherAvg = mean(otherValues);
      if (otherAvg == null || otherAvg <= 0) continue;

      if (price <= otherAvg * PROMO_DISCOUNT) {
        promos.set(field, { promoPrice: price, count, normalAvg: round(otherAvg) });
        break; // one promo per field
      }
    }
  }
  return promos;
}

/** Min of an array, ignoring nulls. */
function safeMin(values) {
  const valid = values.filter(v => v != null && !isNaN(v));
  return valid.length === 0 ? null : Math.min(...valid);
}

/** Max of an array, ignoring nulls. */
function safeMax(values) {
  const valid = values.filter(v => v != null && !isNaN(v));
  return valid.length === 0 ? null : Math.max(...valid);
}

/**
 * Pearson correlation coefficient between two arrays of numbers.
 * Uses only indices where both arrays have valid values.
 */
function pearsonCorrelation(xs, ys) {
  const pairs = [];
  for (let i = 0; i < xs.length && i < ys.length; i++) {
    if (xs[i] != null && ys[i] != null && !isNaN(xs[i]) && !isNaN(ys[i])) {
      pairs.push([xs[i], ys[i]]);
    }
  }
  if (pairs.length < 3) return null;
  const n = pairs.length;
  const meanX = pairs.reduce((s, p) => s + p[0], 0) / n;
  const meanY = pairs.reduce((s, p) => s + p[1], 0) / n;
  let num = 0, denX = 0, denY = 0;
  for (const [x, y] of pairs) {
    const dx = x - meanX;
    const dy = y - meanY;
    num += dx * dy;
    denX += dx * dx;
    denY += dy * dy;
  }
  const den = Math.sqrt(denX * denY);
  return den === 0 ? null : num / den;
}

/** Extract a date-only string (YYYY-MM-DD) from an ISO timestamp. */
function toDateStr(isoString) {
  if (!isoString) return null;
  return isoString.substring(0, 10);
}

/** Get price from a history entry, handling the nested `pricing` object. */
function getHistoryPrice(entry, field) {
  if (entry.pricing) return parsePrice(entry.pricing[field]);
  return parsePrice(entry[field]);
}

/** Get raw (unparsed) price from a history entry — useful for not_available checks. */
function getHistoryRaw(entry, field) {
  if (entry.pricing) return entry.pricing[field];
  return entry[field];
}

/** Get the timestamp string from a history entry (handles multiple field names). */
function getTimestamp(entry) {
  return entry.timeOfScrape || entry.scrapedAt || entry.timestamp || null;
}

/** Round to N decimal places. */
function round(value, decimals = 2) {
  if (value == null || isNaN(value)) return null;
  return Math.round(value * 10 ** decimals) / 10 ** decimals;
}

// ---------------------------------------------------------------------------
// Wavelet Scalogram (Morlet wavelet approximation of pest365 avg series)
// ---------------------------------------------------------------------------

function computeWaveletScalogramData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 6) return null;

  // Extract average pest365 per snapshot date
  const prices = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) vals.push(v);
    }
    prices.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
  }

  // Forward/backward fill nulls
  for (let i = 1; i < prices.length; i++) {
    if (prices[i] == null) prices[i] = prices[i - 1];
  }
  for (let i = prices.length - 2; i >= 0; i--) {
    if (prices[i] == null) prices[i] = prices[i + 1];
  }

  if (prices.every(v => v == null)) return null;

  const N = prices.length;
  const avg = prices.reduce((s, v) => s + v, 0) / N;
  const centered = prices.map(v => v - avg);

  const scales = [2, 3, 4, 5, 6, 8, 10, 12];
  const power = []; // scales.length x N
  const globalSpectrum = [];

  for (const scale of scales) {
    const row = [];
    for (let t = 0; t < N; t++) {
      let w = 0;
      for (let tp = 0; tp < N; tp++) {
        const u = (tp - t) / scale;
        const psi = Math.exp(-u * u / 2) * Math.cos(5 * u);
        w += centered[tp] * psi;
      }
      // Power = W^2 normalized by scale
      row.push(round((w * w) / scale, 4));
    }
    power.push(row);
    // Global spectrum: average power across time for this scale
    const avgPower = row.reduce((s, v) => s + v, 0) / row.length;
    globalSpectrum.push(round(avgPower, 4));
  }

  // Cone of influence: max reliable scale at each time point
  const coneOfInfluence = [];
  for (let t = 0; t < N; t++) {
    coneOfInfluence.push(Math.min(t, N - 1 - t));
  }

  // Peak scale (highest global power)
  let peakIdx = 0;
  for (let i = 1; i < globalSpectrum.length; i++) {
    if (globalSpectrum[i] > globalSpectrum[peakIdx]) peakIdx = i;
  }

  return {
    dates,
    scales,
    power,
    field: 'pest365',
    coneOfInfluence,
    globalSpectrum,
    peakScale: scales[peakIdx],
    peakPeriod: scales[peakIdx],
  };
}

// ---------------------------------------------------------------------------
// Phase Portrait (price(t) vs price(t+1) for pest365 avg)
// ---------------------------------------------------------------------------

function computePhasePortraitData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 3) return null;

  // Extract average pest365 per snapshot
  const prices = [];
  const validDates = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) vals.push(v);
    }
    const avg = vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    if (avg != null) {
      prices.push(avg);
      validDates.push(date);
    }
  }

  if (prices.length < 3) return null;

  // Create points: price(t) vs price(t+1)
  const points = [];
  const trajectory = [];
  for (let i = 0; i < prices.length - 1; i++) {
    points.push({ x: round(prices[i], 4), y: round(prices[i + 1], 4), date: validDates[i] });
    trajectory.push([round(prices[i], 4), round(prices[i + 1], 4)]);
  }

  // Diagonal range
  const allVals = prices.map(v => v);
  const minVal = Math.min(...allVals);
  const maxVal = Math.max(...allVals);

  // Fixed points: where trajectory crosses y=x diagonal
  const fixedPoints = [];
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    // Check if the line segment from p1 to p2 crosses y=x
    const diff1 = p1.y - p1.x;
    const diff2 = p2.y - p2.x;
    if (diff1 * diff2 <= 0 && (diff1 !== 0 || diff2 !== 0)) {
      // Interpolate
      const t = Math.abs(diff1) / (Math.abs(diff1) + Math.abs(diff2) + 1e-12);
      const fpx = p1.x + t * (p2.x - p1.x);
      const fpy = p1.y + t * (p2.y - p1.y);
      // Classify: stable if nearby points converge, unstable if diverge
      const beforeDist = Math.abs(p1.y - p1.x);
      const afterDist = Math.abs(p2.y - p2.x);
      // Look at surrounding points for classification
      let converging = 0;
      let diverging = 0;
      const window = 2;
      for (let j = Math.max(0, i - window); j <= Math.min(points.length - 1, i + window + 1); j++) {
        if (j < points.length - 1) {
          const d1 = Math.abs(points[j].y - points[j].x);
          const d2 = Math.abs(points[j + 1].y - points[j + 1].x);
          if (d2 < d1) converging++;
          else if (d2 > d1) diverging++;
        }
      }
      fixedPoints.push({
        x: round(fpx, 4),
        y: round(fpy, 4),
        type: converging >= diverging ? 'stable' : 'unstable',
      });
    }
  }

  // Approximate Lyapunov exponent: average of ln|Δy/Δx| for consecutive pairs
  const lyapValues = [];
  for (let i = 0; i < points.length - 1; i++) {
    const dx = points[i + 1].x - points[i].x;
    const dy = points[i + 1].y - points[i].y;
    if (Math.abs(dx) > 1e-10) {
      lyapValues.push(Math.log(Math.abs(dy / dx) + 1e-12));
    }
  }
  const lyapunov = lyapValues.length > 0
    ? round(lyapValues.reduce((s, v) => s + v, 0) / lyapValues.length, 4)
    : 0;

  let classification = 'edge_of_chaos';
  if (lyapunov > 0.05) classification = 'chaotic';
  else if (lyapunov < -0.05) classification = 'stable';

  return {
    field: 'pest365',
    points,
    trajectory,
    fixedPoints,
    diagonal: { min: round(minVal, 4), max: round(maxVal, 4) },
    lyapunov,
    classification,
  };
}

// ---------------------------------------------------------------------------
// Lyapunov Exponent (running estimates over time for pest365 avg)
// ---------------------------------------------------------------------------

function computeLyapunovExponentData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 4) return null;

  // Extract average pest365 per snapshot
  const prices = [];
  const validDates = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) vals.push(v);
    }
    const avg = vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    if (avg != null) {
      prices.push(avg);
      validDates.push(date);
    }
  }

  if (prices.length < 4) return null;

  const EPSILON = 0.01;

  // Compute deltas: price(t) - price(t-1)
  const deltas = [];
  for (let i = 1; i < prices.length; i++) {
    deltas.push(prices[i] - prices[i - 1]);
  }

  // Running exponent at each step: ln(|delta(t)| / |delta(t-1)|)
  const exponents = [];
  for (let i = 1; i < deltas.length; i++) {
    const absPrev = Math.abs(deltas[i - 1]) + EPSILON;
    const absCurr = Math.abs(deltas[i]) + EPSILON;
    exponents.push(round(Math.log(absCurr / absPrev), 4));
  }

  if (exponents.length === 0) return null;

  // Cumulative exponent: running average
  const cumulativeExponent = [];
  let runningSum = 0;
  for (let i = 0; i < exponents.length; i++) {
    runningSum += exponents[i];
    cumulativeExponent.push(round(runningSum / (i + 1), 4));
  }

  const overallExponent = round(runningSum / exponents.length, 4);

  let classification = 'edge_of_chaos';
  if (overallExponent > 0.05) classification = 'chaotic';
  else if (overallExponent < -0.05) classification = 'stable';

  // Divergence rate
  const lastN = Math.min(5, exponents.length);
  const shortTerm = round(
    exponents.slice(-lastN).reduce((s, v) => s + v, 0) / lastN, 4
  );

  return {
    dates: validDates.slice(2),
    exponents,
    cumulativeExponent,
    field: 'pest365',
    overallExponent,
    classification,
    divergenceRate: { shortTerm, longTerm: overallExponent },
  };
}

// ---------------------------------------------------------------------------
// State Sync (state-level price synchronization index)
// ---------------------------------------------------------------------------

function computeStateSyncData(snapshots, addresses) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 3 || !addresses || addresses.length === 0) return null;

  // Group addresses by state
  const stateAddrs = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!stateAddrs[st]) stateAddrs[st] = [];
    stateAddrs[st].push(addr.addressId);
  }

  const stateNames = Object.keys(stateAddrs);
  if (stateNames.length < 2) return null;

  // Compute per-state average pest365 for each date
  const stateAvgs = {}; // { state: [avg per date] }
  for (const state of stateNames) {
    stateAvgs[state] = [];
    for (const date of dates) {
      const snap = snapshots[date];
      const vals = [];
      for (const addrId of stateAddrs[state]) {
        const v = snap[addrId]?.pest365;
        if (v != null && !isNaN(v)) vals.push(v);
      }
      stateAvgs[state].push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
    }
  }

  // Per-state price changes (%) between consecutive snapshots
  const stateChanges = {}; // { state: [change pct per pair] }
  for (const state of stateNames) {
    stateChanges[state] = [];
    for (let i = 1; i < dates.length; i++) {
      const prev = stateAvgs[state][i - 1];
      const curr = stateAvgs[state][i];
      if (prev != null && curr != null && prev !== 0) {
        stateChanges[state].push((curr - prev) / Math.abs(prev) * 100);
      } else {
        stateChanges[state].push(null);
      }
    }
  }

  // Sync index at each time step: fraction of states that changed in same direction as majority
  const syncIndex = [];
  const changeDates = dates.slice(1);
  for (let t = 0; t < changeDates.length; t++) {
    const changes = [];
    for (const state of stateNames) {
      if (stateChanges[state][t] != null) {
        changes.push({ state, change: stateChanges[state][t] });
      }
    }
    if (changes.length < 2) {
      syncIndex.push(null);
      continue;
    }
    const positive = changes.filter(c => c.change > 0).length;
    const negative = changes.filter(c => c.change < 0).length;
    const zero = changes.filter(c => c.change === 0).length;
    const majorityDir = positive >= negative ? 1 : -1;
    const aligned = changes.filter(c =>
      majorityDir > 0 ? c.change >= 0 : c.change <= 0
    ).length;
    syncIndex.push(round(aligned / changes.length, 4));
  }

  const validSync = syncIndex.filter(v => v != null);
  const overallSync = validSync.length > 0
    ? round(validSync.reduce((s, v) => s + v, 0) / validSync.length, 4)
    : null;

  // Per-state scores: average alignment with majority
  const stateScores = [];
  for (const state of stateNames) {
    const alignments = [];
    for (let t = 0; t < changeDates.length; t++) {
      if (stateChanges[state][t] == null) continue;
      // Determine majority direction at this time step
      const changes = [];
      for (const st of stateNames) {
        if (stateChanges[st][t] != null) changes.push(stateChanges[st][t]);
      }
      const posCount = changes.filter(c => c > 0).length;
      const negCount = changes.filter(c => c < 0).length;
      const majorDir = posCount >= negCount ? 1 : -1;
      const aligned = majorDir > 0 ? stateChanges[state][t] >= 0 : stateChanges[state][t] <= 0;
      alignments.push(aligned ? 1 : 0);
    }
    const avgSync = alignments.length > 0
      ? round(alignments.reduce((s, v) => s + v, 0) / alignments.length, 4)
      : null;
    // Trend: compare last 3 vs first 3
    const first3 = alignments.slice(0, 3);
    const last3 = alignments.slice(-3);
    const firstAvg = first3.length > 0 ? first3.reduce((s, v) => s + v, 0) / first3.length : 0;
    const lastAvg = last3.length > 0 ? last3.reduce((s, v) => s + v, 0) / last3.length : 0;
    const trend = lastAvg > firstAvg ? 'increasing' : lastAvg < firstAvg ? 'decreasing' : 'stable';
    stateScores.push({ state, avgSync, trend });
  }
  stateScores.sort((a, b) => (b.avgSync || 0) - (a.avgSync || 0));

  // Pairwise sync: for each pair of states, fraction of time steps they moved in same direction
  const pairwiseSync = [];
  for (let i = 0; i < stateNames.length; i++) {
    for (let j = i + 1; j < stateNames.length; j++) {
      const st1 = stateNames[i];
      const st2 = stateNames[j];
      let agree = 0;
      let total = 0;
      for (let t = 0; t < changeDates.length; t++) {
        const c1 = stateChanges[st1][t];
        const c2 = stateChanges[st2][t];
        if (c1 != null && c2 != null) {
          total++;
          if ((c1 >= 0 && c2 >= 0) || (c1 <= 0 && c2 <= 0)) agree++;
        }
      }
      if (total > 0) {
        pairwiseSync.push({ state1: st1, state2: st2, sync: round(agree / total, 4) });
      }
    }
  }
  pairwiseSync.sort((a, b) => b.sync - a.sync);

  return {
    dates: changeDates,
    syncIndex,
    pairwiseSync,
    stateScores,
    overallSync,
    field: 'pest365',
  };
}

// ---------------------------------------------------------------------------
// Spectral Coherence (coherence between price field pairs via DFT)
// ---------------------------------------------------------------------------

function computeSpectralCoherenceData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  const FIELDS = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  if (dates.length < 4) return null;

  // Build average time series for each field
  const series = {};
  for (const field of FIELDS) {
    const prices = [];
    for (const date of dates) {
      const snap = snapshots[date];
      const vals = [];
      for (const addrId of Object.keys(snap)) {
        const v = snap[addrId]?.[field];
        if (v != null && !isNaN(v)) vals.push(v);
      }
      prices.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
    }
    // Forward/backward fill
    for (let i = 1; i < prices.length; i++) {
      if (prices[i] == null) prices[i] = prices[i - 1];
    }
    for (let i = prices.length - 2; i >= 0; i--) {
      if (prices[i] == null) prices[i] = prices[i + 1];
    }
    series[field] = prices;
  }

  // Check we have valid data
  for (const field of FIELDS) {
    if (series[field].every(v => v == null)) return null;
  }

  const N = dates.length;
  const halfN = Math.floor(N / 2);

  // Helper: compute DFT coefficients (real, imag) for a centered series
  function dftCoeffs(data) {
    const avg = data.reduce((s, v) => s + v, 0) / data.length;
    const centered = data.map(v => v - avg);
    const reals = [];
    const imags = [];
    for (let k = 1; k <= halfN; k++) {
      let re = 0;
      let im = 0;
      for (let n = 0; n < N; n++) {
        const angle = (2 * Math.PI * k * n) / N;
        re += centered[n] * Math.cos(angle);
        im -= centered[n] * Math.sin(angle);
      }
      reals.push(re);
      imags.push(im);
    }
    return { reals, imags };
  }

  // Pre-compute DFT for all fields
  const dft = {};
  for (const field of FIELDS) {
    dft[field] = dftCoeffs(series[field]);
  }

  // Compute coherence for each pair
  const pairs = [];
  for (let i = 0; i < FIELDS.length; i++) {
    for (let j = i + 1; j < FIELDS.length; j++) {
      const f1 = FIELDS[i];
      const f2 = FIELDS[j];
      const d1 = dft[f1];
      const d2 = dft[f2];

      const frequencies = [];
      const coherence = [];
      const phase = [];
      const periods = [];

      for (let k = 0; k < halfN; k++) {
        // Cross-spectral density Sxy = X * conj(Y)
        const sxyRe = d1.reals[k] * d2.reals[k] + d1.imags[k] * d2.imags[k];
        const sxyIm = d1.imags[k] * d2.reals[k] - d1.reals[k] * d2.imags[k];
        // Auto-spectral densities
        const sxx = d1.reals[k] * d1.reals[k] + d1.imags[k] * d1.imags[k];
        const syy = d2.reals[k] * d2.reals[k] + d2.imags[k] * d2.imags[k];

        // Coherence = |Sxy|^2 / (Sxx * Syy)
        const sxyMag2 = sxyRe * sxyRe + sxyIm * sxyIm;
        const denom = sxx * syy;
        const coh = denom > 1e-12 ? sxyMag2 / denom : 0;
        const ph = Math.atan2(sxyIm, sxyRe);

        frequencies.push(round((k + 1) / N, 4));
        coherence.push(round(Math.min(coh, 1), 4));
        phase.push(round(ph, 4));
        periods.push(round(N / (k + 1), 2));
      }

      // Peak coherence
      let peakCoh = -1;
      let peakIdx = 0;
      for (let k = 0; k < coherence.length; k++) {
        if (coherence[k] > peakCoh) {
          peakCoh = coherence[k];
          peakIdx = k;
        }
      }

      pairs.push({
        field1: f1,
        field2: f2,
        frequencies,
        coherence,
        phase,
        periods,
        peakCoherence: coherence[peakIdx] || null,
        peakFrequency: frequencies[peakIdx] || null,
        peakPeriod: periods[peakIdx] || null,
      });
    }
  }

  return {
    pairs,
    significanceThreshold: 0.5,
  };
}

// ---------------------------------------------------------------------------
// Regression Discontinuity (detect discontinuities in pest365 avg series)
// ---------------------------------------------------------------------------

function computeRegressionDiscontinuityData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 7) return null;

  // Extract average pest365 per snapshot
  const values = [];
  const validDates = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) vals.push(v);
    }
    const avg = vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    if (avg != null) {
      values.push(avg);
      validDates.push(date);
    }
  }

  const N = values.length;
  if (N < 7) return null;

  // Simple linear regression helper
  function linReg(xArr, yArr) {
    const n = xArr.length;
    if (n < 2) return { slope: 0, intercept: yArr[0] || 0, rss: 0 };
    const xMean = xArr.reduce((s, v) => s + v, 0) / n;
    const yMean = yArr.reduce((s, v) => s + v, 0) / n;
    let num = 0;
    let den = 0;
    for (let i = 0; i < n; i++) {
      num += (xArr[i] - xMean) * (yArr[i] - yMean);
      den += (xArr[i] - xMean) * (xArr[i] - xMean);
    }
    const slope = den > 1e-12 ? num / den : 0;
    const intercept = yMean - slope * xMean;
    let rss = 0;
    for (let i = 0; i < n; i++) {
      const pred = intercept + slope * xArr[i];
      rss += (yArr[i] - pred) ** 2;
    }
    return { slope: round(slope, 6), intercept: round(intercept, 4), rss };
  }

  // Single line RSS (baseline)
  const xAll = values.map((_, i) => i);
  const singleReg = linReg(xAll, values);
  const singleRSS = singleReg.rss;

  // Observations array
  const observations = values.map((v, i) => ({ date: validDates[i], value: round(v, 4), index: i }));

  // Test each possible cutoff
  const cutoffs = [];
  let bestCutoff = null;
  let bestAbsDisc = -1;

  for (let c = 3; c <= N - 3; c++) {
    const xPre = xAll.slice(0, c);
    const yPre = values.slice(0, c);
    const xPost = xAll.slice(c);
    const yPost = values.slice(c);

    const regPre = linReg(xPre, yPre);
    const regPost = linReg(xPost, yPost);

    // Discontinuity at cutoff: predicted post value - predicted pre value
    const prePredAtCut = regPre.intercept + regPre.slope * c;
    const postPredAtCut = regPost.intercept + regPost.slope * c;
    const discontinuity = postPredAtCut - prePredAtCut;

    // Combined RSS
    const combinedRSS = regPre.rss + regPost.rss;

    // F-test: (improvement in RSS / extra params) / (combined RSS / df)
    const dfFull = N - 4; // two regressions: 4 parameters
    const dfReduced = N - 2; // one regression: 2 parameters
    const extraParams = 2;
    const fStat = dfFull > 0 && combinedRSS > 1e-12
      ? ((singleRSS - combinedRSS) / extraParams) / (combinedRSS / dfFull)
      : 0;

    // Approximate p-value using F-distribution approximation
    // For simplicity, use threshold-based significance
    const significant = fStat > 4.0; // rough F(2, N-4) critical value at ~0.05
    const pValue = fStat > 0 ? round(Math.exp(-0.5 * fStat), 4) : 1; // rough approximation

    const absDisc = Math.abs(discontinuity);
    if (absDisc > bestAbsDisc) {
      bestAbsDisc = absDisc;
      bestCutoff = cutoffs.length;
    }

    const meanVal = values.reduce((s, v) => s + v, 0) / N;
    const discPercent = meanVal !== 0 ? (discontinuity / meanVal) * 100 : 0;

    // Build pre/post point arrays
    const prePoints = xPre.map(x => ({ x, y: round(regPre.intercept + regPre.slope * x, 4) }));
    const postPoints = xPost.map(x => ({ x, y: round(regPost.intercept + regPost.slope * x, 4) }));

    cutoffs.push({
      label: `Cutoff at ${validDates[c]}`,
      cutoffDate: validDates[c],
      cutoffIndex: c,
      preBefore: { slope: regPre.slope, intercept: regPre.intercept, points: prePoints },
      postAfter: { slope: regPost.slope, intercept: regPost.intercept, points: postPoints },
      discontinuity: round(discontinuity, 4),
      discontinuityPercent: round(discPercent, 2),
      significant,
      pValue,
    });
  }

  // Select the best cutoff: prefer most significant, fallback to largest absolute discontinuity
  const significantCutoffs = cutoffs.filter(c => c.significant);
  let selected;
  if (significantCutoffs.length > 0) {
    selected = significantCutoffs.reduce((best, c) =>
      Math.abs(c.discontinuity) > Math.abs(best.discontinuity) ? c : best
    );
  } else if (bestCutoff != null) {
    selected = cutoffs[bestCutoff];
  }

  // Return only the selected cutoff (and a few nearby) to keep payload manageable
  const result = selected ? [selected] : cutoffs.slice(0, 1);

  return {
    cutoffs: result,
    observations,
    field: 'pest365',
  };
}

// ---------------------------------------------------------------------------
// Quantile Regression Fan Data
// ---------------------------------------------------------------------------

function computeQuantileRegressionData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 2) return null;

  const quantileLevels = [
    { q: 0.10, p: 10, label: '10th' },
    { q: 0.25, p: 25, label: '25th' },
    { q: 0.50, p: 50, label: 'Median' },
    { q: 0.75, p: 75, label: '75th' },
    { q: 0.90, p: 90, label: '90th' },
  ];

  const quantiles = quantileLevels.map(ql => ({ q: ql.q, values: [], label: ql.label }));

  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) vals.push(v);
    }
    vals.sort((a, b) => a - b);

    if (vals.length === 0) {
      for (const qObj of quantiles) qObj.values.push(null);
    } else {
      for (let i = 0; i < quantileLevels.length; i++) {
        quantiles[i].values.push(round(percentile(vals, quantileLevels[i].p), 4));
      }
    }
  }

  // Compute spread trend: p90 - p10 at first vs last
  const p90Vals = quantiles[4].values.filter(v => v != null);
  const p10Vals = quantiles[0].values.filter(v => v != null);

  let spread = { initial: null, current: null, trend: 'stable' };
  if (p90Vals.length > 0 && p10Vals.length > 0) {
    const firstP90 = quantiles[4].values.find(v => v != null);
    const firstP10 = quantiles[0].values.find(v => v != null);
    const lastP90 = [...quantiles[4].values].reverse().find(v => v != null);
    const lastP10 = [...quantiles[0].values].reverse().find(v => v != null);

    const initial = firstP90 - firstP10;
    const current = lastP90 - lastP10;
    let trend = 'stable';
    if (initial > 0) {
      const changePct = (current - initial) / initial;
      if (changePct > 0.01) trend = 'widening';
      else if (changePct < -0.01) trend = 'narrowing';
    }
    spread = { initial: round(initial, 4), current: round(current, 4), trend };
  }

  return { dates, quantiles, field: 'pest365', spread };
}

// ---------------------------------------------------------------------------
// Regression Residuals Data
// ---------------------------------------------------------------------------

function computeResidualsData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 3) return null;

  // Extract average pest365 per snapshot (observed values)
  const observed = [];
  const validDates = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) vals.push(v);
    }
    if (vals.length > 0) {
      observed.push(vals.reduce((s, v) => s + v, 0) / vals.length);
      validDates.push(date);
    }
  }

  const N = observed.length;
  if (N < 3) return null;

  // Fit simple linear regression: y = intercept + slope * x
  const xArr = observed.map((_, i) => i);
  const xMean = (N - 1) / 2;
  const yMean = observed.reduce((s, v) => s + v, 0) / N;

  let num = 0;
  let den = 0;
  for (let i = 0; i < N; i++) {
    num += (xArr[i] - xMean) * (observed[i] - yMean);
    den += (xArr[i] - xMean) ** 2;
  }
  const slope = den > 1e-12 ? num / den : 0;
  const intercept = yMean - slope * xMean;

  // Compute fitted values and residuals
  const fitted = xArr.map(x => intercept + slope * x);
  const residuals = observed.map((obs, i) => obs - fitted[i]);

  // Standardize residuals
  const resMean = residuals.reduce((s, v) => s + v, 0) / N;
  const resStd = Math.sqrt(residuals.reduce((s, v) => s + (v - resMean) ** 2, 0) / N);
  const standardized = resStd > 1e-12
    ? residuals.map(r => (r - resMean) / resStd)
    : residuals.map(() => 0);

  // Diagnostics
  const meanResidual = round(resMean, 6);
  const stdResidual = round(resStd, 6);

  // Skewness: E[(x-mu)^3] / sigma^3
  const skewness = resStd > 1e-12
    ? round(residuals.reduce((s, r) => s + ((r - resMean) / resStd) ** 3, 0) / N, 4)
    : 0;

  // Kurtosis: E[(x-mu)^4] / sigma^4
  const kurtosis = resStd > 1e-12
    ? round(residuals.reduce((s, r) => s + ((r - resMean) / resStd) ** 4, 0) / N, 4)
    : 0;

  // Durbin-Watson: sum((e_t - e_{t-1})^2) / sum(e_t^2)
  let dwNum = 0;
  let dwDen = 0;
  for (let i = 0; i < N; i++) {
    dwDen += residuals[i] ** 2;
    if (i > 0) dwNum += (residuals[i] - residuals[i - 1]) ** 2;
  }
  const durbinWatson = dwDen > 1e-12 ? round(dwNum / dwDen, 4) : null;

  // Shapiro-Wilk approximation: correlation of sorted residuals vs normal quantiles squared
  const sortedRes = [...residuals].sort((a, b) => a - b);
  // Normal quantiles approximation using inverse normal CDF (Beasley-Springer-Moro)
  function approxNormInv(p) {
    if (p <= 0) return -3.5;
    if (p >= 1) return 3.5;
    if (p === 0.5) return 0;
    const a = [-3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2, 1.383577518672690e2, -3.066479806614716e1, 2.506628277459239e0];
    const b = [-5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1, -1.328068155288572e1];
    const c = [-7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838e0, -2.549732539343734e0, 4.374664141464968e0, 2.938163982698783e0];
    const d = [7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996e0, 3.754408661907416e0];
    const pLow = 0.02425;
    const pHigh = 1 - pLow;
    let q, r;
    if (p < pLow) {
      q = Math.sqrt(-2 * Math.log(p));
      return (((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) / ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
    } else if (p <= pHigh) {
      q = p - 0.5;
      r = q * q;
      return (((((a[0]*r+a[1])*r+a[2])*r+a[3])*r+a[4])*r+a[5])*q / (((((b[0]*r+b[1])*r+b[2])*r+b[3])*r+b[4])*r+1);
    } else {
      q = Math.sqrt(-2 * Math.log(1 - p));
      return -(((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) / ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
    }
  }

  const normalQ = sortedRes.map((_, i) => approxNormInv((i + 0.375) / (N + 0.25)));
  // Correlation between sorted residuals and normal quantiles
  const nqMean = normalQ.reduce((s, v) => s + v, 0) / N;
  const srMean = sortedRes.reduce((s, v) => s + v, 0) / N;
  let corrNum = 0, corrDenA = 0, corrDenB = 0;
  for (let i = 0; i < N; i++) {
    corrNum += (sortedRes[i] - srMean) * (normalQ[i] - nqMean);
    corrDenA += (sortedRes[i] - srMean) ** 2;
    corrDenB += (normalQ[i] - nqMean) ** 2;
  }
  const corr = (corrDenA > 1e-12 && corrDenB > 1e-12)
    ? corrNum / Math.sqrt(corrDenA * corrDenB) : 0;
  const shapiroWilk = round(corr * corr, 4);

  // Heteroscedasticity: compare std of first half vs second half residuals
  const half = Math.floor(N / 2);
  const firstHalf = residuals.slice(0, half);
  const secondHalf = residuals.slice(half);
  const stdFirst = Math.sqrt(firstHalf.reduce((s, r) => s + r * r, 0) / firstHalf.length);
  const stdSecond = Math.sqrt(secondHalf.reduce((s, r) => s + r * r, 0) / secondHalf.length);
  const heteroscedastic = stdFirst > 1e-12
    ? Math.abs(stdSecond - stdFirst) / stdFirst > 0.5
    : false;

  return {
    dates: validDates,
    residuals: residuals.map(r => round(r, 4)),
    standardized: standardized.map(s => round(s, 4)),
    fitted: fitted.map(f => round(f, 4)),
    observed: observed.map(o => round(o, 4)),
    field: 'pest365',
    diagnostics: {
      meanResidual,
      stdResidual,
      skewness,
      kurtosis,
      durbinWatson,
      shapiroWilk,
      heteroscedastic,
    },
  };
}

// ---------------------------------------------------------------------------
// Value at Risk Data
// ---------------------------------------------------------------------------

function computeValueAtRiskData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 3) return null;

  // Extract average pest365 per snapshot
  const avgValues = [];
  const validDates = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) vals.push(v);
    }
    if (vals.length > 0) {
      avgValues.push(vals.reduce((s, v) => s + v, 0) / vals.length);
      validDates.push(date);
    }
  }

  if (avgValues.length < 3) return null;

  // Compute period-over-period changes (first differences)
  const changes = [];
  for (let i = 1; i < avgValues.length; i++) {
    changes.push(avgValues[i] - avgValues[i - 1]);
  }
  const changeDates = validDates.slice(1);

  if (changes.length < 2) return null;

  // Overall VaR as fallback for early dates
  const sortedAll = [...changes].sort((a, b) => a - b);
  const overallVar95 = percentile(sortedAll, 5);
  const overallVar99 = percentile(sortedAll, 1);
  const belowOverall95 = sortedAll.filter(c => c <= overallVar95);
  const overallCvar95 = belowOverall95.length > 0
    ? belowOverall95.reduce((s, v) => s + v, 0) / belowOverall95.length
    : overallVar95;

  // Rolling VaR
  const var95 = [];
  const var99 = [];
  const cvar95 = [];
  const breaches95 = [];
  const MIN_POINTS = 5;

  for (let t = 0; t < changes.length; t++) {
    if (t + 1 < MIN_POINTS) {
      // Not enough data yet — use overall VaR
      var95.push(round(overallVar95, 4));
      var99.push(round(overallVar99, 4));
      cvar95.push(round(overallCvar95, 4));
      breaches95.push(changes[t] < overallVar95);
    } else {
      // Use all changes up to t (inclusive)
      const windowChanges = changes.slice(0, t + 1);
      const sorted = [...windowChanges].sort((a, b) => a - b);
      const v95 = percentile(sorted, 5);
      const v99 = percentile(sorted, 1);
      const belowV95 = sorted.filter(c => c <= v95);
      const cv95 = belowV95.length > 0
        ? belowV95.reduce((s, v) => s + v, 0) / belowV95.length
        : v95;

      var95.push(round(v95, 4));
      var99.push(round(v99, 4));
      cvar95.push(round(cv95, 4));
      breaches95.push(changes[t] < v95);
    }
  }

  // Summary stats
  const currentVar95 = var95[var95.length - 1];
  const currentVar99 = var99[var99.length - 1];
  const breach95Count = breaches95.filter(b => b).length;
  const breach99Count = changes.filter((c, i) => c < var99[i]).length;
  const breachRate95 = round(breach95Count / changes.length, 4);
  const breachRate99 = round(breach99Count / changes.length, 4);
  const maxLoss = round(Math.min(...changes), 4);
  const avgLoss = (() => {
    const losses = changes.filter(c => c < 0);
    return losses.length > 0 ? round(losses.reduce((s, v) => s + v, 0) / losses.length, 4) : 0;
  })();

  return {
    dates: changeDates,
    changes: changes.map(c => round(c, 4)),
    var95,
    var99,
    cvar95,
    breaches95,
    field: 'pest365',
    summary: {
      currentVar95,
      currentVar99,
      breachRate95,
      breachRate99,
      maxLoss,
      avgLoss,
    },
  };
}

// ---------------------------------------------------------------------------
// PCA Biplot Data
// ---------------------------------------------------------------------------

function computePCABiplotData(addresses) {
  if (!addresses || addresses.length < 3) return null;

  const fields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];

  // Build data matrix: each row is an address
  const rows = [];
  const addrMeta = [];
  for (const addr of addresses) {
    const vals = fields.map(f => parsePrice(addr[f]));
    if (vals.some(v => v == null)) continue; // skip addresses with nulls
    rows.push(vals);
    addrMeta.push({
      addressId: addr.addressId,
      label: `${addr.city || ''}, ${addr.state || ''}`,
      state: addr.state || 'Unknown',
    });
  }

  if (rows.length < 3) return null;
  const n = rows.length;
  const p = fields.length;

  // Compute column means and stds for standardization
  const colMean = [];
  const colStd = [];
  for (let j = 0; j < p; j++) {
    const col = rows.map(r => r[j]);
    const m = col.reduce((s, v) => s + v, 0) / n;
    const std = Math.sqrt(col.reduce((s, v) => s + (v - m) ** 2, 0) / n);
    colMean.push(m);
    colStd.push(std > 1e-12 ? std : 1);
  }

  // Standardize
  const Z = rows.map(row => row.map((v, j) => (v - colMean[j]) / colStd[j]));

  // Compute covariance matrix (4x4)
  const C = [];
  for (let i = 0; i < p; i++) {
    C[i] = [];
    for (let j = 0; j < p; j++) {
      let sum = 0;
      for (let k = 0; k < n; k++) {
        sum += Z[k][i] * Z[k][j];
      }
      C[i][j] = sum / n;
    }
  }

  // Power iteration to find eigenvalue/eigenvector
  function powerIteration(matrix, dim, maxIter) {
    let v = Array(dim).fill(0).map((_, i) => (i === 0 ? 1 : 0.5 / (i + 1)));
    // Normalize initial vector
    let norm = Math.sqrt(v.reduce((s, x) => s + x * x, 0));
    v = v.map(x => x / norm);

    let eigenvalue = 0;
    for (let iter = 0; iter < maxIter; iter++) {
      // Multiply: w = M * v
      const w = Array(dim).fill(0);
      for (let i = 0; i < dim; i++) {
        for (let j = 0; j < dim; j++) {
          w[i] += matrix[i][j] * v[j];
        }
      }
      // Eigenvalue = v^T * w
      eigenvalue = v.reduce((s, vi, i) => s + vi * w[i], 0);
      // Normalize
      norm = Math.sqrt(w.reduce((s, x) => s + x * x, 0));
      if (norm < 1e-12) break;
      v = w.map(x => x / norm);
    }
    return { eigenvalue, eigenvector: v };
  }

  // PC1
  const pc1Result = powerIteration(C, p, 100);
  const lambda1 = pc1Result.eigenvalue;
  const v1 = pc1Result.eigenvector;

  // Deflate: C2 = C - lambda1 * v1 * v1^T
  const C2 = C.map((row, i) => row.map((val, j) => val - lambda1 * v1[i] * v1[j]));

  // PC2
  const pc2Result = powerIteration(C2, p, 100);
  const lambda2 = pc2Result.eigenvalue;
  const v2 = pc2Result.eigenvector;

  // Total variance = sum of all eigenvalues = trace of C (since standardized, should ≈ p)
  let traceC = 0;
  for (let i = 0; i < p; i++) traceC += C[i][i];

  const ve1 = traceC > 1e-12 ? lambda1 / traceC : 0;
  const ve2 = traceC > 1e-12 ? lambda2 / traceC : 0;

  // Project each address onto PC1 and PC2
  const scores = [];
  for (let k = 0; k < n; k++) {
    const pc1 = Z[k].reduce((s, zj, j) => s + zj * v1[j], 0);
    const pc2 = Z[k].reduce((s, zj, j) => s + zj * v2[j], 0);
    scores.push({
      addressId: addrMeta[k].addressId,
      label: addrMeta[k].label,
      pc1: round(pc1, 4),
      pc2: round(pc2, 4),
      state: addrMeta[k].state,
    });
  }

  // Loadings = eigenvector * sqrt(eigenvalue)
  const loadings = fields.map((f, j) => ({
    field: f,
    pc1: round(v1[j] * Math.sqrt(Math.abs(lambda1)), 4),
    pc2: round(v2[j] * Math.sqrt(Math.abs(lambda2)), 4),
  }));

  return {
    scores,
    loadings,
    varianceExplained: [round(ve1, 4), round(ve2, 4)],
    totalVarianceExplained: round(ve1 + ve2, 4),
  };
}

// ---------------------------------------------------------------------------
// t-SNE Cluster Data (simplified MDS + K-means)
// ---------------------------------------------------------------------------

function computeTSNEClusterData(addresses, historyByAddress) {
  if (!addresses || addresses.length < 5) return null;

  const hist = historyByAddress || {};

  // Build feature vectors for each address
  const featureRows = [];
  const addrMeta = [];

  for (const addr of addresses) {
    const p365 = parsePrice(addr.pest365);
    const p365p = parsePrice(addr.pest365Plus);
    const ot = parsePrice(addr.oneTime);
    const isv = parsePrice(addr.initialServiceVisit);
    if (p365 == null || p365p == null || ot == null || isv == null) continue;

    // Compute volatility and price change from history
    const entries = hist[addr.addressId];
    let volatility = 0;
    let priceChange = 0;
    if (entries && entries.length > 1) {
      const sorted = [...entries].sort(
        (a, b) => new Date(getTimestamp(a)) - new Date(getTimestamp(b))
      );
      const histPrices = sorted.map(e => getHistoryPrice(e, 'pest365')).filter(v => v != null);
      if (histPrices.length > 1) {
        const hMean = histPrices.reduce((s, v) => s + v, 0) / histPrices.length;
        const hStd = Math.sqrt(histPrices.reduce((s, v) => s + (v - hMean) ** 2, 0) / histPrices.length);
        volatility = hMean > 1e-12 ? hStd / hMean : 0;
        const earliest = histPrices[0];
        const latest = histPrices[histPrices.length - 1];
        priceChange = earliest > 1e-12 ? ((latest - earliest) / earliest) * 100 : 0;
      }
    }

    featureRows.push([p365, p365p, ot, isv, priceChange, volatility]);
    addrMeta.push({
      addressId: addr.addressId,
      label: `${addr.city || ''}, ${addr.state || ''}`,
      state: addr.state || 'Unknown',
    });
  }

  const n = featureRows.length;
  if (n < 5) return null;
  const dim = featureRows[0].length;

  // Standardize features
  const fMean = [];
  const fStd = [];
  for (let j = 0; j < dim; j++) {
    const col = featureRows.map(r => r[j]);
    const m = col.reduce((s, v) => s + v, 0) / n;
    const std = Math.sqrt(col.reduce((s, v) => s + (v - m) ** 2, 0) / n);
    fMean.push(m);
    fStd.push(std > 1e-12 ? std : 1);
  }
  const Z = featureRows.map(row => row.map((v, j) => (v - fMean[j]) / fStd[j]));

  // Compute pairwise Euclidean distances
  const dist = [];
  for (let i = 0; i < n; i++) {
    dist[i] = [];
    for (let j = 0; j < n; j++) {
      if (i === j) { dist[i][j] = 0; continue; }
      if (j < i) { dist[i][j] = dist[j][i]; continue; }
      let d = 0;
      for (let k = 0; k < dim; k++) {
        d += (Z[i][k] - Z[j][k]) ** 2;
      }
      dist[i][j] = Math.sqrt(d);
    }
  }

  // Simplified MDS: start with random 2D positions, adjust to match distances
  // Use deterministic seed for reproducibility
  const positions = [];
  for (let i = 0; i < n; i++) {
    // Spread initial positions deterministically
    const angle = (2 * Math.PI * i) / n;
    positions.push([Math.cos(angle) * 10, Math.sin(angle) * 10]);
  }

  const maxIter = 200;
  for (let iter = 0; iter < maxIter; iter++) {
    const lr = 0.01 * (1 - iter / maxIter); // decaying learning rate
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const dx = positions[i][0] - positions[j][0];
        const dy = positions[i][1] - positions[j][1];
        const lowDist = Math.sqrt(dx * dx + dy * dy);
        if (lowDist < 1e-12) continue;
        const highDist = dist[i][j];
        const delta = (lowDist - highDist) / lowDist;
        const moveX = dx * delta * lr * 0.5;
        const moveY = dy * delta * lr * 0.5;
        positions[i][0] -= moveX;
        positions[i][1] -= moveY;
        positions[j][0] += moveX;
        positions[j][1] += moveY;
      }
    }
  }

  // K-means clustering (k=3)
  const K = 3;
  const clusterColors = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#8b5cf6'];

  // Initialize centroids spread across the data
  const sortedByX = [...Array(n).keys()].sort((a, b) => positions[a][0] - positions[b][0]);
  const centroids = [];
  for (let c = 0; c < K; c++) {
    const idx = sortedByX[Math.floor((c + 0.5) * n / K)];
    centroids.push([positions[idx][0], positions[idx][1]]);
  }

  const assignments = Array(n).fill(0);

  for (let iter = 0; iter < 50; iter++) {
    // Assign points to nearest centroid
    for (let i = 0; i < n; i++) {
      let bestC = 0;
      let bestD = Infinity;
      for (let c = 0; c < K; c++) {
        const d = (positions[i][0] - centroids[c][0]) ** 2 + (positions[i][1] - centroids[c][1]) ** 2;
        if (d < bestD) { bestD = d; bestC = c; }
      }
      assignments[i] = bestC;
    }
    // Recompute centroids
    for (let c = 0; c < K; c++) {
      const members = assignments.reduce((acc, a, i) => {
        if (a === c) acc.push(i);
        return acc;
      }, []);
      if (members.length > 0) {
        centroids[c][0] = members.reduce((s, i) => s + positions[i][0], 0) / members.length;
        centroids[c][1] = members.reduce((s, i) => s + positions[i][1], 0) / members.length;
      }
    }
  }

  // Label clusters based on average pest365 in each cluster
  const clusterAvgPrice = [];
  for (let c = 0; c < K; c++) {
    const members = assignments.reduce((acc, a, i) => {
      if (a === c) acc.push(i);
      return acc;
    }, []);
    const avgP = members.length > 0
      ? members.reduce((s, i) => s + featureRows[i][0], 0) / members.length
      : 0;
    clusterAvgPrice.push({ cluster: c, avgPrice: avgP, count: members.length });
  }

  // Sort by average price to assign labels
  const sortedClusters = [...clusterAvgPrice].sort((a, b) => a.avgPrice - b.avgPrice);
  const clusterLabels = {};
  if (sortedClusters.length >= 3) {
    clusterLabels[sortedClusters[0].cluster] = 'Low-price';
    clusterLabels[sortedClusters[1].cluster] = 'Mid-price';
    clusterLabels[sortedClusters[2].cluster] = 'High-price';
  } else {
    sortedClusters.forEach((c, i) => { clusterLabels[c.cluster] = `Cluster ${i + 1}`; });
  }

  const points = [];
  for (let i = 0; i < n; i++) {
    points.push({
      addressId: addrMeta[i].addressId,
      label: addrMeta[i].label,
      x: round(positions[i][0], 4),
      y: round(positions[i][1], 4),
      state: addrMeta[i].state,
      cluster: assignments[i],
    });
  }

  const clusters = [];
  for (let c = 0; c < K; c++) {
    const info = clusterAvgPrice.find(cp => cp.cluster === c);
    clusters.push({
      id: c,
      label: clusterLabels[c] || `Cluster ${c + 1}`,
      count: info ? info.count : 0,
      color: clusterColors[c],
      centroid: { x: round(centroids[c][0], 4), y: round(centroids[c][1], 4) },
    });
  }

  return {
    points,
    clusters,
    perplexity: 30,
    numClusters: K,
  };
}

// ---------------------------------------------------------------------------
// Factor Loadings Data
// ---------------------------------------------------------------------------

function computeFactorLoadingsData(addresses) {
  if (!addresses || addresses.length < 5) return null;

  // Determine if mosquitoTickControl is usable (enough numeric values)
  const allFields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  let mtcUsable = false;
  const mtcNumericCount = addresses.filter(a => {
    const v = parsePrice(a.mosquitoTickControl);
    return v != null;
  }).length;
  if (mtcNumericCount >= addresses.length * 0.5) {
    mtcUsable = true;
  }

  const fields = mtcUsable
    ? [...allFields, 'mosquitoTickControl']
    : [...allFields];

  // Build data matrix
  const rows = [];
  for (const addr of addresses) {
    const vals = fields.map(f => parsePrice(addr[f]));
    if (vals.some(v => v == null)) continue;
    rows.push(vals);
  }

  const n = rows.length;
  const p = fields.length;
  if (n < 5 || p < 2) return null;

  // Standardize columns
  const colMean = [];
  const colStd = [];
  for (let j = 0; j < p; j++) {
    const col = rows.map(r => r[j]);
    const m = col.reduce((s, v) => s + v, 0) / n;
    const std = Math.sqrt(col.reduce((s, v) => s + (v - m) ** 2, 0) / n);
    colMean.push(m);
    colStd.push(std > 1e-12 ? std : 1);
  }
  const Z = rows.map(row => row.map((v, j) => (v - colMean[j]) / colStd[j]));

  // Compute correlation matrix (since data is standardized, cov = corr)
  const R = [];
  for (let i = 0; i < p; i++) {
    R[i] = [];
    for (let j = 0; j < p; j++) {
      let sum = 0;
      for (let k = 0; k < n; k++) {
        sum += Z[k][i] * Z[k][j];
      }
      R[i][j] = sum / n;
    }
  }

  // Power iteration for top 3 eigenvalues/eigenvectors
  function powerIteration(matrix, dim, maxIter) {
    let v = Array(dim).fill(0).map((_, i) => (i === 0 ? 1 : 0.5 / (i + 1)));
    let norm = Math.sqrt(v.reduce((s, x) => s + x * x, 0));
    v = v.map(x => x / norm);
    let eigenvalue = 0;
    for (let iter = 0; iter < maxIter; iter++) {
      const w = Array(dim).fill(0);
      for (let i = 0; i < dim; i++) {
        for (let j = 0; j < dim; j++) {
          w[i] += matrix[i][j] * v[j];
        }
      }
      eigenvalue = v.reduce((s, vi, i) => s + vi * w[i], 0);
      norm = Math.sqrt(w.reduce((s, x) => s + x * x, 0));
      if (norm < 1e-12) break;
      v = w.map(x => x / norm);
    }
    return { eigenvalue, eigenvector: v };
  }

  const numFactors = Math.min(3, p);
  const eigenvalues = [];
  const eigenvectors = [];
  let M = R.map(row => [...row]); // copy

  for (let f = 0; f < numFactors; f++) {
    const result = powerIteration(M, p, 100);
    eigenvalues.push(result.eigenvalue);
    eigenvectors.push(result.eigenvector);
    // Deflate
    const lam = result.eigenvalue;
    const vec = result.eigenvector;
    M = M.map((row, i) => row.map((val, j) => val - lam * vec[i] * vec[j]));
  }

  // Total variance = trace of R (= p for standardized data)
  let traceR = 0;
  for (let i = 0; i < p; i++) traceR += R[i][i];

  // Factor loadings = eigenvector * sqrt(eigenvalue)
  const loadings = fields.map((field, j) => {
    const factorLoadings = [];
    for (let f = 0; f < numFactors; f++) {
      factorLoadings.push(round(eigenvectors[f][j] * Math.sqrt(Math.abs(eigenvalues[f])), 4));
    }
    return { field, factors: factorLoadings };
  });

  // Communalities = sum of squared loadings per field
  const communalities = fields.map((field, j) => {
    let comm = 0;
    for (let f = 0; f < numFactors; f++) {
      const loading = eigenvectors[f][j] * Math.sqrt(Math.abs(eigenvalues[f]));
      comm += loading * loading;
    }
    return { field, value: round(comm, 4) };
  });

  // Label factors based on which fields load highest
  function labelFactor(factorIdx) {
    const absLoadings = fields.map((f, j) => ({
      field: f,
      absLoad: Math.abs(eigenvectors[factorIdx][j] * Math.sqrt(Math.abs(eigenvalues[factorIdx]))),
    }));
    absLoadings.sort((a, b) => b.absLoad - a.absLoad);
    const top1 = absLoadings[0].field;
    const top2 = absLoadings.length > 1 ? absLoadings[1].field : null;

    const recurringFields = ['pest365', 'pest365Plus'];
    const serviceFields = ['oneTime', 'initialServiceVisit'];

    if (recurringFields.includes(top1) && recurringFields.includes(top2)) return 'Price Level';
    if (serviceFields.includes(top1) && serviceFields.includes(top2)) return 'Service Premium';
    if (top1 === 'mosquitoTickControl' || top2 === 'mosquitoTickControl') return 'Regional Effect';
    return `Factor ${factorIdx + 1}`;
  }

  const factors = [];
  for (let f = 0; f < numFactors; f++) {
    factors.push({
      id: f,
      label: labelFactor(f),
      varianceExplained: round(traceR > 1e-12 ? eigenvalues[f] / traceR : 0, 4),
    });
  }

  const totalVarianceExplained = round(
    traceR > 1e-12 ? eigenvalues.reduce((s, v) => s + v, 0) / traceR : 0, 4
  );

  return {
    factors,
    loadings,
    communalities,
    totalVarianceExplained,
  };
}

// ---------------------------------------------------------------------------
// Relative Strength Index (RSI) for average pest365 series
// ---------------------------------------------------------------------------

function computeRelativeStrengthIndexData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 3) return null;

  // Extract average pest365 per snapshot date
  const prices = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) vals.push(v);
    }
    prices.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
  }

  // Forward/backward fill nulls
  for (let i = 1; i < prices.length; i++) {
    if (prices[i] == null) prices[i] = prices[i - 1];
  }
  for (let i = prices.length - 2; i >= 0; i--) {
    if (prices[i] == null) prices[i] = prices[i + 1];
  }
  if (prices.every(v => v == null)) return null;

  // Compute price changes between consecutive periods
  const changes = [];
  for (let i = 1; i < prices.length; i++) {
    changes.push(prices[i] - prices[i - 1]);
  }

  const period = Math.min(14, changes.length);
  const gains = changes.map(c => (c > 0 ? c : 0));
  const losses = changes.map(c => (c < 0 ? -c : 0));

  // Compute RSI using EMA
  const rsi = [];
  const alpha = 2 / (period + 1);

  // First RSI point uses simple average
  let avgGain = gains.slice(0, period).reduce((s, v) => s + v, 0) / period;
  let avgLoss = losses.slice(0, period).reduce((s, v) => s + v, 0) / period;

  // Fill initial points with simple-average RSI where possible
  for (let i = 0; i < Math.min(period, changes.length); i++) {
    const simpleGain = gains.slice(0, i + 1).reduce((s, v) => s + v, 0) / (i + 1);
    const simpleLoss = losses.slice(0, i + 1).reduce((s, v) => s + v, 0) / (i + 1);
    if (simpleLoss === 0) {
      rsi.push(simpleGain === 0 ? 50 : 100);
    } else {
      const rs = simpleGain / simpleLoss;
      rsi.push(round(100 - (100 / (1 + rs)), 2));
    }
  }

  // Continue with EMA for remaining points
  for (let i = period; i < changes.length; i++) {
    avgGain = alpha * gains[i] + (1 - alpha) * avgGain;
    avgLoss = alpha * losses[i] + (1 - alpha) * avgLoss;
    if (avgLoss === 0) {
      rsi.push(avgGain === 0 ? 50 : 100);
    } else {
      const rs = avgGain / avgLoss;
      rsi.push(round(100 - (100 / (1 + rs)), 2));
    }
  }

  const currentRSI = rsi.length > 0 ? rsi[rsi.length - 1] : 50;
  const signal = currentRSI > 70 ? 'overbought' : currentRSI < 30 ? 'oversold' : 'neutral';

  // Detect divergences over last 3 periods
  const divergences = [];
  const rsiLen = rsi.length;
  const priceLen = prices.length;
  if (rsiLen >= 3 && priceLen >= 3) {
    const recentPrices = prices.slice(priceLen - 3);
    const recentRSI = rsi.slice(rsiLen - 3);
    const priceRising = recentPrices[2] > recentPrices[0];
    const priceFalling = recentPrices[2] < recentPrices[0];
    const rsiRising = recentRSI[2] > recentRSI[0];
    const rsiFalling = recentRSI[2] < recentRSI[0];

    if (priceRising && rsiFalling) {
      divergences.push({
        date: dates[dates.length - 1],
        type: 'bearish',
        description: 'Price rising while RSI falling — bearish divergence',
      });
    }
    if (priceFalling && rsiRising) {
      divergences.push({
        date: dates[dates.length - 1],
        type: 'bullish',
        description: 'Price falling while RSI rising — bullish divergence',
      });
    }
  }

  return {
    dates: dates.slice(1),
    rsi,
    field: 'pest365',
    periods: 14,
    currentRSI,
    signal,
    divergences,
  };
}

// ---------------------------------------------------------------------------
// Performance Attribution (decompose total price change into factors)
// ---------------------------------------------------------------------------

function computePerformanceAttributionData(snapshots, addresses) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 2 || !addresses || addresses.length === 0) return null;

  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];
  const firstSnap = snapshots[firstDate];
  const lastSnap = snapshots[lastDate];

  // Map addressId -> state
  const addrState = {};
  for (const addr of addresses) {
    addrState[addr.addressId] = (addr.state || '').toUpperCase();
  }

  // Compute first and last average pest365
  const firstVals = [];
  const lastVals = [];
  for (const addrId of Object.keys(firstSnap)) {
    const v = firstSnap[addrId]?.pest365;
    if (v != null && !isNaN(v)) firstVals.push(v);
  }
  for (const addrId of Object.keys(lastSnap)) {
    const v = lastSnap[addrId]?.pest365;
    if (v != null && !isNaN(v)) lastVals.push(v);
  }

  const firstAvg = firstVals.length > 0 ? firstVals.reduce((s, v) => s + v, 0) / firstVals.length : null;
  const lastAvg = lastVals.length > 0 ? lastVals.reduce((s, v) => s + v, 0) / lastVals.length : null;
  if (firstAvg == null || lastAvg == null || firstAvg === 0) return null;

  const totalChange = ((lastAvg - firstAvg) / firstAvg) * 100;

  // --- State Effect ---
  // Group addresses by state, compute each state's contribution
  const stateAddrs = {};
  const totalAddresses = addresses.length;
  for (const addr of addresses) {
    const st = (addr.state || 'UNKNOWN').toUpperCase();
    if (!stateAddrs[st]) stateAddrs[st] = [];
    stateAddrs[st].push(addr.addressId);
  }

  const byState = [];
  let stateEffectSum = 0;
  const overallAvgChange = lastAvg - firstAvg;

  for (const [state, addrIds] of Object.entries(stateAddrs)) {
    const stateWeight = addrIds.length / totalAddresses;
    // State-level average change
    const stateFirstVals = [];
    const stateLastVals = [];
    for (const addrId of addrIds) {
      const fv = firstSnap[addrId]?.pest365;
      const lv = lastSnap[addrId]?.pest365;
      if (fv != null && !isNaN(fv)) stateFirstVals.push(fv);
      if (lv != null && !isNaN(lv)) stateLastVals.push(lv);
    }
    const stateFirstAvg = stateFirstVals.length > 0 ? stateFirstVals.reduce((s, v) => s + v, 0) / stateFirstVals.length : 0;
    const stateLastAvg = stateLastVals.length > 0 ? stateLastVals.reduce((s, v) => s + v, 0) / stateLastVals.length : 0;
    const stateAvgChange = stateLastAvg - stateFirstAvg;

    const contribution = overallAvgChange !== 0
      ? (stateAvgChange / overallAvgChange) * stateWeight * totalChange
      : 0;
    stateEffectSum += contribution;
    byState.push({
      state,
      contribution: round(contribution, 4),
      addresses: addrIds.length,
    });
  }
  byState.sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));

  // --- Field Mix Effect ---
  const fieldChanges = {};
  const analyzeFields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  let sumAllFieldChanges = 0;

  for (const f of analyzeFields) {
    const fFirst = [];
    const fLast = [];
    for (const addrId of Object.keys(firstSnap)) {
      const v = firstSnap[addrId]?.[f];
      if (v != null && !isNaN(v)) fFirst.push(v);
    }
    for (const addrId of Object.keys(lastSnap)) {
      const v = lastSnap[addrId]?.[f];
      if (v != null && !isNaN(v)) fLast.push(v);
    }
    const fFirstAvg = fFirst.length > 0 ? fFirst.reduce((s, v) => s + v, 0) / fFirst.length : 0;
    const fLastAvg = fLast.length > 0 ? fLast.reduce((s, v) => s + v, 0) / fLast.length : 0;
    fieldChanges[f] = Math.abs(fLastAvg - fFirstAvg);
    sumAllFieldChanges += fieldChanges[f];
  }

  const byField = [];
  let fieldEffectSum = 0;
  for (const f of analyzeFields) {
    const contribution = sumAllFieldChanges > 0
      ? (fieldChanges[f] / sumAllFieldChanges) * totalChange
      : 0;
    fieldEffectSum += contribution;
    byField.push({ field: f, contribution: round(contribution, 4) });
  }

  // --- Interaction (residual) ---
  const interaction = totalChange - stateEffectSum - fieldEffectSum;

  const attributions = [
    { factor: 'State Effect', contribution: round(stateEffectSum, 4), percent: round(totalChange !== 0 ? (stateEffectSum / totalChange) * 100 : 0, 2), color: '#2563eb' },
    { factor: 'Field Mix', contribution: round(fieldEffectSum, 4), percent: round(totalChange !== 0 ? (fieldEffectSum / totalChange) * 100 : 0, 2), color: '#16a34a' },
    { factor: 'Interaction', contribution: round(interaction, 4), percent: round(totalChange !== 0 ? (interaction / totalChange) * 100 : 0, 2), color: '#f59e0b' },
  ];

  return {
    totalChange: round(totalChange, 4),
    attributions,
    byState,
    byField,
    period: { from: firstDate, to: lastDate },
  };
}

// ---------------------------------------------------------------------------
// Address Similarity (cosine similarity matrix + clustering)
// ---------------------------------------------------------------------------

function computeAddressSimilarityData(addresses, historyByAddress) {
  if (!addresses || addresses.length < 3) return null;

  const hist = historyByAddress || {};

  // Limit to first 50 addresses for performance
  const limitedAddresses = addresses.slice(0, 50);

  // Build feature vectors: [current pest365, pest365Plus, oneTime, ISV, avg pest365 history, volatility]
  const featureRows = [];
  const validAddresses = [];

  for (const addr of limitedAddresses) {
    const p365 = parsePrice(addr.pest365);
    const p365p = parsePrice(addr.pest365Plus);
    const ot = parsePrice(addr.oneTime);
    const isv = parsePrice(addr.initialServiceVisit);
    if (p365 == null || p365p == null || ot == null) continue;

    const entries = hist[addr.addressId];
    let avgHist = p365;
    let volatility = 0;
    if (entries && entries.length > 1) {
      const sorted = [...entries].sort(
        (a, b) => new Date(getTimestamp(a)) - new Date(getTimestamp(b))
      );
      const histPrices = sorted.map(e => getHistoryPrice(e, 'pest365')).filter(v => v != null);
      if (histPrices.length > 0) {
        avgHist = histPrices.reduce((s, v) => s + v, 0) / histPrices.length;
        if (histPrices.length > 1) {
          const hMean = avgHist;
          const hStd = Math.sqrt(histPrices.reduce((s, v) => s + (v - hMean) ** 2, 0) / histPrices.length);
          volatility = hMean > 1e-12 ? hStd / hMean : 0;
        }
      }
    }

    featureRows.push([p365, p365p, ot, isv != null ? isv : 0, avgHist, volatility]);
    validAddresses.push(addr);
  }

  const n = featureRows.length;
  if (n < 3) return null;
  const dim = featureRows[0].length;

  // Standardize features
  const fMean = [];
  const fStd = [];
  for (let j = 0; j < dim; j++) {
    const col = featureRows.map(r => r[j]);
    const m = col.reduce((s, v) => s + v, 0) / n;
    const std = Math.sqrt(col.reduce((s, v) => s + (v - m) ** 2, 0) / n);
    fMean.push(m);
    fStd.push(std > 1e-12 ? std : 1);
  }
  const Z = featureRows.map(row => row.map((v, j) => (v - fMean[j]) / fStd[j]));

  // Compute norms
  const norms = Z.map(row => {
    const sumSq = row.reduce((s, v) => s + v * v, 0);
    return Math.sqrt(sumSq);
  });

  // Compute pairwise cosine similarity, normalize to 0-1
  const matrix = [];
  for (let i = 0; i < n; i++) {
    matrix[i] = [];
    for (let j = 0; j < n; j++) {
      if (i === j) {
        matrix[i][j] = 1;
      } else if (j < i) {
        matrix[i][j] = matrix[j][i];
      } else {
        let dot = 0;
        for (let k = 0; k < dim; k++) {
          dot += Z[i][k] * Z[j][k];
        }
        const denom = norms[i] * norms[j];
        const cosSim = denom > 1e-12 ? dot / denom : 0;
        // Normalize from [-1,1] to [0,1]
        matrix[i][j] = round((cosSim + 1) / 2, 4);
      }
    }
  }

  // Build labels
  const labels = validAddresses.map(a => {
    const city = a.city || '';
    const state = (a.state || '').toUpperCase();
    return `${city}, ${state}`;
  });
  const addressIds = validAddresses.map(a => a.addressId);

  // Find top 10 most similar and top 10 most dissimilar pairs
  const allPairs = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      allPairs.push({ i, j, similarity: matrix[i][j], label1: labels[i], label2: labels[j] });
    }
  }
  allPairs.sort((a, b) => b.similarity - a.similarity);
  const topPairs = allPairs.slice(0, 10);
  const bottomPairs = allPairs.slice(-10).reverse();

  // Simple clustering: group addresses with similarity > 0.8
  const visited = new Set();
  const clusters = [];
  let clusterId = 0;

  for (let i = 0; i < n; i++) {
    if (visited.has(i)) continue;
    const members = [i];
    visited.add(i);
    for (let j = i + 1; j < n; j++) {
      if (visited.has(j)) continue;
      if (matrix[i][j] > 0.8) {
        members.push(j);
        visited.add(j);
      }
    }
    const clusterLabel = members.length > 1
      ? `Cluster ${clusterId + 1} (${members.length} addresses)`
      : labels[members[0]];
    clusters.push({ id: clusterId, members, label: clusterLabel });
    clusterId++;
  }

  return {
    labels,
    addressIds,
    matrix,
    clusters,
    topPairs,
    bottomPairs,
  };
}

// ---------------------------------------------------------------------------
// Signal-Noise Decomposition (EMA signal + noise residual)
// ---------------------------------------------------------------------------

function computeSignalNoiseData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 4) return null;

  // Extract average pest365 per snapshot date
  const observed = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) vals.push(v);
    }
    observed.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
  }

  // Forward/backward fill nulls
  for (let i = 1; i < observed.length; i++) {
    if (observed[i] == null) observed[i] = observed[i - 1];
  }
  for (let i = observed.length - 2; i >= 0; i--) {
    if (observed[i] == null) observed[i] = observed[i + 1];
  }
  if (observed.every(v => v == null)) return null;

  const N = observed.length;
  const alpha = 0.3;

  // Signal: EMA
  const signal = [observed[0]];
  for (let t = 1; t < N; t++) {
    signal.push(alpha * observed[t] + (1 - alpha) * signal[t - 1]);
  }

  // Noise: observed - signal
  const noise = observed.map((v, i) => round(v - signal[i], 6));

  // Rolling SNR with window of 5
  const window = 5;
  const snr = [];
  for (let t = 0; t < N; t++) {
    if (t < window - 1) {
      snr.push(null);
      continue;
    }
    const sigChanges = [];
    const noiseWindow = [];
    for (let w = t - window + 1; w <= t; w++) {
      if (w > 0) sigChanges.push(signal[w] - signal[w - 1]);
      noiseWindow.push(noise[w]);
    }
    const sigStd = stdDev(sigChanges);
    const noiseStd = stdDev(noiseWindow);
    const epsilon = 1e-12;
    snr.push(round(noiseStd > epsilon ? sigStd / noiseStd : sigStd > 0 ? 100 : 0, 4));
  }

  // Overall SNR
  const overallSignalStd = stdDev(signal);
  const overallNoiseStd = stdDev(noise);
  const overallSNR = round(overallNoiseStd > 1e-12 ? overallSignalStd / overallNoiseStd : 100, 4);
  const signalStrength = overallSNR > 10 ? 'strong' : overallSNR >= 5 ? 'moderate' : 'weak';

  return {
    dates,
    observed: observed.map(v => round(v, 4)),
    signal: signal.map(v => round(v, 4)),
    noise,
    snr,
    field: 'pest365',
    overallSNR,
    noiseStd: round(overallNoiseStd, 4),
    signalStrength,
  };
}

// ---------------------------------------------------------------------------
// Event Study Analysis (abnormal returns around significant price events)
// ---------------------------------------------------------------------------

function computeEventStudyData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 5) return null;

  // Extract average pest365 per snapshot
  const prices = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) vals.push(v);
    }
    prices.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
  }

  // Forward/backward fill
  for (let i = 1; i < prices.length; i++) {
    if (prices[i] == null) prices[i] = prices[i - 1];
  }
  for (let i = prices.length - 2; i >= 0; i--) {
    if (prices[i] == null) prices[i] = prices[i + 1];
  }
  if (prices.every(v => v == null)) return null;

  // Compute changes
  const changes = [];
  for (let i = 1; i < prices.length; i++) {
    changes.push(prices[i] - prices[i - 1]);
  }
  if (changes.length < 3) return null;

  const changeStd = stdDev(changes);
  const changeMean = mean(changes);
  const threshold = 2 * changeStd;

  // Detect events: periods where |change| > 2 * std
  let eventIndices = []; // indices into changes array (which is offset by 1 from dates)
  for (let i = 0; i < changes.length; i++) {
    if (Math.abs(changes[i]) > threshold) {
      eventIndices.push(i);
    }
  }

  // If fewer than 2, use top 2 largest absolute changes
  if (eventIndices.length < 2) {
    const sorted = changes.map((c, i) => ({ i, absC: Math.abs(c) })).sort((a, b) => b.absC - a.absC);
    eventIndices = sorted.slice(0, 2).map(e => e.i).sort((a, b) => a - b);
  }

  const windowSize = 3;
  const expectedReturn = changeMean; // mean of all non-event changes would be ideal, but use overall mean

  const events = [];
  const allEventDayARs = [];

  for (const eventIdx of eventIndices) {
    const eventDateIdx = eventIdx + 1; // index into dates/prices
    const windowDates = [];
    const abnormalReturns = [];
    const cumulativeAR = [];
    let cumSum = 0;

    for (let offset = -windowSize; offset <= windowSize; offset++) {
      const changeIdx = eventIdx + offset;
      const dateIdx = eventDateIdx + offset;
      if (dateIdx < 0 || dateIdx >= dates.length) continue;

      windowDates.push(offset);
      const actualChange = changeIdx >= 0 && changeIdx < changes.length ? changes[changeIdx] : 0;
      const ar = actualChange - expectedReturn;
      abnormalReturns.push(round(ar, 4));
      cumSum += ar;
      cumulativeAR.push(round(cumSum, 4));

      if (offset === 0) {
        allEventDayARs.push(ar);
      }
    }

    events.push({
      label: `Event at ${dates[eventDateIdx]}`,
      eventDate: dates[eventDateIdx],
      windowDates,
      abnormalReturns,
      cumulativeAR,
    });
  }

  // Average AR and CAR across events
  const maxWindowLen = 2 * windowSize + 1;
  const averageAR = [];
  const averageCAR = [];
  for (let w = 0; w < maxWindowLen; w++) {
    const arsAtW = events.map(e => e.abnormalReturns[w]).filter(v => v != null);
    const carsAtW = events.map(e => e.cumulativeAR[w]).filter(v => v != null);
    averageAR.push(arsAtW.length > 0 ? round(arsAtW.reduce((s, v) => s + v, 0) / arsAtW.length, 4) : 0);
    averageCAR.push(carsAtW.length > 0 ? round(carsAtW.reduce((s, v) => s + v, 0) / carsAtW.length, 4) : 0);
  }

  // T-test on event-day ARs
  const nEvents = allEventDayARs.length;
  const arMean = nEvents > 0 ? allEventDayARs.reduce((s, v) => s + v, 0) / nEvents : 0;
  const arStd = stdDev(allEventDayARs);
  const tStatistic = nEvents > 1 && arStd > 1e-12
    ? round(arMean / (arStd / Math.sqrt(nEvents)), 4)
    : 0;
  // Rough p-value approximation
  const pValue = round(2 * Math.exp(-0.7 * tStatistic * tStatistic), 4);

  return {
    events,
    averageCAR,
    averageAR,
    windowSize,
    significantAtEvent: Math.abs(tStatistic) > 1.96,
    tStatistic,
    pValue: Math.min(pValue, 1),
  };
}

// ---------------------------------------------------------------------------
// Counterfactual Analysis (extrapolate pre-changepoint trend)
// ---------------------------------------------------------------------------

function computeCounterfactualData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 5) return null;

  // Extract average pest365 per snapshot
  const actual = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) vals.push(v);
    }
    actual.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
  }

  // Forward/backward fill
  for (let i = 1; i < actual.length; i++) {
    if (actual[i] == null) actual[i] = actual[i - 1];
  }
  for (let i = actual.length - 2; i >= 0; i--) {
    if (actual[i] == null) actual[i] = actual[i + 1];
  }
  if (actual.every(v => v == null)) return null;

  const N = actual.length;

  // Fit linear regression on first half
  const halfN = Math.max(Math.floor(N / 2), 2);

  // Simple linear regression: y = intercept + slope * t
  let sumT = 0, sumY = 0, sumTY = 0, sumTT = 0;
  for (let t = 0; t < halfN; t++) {
    sumT += t;
    sumY += actual[t];
    sumTY += t * actual[t];
    sumTT += t * t;
  }
  const denom = halfN * sumTT - sumT * sumT;
  const slope = denom !== 0 ? (halfN * sumTY - sumT * sumY) / denom : 0;
  const intercept = (sumY - slope * sumT) / halfN;

  // Compute trend line for first half and find residuals
  const residuals = [];
  for (let t = 0; t < halfN; t++) {
    const predicted = intercept + slope * t;
    residuals.push(actual[t] - predicted);
  }
  const residualStd = stdDev(residuals);

  // Find changepoint: the point with the largest deviation from the full trend
  let maxDeviation = 0;
  let changePointIndex = halfN; // default to midpoint
  for (let t = 0; t < N; t++) {
    const predicted = intercept + slope * t;
    const deviation = Math.abs(actual[t] - predicted);
    if (deviation > maxDeviation) {
      maxDeviation = deviation;
      changePointIndex = t;
    }
  }

  // Counterfactual: extrapolate the linear trend for all dates
  const counterfactual = [];
  const upper = [];
  const lower = [];
  for (let t = 0; t < N; t++) {
    const cf = intercept + slope * t;
    counterfactual.push(round(cf, 4));
    upper.push(round(cf + 1.96 * residualStd, 4));
    lower.push(round(cf - 1.96 * residualStd, 4));
  }

  // Gap = actual - counterfactual (0 before changepoint)
  const gap = [];
  let cumulativeGap = 0;
  for (let t = 0; t < N; t++) {
    if (t < changePointIndex) {
      gap.push(0);
    } else {
      const g = actual[t] - counterfactual[t];
      gap.push(round(g, 4));
      cumulativeGap += g;
    }
  }

  const actualMean = actual.reduce((s, v) => s + v, 0) / N;
  const cumulativeGapPercent = actualMean !== 0 ? round((cumulativeGap / actualMean) * 100, 4) : 0;

  return {
    dates,
    actual: actual.map(v => round(v, 4)),
    counterfactual,
    changePoint: dates[changePointIndex],
    changePointIndex,
    gap,
    cumulativeGap: round(cumulativeGap, 4),
    cumulativeGapPercent,
    field: 'pest365',
    confidence: { upper, lower },
  };
}

// ---------------------------------------------------------------------------
// Main Export
// ---------------------------------------------------------------------------

export function computeAnalytics(addresses, historyByAddress) {
  if (!addresses || addresses.length === 0) {
    return emptyResult();
  }

  const hist = historyByAddress || {};

  // Pre-compute sorted histories (chronological) for every address
  const sortedHistories = {};
  for (const addrId of Object.keys(hist)) {
    const entries = hist[addrId];
    if (!entries || entries.length === 0) continue;
    sortedHistories[addrId] = [...entries].sort(
      (a, b) => new Date(getTimestamp(a)) - new Date(getTimestamp(b))
    );
  }

  // Collect all unique scrape dates (YYYY-MM-DD) across all addresses
  const allDatesSet = new Set();
  for (const entries of Object.values(sortedHistories)) {
    for (const e of entries) {
      const d = toDateStr(getTimestamp(e));
      if (d) allDatesSet.add(d);
    }
  }
  const allDates = [...allDatesSet].sort();

  // Build per-date snapshots: for each date, the prices each address had
  // (use latest entry on that date if multiple scrapes same day)
  const rawSnapshots = buildSnapshots(sortedHistories, allDates);

  // Normalize midnight-split batches: a single scrape that starts before
  // midnight and finishes after produces two dates — merge them.
  const normalized = mergeAdjacentDates(allDates, rawSnapshots);
  const snapshotsByDate = normalized.snapshots;
  const mergedDates = normalized.dates;

  // Build an enriched array variant: snapshotsByDateArr[date] = [{ addressId, state, city, ...prices }]
  // Some compute functions expect arrays instead of the { addressId: {prices} } object format.
  const addressLookup = {};
  for (const addr of addresses) {
    if (addr.addressId) addressLookup[addr.addressId] = addr;
  }
  const snapshotsByDateArr = {};
  for (const [date, addrMap] of Object.entries(snapshotsByDate)) {
    snapshotsByDateArr[date] = Object.entries(addrMap).map(([addressId, prices]) => ({
      addressId,
      state: addressLookup[addressId]?.state || null,
      city: addressLookup[addressId]?.city || null,
      address: addressLookup[addressId]?.address || null,
      ...prices,
    }));
  }

  // Compute all sections (use mergedDates everywhere instead of allDates)
  // Safe wrapper: isolate each compute so one crash doesn't kill the rest
  function safe(fn, label) {
    try { return fn(); } catch (e) { console.error(`[analytics] ${label} failed:`, e); return null; }
  }

  const priceIndex = safe(() => computePriceIndex(snapshotsByDate, mergedDates, addresses), 'priceIndex');
  const kpis = safe(() => computeKPIs(addresses, sortedHistories, mergedDates, snapshotsByDate), 'kpis');
  const stateAnalysis = safe(() => computeStateAnalysis(addresses, sortedHistories), 'stateAnalysis');
  const priceArchitecture = safe(() => computePriceArchitecture(addresses, snapshotsByDate, mergedDates), 'priceArchitecture');
  const changeEvents = safe(() => computeChangeEvents(sortedHistories, mergedDates, snapshotsByDate, addresses), 'changeEvents');
  const heatmapData = safe(() => computeHeatmapData(addresses, sortedHistories, mergedDates, snapshotsByDate), 'heatmapData');
  const priceTiers = safe(() => computePriceTiers(addresses), 'priceTiers');
  const correlations = safe(() => computeCorrelations(addresses), 'correlations');
  const serviceAvailability = safe(() => computeServiceAvailability(addresses, sortedHistories), 'serviceAvailability');
  const anomalies = safe(() => computeAnomalies(addresses), 'anomalies');
  const weeklyTrends = safe(() => computeWeeklyTrends(snapshotsByDate, mergedDates), 'weeklyTrends');
  const distributions = safe(() => computeDistributions(addresses, snapshotsByDate, mergedDates), 'distributions');
  const rankData = safe(() => computeRankData(snapshotsByDate, mergedDates, addresses), 'rankData');
  const geoData = safe(() => computeGeoData(addresses, sortedHistories), 'geoData');
  const calendarData = safe(() => computeCalendarData(snapshotsByDate, mergedDates, addresses), 'calendarData');
  const candlestickData = safe(() => computeCandlestickData(snapshotsByDate, mergedDates), 'candlestickData');
  const dispersionData = safe(() => computeDispersionData(snapshotsByDate, mergedDates), 'dispersionData');
  const ridgelineData = safe(() => computeRidgelineData(snapshotsByDate, mergedDates), 'ridgelineData');
  const ragStatus = safe(() => computeRAGStatus(addresses, sortedHistories, mergedDates, snapshotsByDate), 'ragStatus');
  const priceWarIndicators = safe(() => computePriceWarIndicators(snapshotsByDate, mergedDates, addresses, changeEvents), 'priceWarIndicators');
  const smallMultiplesData = safe(() => computeSmallMultiplesData(addresses, sortedHistories, mergedDates, snapshotsByDate), 'smallMultiplesData');
  const sankeyData = safe(() => computeSankeyData(snapshotsByDate, mergedDates), 'sankeyData');
  const forecastData = safe(() => computeForecastData(snapshotsByDate, mergedDates), 'forecastData');
  const radarData = safe(() => computeRadarData(addresses), 'radarData');
  const percentileBands = safe(() => computePercentileBands(snapshotsByDate, mergedDates), 'percentileBands');
  const topMovers = safe(() => computeTopMovers(addresses, sortedHistories, mergedDates, snapshotsByDate), 'topMovers');
  const waterfallData = safe(() => computeWaterfallData(snapshotsByDate, mergedDates), 'waterfallData');
  const boxPlotData = safe(() => computeBoxPlotData(snapshotsByDate, mergedDates), 'boxPlotData');
  const convergenceData = safe(() => computeConvergenceData(snapshotsByDate, mergedDates), 'convergenceData');
  const donutData = safe(() => computeDonutData(addresses, sortedHistories), 'donutData');
  const healthScore = safe(() => computeHealthScore(addresses, sortedHistories, mergedDates, snapshotsByDate), 'healthScore');
  const fieldComparisonData = safe(() => computeFieldComparisonData(addresses), 'fieldComparisonData');
  const slopeData = safe(() => computeSlopeData(addresses, snapshotsByDate, mergedDates), 'slopeData');
  const scatterData = safe(() => computeScatterData(addresses, sortedHistories), 'scatterData');
  const treemapData = safe(() => computeTreemapData(addresses), 'treemapData');
  const parallelData = safe(() => computeParallelData(addresses), 'parallelData');
  const dumbbellData = safe(() => computeDumbbellData(addresses), 'dumbbellData');
  const horizonData = safe(() => computeHorizonData(snapshotsByDate, mergedDates), 'horizonData');
  const volatilityMatrix = safe(() => computeVolatilityMatrix(addresses, sortedHistories, mergedDates, snapshotsByDate), 'volatilityMatrix');
  const lollipopData = safe(() => computeLollipopData(addresses), 'lollipopData');
  const beeswarmData = safe(() => computeBeeswarmData(addresses), 'beeswarmData');
  const bandwidthData = safe(() => computeBandwidthData(snapshotsByDate, mergedDates), 'bandwidthData');
  const streamData = safe(() => computeStreamData(snapshotsByDate, mergedDates, addresses), 'streamData');
  const velocityData = safe(() => computeVelocityData(snapshotsByDate, mergedDates), 'velocityData');
  const marimekkoData = safe(() => computeMarimekkoData(addresses), 'marimekkoData');
  const cumulativeReturnData = safe(() => computeCumulativeReturnData(snapshotsByDate, mergedDates, addresses), 'cumulativeReturnData');
  const dotMatrixData = safe(() => computeDotMatrixData(addresses, ragStatus), 'dotMatrixData');
  const bulletData = safe(() => computeBulletData(addresses), 'bulletData');
  const corridorData = safe(() => computeCorridorData(snapshotsByDate, mergedDates, addresses), 'corridorData');
  const networkData = safe(() => computeNetworkData(snapshotsByDate, mergedDates, addresses), 'networkData');
  const ganttData = safe(() => computeGanttData(snapshotsByDate, mergedDates, addresses), 'ganttData');
  const priceGapData = safe(() => computePriceGapData(snapshotsByDate, mergedDates, addresses), 'priceGapData');
  const radialBarData = safe(() => computeRadialBarData(addresses), 'radialBarData');
  const priceIndexComparison = safe(() => computePriceIndexComparison(snapshotsByDate, mergedDates), 'priceIndexComparison');
  const histogramData = safe(() => computeHistogramData(addresses), 'histogramData');
  const polarAreaData = safe(() => computePolarAreaData(snapshotsByDate, mergedDates, addresses), 'polarAreaData');
  const sparkTableData = safe(() => computeSparkTableData(snapshotsByDate, mergedDates, addresses), 'sparkTableData');
  const areaStackData = safe(() => computeAreaStackData(snapshotsByDate, mergedDates), 'areaStackData');
  const scatterMatrixData = safe(() => computeScatterMatrixData(addresses), 'scatterMatrixData');
  const movingAverageData = safe(() => computeMovingAverageData(snapshotsByDate, mergedDates), 'movingAverageData');
  const treemapPricingData = safe(() => computeTreemapPricingData(addresses, sortedHistories), 'treemapPricingData');
  const sunburstData = safe(() => computeSunburstData(addresses, sortedHistories), 'sunburstData');
  const priceRangeData = safe(() => computePriceRangeData(addresses), 'priceRangeData');
  const zScoreData = safe(() => computeZScoreData(snapshotsByDate, mergedDates, addresses), 'zScoreData');
  const chordData = safe(() => computeChordData(snapshotsByDate, mergedDates, addresses), 'chordData');
  const heatmapGridData = safe(() => computeHeatmapGridData(addresses), 'heatmapGridData');
  const competitivePositionData = safe(() => computeCompetitivePositionData(snapshotsByDate, mergedDates, addresses), 'competitivePositionData');
  const miniDashboardData = safe(() => computeMiniDashboardData(snapshotsByDate, mergedDates, addresses), 'miniDashboardData');
  const violinData = safe(() => computeViolinData(addresses, snapshotsByDate), 'violinData');
  const momentumData = safe(() => computeMomentumData(snapshotsByDate), 'momentumData');
  const seasonalData = safe(() => computeSeasonalData(snapshotsByDate), 'seasonalData');
  const elasticityData = safe(() => computeElasticityData(addresses, snapshotsByDate), 'elasticityData');
  const rollingVolatilityData = safe(() => computeRollingVolatilityData(snapshotsByDate), 'rollingVolatilityData');
  const regimeData = safe(() => computeRegimeData(snapshotsByDate), 'regimeData');
  const anomalyTimelineData = safe(() => computeAnomalyTimelineData(addresses, snapshotsByDate), 'anomalyTimelineData');
  const pyramidData = safe(() => computePyramidData(addresses, snapshotsByDate), 'pyramidData');
  const decileData = safe(() => computeDecileData(snapshotsByDate), 'decileData');
  const retentionData = safe(() => computeRetentionData(snapshotsByDate), 'retentionData');
  const crossCorrelationData = safe(() => computeCrossCorrelationData(snapshotsByDate), 'crossCorrelationData');
  const quartileFlowData = safe(() => computeQuartileFlowData(snapshotsByDate), 'quartileFlowData');
  const varianceDecomposition = safe(() => computeVarianceDecomposition(addresses, snapshotsByDate), 'varianceDecomposition');
  const cohortData = safe(() => computeCohortData(addresses, snapshotsByDate), 'cohortData');
  const spreadTimelineData = safe(() => computeSpreadTimelineData(snapshotsByDate), 'spreadTimelineData');
  const stabilityGaugeData = safe(() => computeStabilityGaugeData(snapshotsByDate), 'stabilityGaugeData');
  const geographicClusterData = safe(() => computeGeographicClusterData(addresses, snapshotsByDate), 'geographicClusterData');
  const priceChangeDistribution = safe(() => computePriceChangeDistribution(snapshotsByDate), 'priceChangeDistribution');
  const stateRankingData = safe(() => computeStateRankingData(addresses, snapshotsByDate), 'stateRankingData');
  const distributionShiftData = safe(() => computeDistributionShiftData(snapshotsByDate), 'distributionShiftData');
  const targetData = safe(() => computeTargetData(addresses, snapshotsByDate), 'targetData');
  const indexGrowthData = safe(() => computeIndexGrowthData(addresses, snapshotsByDate), 'indexGrowthData');
  const addressOutlierData = safe(() => computeAddressOutlierData(addresses, snapshotsByDate), 'addressOutlierData');
  const volatilityHeatmapData = safe(() => computeVolatilityHeatmapData(addresses, snapshotsByDate), 'volatilityHeatmapData');
  const accelerationData = safe(() => computeAccelerationData(snapshotsByDate), 'accelerationData');
  const competitiveLandscapeData = safe(() => computeCompetitiveLandscapeData(addresses, snapshotsByDate), 'competitiveLandscapeData');
  const funnelData = safe(() => computeFunnelData(snapshotsByDate), 'funnelData');
  const elasticityHeatmapData = safe(() => computeElasticityHeatmapData(snapshotsByDateArr), 'elasticityHeatmapData');
  const trendRibbonsData = safe(() => computeTrendRibbonsData(snapshotsByDateArr), 'trendRibbonsData');
  const statePriceProfileData = safe(() => computeStatePriceProfileData(addresses, snapshotsByDateArr), 'statePriceProfileData');
  const priceMemoryData = safe(() => computePriceMemoryData(snapshotsByDateArr), 'priceMemoryData');
  const clusterDendrogramData = safe(() => computeClusterDendrogramData(addresses, snapshotsByDateArr), 'clusterDendrogramData');
  const pricePaceData = safe(() => computePricePaceData(snapshotsByDateArr), 'pricePaceData');
  const stickinessData = safe(() => computeStickinessData(snapshotsByDateArr), 'stickinessData');
  const surpriseData = safe(() => computeSurpriseData(snapshotsByDateArr), 'surpriseData');
  const cumulativeAnomalyData = safe(() => computeCumulativeAnomalyData(snapshotsByDateArr), 'cumulativeAnomalyData');
  const changepointData = safe(() => computeChangepointData(snapshotsByDateArr), 'changepointData');
  const ratioStabilityData = safe(() => computeRatioStabilityData(snapshotsByDateArr), 'ratioStabilityData');
  const mosquitoBoundaryData = safe(() => computeMosquitoBoundaryData(addresses, snapshotsByDateArr), 'mosquitoBoundaryData');
  const bucketMigrationData = safe(() => computeBucketMigrationData(snapshotsByDateArr), 'bucketMigrationData');
  const anomalyScorecardData = safe(() => computeAnomalyScorecardData(addresses, snapshotsByDateArr), 'anomalyScorecardData');
  const rankStabilityData = safe(() => computeRankStabilityData(snapshotsByDateArr), 'rankStabilityData');
  const deviationBenchmarkData = safe(() => computeDeviationBenchmarkData(addresses, snapshotsByDateArr), 'deviationBenchmarkData');
  const cusumData = safe(() => computeCUSUMData(snapshotsByDateArr), 'cusumData');
  const shewhartData = safe(() => computeShewhartData(snapshotsByDateArr), 'shewhartData');
  const ewmaData = safe(() => computeEWMAData(snapshotsByDateArr), 'ewmaData');
  const entropyData = safe(() => computeEntropyData(snapshotsByDateArr), 'entropyData');
  const benfordData = safe(() => computeBenfordData(snapshotsByDateArr), 'benfordData');
  const complexityData = safe(() => computeComplexityData(snapshotsByDateArr), 'complexityData');
  const lorenzCurveData = safe(() => computeLorenzCurveData(addresses), 'lorenzCurveData');
  const qqPlotData = safe(() => computeQQPlotData(addresses), 'qqPlotData');
  const herfindahlData = safe(() => computeHerfindahlData(snapshotsByDate, addresses), 'herfindahlData');
  const markovTransitionData = safe(() => computeMarkovTransitionData(snapshotsByDate), 'markovTransitionData');
  const monteCarloFanData = safe(() => computeMonteCarloFanData(snapshotsByDate), 'monteCarloFanData');
  const bayesianUpdateData = safe(() => computeBayesianUpdateData(snapshotsByDate), 'bayesianUpdateData');
  const grangerCausalityData = safe(() => computeGrangerCausalityData(snapshotsByDate), 'grangerCausalityData');
  const leadLagData = safe(() => computeLeadLagData(snapshotsByDate), 'leadLagData');
  const impulseResponseData = safe(() => computeImpulseResponseData(snapshotsByDate), 'impulseResponseData');
  const recurrencePlotData = safe(() => computeRecurrencePlotData(snapshotsByDate), 'recurrencePlotData');
  const runsTestData = safe(() => computeRunsTestData(snapshotsByDate), 'runsTestData');
  const spectralDensityData = safe(() => computeSpectralDensityData(snapshotsByDate), 'spectralDensityData');
  const waveletScalogramData = safe(() => computeWaveletScalogramData(snapshotsByDate), 'waveletScalogramData');
  const phasePortraitData = safe(() => computePhasePortraitData(snapshotsByDate), 'phasePortraitData');
  const lyapunovExponentData = safe(() => computeLyapunovExponentData(snapshotsByDate), 'lyapunovExponentData');
  const stateSyncData = safe(() => computeStateSyncData(snapshotsByDate, addresses), 'stateSyncData');
  const spectralCoherenceData = safe(() => computeSpectralCoherenceData(snapshotsByDate), 'spectralCoherenceData');
  const regressionDiscontinuityData = safe(() => computeRegressionDiscontinuityData(snapshotsByDate), 'regressionDiscontinuityData');
  const quantileRegressionData = safe(() => computeQuantileRegressionData(snapshotsByDate), 'quantileRegressionData');
  const residualsData = safe(() => computeResidualsData(snapshotsByDate), 'residualsData');
  const valueAtRiskData = safe(() => computeValueAtRiskData(snapshotsByDate), 'valueAtRiskData');
  const pcaBiplotData = safe(() => computePCABiplotData(addresses), 'pcaBiplotData');
  const tsneClusterData = safe(() => computeTSNEClusterData(addresses, historyByAddress), 'tsneClusterData');
  const factorLoadingsData = safe(() => computeFactorLoadingsData(addresses), 'factorLoadingsData');
  const rsiData = safe(() => computeRelativeStrengthIndexData(snapshotsByDate), 'rsiData');
  const performanceAttributionData = safe(() => computePerformanceAttributionData(snapshotsByDate, addresses), 'performanceAttributionData');
  const addressSimilarityData = safe(() => computeAddressSimilarityData(addresses, historyByAddress), 'addressSimilarityData');
  const signalNoiseData = safe(() => computeSignalNoiseData(snapshotsByDate), 'signalNoiseData');
  const eventStudyData = safe(() => computeEventStudyData(snapshotsByDate), 'eventStudyData');
  const counterfactualData = safe(() => computeCounterfactualData(snapshotsByDate), 'counterfactualData');
  const arimaForecastData = safe(() => computeARIMAForecastData(snapshotsByDate), 'arimaForecastData');
  const ensembleForecastData = safe(() => computeEnsembleForecastData(snapshotsByDate), 'ensembleForecastData');
  const priceHalfLifeData = safe(() => computePriceHalfLifeData(snapshotsByDate, addresses), 'priceHalfLifeData');
  const priceContagionData = safe(() => computePriceContagionData(snapshotsByDate, addresses), 'priceContagionData');
  const regimeTransitionData = safe(() => computeRegimeTransitionDiagramData(snapshotsByDate), 'regimeTransitionData');
  const informationRatioData = safe(() => computeInformationRatioData(snapshotsByDate, addresses), 'informationRatioData');
  const pricePathDivergenceData = safe(() => computePricePathDivergenceData(snapshotsByDate, addresses, historyByAddress), 'pricePathDivergenceData');
  const psychologicalPricingData = safe(() => computePsychologicalPricing(snapshotsByDate, addresses), 'psychologicalPricingData');
  const insights = safe(() => generateInsights(
    kpis, stateAnalysis, priceArchitecture, changeEvents, correlations, serviceAvailability, priceIndex, addresses
  ), 'insights');

  return {
    priceIndex,
    kpis,
    stateAnalysis,
    priceArchitecture,
    changeEvents,
    heatmapData,
    priceTiers,
    correlations,
    serviceAvailability,
    anomalies,
    insights,
    psychologicalPricingData,
    weeklyTrends,
    distributions,
    rankData,
    geoData,
    calendarData,
    candlestickData,
    dispersionData,
    ridgelineData,
    ragStatus,
    priceWarIndicators,
    smallMultiplesData,
    sankeyData,
    forecastData,
    radarData,
    percentileBands,
    topMovers,
    waterfallData,
    boxPlotData,
    convergenceData,
    donutData,
    healthScore,
    fieldComparisonData,
    slopeData,
    scatterData,
    treemapData,
    parallelData,
    dumbbellData,
    horizonData,
    volatilityMatrix,
    lollipopData,
    beeswarmData,
    bandwidthData,
    streamData,
    velocityData,
    marimekkoData,
    cumulativeReturnData,
    dotMatrixData,
    bulletData,
    corridorData,
    networkData,
    ganttData,
    priceGapData,
    radialBarData,
    priceIndexComparison,
    histogramData,
    polarAreaData,
    sparkTableData,
    areaStackData,
    scatterMatrixData,
    movingAverageData,
    movingAvgData: movingAverageData,
    treemapPricingData,
    sunburstData,
    priceRangeData,
    zScoreData,
    chordData,
    heatmapGridData,
    competitivePositionData,
    miniDashboardData,
    violinData,
    violinPlotData: violinData,
    momentumData,
    seasonalData,
    elasticityData,
    rollingVolatilityData,
    regimeData,
    anomalyTimelineData,
    pyramidData,
    decileData,
    retentionData,
    crossCorrelationData,
    quartileFlowData,
    varianceDecomposition,
    cohortData,
    spreadTimelineData,
    stabilityGaugeData,
    geographicClusterData,
    priceChangeDistribution,
    stateRankingData,
    distributionShiftData,
    targetData,
    indexGrowthData,
    addressOutlierData,
    volatilityHeatmapData,
    accelerationData,
    competitiveLandscapeData,
    funnelData,
    elasticityHeatmapData,
    trendRibbonsData,
    statePriceProfileData,
    priceMemoryData,
    clusterDendrogramData,
    pricePaceData,
    stickinessData,
    surpriseData,
    cumulativeAnomalyData,
    changepointData,
    ratioStabilityData,
    mosquitoBoundaryData,
    bucketMigrationData,
    anomalyScorecardData,
    rankStabilityData,
    deviationBenchmarkData,
    cusumData,
    shewhartData,
    ewmaData,
    entropyData,
    benfordData,
    complexityData,
    lorenzCurveData,
    qqPlotData,
    herfindahlData,
    markovTransitionData,
    monteCarloFanData,
    bayesianUpdateData,
    grangerCausalityData,
    leadLagData,
    impulseResponseData,
    recurrencePlotData,
    runsTestData,
    spectralDensityData,
    waveletScalogramData,
    phasePortraitData,
    lyapunovExponentData,
    stateSyncData,
    spectralCoherenceData,
    regressionDiscontinuityData,
    quantileRegressionData,
    residualsData,
    valueAtRiskData,
    pcaBiplotData,
    tsneClusterData,
    factorLoadingsData,
    rsiData,
    performanceAttributionData,
    addressSimilarityData,
    signalNoiseData,
    eventStudyData,
    counterfactualData,
    arimaForecastData,
    ensembleForecastData,
    priceHalfLifeData,
    priceContagionData,
    regimeTransitionData,
    informationRatioData,
    pricePathDivergenceData,
  };
}

// ---------------------------------------------------------------------------
// RAG Status (Red / Amber / Green per address)
// ---------------------------------------------------------------------------

function computeRAGStatus(addresses, sortedHistories, mergedDates, snapshotsByDate) {
  // Compute median data-point count across addresses for the "sparse data" amber check
  const dataPointCounts = addresses.map(addr => {
    const hist = sortedHistories[addr.addressId];
    return hist ? hist.length : 0;
  });
  const sortedCounts = [...dataPointCounts].sort((a, b) => a - b);
  const medianCount = sortedCounts.length > 0
    ? sortedCounts[Math.floor(sortedCounts.length / 2)]
    : 0;

  return addresses.map(addr => {
    const hist = sortedHistories[addr.addressId];
    const pest365 = parsePrice(addr.pest365);
    const pest365Plus = parsePrice(addr.pest365Plus);
    const oneTime = parsePrice(addr.oneTime);
    const initialServiceVisit = parsePrice(addr.initialServiceVisit);
    const mosquitoTickControl = parsePrice(addr.mosquitoTickControl);

    // Change percent: first vs last pest365 in history
    let changePercent = null;
    if (hist && hist.length >= 2) {
      const first = getHistoryPrice(hist[0], 'pest365');
      const last = getHistoryPrice(hist[hist.length - 1], 'pest365');
      if (first != null && last != null && first > 0) {
        changePercent = round(((last - first) / first) * 100);
      }
    }

    // Last scrape date
    let lastScrapeDate = null;
    if (hist && hist.length > 0) {
      lastScrapeDate = toDateStr(getTimestamp(hist[hist.length - 1]));
    }

    // Determine status
    let status = 'green';
    let statusReason = 'Stable — all data present and prices within normal range';

    // --- RED checks ---
    // Missing critical price fields
    if (pest365 == null || pest365Plus == null || oneTime == null) {
      status = 'red';
      const missing = [];
      if (pest365 == null) missing.push('pest365');
      if (pest365Plus == null) missing.push('pest365Plus');
      if (oneTime == null) missing.push('oneTime');
      statusReason = `Missing critical price data: ${missing.join(', ')}`;
    }
    // Large price swing (>15%)
    else if (changePercent != null && Math.abs(changePercent) > 15) {
      status = 'red';
      statusReason = `Large price swing: ${changePercent > 0 ? '+' : ''}${changePercent}% change from first to last scrape`;
    }
    // ISV data loss: was previously available but now null
    else if (initialServiceVisit == null && hist && hist.length >= 2) {
      let hadISV = false;
      for (const entry of hist) {
        if (getHistoryPrice(entry, 'initialServiceVisit') != null) {
          hadISV = true;
          break;
        }
      }
      if (hadISV) {
        status = 'red';
        statusReason = 'Initial Service Visit data lost — was previously available';
      }
    }

    // --- AMBER checks (only if not already red) ---
    if (status !== 'red') {
      // Medium price swing (5-15%)
      if (changePercent != null && Math.abs(changePercent) > 5 && Math.abs(changePercent) <= 15) {
        status = 'amber';
        statusReason = `Moderate price change: ${changePercent > 0 ? '+' : ''}${changePercent}% from first to last scrape`;
      }
      // MTC availability flip
      else if (hist && hist.length >= 2) {
        const firstMTCRaw = getHistoryRaw(hist[0], 'mosquitoTickControl');
        const lastMTCRaw = getHistoryRaw(hist[hist.length - 1], 'mosquitoTickControl');
        const firstNA = isNotAvailable(firstMTCRaw);
        const lastNA = isNotAvailable(lastMTCRaw);
        if (firstNA !== lastNA) {
          status = 'amber';
          statusReason = `Mosquito & Tick Control availability changed (${firstNA ? 'unavailable' : 'available'} → ${lastNA ? 'unavailable' : 'available'})`;
        }
      }
      // Sparse data: fewer data points than 50% of median
      if (status !== 'amber' && medianCount > 0) {
        const addrCount = hist ? hist.length : 0;
        if (addrCount < medianCount * 0.5) {
          status = 'amber';
          statusReason = `Sparse data: ${addrCount} data points vs median of ${medianCount}`;
        }
      }
    }

    return {
      addressId: addr.addressId,
      address: addr.address,
      city: addr.city,
      state: addr.state,
      status,
      statusReason,
      pest365,
      pest365Plus,
      oneTime,
      initialServiceVisit,
      mosquitoTickControl,
      changePercent,
      lastScrapeDate,
    };
  });
}

// ---------------------------------------------------------------------------
// Price War Indicators
// ---------------------------------------------------------------------------

function computePriceWarIndicators(snapshotsByDate, mergedDates, addresses, changeEvents) {
  if (mergedDates.length < 2) {
    return {
      overallRisk: 'low',
      riskScore: 0,
      indicators: [],
      recentTrend: 'stable',
      summary: 'Insufficient data to compute price war indicators.',
    };
  }

  const totalAddresses = addresses.length || 1;

  // Determine the "last 30 days of data" window
  const lastDate = new Date(mergedDates[mergedDates.length - 1]);
  const thirtyDaysAgo = new Date(lastDate);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const recentDates = mergedDates.filter(d => new Date(d) >= thirtyDaysAgo);

  // Build per-date change info for all merged dates with a predecessor
  const dateChanges = [];
  for (let i = 1; i < mergedDates.length; i++) {
    const date = mergedDates[i];
    const prevDate = mergedDates[i - 1];
    const curSnap = snapshotsByDate[date];
    const prevSnap = snapshotsByDate[prevDate];

    const changes = [];
    for (const addrId of Object.keys(curSnap)) {
      if (!prevSnap[addrId]) continue;
      for (const f of PRICE_FIELDS) {
        const curr = curSnap[addrId][f];
        const prev = prevSnap[addrId][f];
        if (curr != null && prev != null && prev !== 0 && Math.abs(curr - prev) > 0.001) {
          const pctChange = ((curr - prev) / prev) * 100;
          changes.push({ addrId, field: f, pctChange, direction: pctChange > 0 ? 'up' : 'down' });
        }
      }
    }

    dateChanges.push({ date, changes, changedAddresses: new Set(changes.map(c => c.addrId)) });
  }

  // Recent date changes (within the 30-day window)
  const recentDateChanges = dateChanges.filter(dc => {
    const d = new Date(dc.date);
    return d >= thirtyDaysAgo;
  });

  // --- Indicator 1: Velocity ---
  // Average number of price changes per address per day over last 30 days
  const recentTotalChanges = recentDateChanges.reduce((sum, dc) => sum + dc.changes.length, 0);
  const daySpan = recentDates.length > 1
    ? Math.max(1, (new Date(recentDates[recentDates.length - 1]) - new Date(recentDates[0])) / (1000 * 60 * 60 * 24))
    : 1;
  const velocityValue = round(recentTotalChanges / totalAddresses / daySpan, 4);
  const velocityThreshold = 0.05;
  const velocityStatus = velocityValue < velocityThreshold ? 'normal'
    : velocityValue < velocityThreshold * 2 ? 'warning' : 'critical';

  // --- Indicator 2: Breadth ---
  // Percentage of addresses that had any price change in most recent vs previous
  const mostRecentChange = dateChanges.length > 0 ? dateChanges[dateChanges.length - 1] : null;
  const breadthValue = mostRecentChange
    ? round((mostRecentChange.changedAddresses.size / totalAddresses) * 100, 2)
    : 0;
  const breadthThreshold = 30;
  const breadthStatus = breadthValue < breadthThreshold ? 'normal'
    : breadthValue < 60 ? 'warning' : 'critical';

  // --- Indicator 3: Magnitude ---
  // Average absolute % change across all changed addresses in most recent period
  const magnitudeValues = mostRecentChange
    ? mostRecentChange.changes.map(c => Math.abs(c.pctChange))
    : [];
  const magnitudeValue = magnitudeValues.length > 0 ? round(mean(magnitudeValues), 2) : 0;
  const magnitudeThreshold = 5;
  const magnitudeStatus = magnitudeValue < magnitudeThreshold ? 'normal'
    : magnitudeValue < 10 ? 'warning' : 'critical';

  // --- Indicator 4: Frequency ---
  // Number of distinct dates with any price changes in last 30 days
  const frequencyValue = recentDateChanges.filter(dc => dc.changes.length > 0).length;
  const frequencyThreshold = 3;
  const frequencyStatus = frequencyValue < frequencyThreshold ? 'normal'
    : frequencyValue < 6 ? 'warning' : 'critical';

  // --- Indicator 5: Direction ---
  // Percentage of changes in same direction in most recent change
  let directionValue = 0;
  if (mostRecentChange && mostRecentChange.changes.length > 0) {
    const ups = mostRecentChange.changes.filter(c => c.direction === 'up').length;
    const downs = mostRecentChange.changes.filter(c => c.direction === 'down').length;
    directionValue = round((Math.max(ups, downs) / mostRecentChange.changes.length) * 100, 2);
  }
  const directionThreshold = 70;
  const directionStatus = directionValue < directionThreshold ? 'normal'
    : directionValue < 85 ? 'warning' : 'critical';

  // Build indicator objects
  const indicators = [
    {
      name: 'Velocity',
      value: velocityValue,
      threshold: velocityThreshold,
      status: velocityStatus,
      description: `${velocityValue} price changes per address per day (threshold: ${velocityThreshold})`,
    },
    {
      name: 'Breadth',
      value: breadthValue,
      threshold: breadthThreshold,
      status: breadthStatus,
      description: `${breadthValue}% of addresses affected in most recent scrape (threshold: ${breadthThreshold}%)`,
    },
    {
      name: 'Magnitude',
      value: magnitudeValue,
      threshold: magnitudeThreshold,
      status: magnitudeStatus,
      description: `${magnitudeValue}% average absolute change in most recent period (threshold: ${magnitudeThreshold}%)`,
    },
    {
      name: 'Frequency',
      value: frequencyValue,
      threshold: frequencyThreshold,
      status: frequencyStatus,
      description: `${frequencyValue} change events in last 30 days of data (threshold: ${frequencyThreshold})`,
    },
    {
      name: 'Direction',
      value: directionValue,
      threshold: directionThreshold,
      status: directionStatus,
      description: `${directionValue}% of changes in same direction (threshold: ${directionThreshold}%)`,
    },
  ];

  // Overall risk score: average of indicator scores (normal=0, warning=50, critical=100)
  const statusScores = { normal: 0, warning: 50, critical: 100 };
  const indicatorScores = indicators.map(ind => statusScores[ind.status]);
  const riskScore = round(indicatorScores.reduce((s, v) => s + v, 0) / indicatorScores.length);
  const overallRisk = riskScore <= 25 ? 'low'
    : riskScore <= 50 ? 'moderate'
    : riskScore <= 75 ? 'elevated' : 'high';

  // Recent trend: compare velocity of last 2 periods vs previous 2 periods
  let recentTrend = 'stable';
  if (recentDateChanges.length >= 4) {
    const half = Math.floor(recentDateChanges.length / 2);
    const firstHalf = recentDateChanges.slice(0, half);
    const secondHalf = recentDateChanges.slice(half);
    const firstHalfChanges = firstHalf.reduce((s, dc) => s + dc.changes.length, 0);
    const secondHalfChanges = secondHalf.reduce((s, dc) => s + dc.changes.length, 0);
    if (secondHalfChanges > firstHalfChanges * 1.2) {
      recentTrend = 'accelerating';
    } else if (secondHalfChanges < firstHalfChanges * 0.8) {
      recentTrend = 'decelerating';
    }
  }

  // Summary
  const criticalCount = indicators.filter(i => i.status === 'critical').length;
  const warningCount = indicators.filter(i => i.status === 'warning').length;
  let summary;
  if (overallRisk === 'low') {
    summary = 'Price environment is stable with no significant competitive pressure detected.';
  } else if (overallRisk === 'moderate') {
    summary = `Moderate pricing activity detected — ${warningCount} indicator${warningCount !== 1 ? 's' : ''} at warning level. Monitoring recommended.`;
  } else if (overallRisk === 'elevated') {
    summary = `Elevated pricing activity with ${criticalCount} critical and ${warningCount} warning indicator${warningCount !== 1 ? 's' : ''}. Active price changes are ${recentTrend}.`;
  } else {
    summary = `High-risk pricing environment — ${criticalCount} indicators at critical level. Prices are changing rapidly across a broad set of addresses.`;
  }

  return {
    overallRisk,
    riskScore,
    indicators,
    recentTrend,
    summary,
  };
}

// ---------------------------------------------------------------------------
// Small Multiples Data (sparkline data per address)
// ---------------------------------------------------------------------------

function computeSmallMultiplesData(addresses, sortedHistories, mergedDates, snapshotsByDate) {
  return addresses.map(addr => {
    const values = [];
    const dates = [];

    for (const date of mergedDates) {
      const snap = snapshotsByDate[date];
      const val = snap[addr.addressId]?.pest365;
      if (val != null) {
        values.push(val);
        dates.push(date);
      }
    }

    let changePercent = null;
    if (values.length >= 2) {
      const first = values[0];
      const last = values[values.length - 1];
      if (first > 0) {
        changePercent = round(((last - first) / first) * 100);
      }
    }

    const currentPrice = values.length > 0 ? values[values.length - 1] : null;

    return {
      addressId: addr.addressId,
      label: `${addr.city}, ${addr.state}`,
      values,
      dates,
      changePercent,
      currentPrice,
    };
  });
}

// ---------------------------------------------------------------------------
// Sankey Flow Data (tier migration between scrape dates)
// ---------------------------------------------------------------------------

function computeSankeyData(snapshotsByDate, mergedDates) {
  if (mergedDates.length < 2) {
    return { periods: [], tiers: [], flows: [] };
  }

  // Collect ALL pest365 values across ALL dates to determine tier boundaries
  const allPest365 = [];
  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val != null) allPest365.push(val);
    }
  }

  if (allPest365.length === 0) {
    return { periods: [], tiers: [], flows: [] };
  }

  // Determine tier boundaries: ~5 equal-width buckets (d3-style)
  const minVal = Math.min(...allPest365);
  const maxVal = Math.max(...allPest365);
  const NUM_TIERS = 5;
  const bucketWidth = (maxVal - minVal) / NUM_TIERS;

  // Handle edge case: all same price
  if (bucketWidth < 0.01) {
    const tierLabel = `$${Math.floor(minVal)}`;
    // Only use last 6 periods
    const datesToUse = mergedDates.length > 7 ? mergedDates.slice(-(7)) : mergedDates;
    const periods = [];
    const flows = [];
    for (let i = 0; i < datesToUse.length - 1; i++) {
      const fromDate = datesToUse[i];
      const toDate = datesToUse[i + 1];
      const fd = new Date(fromDate);
      const td = new Date(toDate);
      periods.push({
        fromDate,
        toDate,
        fromLabel: fd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        toLabel: td.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      });
      // Count addresses present in both
      const fromSnap = snapshotsByDate[fromDate];
      const toSnap = snapshotsByDate[toDate];
      let count = 0;
      for (const addrId of Object.keys(fromSnap)) {
        if (toSnap[addrId] && fromSnap[addrId]?.pest365 != null && toSnap[addrId]?.pest365 != null) {
          count++;
        }
      }
      if (count > 0) {
        flows.push({ periodIndex: i, fromTier: tierLabel, toTier: tierLabel, count });
      }
    }
    return { periods, tiers: [tierLabel], flows };
  }

  // Build tier boundaries and labels
  const boundaries = [];
  const tiers = [];
  for (let i = 0; i < NUM_TIERS; i++) {
    const lower = minVal + i * bucketWidth;
    boundaries.push(lower);
    if (i < NUM_TIERS - 1) {
      const upper = minVal + (i + 1) * bucketWidth;
      tiers.push(`$${Math.floor(lower)}-$${Math.floor(upper)}`);
    } else {
      tiers.push(`$${Math.floor(lower)}+`);
    }
  }

  // Helper: determine which tier a price falls into
  function getTier(price) {
    if (price == null) return null;
    for (let i = NUM_TIERS - 1; i >= 0; i--) {
      if (price >= boundaries[i]) return tiers[i];
    }
    return tiers[0]; // fallback
  }

  // Only use the last 6 periods (7 dates) if there are too many
  const datesToUse = mergedDates.length > 7 ? mergedDates.slice(-7) : mergedDates;

  const periods = [];
  const flows = [];

  for (let i = 0; i < datesToUse.length - 1; i++) {
    const fromDate = datesToUse[i];
    const toDate = datesToUse[i + 1];
    const fd = new Date(fromDate);
    const td = new Date(toDate);

    periods.push({
      fromDate,
      toDate,
      fromLabel: fd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      toLabel: td.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    });

    // Count flows between tiers for this period
    const flowCounts = {}; // "fromTier|toTier" -> count
    const fromSnap = snapshotsByDate[fromDate];
    const toSnap = snapshotsByDate[toDate];

    for (const addrId of Object.keys(fromSnap)) {
      if (!toSnap[addrId]) continue;
      const fromPrice = fromSnap[addrId]?.pest365;
      const toPrice = toSnap[addrId]?.pest365;
      const ft = getTier(fromPrice);
      const tt = getTier(toPrice);
      if (ft == null || tt == null) continue;
      const key = `${ft}|${tt}`;
      flowCounts[key] = (flowCounts[key] || 0) + 1;
    }

    for (const [key, count] of Object.entries(flowCounts)) {
      const [fromTier, toTier] = key.split('|');
      flows.push({ periodIndex: i, fromTier, toTier, count });
    }
  }

  return { periods, tiers, flows };
}

// ---------------------------------------------------------------------------
// Forecast Data (linear regression + 90-day projection)
// ---------------------------------------------------------------------------

function computeForecastData(snapshotsByDate, mergedDates) {
  if (mergedDates.length < 2) {
    return null;
  }

  // Build historical data: avg/min/max pest365 per date
  const historical = [];
  const firstDate = new Date(mergedDates[0]);

  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const prices = [];
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val != null) prices.push(val);
    }
    if (prices.length === 0) continue;

    historical.push({
      date,
      avgPrice: round(mean(prices)),
      minPrice: round(Math.min(...prices)),
      maxPrice: round(Math.max(...prices)),
    });
  }

  if (historical.length < 2) {
    return null;
  }

  // Linear regression on (dayIndex, avgPrice)
  const dayIndices = historical.map(h => {
    return (new Date(h.date) - firstDate) / (1000 * 60 * 60 * 24);
  });
  const avgPrices = historical.map(h => h.avgPrice);

  const n = dayIndices.length;
  const meanX = dayIndices.reduce((s, v) => s + v, 0) / n;
  const meanY = avgPrices.reduce((s, v) => s + v, 0) / n;

  let sumXYDev = 0;
  let sumXXDev = 0;
  let sumYYDev = 0;
  for (let i = 0; i < n; i++) {
    const dx = dayIndices[i] - meanX;
    const dy = avgPrices[i] - meanY;
    sumXYDev += dx * dy;
    sumXXDev += dx * dx;
    sumYYDev += dy * dy;
  }

  const slope = sumXXDev === 0 ? 0 : sumXYDev / sumXXDev;
  const intercept = meanY - slope * meanX;
  const rSquared = (sumXXDev === 0 || sumYYDev === 0)
    ? 0
    : round((sumXYDev * sumXYDev) / (sumXXDev * sumYYDev), 4);

  // Standard error of regression
  let sumResidualSq = 0;
  for (let i = 0; i < n; i++) {
    const predicted = slope * dayIndices[i] + intercept;
    sumResidualSq += (avgPrices[i] - predicted) ** 2;
  }
  const stdError = n > 2 ? Math.sqrt(sumResidualSq / (n - 2)) : 0;

  // Generate 90-day forecast from the last historical date
  const lastDate = new Date(mergedDates[mergedDates.length - 1]);
  const lastDayIndex = dayIndices[dayIndices.length - 1];
  const FORECAST_DAYS = 90;
  const NUM_POINTS = 6;
  const forecast = [];

  for (let i = 1; i <= NUM_POINTS; i++) {
    const daysAhead = Math.round((FORECAST_DAYS / NUM_POINTS) * i);
    const forecastDate = new Date(lastDate);
    forecastDate.setDate(forecastDate.getDate() + daysAhead);
    const dayIndex = lastDayIndex + daysAhead;
    const predicted = slope * dayIndex + intercept;

    forecast.push({
      date: forecastDate.toISOString().substring(0, 10),
      predicted: round(predicted),
      lower: round(predicted - 2 * stdError),
      upper: round(predicted + 2 * stdError),
    });
  }

  // Trend metrics
  const monthlyChange = round(slope * 30);
  const direction = monthlyChange > 0.01 ? 'up' : monthlyChange < -0.01 ? 'down' : 'flat';

  return {
    historical,
    forecast,
    trend: {
      slope: round(slope, 4),
      intercept: round(intercept),
      rSquared,
      monthlyChange,
      direction,
    },
  };
}

// ---------------------------------------------------------------------------
// Radar Data (normalized 0-1 price comparison across states)
// ---------------------------------------------------------------------------

function computeRadarData(addresses) {
  // Group addresses by state
  const byState = {};
  for (const addr of addresses) {
    const st = addr.state;
    if (!st) continue;
    if (!byState[st]) byState[st] = [];
    byState[st].push(addr);
  }

  // Compute average of each price field per state
  const stateAverages = {};
  for (const [state, addrs] of Object.entries(byState)) {
    const avgs = {};
    for (const field of PRICE_FIELDS) {
      if (field === 'mosquitoTickControl') {
        // Only average over addresses where the value is a valid number
        const vals = addrs
          .map(a => parsePrice(a[field]))
          .filter(v => v != null);
        avgs[field] = vals.length > 0
          ? vals.reduce((s, v) => s + v, 0) / vals.length
          : null;
      } else {
        const vals = addrs
          .map(a => parsePrice(a[field]))
          .filter(v => v != null);
        avgs[field] = vals.length > 0
          ? vals.reduce((s, v) => s + v, 0) / vals.length
          : null;
      }
    }
    stateAverages[state] = avgs;
  }

  // Find max average across all states for each field (for normalization)
  const fieldMaxes = {};
  for (const field of PRICE_FIELDS) {
    let maxVal = 0;
    for (const avgs of Object.values(stateAverages)) {
      if (avgs[field] != null && avgs[field] > maxVal) {
        maxVal = avgs[field];
      }
    }
    fieldMaxes[field] = maxVal;
  }

  // Build result array
  const result = [];
  for (const [state, avgs] of Object.entries(stateAverages)) {
    const entry = { state };
    const raw = {};
    for (const field of PRICE_FIELDS) {
      raw[field] = avgs[field] != null ? round(avgs[field]) : null;
      if (avgs[field] != null && fieldMaxes[field] > 0) {
        entry[field] = round(avgs[field] / fieldMaxes[field], 4);
      } else {
        entry[field] = 0;
      }
    }
    entry.raw = raw;
    result.push(entry);
  }

  // Sort by state alphabetically
  result.sort((a, b) => a.state.localeCompare(b.state));
  return result;
}

// ---------------------------------------------------------------------------
// Percentile Bands (pest365 distribution over time)
// ---------------------------------------------------------------------------

function computePercentileBands(snapshotsByDate, mergedDates) {
  /** Nearest-rank percentile on a pre-sorted array. */
  function percentile(sorted, p) {
    if (sorted.length === 0) return null;
    const idx = Math.ceil(p / 100 * sorted.length) - 1;
    return sorted[Math.max(0, idx)];
  }

  const dates = [];
  const avg = [];
  const median = [];
  const p10 = [];
  const p25 = [];
  const p75 = [];
  const p90 = [];
  const min = [];
  const max = [];

  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const values = [];
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val != null) values.push(val);
    }

    // Skip dates with fewer than 5 addresses
    if (values.length < 5) continue;

    values.sort((a, b) => a - b);

    dates.push(date);
    avg.push(round(mean(values)));
    median.push(round(percentile(values, 50)));
    p10.push(round(percentile(values, 10)));
    p25.push(round(percentile(values, 25)));
    p75.push(round(percentile(values, 75)));
    p90.push(round(percentile(values, 90)));
    min.push(round(Math.min(...values)));
    max.push(round(Math.max(...values)));
  }

  return { dates, avg, median, p10, p25, p75, p90, min, max };
}

// ---------------------------------------------------------------------------
// Top Movers (biggest price changes + most volatile addresses)
// ---------------------------------------------------------------------------

function computeTopMovers(addresses, sortedHistories, mergedDates, snapshotsByDate) {
  // --- Top Increases & Decreases ---
  const movers = [];

  for (const addr of addresses) {
    // Find the most recent consecutive date pair where this address has data in both
    let bestPair = null;
    for (let i = mergedDates.length - 1; i >= 1; i--) {
      const curDate = mergedDates[i];
      const prevDate = mergedDates[i - 1];
      const curSnap = snapshotsByDate[curDate]?.[addr.addressId];
      const prevSnap = snapshotsByDate[prevDate]?.[addr.addressId];
      if (curSnap && prevSnap) {
        bestPair = { curDate, prevDate, curSnap, prevSnap };
        break;
      }
    }
    if (!bestPair) continue;

    // For each price field, compute % change
    let largestAbsChange = -1;
    let bestField = null;
    let bestPrev = null;
    let bestCurr = null;

    for (const field of PRICE_FIELDS) {
      const prev = bestPair.prevSnap[field];
      const curr = bestPair.curSnap[field];
      if (prev != null && curr != null && prev !== 0) {
        const absPct = Math.abs(((curr - prev) / prev) * 100);
        if (absPct > largestAbsChange) {
          largestAbsChange = absPct;
          bestField = field;
          bestPrev = prev;
          bestCurr = curr;
        }
      }
    }

    if (bestField != null && largestAbsChange > 0.001) {
      const changePercent = round(((bestCurr - bestPrev) / bestPrev) * 100);
      movers.push({
        addressId: addr.addressId,
        address: addr.address,
        city: addr.city,
        state: addr.state,
        field: bestField,
        previousPrice: round(bestPrev),
        currentPrice: round(bestCurr),
        changePercent,
        changeAbsolute: round(bestCurr - bestPrev),
        date: bestPair.curDate,
      });
    }
  }

  // Top increases: positive changes, sorted descending
  const topIncreases = movers
    .filter(m => m.changePercent > 0)
    .sort((a, b) => b.changePercent - a.changePercent)
    .slice(0, 5);

  // Top decreases: negative changes, sorted ascending (most negative first)
  const topDecreases = movers
    .filter(m => m.changePercent < 0)
    .sort((a, b) => a.changePercent - b.changePercent)
    .slice(0, 5);

  // --- Most Volatile ---
  const volatility = [];

  for (const addr of addresses) {
    // Collect all pest365 values across all merged dates
    const values = [];
    for (const date of mergedDates) {
      const snap = snapshotsByDate[date]?.[addr.addressId];
      if (snap && snap.pest365 != null) {
        values.push(snap.pest365);
      }
    }

    if (values.length < 2) continue;

    const avg = mean(values);
    if (avg == null || avg === 0) continue;

    const sd = stdDev(values);
    const cv = round((sd / avg) * 100, 2);

    // Count distinct price changes
    let changeCount = 0;
    for (let i = 1; i < values.length; i++) {
      if (Math.abs(values[i] - values[i - 1]) > 0.001) {
        changeCount++;
      }
    }

    const minVal = round(Math.min(...values));
    const maxVal = round(Math.max(...values));

    volatility.push({
      addressId: addr.addressId,
      address: addr.address,
      city: addr.city,
      state: addr.state,
      volatility: cv,
      priceRange: `$${minVal} - $${maxVal}`,
      changeCount,
    });
  }

  // Sort by CV descending, take top 5
  const mostVolatile = volatility
    .sort((a, b) => b.volatility - a.volatility)
    .slice(0, 5);

  return { topIncreases, topDecreases, mostVolatile };
}

// ---------------------------------------------------------------------------
// Waterfall Data (cumulative price changes over time)
// ---------------------------------------------------------------------------

function computeWaterfallData(snapshotsByDate, mergedDates) {
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function formatLabel(dateStr) {
    const parts = dateStr.split('-');
    const monthIdx = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return `${MONTHS[monthIdx]} ${day}`;
  }

  const entries = [];
  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const values = [];
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val != null) values.push(val);
    }
    if (values.length === 0) continue;
    entries.push({ date, avgPrice: round(mean(values)) });
  }

  if (entries.length === 0) return [];

  const result = [];

  // First entry
  result.push({
    date: entries[0].date,
    label: formatLabel(entries[0].date),
    avgPrice: entries[0].avgPrice,
    delta: 0,
    cumulative: entries[0].avgPrice,
    type: 'start',
  });

  // Subsequent entries
  for (let i = 1; i < entries.length; i++) {
    const delta = round(entries[i].avgPrice - entries[i - 1].avgPrice);
    let type;
    if (delta > 0.001) type = 'increase';
    else if (delta < -0.001) type = 'decrease';
    else type = 'neutral';

    result.push({
      date: entries[i].date,
      label: formatLabel(entries[i].date),
      avgPrice: entries[i].avgPrice,
      delta,
      cumulative: entries[i].avgPrice,
      type,
    });
  }

  // Final "total" entry
  const first = entries[0];
  const last = entries[entries.length - 1];
  result.push({
    date: last.date + ' (Total)',
    label: 'Current',
    avgPrice: last.avgPrice,
    delta: round(last.avgPrice - first.avgPrice),
    cumulative: last.avgPrice,
    type: 'total',
  });

  return result;
}

// ---------------------------------------------------------------------------
// Box Plot Data (distribution stats per date)
// ---------------------------------------------------------------------------

function computeBoxPlotData(snapshotsByDate, mergedDates) {
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function formatLabel(dateStr) {
    const parts = dateStr.split('-');
    const monthIdx = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return `${MONTHS[monthIdx]} ${day}`;
  }

  /** Nearest-rank percentile on a pre-sorted array. */
  function percentile(sorted, p) {
    if (sorted.length === 0) return null;
    const idx = Math.ceil((p / 100) * sorted.length) - 1;
    return sorted[Math.max(0, Math.min(idx, sorted.length - 1))];
  }

  const result = [];

  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const values = [];
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val != null) values.push(val);
    }

    if (values.length < 5) continue;

    values.sort((a, b) => a - b);

    const q1 = percentile(values, 25);
    const med = percentile(values, 50);
    const q3 = percentile(values, 75);
    const iqr = q3 - q1;

    const lowerWhisker = q1 - 1.5 * iqr;
    const upperWhisker = q3 + 1.5 * iqr;

    const outliers = [];
    const nonOutliers = [];
    for (const v of values) {
      if (v < lowerWhisker || v > upperWhisker) {
        outliers.push(round(v));
      } else {
        nonOutliers.push(v);
      }
    }

    const adjMin = nonOutliers.length > 0 ? Math.min(...nonOutliers) : values[0];
    const adjMax = nonOutliers.length > 0 ? Math.max(...nonOutliers) : values[values.length - 1];

    result.push({
      date,
      label: formatLabel(date),
      min: round(adjMin),
      q1: round(q1),
      median: round(med),
      q3: round(q3),
      max: round(adjMax),
      mean: round(mean(values)),
      outliers,
      count: values.length,
    });
  }

  return result;
}

// ---------------------------------------------------------------------------
// Convergence Data (price dispersion trends over time)
// ---------------------------------------------------------------------------

function computeConvergenceData(snapshotsByDate, mergedDates) {
  /** Nearest-rank percentile on a pre-sorted array. */
  function percentile(sorted, p) {
    if (sorted.length === 0) return null;
    const idx = Math.ceil((p / 100) * sorted.length) - 1;
    return sorted[Math.max(0, Math.min(idx, sorted.length - 1))];
  }

  const dates = [];
  const stdDevSeries = [];
  const iqrSeries = [];
  const rangeSeries = [];
  const cvSeries = [];

  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const values = [];
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val != null) values.push(val);
    }

    if (values.length < 5) continue;

    values.sort((a, b) => a - b);

    const sd = stdDev(values);
    const avg = mean(values);
    const q1 = percentile(values, 25);
    const q3 = percentile(values, 75);

    dates.push(date);
    stdDevSeries.push(round(sd));
    iqrSeries.push(round(q3 - q1));
    rangeSeries.push(round(values[values.length - 1] - values[0]));
    cvSeries.push(round((avg != null && avg !== 0) ? (sd / avg) * 100 : 0));
  }

  if (dates.length < 2) {
    return {
      dates,
      stdDev: stdDevSeries,
      iqr: iqrSeries,
      range: rangeSeries,
      cv: cvSeries,
      trend: 'stable',
      trendStrength: 0,
    };
  }

  // Linear regression on the stdDev series to detect trend
  const n = stdDevSeries.length;
  const xs = stdDevSeries.map((_, i) => i);
  const meanX = xs.reduce((s, v) => s + v, 0) / n;
  const meanY = stdDevSeries.reduce((s, v) => s + v, 0) / n;

  let sumXYDev = 0;
  let sumXXDev = 0;
  let sumYYDev = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    const dy = stdDevSeries[i] - meanY;
    sumXYDev += dx * dy;
    sumXXDev += dx * dx;
    sumYYDev += dy * dy;
  }

  const slope = sumXXDev === 0 ? 0 : sumXYDev / sumXXDev;
  const rSquared = (sumXXDev === 0 || sumYYDev === 0)
    ? 0
    : (sumXYDev * sumXYDev) / (sumXXDev * sumYYDev);

  let trend;
  if (Math.abs(slope) < 0.001) trend = 'stable';
  else if (slope < 0) trend = 'converging';
  else trend = 'diverging';

  return {
    dates,
    stdDev: stdDevSeries,
    iqr: iqrSeries,
    range: rangeSeries,
    cv: cvSeries,
    trend,
    trendStrength: round(Math.abs(rSquared), 2),
  };
}

// ---------------------------------------------------------------------------
// Donut Data (price tier donut chart)
// ---------------------------------------------------------------------------

function computeDonutData(addresses, sortedHistories) {
  const pest365Values = addresses
    .map(a => parsePrice(a.pest365))
    .filter(v => v != null);

  if (pest365Values.length === 0) {
    return {
      tiers: [],
      totalAddresses: addresses.length,
      changeDistribution: { increased: 0, decreased: 0, unchanged: 0 },
    };
  }

  const minVal = Math.min(...pest365Values);
  const maxVal = Math.max(...pest365Values);
  const tierCount = 5;
  const bucketWidth = (maxVal - minVal) / tierCount;
  const colors = ['#3b82f6', '#8b5cf6', '#f59e0b', '#16a34a', '#dc2626'];

  // Build tier boundaries
  const tiers = [];
  for (let i = 0; i < tierCount; i++) {
    const lo = minVal + i * bucketWidth;
    const hi = (i < tierCount - 1) ? minVal + (i + 1) * bucketWidth : Infinity;
    const label = (i < tierCount - 1)
      ? `$${round(lo)}-$${round(minVal + (i + 1) * bucketWidth)}`
      : `$${round(lo)}+`;

    // Collect values in this tier
    const tierValues = pest365Values.filter(v => {
      if (i < tierCount - 1) return v >= lo && v < hi;
      return v >= lo;
    });

    const count = tierValues.length;
    const percent = round((count / pest365Values.length) * 100);
    const avgPrice = round(mean(tierValues));

    tiers.push({ label, count, percent, color: colors[i], avgPrice });
  }

  // Change distribution: compare first vs last pest365 in history
  let increased = 0;
  let decreased = 0;
  let unchanged = 0;

  for (const addr of addresses) {
    const hist = sortedHistories[addr.addressId];
    if (!hist || hist.length < 2) {
      unchanged++;
      continue;
    }
    const first = getHistoryPrice(hist[0], 'pest365');
    const last = getHistoryPrice(hist[hist.length - 1], 'pest365');
    if (first == null || last == null) {
      unchanged++;
      continue;
    }
    if (last > first + 0.01) increased++;
    else if (last < first - 0.01) decreased++;
    else unchanged++;
  }

  return {
    tiers,
    totalAddresses: addresses.length,
    changeDistribution: { increased, decreased, unchanged },
  };
}

// ---------------------------------------------------------------------------
// Health Score (overall data quality assessment)
// ---------------------------------------------------------------------------

function computeHealthScore(addresses, sortedHistories, mergedDates, snapshotsByDate) {
  const totalAddresses = addresses.length;
  if (totalAddresses === 0) {
    return {
      overallScore: 0,
      grade: 'F',
      components: [],
      summary: 'No addresses available for health assessment.',
    };
  }

  // 1. Data Completeness (weight: 0.25)
  const criticalFields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  let completeCount = 0;
  for (const addr of addresses) {
    const allPresent = criticalFields.every(f => parsePrice(addr[f]) != null);
    if (allPresent) completeCount++;
  }
  const completenessScore = round((completeCount / totalAddresses) * 100);

  // 2. Price Stability (weight: 0.20)
  const absChanges = [];
  for (const addr of addresses) {
    const hist = sortedHistories[addr.addressId];
    if (!hist || hist.length < 2) continue;
    const first = getHistoryPrice(hist[0], 'pest365');
    const last = getHistoryPrice(hist[hist.length - 1], 'pest365');
    if (first != null && last != null && first > 0) {
      absChanges.push(Math.abs((last - first) / first) * 100);
    }
  }
  const avgAbsChange = absChanges.length > 0 ? mean(absChanges) : 0;
  const stabilityScore = Math.max(0, Math.min(100, round(100 - avgAbsChange * 5)));

  // 3. Coverage (weight: 0.20) - mosquitoTickControl availability
  let mtcAvailableCount = 0;
  for (const addr of addresses) {
    const raw = addr.mosquitoTickControl;
    const parsed = parsePrice(raw);
    if (parsed != null && !isNotAvailable(raw)) {
      mtcAvailableCount++;
    }
  }
  const coverageScore = round((mtcAvailableCount / totalAddresses) * 100);

  // 4. Freshness (weight: 0.20) - days since last scrape
  let lastScrapeDate = null;
  if (mergedDates.length > 0) {
    lastScrapeDate = mergedDates[mergedDates.length - 1];
  }
  let daysSinceLastScrape = null;
  let freshnessScore = 20;
  if (lastScrapeDate) {
    const now = new Date();
    const last = new Date(lastScrapeDate);
    daysSinceLastScrape = Math.floor((now - last) / (1000 * 60 * 60 * 24));
    if (daysSinceLastScrape <= 3) freshnessScore = 100;
    else if (daysSinceLastScrape <= 7) freshnessScore = 80;
    else if (daysSinceLastScrape <= 14) freshnessScore = 60;
    else if (daysSinceLastScrape <= 30) freshnessScore = 40;
    else freshnessScore = 20;
  }

  // 5. Consistency (weight: 0.15) - coefficient of variation of pest365
  const pest365Values = addresses.map(a => parsePrice(a.pest365)).filter(v => v != null);
  let consistencyScore = 100;
  let cvPercent = 0;
  if (pest365Values.length >= 2) {
    const avg = mean(pest365Values);
    const sd = stdDev(pest365Values);
    if (avg != null && avg > 0) {
      cvPercent = round((sd / avg) * 100);
      consistencyScore = Math.max(0, Math.min(100, round(100 - cvPercent * 10)));
    }
  }

  // Build components
  const components = [
    {
      name: 'Data Completeness',
      score: completenessScore,
      weight: 0.25,
      description: `${completeCount} of ${totalAddresses} addresses have complete pricing data`,
      status: completenessScore >= 70 ? 'good' : completenessScore >= 40 ? 'fair' : 'poor',
    },
    {
      name: 'Price Stability',
      score: stabilityScore,
      weight: 0.20,
      description: `Average price change is ${round(avgAbsChange)}%`,
      status: stabilityScore >= 70 ? 'good' : stabilityScore >= 40 ? 'fair' : 'poor',
    },
    {
      name: 'Coverage',
      score: coverageScore,
      weight: 0.20,
      description: `${mtcAvailableCount} of ${totalAddresses} addresses have mosquito/tick pricing`,
      status: coverageScore >= 70 ? 'good' : coverageScore >= 40 ? 'fair' : 'poor',
    },
    {
      name: 'Freshness',
      score: freshnessScore,
      weight: 0.20,
      description: daysSinceLastScrape != null
        ? `Last scrape was ${daysSinceLastScrape} days ago`
        : 'No scrape data available',
      status: freshnessScore >= 70 ? 'good' : freshnessScore >= 40 ? 'fair' : 'poor',
    },
    {
      name: 'Consistency',
      score: consistencyScore,
      weight: 0.15,
      description: `Price variation across addresses is ${cvPercent}%`,
      status: consistencyScore >= 70 ? 'good' : consistencyScore >= 40 ? 'fair' : 'poor',
    },
  ];

  // Overall score = weighted sum
  const overallScore = round(components.reduce((sum, c) => sum + c.score * c.weight, 0));

  // Grade
  let grade;
  if (overallScore >= 85) grade = 'A';
  else if (overallScore >= 70) grade = 'B';
  else if (overallScore >= 55) grade = 'C';
  else if (overallScore >= 40) grade = 'D';
  else grade = 'F';

  // Summary
  const goodCount = components.filter(c => c.status === 'good').length;
  const poorCount = components.filter(c => c.status === 'poor').length;
  let summary;
  if (grade === 'A') {
    summary = `Excellent data health (${overallScore}/100). All ${goodCount} components performing well.`;
  } else if (grade === 'B') {
    summary = `Good data health (${overallScore}/100). ${goodCount} of 5 components performing well.`;
  } else if (grade === 'C') {
    summary = `Fair data health (${overallScore}/100). Some components need attention.`;
  } else if (grade === 'D') {
    summary = `Below average data health (${overallScore}/100). ${poorCount} component(s) in poor condition.`;
  } else {
    summary = `Poor data health (${overallScore}/100). Immediate attention required on ${poorCount} component(s).`;
  }

  return {
    overallScore,
    grade,
    components,
    summary,
  };
}

// ---------------------------------------------------------------------------
// Field Comparison Data (average prices by state)
// ---------------------------------------------------------------------------

function computeFieldComparisonData(addresses) {
  // Group addresses by state
  const byState = {};
  for (const addr of addresses) {
    const state = addr.state || addr.STATE || 'Unknown';
    if (!byState[state]) byState[state] = [];
    byState[state].push(addr);
  }

  const result = [];
  for (const state of Object.keys(byState).sort()) {
    const stateAddresses = byState[state];

    const pest365Vals = stateAddresses.map(a => parsePrice(a.pest365)).filter(v => v != null);
    const pest365PlusVals = stateAddresses.map(a => parsePrice(a.pest365Plus)).filter(v => v != null);
    const oneTimeVals = stateAddresses.map(a => parsePrice(a.oneTime)).filter(v => v != null);
    const isvVals = stateAddresses.map(a => parsePrice(a.initialServiceVisit)).filter(v => v != null);

    // For mosquitoTickControl, exclude "not_available" values
    const mtcVals = stateAddresses
      .filter(a => !isNotAvailable(a.mosquitoTickControl))
      .map(a => parsePrice(a.mosquitoTickControl))
      .filter(v => v != null);

    result.push({
      state,
      pest365: pest365Vals.length > 0 ? round(mean(pest365Vals)) : null,
      pest365Plus: pest365PlusVals.length > 0 ? round(mean(pest365PlusVals)) : null,
      oneTime: oneTimeVals.length > 0 ? round(mean(oneTimeVals)) : null,
      initialServiceVisit: isvVals.length > 0 ? round(mean(isvVals)) : null,
      mosquitoTickControl: mtcVals.length > 0 ? round(mean(mtcVals)) : null,
      addressCount: stateAddresses.length,
    });
  }

  return result;
}

// ---------------------------------------------------------------------------
// Slope Data (state-level price change from first to last date)
// ---------------------------------------------------------------------------

function computeSlopeData(addresses, snapshotsByDate, mergedDates) {
  if (mergedDates.length < 2) {
    return { slopes: [], firstDate: '', lastDate: '' };
  }

  const COLORS = ['#4e79a7','#f28e2b','#e15759','#76b7b2','#59a14f','#edc948','#b07aa1','#ff9da7','#9c755f','#bab0ac'];

  const firstDateStr = mergedDates[0];
  const lastDateStr = mergedDates[mergedDates.length - 1];
  const firstSnap = snapshotsByDate[firstDateStr] || {};
  const lastSnap = snapshotsByDate[lastDateStr] || {};

  // Group addresses by state
  const byState = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!byState[st]) byState[st] = [];
    byState[st].push(addr);
  }

  const slopes = [];
  let colorIdx = 0;
  for (const [state, stateAddresses] of Object.entries(byState)) {
    // Compute avg pest365 on first date
    const firstPrices = stateAddresses
      .map(a => {
        const snap = firstSnap[a.addressId];
        return snap ? parsePrice(snap.pest365) : null;
      })
      .filter(v => v != null);
    const firstPrice = firstPrices.length > 0 ? mean(firstPrices) : null;

    // Compute avg pest365 on last date
    const lastPrices = stateAddresses
      .map(a => {
        const snap = lastSnap[a.addressId];
        return snap ? parsePrice(snap.pest365) : null;
      })
      .filter(v => v != null);
    const lastPrice = lastPrices.length > 0 ? mean(lastPrices) : null;

    let changePercent = null;
    if (firstPrice != null && lastPrice != null && firstPrice > 0) {
      changePercent = round(((lastPrice - firstPrice) / firstPrice) * 100);
    }

    slopes.push({
      state,
      firstPrice: round(firstPrice),
      lastPrice: round(lastPrice),
      changePercent,
      addressCount: stateAddresses.length,
      color: COLORS[colorIdx % COLORS.length],
    });
    colorIdx++;
  }

  // Sort by changePercent descending
  slopes.sort((a, b) => (b.changePercent || 0) - (a.changePercent || 0));

  // Format dates as "Mon DD, YYYY"
  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return {
    slopes,
    firstDate: formatDate(firstDateStr),
    lastDate: formatDate(lastDateStr),
  };
}

// ---------------------------------------------------------------------------
// Scatter Data (per-address price vs change with outlier detection)
// ---------------------------------------------------------------------------

function computeScatterData(addresses, sortedHistories) {
  const items = [];

  for (const addr of addresses) {
    const currentPrice = parsePrice(addr.pest365);
    if (currentPrice == null) continue;

    const hist = sortedHistories[addr.addressId];
    let changePercent = null;
    let dataPoints = 0;

    if (hist && hist.length >= 2) {
      const firstPrice = getHistoryPrice(hist[0], 'pest365');
      const lastPrice = getHistoryPrice(hist[hist.length - 1], 'pest365');
      if (firstPrice != null && lastPrice != null && firstPrice > 0) {
        changePercent = ((lastPrice - firstPrice) / firstPrice) * 100;
      }
      dataPoints = hist.length;
    } else if (hist) {
      dataPoints = hist.length;
    }

    items.push({
      addressId: addr.addressId,
      label: `${addr.city || ''}, ${addr.state || ''}`,
      currentPrice,
      changePercent,
      state: addr.state || 'Unknown',
      dataPoints,
    });
  }

  // Compute z-scores
  const allPrices = items.map(i => i.currentPrice);
  const allChanges = items.map(i => i.changePercent).filter(v => v != null);

  const priceMean = mean(allPrices);
  const priceStdDev = stdDev(allPrices);
  const changeMean = mean(allChanges);
  const changeStdDev = stdDev(allChanges);

  for (const item of items) {
    item.zScorePrice = priceStdDev > 0 ? round((item.currentPrice - priceMean) / priceStdDev) : 0;
    item.zScoreChange = (item.changePercent != null && changeStdDev > 0)
      ? round((item.changePercent - changeMean) / changeStdDev)
      : 0;
    item.changePercent = item.changePercent != null ? round(item.changePercent) : null;
    item.isOutlier = Math.abs(item.zScorePrice) > 2 || Math.abs(item.zScoreChange) > 2;
  }

  return items;
}

// ---------------------------------------------------------------------------
// Treemap Data (state-level MTC coverage and address counts)
// ---------------------------------------------------------------------------

function computeTreemapData(addresses) {
  const byState = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!byState[st]) byState[st] = [];
    byState[st].push(addr);
  }

  const result = [];
  for (const [state, stateAddresses] of Object.entries(byState)) {
    const addressCount = stateAddresses.length;

    // MTC available = parsePrice returns a valid number (not null, not "not_available")
    const mtcAvailable = stateAddresses.filter(a => {
      const val = parsePrice(a.mosquitoTickControl);
      return val != null;
    }).length;

    const mtcCoverage = round((mtcAvailable / addressCount) * 100);

    const pest365Vals = stateAddresses.map(a => parsePrice(a.pest365)).filter(v => v != null);
    const avgPest365 = pest365Vals.length > 0 ? round(mean(pest365Vals)) : null;

    result.push({
      state,
      addressCount,
      mtcAvailable,
      mtcCoverage,
      avgPest365,
    });
  }

  // Sort by addressCount descending
  result.sort((a, b) => b.addressCount - a.addressCount);

  return result;
}

// ---------------------------------------------------------------------------
// Parallel Coordinates Data (all 5 fields per address)
// ---------------------------------------------------------------------------

function computeParallelData(addresses) {
  return addresses.map(addr => ({
    addressId: addr.addressId,
    label: `${addr.city}, ${addr.state}`,
    state: addr.state || 'Unknown',
    pest365: parsePrice(addr.pest365),
    pest365Plus: parsePrice(addr.pest365Plus),
    oneTime: parsePrice(addr.oneTime),
    initialServiceVisit: parsePrice(addr.initialServiceVisit),
    mosquitoTickControl: parsePrice(addr.mosquitoTickControl),
  }));
}

// ---------------------------------------------------------------------------
// Dumbbell Data (min/max pest365 spread per state)
// ---------------------------------------------------------------------------

function computeDumbbellData(addresses) {
  const byState = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!byState[st]) byState[st] = [];
    byState[st].push(addr);
  }

  const result = [];
  for (const [state, stateAddresses] of Object.entries(byState)) {
    const values = stateAddresses
      .map(a => parsePrice(a.pest365))
      .filter(v => v != null);
    if (values.length < 1) continue;

    const minPrice = Math.min(...values);
    const maxPrice = Math.max(...values);
    const avgPrice = round(mean(values), 2);
    const spread = round(maxPrice - minPrice, 2);

    result.push({
      state,
      minPrice,
      maxPrice,
      avgPrice,
      spread,
      addressCount: stateAddresses.length,
    });
  }

  result.sort((a, b) => b.spread - a.spread);
  return result;
}

// ---------------------------------------------------------------------------
// Horizon Data (deviation % from baseline per field over time)
// ---------------------------------------------------------------------------

function computeHorizonData(snapshotsByDate, mergedDates) {
  const fieldDefs = [
    { key: 'pest365', label: 'Pest365', color: '#2563eb' },
    { key: 'pest365Plus', label: 'Pest365+', color: '#8b5cf6' },
    { key: 'oneTime', label: 'One-Time', color: '#f59e0b' },
    { key: 'initialServiceVisit', label: 'Init. Visit', color: '#16a34a' },
    { key: 'mosquitoTickControl', label: 'Mosquito/Tick', color: '#dc2626' },
  ];

  // Filter to dates with at least 3 addresses
  const validDates = mergedDates.filter(date => {
    const snap = snapshotsByDate[date];
    return snap && Object.keys(snap).length >= 3;
  });

  if (validDates.length === 0) {
    return { dates: [], fields: [] };
  }

  // Compute per-date average for each field
  const avgsByField = {};
  for (const fd of fieldDefs) {
    avgsByField[fd.key] = [];
    for (const date of validDates) {
      const snap = snapshotsByDate[date];
      const values = Object.values(snap)
        .map(addrData => addrData[fd.key])
        .filter(v => v != null && !isNaN(v));
      avgsByField[fd.key].push(values.length > 0 ? mean(values) : null);
    }
  }

  // First date avg is the baseline; compute deviation %
  const fields = fieldDefs.map(fd => {
    const avgs = avgsByField[fd.key];
    const baselineAvg = avgs[0];
    const deviations = avgs.map(avg => {
      if (avg == null || baselineAvg == null || baselineAvg === 0) return 0;
      return round(((avg - baselineAvg) / baselineAvg) * 100, 2);
    });
    return {
      key: fd.key,
      label: fd.label,
      color: fd.color,
      values: deviations,
    };
  });

  return { dates: validDates, fields };
}

// ---------------------------------------------------------------------------
// Volatility Matrix (CV per state per price field)
// ---------------------------------------------------------------------------

function computeVolatilityMatrix(addresses, sortedHistories, mergedDates, snapshotsByDate) {
  const fields = [
    { key: 'pest365', label: 'Pest365' },
    { key: 'pest365Plus', label: 'Pest365+' },
    { key: 'oneTime', label: 'One-Time' },
    { key: 'initialServiceVisit', label: 'Init. Visit' },
    { key: 'mosquitoTickControl', label: 'Mosquito/Tick' },
  ];

  // Group addresses by state
  const addressesByState = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!addressesByState[st]) addressesByState[st] = [];
    addressesByState[st].push(addr);
  }

  const states = Object.keys(addressesByState).sort();
  const matrix = {};
  let maxCV = 0;

  // Helper: compute cell stats for a set of address IDs across all dates
  function computeCell(addrIds, fieldKey) {
    const values = [];
    for (const date of mergedDates) {
      const snap = snapshotsByDate[date];
      if (!snap) continue;
      for (const addrId of addrIds) {
        if (!snap[addrId]) continue;
        const v = parsePrice(snap[addrId][fieldKey]);
        if (v != null) values.push(v);
      }
    }
    const count = values.length;
    const m = mean(values);
    const sd = stdDev(values);
    let cv = null;
    if (count >= 2 && m != null && m > 0) {
      cv = round((sd / m) * 100);
    }
    return { cv, mean: round(m), stdDev: round(sd), count };
  }

  // Per-state cells
  for (const st of states) {
    const addrIds = addressesByState[st].map(a => a.addressId);
    matrix[st] = {};
    for (const f of fields) {
      const cell = computeCell(addrIds, f.key);
      matrix[st][f.key] = cell;
      if (cell.cv != null && cell.cv > maxCV) maxCV = cell.cv;
    }
  }

  // Overall entry
  const allAddrIds = addresses.map(a => a.addressId);
  matrix['__overall__'] = {};
  for (const f of fields) {
    const cell = computeCell(allAddrIds, f.key);
    matrix['__overall__'][f.key] = cell;
    if (cell.cv != null && cell.cv > maxCV) maxCV = cell.cv;
  }

  return { states, fields, matrix, maxCV: round(maxCV) };
}

// ---------------------------------------------------------------------------
// Lollipop Data (state avg pest365 ranking)
// ---------------------------------------------------------------------------

function computeLollipopData(addresses) {
  // Group addresses by state
  const addressesByState = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!addressesByState[st]) addressesByState[st] = [];
    addressesByState[st].push(addr);
  }

  // Compute per-state average pest365
  const stateEntries = [];
  for (const [st, addrs] of Object.entries(addressesByState)) {
    const prices = addrs.map(a => parsePrice(a.pest365)).filter(v => v != null);
    if (prices.length === 0) continue;
    const avgPrice = round(mean(prices));
    stateEntries.push({ state: st, avgPrice, addressCount: addrs.length });
  }

  // Sort descending by avgPrice
  stateEntries.sort((a, b) => b.avgPrice - a.avgPrice);

  // Assign rank
  const statesResult = stateEntries.map((entry, idx) => ({
    ...entry,
    rank: idx + 1,
  }));

  // National average
  const allPrices = addresses.map(a => parsePrice(a.pest365)).filter(v => v != null);
  const nationalAvg = round(mean(allPrices));

  return { states: statesResult, nationalAvg };
}

// ---------------------------------------------------------------------------
// Beeswarm Data (individual address prices by state)
// ---------------------------------------------------------------------------

function computeBeeswarmData(addresses) {
  const result = [];
  for (const addr of addresses) {
    const price = parsePrice(addr.pest365);
    if (price == null) continue;
    result.push({
      addressId: addr.addressId,
      label: `${addr.city}, ${addr.state}`,
      state: addr.state || 'Unknown',
      price,
    });
  }

  // Sort by state, then by price within each state
  result.sort((a, b) => {
    if (a.state < b.state) return -1;
    if (a.state > b.state) return 1;
    return a.price - b.price;
  });

  return result;
}

// ---------------------------------------------------------------------------
// Bandwidth Data (mean +/- stdDev bands with min/max over time)
// ---------------------------------------------------------------------------

function computeBandwidthData(snapshotsByDate, mergedDates) {
  const dates = [];
  const meanSeries = [];
  const upper2 = [];
  const upper1 = [];
  const lower1 = [];
  const lower2 = [];
  const minSeries = [];
  const maxSeries = [];

  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const values = [];
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val != null) values.push(val);
    }

    if (values.length < 5) continue;

    const avg = mean(values);
    const sd = stdDev(values);

    dates.push(date);
    meanSeries.push(round(avg));
    upper2.push(round(avg + 2 * sd));
    upper1.push(round(avg + 1 * sd));
    lower1.push(round(avg - 1 * sd));
    lower2.push(round(avg - 2 * sd));
    minSeries.push(round(Math.min(...values)));
    maxSeries.push(round(Math.max(...values)));
  }

  return {
    dates,
    mean: meanSeries,
    upper2,
    upper1,
    lower1,
    lower2,
    min: minSeries,
    max: maxSeries,
  };
}

// ---------------------------------------------------------------------------
// Stream Data (average pest365 per state over time for streamgraph)
// ---------------------------------------------------------------------------

function computeStreamData(snapshotsByDate, mergedDates, addresses) {
  // Build addressId -> state lookup
  const addrStateMap = {};
  for (const addr of addresses) {
    addrStateMap[addr.addressId] = addr.state || 'Unknown';
  }

  // Collect all unique states
  const statesSet = new Set();
  for (const addr of addresses) {
    statesSet.add(addr.state || 'Unknown');
  }
  const states = [...statesSet].sort();

  const dates = [];
  const series = {};
  for (const st of states) {
    series[st] = [];
  }

  // Track previous values for carry-forward when a state has no data on a date
  const lastKnown = {};
  for (const st of states) {
    lastKnown[st] = 0;
  }

  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const addrIds = Object.keys(snap);
    if (addrIds.length < 3) continue;

    dates.push(date);

    // Group pest365 values by state for this date
    const byState = {};
    for (const st of states) {
      byState[st] = [];
    }

    for (const addrId of addrIds) {
      const val = snap[addrId]?.pest365;
      const st = addrStateMap[addrId] || 'Unknown';
      if (val != null && byState[st]) {
        byState[st].push(val);
      }
    }

    for (const st of states) {
      if (byState[st].length > 0) {
        const avg = round(mean(byState[st]));
        series[st].push(avg);
        lastKnown[st] = avg;
      } else {
        // Use previous value if state has no data for this date
        series[st].push(lastKnown[st]);
      }
    }
  }

  return { dates, states, series };
}

// ---------------------------------------------------------------------------
// Velocity Data (rate of change + acceleration of avg pest365)
// ---------------------------------------------------------------------------

function computeVelocityData(snapshotsByDate, mergedDates) {
  // First, compute the overall average pest365 per date
  const avgPrices = [];
  const validDates = [];

  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const values = [];
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val != null) values.push(val);
    }

    if (values.length < 5) continue;

    validDates.push(date);
    avgPrices.push(mean(values));
  }

  if (validDates.length < 2) {
    return { dates: [], velocity: [], velocityPercent: [], acceleration: [] };
  }

  // velocity[i] = avgPrice[i+1] - avgPrice[i]
  const dates = [];
  const velocity = [];
  const velocityPercent = [];

  for (let i = 0; i < avgPrices.length - 1; i++) {
    dates.push(validDates[i + 1]);
    const v = avgPrices[i + 1] - avgPrices[i];
    velocity.push(round(v, 4));
    velocityPercent.push(
      avgPrices[i] !== 0 ? round((v / avgPrices[i]) * 100, 4) : null
    );
  }

  // acceleration[i] = velocity[i+1] - velocity[i]; first element is null
  const acceleration = [null];
  for (let i = 0; i < velocity.length - 1; i++) {
    acceleration.push(round(velocity[i + 1] - velocity[i], 4));
  }

  return { dates, velocity, velocityPercent, acceleration };
}

// ---------------------------------------------------------------------------
// Marimekko Data (price tier distribution by state)
// ---------------------------------------------------------------------------

function computeMarimekkoData(addresses) {
  const TIERS = [
    { tier: 'Budget', min: -Infinity, max: 40, color: '#16a34a' },
    { tier: 'Standard', min: 40, max: 50, color: '#2563eb' },
    { tier: 'Premium', min: 50, max: 60, color: '#f59e0b' },
    { tier: 'Ultra', min: 60, max: Infinity, color: '#dc2626' },
  ];
  const tierLabels = TIERS.map(t => t.tier);
  const tierColors = TIERS.map(t => t.color);

  // Group addresses by state
  const byState = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!byState[st]) byState[st] = [];
    byState[st].push(addr);
  }

  const states = [];
  for (const [state, stateAddresses] of Object.entries(byState)) {
    const tiers = TIERS.map(t => ({ tier: t.tier, count: 0, color: t.color }));

    for (const addr of stateAddresses) {
      const p = parsePrice(addr.pest365);
      if (p == null) continue;
      // Budget: < $40; Standard: $40-$50; Premium: $50-$60; Ultra: > $60
      for (let i = 0; i < TIERS.length; i++) {
        if (p < TIERS[i].max || i === TIERS.length - 1) {
          tiers[i].count++;
          break;
        }
      }
    }

    states.push({
      state,
      addressCount: stateAddresses.length,
      tiers,
    });
  }

  // Sort by addressCount descending
  states.sort((a, b) => b.addressCount - a.addressCount);

  return {
    states,
    totalAddresses: addresses.length,
    tierLabels,
    tierColors,
  };
}

// ---------------------------------------------------------------------------
// Cumulative Return Data (cumulative % change of avg pest365 per state)
// ---------------------------------------------------------------------------

function computeCumulativeReturnData(snapshotsByDate, mergedDates, addresses) {
  if (mergedDates.length < 2) {
    return { dates: [], states: [], series: {}, overallAvg: [] };
  }

  // Build addressId -> state lookup
  const addrStateMap = {};
  for (const addr of addresses) {
    addrStateMap[addr.addressId] = addr.state || 'Unknown';
  }

  // Collect all unique states
  const statesSet = new Set();
  for (const addr of addresses) {
    statesSet.add(addr.state || 'Unknown');
  }
  const states = [...statesSet].sort();

  const dates = [];
  const series = {};
  for (const st of states) {
    series[st] = [];
  }

  // Track baseline (first valid avg) per state
  const baseline = {};
  const lastKnown = {};
  for (const st of states) {
    baseline[st] = null;
    lastKnown[st] = null;
  }

  // Also track overall avg baseline
  let overallBaseline = null;
  let overallLastKnown = null;
  const overallAvg = [];

  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const addrIds = Object.keys(snap);
    if (addrIds.length < 3) continue;

    dates.push(date);

    // Group pest365 values by state for this date
    const byState = {};
    for (const st of states) {
      byState[st] = [];
    }
    const allValues = [];

    for (const addrId of addrIds) {
      const val = snap[addrId]?.pest365;
      const st = addrStateMap[addrId] || 'Unknown';
      if (val != null) {
        if (byState[st]) byState[st].push(val);
        allValues.push(val);
      }
    }

    // Per-state cumulative return
    for (const st of states) {
      if (byState[st].length > 0) {
        const avg = mean(byState[st]);
        if (baseline[st] == null) {
          baseline[st] = avg;
          series[st].push(0);
        } else {
          series[st].push(round(((avg - baseline[st]) / baseline[st]) * 100));
        }
        lastKnown[st] = series[st][series[st].length - 1];
      } else {
        // Carry forward previous value
        series[st].push(lastKnown[st] != null ? lastKnown[st] : 0);
      }
    }

    // Overall avg cumulative return
    if (allValues.length > 0) {
      const avg = mean(allValues);
      if (overallBaseline == null) {
        overallBaseline = avg;
        overallAvg.push(0);
      } else {
        overallAvg.push(round(((avg - overallBaseline) / overallBaseline) * 100));
      }
      overallLastKnown = overallAvg[overallAvg.length - 1];
    } else {
      overallAvg.push(overallLastKnown != null ? overallLastKnown : 0);
    }
  }

  return { dates, states, series, overallAvg };
}

// ---------------------------------------------------------------------------
// Dot Matrix Data (RAG status per address for dot matrix visualization)
// ---------------------------------------------------------------------------

function computeDotMatrixData(addresses, ragStatus) {
  // If ragStatus is available, map directly from it
  if (ragStatus && ragStatus.length > 0) {
    const dotAddresses = ragStatus.map(entry => ({
      addressId: entry.addressId,
      label: entry.address,
      state: entry.state,
      status: entry.status,
    }));

    const counts = { green: 0, amber: 0, red: 0 };
    for (const entry of dotAddresses) {
      if (counts[entry.status] != null) {
        counts[entry.status]++;
      }
    }

    return {
      addresses: dotAddresses,
      counts,
      total: dotAddresses.length,
    };
  }

  // Fallback: derive status from addresses based on price field presence
  const dotAddresses = addresses.map(addr => {
    let missingCount = 0;
    for (const field of PRICE_FIELDS) {
      const val = parsePrice(addr[field]);
      if (val == null && !isNotAvailable(addr[field])) {
        missingCount++;
      }
    }

    let status;
    if (missingCount >= 3) {
      status = 'red';
    } else if (missingCount >= 1) {
      status = 'amber';
    } else {
      status = 'green';
    }

    return {
      addressId: addr.addressId,
      label: addr.address,
      state: addr.state,
      status,
    };
  });

  const counts = { green: 0, amber: 0, red: 0 };
  for (const entry of dotAddresses) {
    if (counts[entry.status] != null) {
      counts[entry.status]++;
    }
  }

  return {
    addresses: dotAddresses,
    counts,
    total: dotAddresses.length,
  };
}

// ---------------------------------------------------------------------------
// Bullet Chart Data (KPI benchmarks)
// ---------------------------------------------------------------------------

function computeBulletData(addresses) {
  /** Nearest-rank percentile on a pre-sorted array. */
  function percentile(sorted, p) {
    if (sorted.length === 0) return null;
    const idx = Math.ceil(p / 100 * sorted.length) - 1;
    return sorted[Math.max(0, idx)];
  }

  const bullets = [];

  // Collect pest365 values
  const pest365Values = addresses.map(a => parsePrice(a.pest365)).filter(v => v != null);
  const sortedPest365 = [...pest365Values].sort((a, b) => a - b);

  // --- Avg Pest365 ---
  const avgPest365 = pest365Values.length > 0 ? round(mean(pest365Values)) : 0;
  const medianPest365 = pest365Values.length > 0 ? round(percentile(sortedPest365, 50)) : 0;
  const p25Pest365 = pest365Values.length > 0 ? round(percentile(sortedPest365, 25)) : 0;
  const p75Pest365 = pest365Values.length > 0 ? round(percentile(sortedPest365, 75)) : 0;
  const maxPest365 = pest365Values.length > 0 ? round(Math.max(...pest365Values)) : 0;

  bullets.push({
    label: 'Avg Pest365',
    subtitle: 'Current average pest365 across all addresses',
    actual: avgPest365,
    target: medianPest365,
    ranges: [p25Pest365, p75Pest365, maxPest365],
    format: 'dollar',
  });

  // --- Data Completeness ---
  let completeCount = 0;
  for (const addr of addresses) {
    let allPresent = true;
    for (const field of PRICE_FIELDS) {
      if (field === 'mosquitoTickControl') {
        // Treat 'not_available' as present
        const val = parsePrice(addr[field]);
        if (val == null && !isNotAvailable(addr[field])) {
          allPresent = false;
          break;
        }
      } else {
        const val = parsePrice(addr[field]);
        if (val == null) {
          allPresent = false;
          break;
        }
      }
    }
    if (allPresent) completeCount++;
  }
  const completeness = addresses.length > 0 ? round((completeCount / addresses.length) * 100) : 0;

  bullets.push({
    label: 'Data Completeness',
    subtitle: '% of addresses with all 5 price fields present',
    actual: completeness,
    target: 95,
    ranges: [70, 85, 100],
    format: 'percent',
  });

  // --- Price Stability ---
  // % of addresses where pest365 hasn't changed in latest 2 scrapes.
  // Addresses only carry current prices (no history), so all addresses with
  // pest365 present are counted as stable from the current-snapshot perspective.
  const stableCount = pest365Values.length;
  const stabilityDenominator = pest365Values.length || 1;
  const stability = round((stableCount / stabilityDenominator) * 100);

  bullets.push({
    label: 'Price Stability',
    subtitle: '% of addresses where pest365 unchanged in latest 2 scrapes',
    actual: stability,
    target: 80,
    ranges: [50, 75, 100],
    format: 'percent',
  });

  // --- Coverage Rate ---
  // % of addresses with mosquitoTickControl available (not 'not_available' and not null)
  let mtcAvailableCount = 0;
  for (const addr of addresses) {
    const val = parsePrice(addr.mosquitoTickControl);
    if (val != null) {
      mtcAvailableCount++;
    }
  }
  const coverageRate = addresses.length > 0 ? round((mtcAvailableCount / addresses.length) * 100) : 0;

  bullets.push({
    label: 'Coverage Rate',
    subtitle: '% of addresses with mosquitoTickControl available',
    actual: coverageRate,
    target: 70,
    ranges: [40, 60, 100],
    format: 'percent',
  });

  // --- Avg ISV ---
  const isvValues = addresses.map(a => parsePrice(a.initialServiceVisit)).filter(v => v != null);
  const sortedISV = [...isvValues].sort((a, b) => a - b);
  const avgISV = isvValues.length > 0 ? round(mean(isvValues)) : 0;
  const medianISV = isvValues.length > 0 ? round(percentile(sortedISV, 50)) : 0;
  const p25ISV = isvValues.length > 0 ? round(percentile(sortedISV, 25)) : 0;
  const p75ISV = isvValues.length > 0 ? round(percentile(sortedISV, 75)) : 0;
  const maxISV = isvValues.length > 0 ? round(Math.max(...isvValues)) : 0;

  bullets.push({
    label: 'Avg ISV',
    subtitle: 'Average initialServiceVisit across all addresses',
    actual: avgISV,
    target: medianISV,
    ranges: [p25ISV, p75ISV, maxISV],
    format: 'dollar',
  });

  return bullets;
}

// ---------------------------------------------------------------------------
// Corridor Data (per-state pest365 min/max/avg over time)
// ---------------------------------------------------------------------------

function computeCorridorData(snapshotsByDate, mergedDates, addresses) {
  // Build addressId -> state lookup
  const addrStateMap = {};
  const stateAddrCount = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    addrStateMap[addr.addressId] = st;
    stateAddrCount[st] = (stateAddrCount[st] || 0) + 1;
  }

  // Collect all unique states, sorted by address count descending
  const statesSet = new Set();
  for (const addr of addresses) {
    statesSet.add(addr.state || 'Unknown');
  }
  const states = [...statesSet].sort((a, b) => (stateAddrCount[b] || 0) - (stateAddrCount[a] || 0));

  const dates = [];

  // Initialize per-state series
  const stateData = {};
  for (const st of states) {
    stateData[st] = { min: [], max: [], avg: [] };
  }

  // Track previous values for carry-forward
  const lastKnown = {};
  for (const st of states) {
    lastKnown[st] = { min: null, max: null, avg: null };
  }

  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const addrIds = Object.keys(snap);
    if (addrIds.length < 3) continue;

    dates.push(date);

    // Group pest365 values by state for this date
    const byState = {};
    for (const st of states) {
      byState[st] = [];
    }

    for (const addrId of addrIds) {
      const val = snap[addrId]?.pest365;
      const st = addrStateMap[addrId] || 'Unknown';
      if (val != null && byState[st]) {
        byState[st].push(val);
      }
    }

    for (const st of states) {
      if (byState[st].length > 0) {
        const vals = byState[st];
        const stMin = round(Math.min(...vals));
        const stMax = round(Math.max(...vals));
        const stAvg = round(mean(vals));
        stateData[st].min.push(stMin);
        stateData[st].max.push(stMax);
        stateData[st].avg.push(stAvg);
        lastKnown[st] = { min: stMin, max: stMax, avg: stAvg };
      } else {
        // Carry forward previous values
        stateData[st].min.push(lastKnown[st].min);
        stateData[st].max.push(lastKnown[st].max);
        stateData[st].avg.push(lastKnown[st].avg);
      }
    }
  }

  // Build result array
  const statesResult = states.map(st => ({
    state: st,
    min: stateData[st].min,
    max: stateData[st].max,
    avg: stateData[st].avg,
  }));

  return { dates, states: statesResult };
}

// ---------------------------------------------------------------------------
// Network Data (state correlation network)
// ---------------------------------------------------------------------------

function computeNetworkData(snapshotsByDate, mergedDates, addresses) {
  const STATE_NAMES = {
    TX: 'Texas', CA: 'California', FL: 'Florida', AZ: 'Arizona', NV: 'Nevada',
    GA: 'Georgia', NC: 'North Carolina', SC: 'South Carolina', VA: 'Virginia',
    MD: 'Maryland', PA: 'Pennsylvania', NJ: 'New Jersey', NY: 'New York',
    OH: 'Ohio', IL: 'Illinois', MI: 'Michigan', IN: 'Indiana', TN: 'Tennessee',
    AL: 'Alabama', MS: 'Mississippi', LA: 'Louisiana', OK: 'Oklahoma',
    CO: 'Colorado', WA: 'Washington', OR: 'Oregon',
  };

  // Build addressId -> state lookup and count addresses per state
  const addrStateMap = {};
  const stateAddrCount = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    addrStateMap[addr.addressId] = st;
    stateAddrCount[st] = (stateAddrCount[st] || 0) + 1;
  }

  // Collect unique states
  const statesSet = new Set();
  for (const addr of addresses) {
    statesSet.add(addr.state || 'Unknown');
  }
  const states = [...statesSet].sort();

  // Build per-state pest365 time series (avg per date)
  const stateSeries = {};
  for (const st of states) {
    stateSeries[st] = [];
  }

  const validDates = [];
  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const addrIds = Object.keys(snap);
    if (addrIds.length < 3) continue;

    validDates.push(date);

    // Group pest365 by state
    const byState = {};
    for (const st of states) {
      byState[st] = [];
    }
    for (const addrId of addrIds) {
      const val = snap[addrId]?.pest365;
      const st = addrStateMap[addrId] || 'Unknown';
      if (val != null && byState[st]) {
        byState[st].push(val);
      }
    }

    for (const st of states) {
      if (byState[st].length > 0) {
        stateSeries[st].push(round(mean(byState[st])));
      } else {
        stateSeries[st].push(null);
      }
    }
  }

  // Compute average pest365 per state (for node data)
  const stateAvgPrice = {};
  for (const st of states) {
    const valid = stateSeries[st].filter(v => v != null);
    stateAvgPrice[st] = valid.length > 0 ? round(mean(valid)) : null;
  }

  // Filter states with at least 3 data points
  const eligibleStates = states.filter(st => {
    const valid = stateSeries[st].filter(v => v != null);
    return valid.length >= 3;
  });

  // Build nodes
  const nodes = eligibleStates.map(st => ({
    id: st,
    label: STATE_NAMES[st] || st,
    addressCount: stateAddrCount[st] || 0,
    avgPrice: stateAvgPrice[st],
  }));

  // Compute Pearson correlation between each pair of eligible states
  const links = [];
  for (let i = 0; i < eligibleStates.length; i++) {
    for (let j = i + 1; j < eligibleStates.length; j++) {
      const stA = eligibleStates[i];
      const stB = eligibleStates[j];
      const corr = pearsonCorrelation(stateSeries[stA], stateSeries[stB]);
      if (corr != null && Math.abs(corr) >= 0.3) {
        links.push({
          source: stA,
          target: stB,
          correlation: round(corr, 4),
        });
      }
    }
  }

  return {
    nodes,
    links,
    threshold: 0.5,
  };
}

// ---------------------------------------------------------------------------
// Gantt Data (per-state price-change timeline segments)
// ---------------------------------------------------------------------------

function computeGanttData(snapshotsByDate, mergedDates, addresses) {
  if (mergedDates.length < 2) {
    return { states: [], dateRange: { start: null, end: null } };
  }

  // Build addressId -> state lookup
  const addrStateMap = {};
  const stateAddrCount = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    addrStateMap[addr.addressId] = st;
    stateAddrCount[st] = (stateAddrCount[st] || 0) + 1;
  }

  // Collect all unique states
  const statesSet = new Set();
  for (const addr of addresses) {
    statesSet.add(addr.state || 'Unknown');
  }
  const allStates = [...statesSet].sort();

  // For each consecutive pair of dates, determine per-state change counts
  // stateSegments[state] = [{ startDate, endDate, status, changeCount }, ...]
  const stateSegments = {};
  const stateTotalChanges = {};
  for (const st of allStates) {
    stateSegments[st] = [];
    stateTotalChanges[st] = 0;
  }

  for (let i = 0; i < mergedDates.length - 1; i++) {
    const startDate = mergedDates[i];
    const endDate = mergedDates[i + 1];
    const curSnap = snapshotsByDate[startDate];
    const nextSnap = snapshotsByDate[endDate];

    // Count per-state how many addresses changed pest365
    const stateChangeCounts = {};
    for (const st of allStates) {
      stateChangeCounts[st] = 0;
    }

    for (const addrId of Object.keys(nextSnap)) {
      if (!curSnap[addrId]) continue;
      const st = addrStateMap[addrId] || 'Unknown';
      const curVal = curSnap[addrId]?.pest365;
      const nextVal = nextSnap[addrId]?.pest365;
      if (curVal != null && nextVal != null && Math.abs(curVal - nextVal) > 0.001) {
        stateChangeCounts[st] = (stateChangeCounts[st] || 0) + 1;
      }
    }

    for (const st of allStates) {
      const changeCount = stateChangeCounts[st] || 0;
      const status = changeCount > 0 ? 'changed' : 'stable';
      stateTotalChanges[st] += changeCount;

      // Try to merge with previous segment if same status
      const segs = stateSegments[st];
      if (segs.length > 0 && segs[segs.length - 1].status === status) {
        segs[segs.length - 1].endDate = endDate;
        segs[segs.length - 1].changeCount += changeCount;
      } else {
        segs.push({ startDate, endDate, status, changeCount });
      }
    }
  }

  // Build result array, sorted by total changeCount descending
  const states = allStates
    .map(st => ({
      state: st,
      addressCount: stateAddrCount[st] || 0,
      segments: stateSegments[st],
    }))
    .sort((a, b) => (stateTotalChanges[b.state] || 0) - (stateTotalChanges[a.state] || 0));

  const dateRange = {
    start: mergedDates[0],
    end: mergedDates[mergedDates.length - 1],
  };

  return { states, dateRange };
}

// ---------------------------------------------------------------------------
// Price Gap Data (pest365Plus - pest365 gap analysis)
// ---------------------------------------------------------------------------

function computePriceGapData(snapshotsByDate, mergedDates, addresses) {
  if (mergedDates.length === 0) {
    return { timeline: { dates: [], avgGap: [], avgGapPercent: [], minGap: [], maxGap: [] }, byState: [], overallAvgGap: null, overallAvgGapPercent: null };
  }

  // Build addressId -> state lookup
  const addrStateMap = {};
  for (const addr of addresses) {
    addrStateMap[addr.addressId] = addr.state || 'Unknown';
  }

  // Timeline: per-date gap stats
  const timelineDates = [];
  const avgGapArr = [];
  const avgGapPercentArr = [];
  const minGapArr = [];
  const maxGapArr = [];

  let latestSnap = null;
  let latestDate = null;

  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const addrIds = Object.keys(snap);

    const gaps = [];
    const gapPercents = [];

    for (const addrId of addrIds) {
      const p365 = snap[addrId]?.pest365;
      const p365Plus = snap[addrId]?.pest365Plus;
      if (p365 != null && p365Plus != null && p365 > 0) {
        const gap = p365Plus - p365;
        gaps.push(gap);
        gapPercents.push((gap / p365) * 100);
      }
    }

    if (gaps.length === 0) continue;

    timelineDates.push(date);
    avgGapArr.push(round(mean(gaps)));
    avgGapPercentArr.push(round(mean(gapPercents)));
    minGapArr.push(round(Math.min(...gaps)));
    maxGapArr.push(round(Math.max(...gaps)));

    latestSnap = snap;
    latestDate = date;
  }

  // byState: using latest date's data
  const byState = [];
  let overallAvgGap = null;
  let overallAvgGapPercent = null;

  if (latestSnap) {
    const stateGaps = {};
    const stateGapPercents = {};
    const stateAddrCounts = {};
    const allGaps = [];
    const allGapPercents = [];

    for (const addrId of Object.keys(latestSnap)) {
      const p365 = latestSnap[addrId]?.pest365;
      const p365Plus = latestSnap[addrId]?.pest365Plus;
      if (p365 != null && p365Plus != null && p365 > 0) {
        const st = addrStateMap[addrId] || 'Unknown';
        const gap = p365Plus - p365;
        const gapPct = (gap / p365) * 100;

        if (!stateGaps[st]) {
          stateGaps[st] = [];
          stateGapPercents[st] = [];
          stateAddrCounts[st] = 0;
        }
        stateGaps[st].push(gap);
        stateGapPercents[st].push(gapPct);
        stateAddrCounts[st]++;
        allGaps.push(gap);
        allGapPercents.push(gapPct);
      }
    }

    for (const st of Object.keys(stateGaps)) {
      byState.push({
        state: st,
        avgGap: round(mean(stateGaps[st])),
        avgGapPercent: round(mean(stateGapPercents[st])),
        addressCount: stateAddrCounts[st],
      });
    }

    // Sort by avgGap descending
    byState.sort((a, b) => (b.avgGap || 0) - (a.avgGap || 0));

    overallAvgGap = round(mean(allGaps));
    overallAvgGapPercent = round(mean(allGapPercents));
  }

  return {
    timeline: {
      dates: timelineDates,
      avgGap: avgGapArr,
      avgGapPercent: avgGapPercentArr,
      minGap: minGapArr,
      maxGap: maxGapArr,
    },
    byState,
    overallAvgGap,
    overallAvgGapPercent,
  };
}

// ---------------------------------------------------------------------------
// Radial Bar Data (avg pest365 by state)
// ---------------------------------------------------------------------------

function computeRadialBarData(addresses) {
  // Group addresses by state
  const stateValues = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    const val = parsePrice(addr.pest365);
    if (val == null) continue;
    if (!stateValues[st]) {
      stateValues[st] = [];
    }
    stateValues[st].push(val);
  }

  // Build result array
  const result = [];
  for (const st of Object.keys(stateValues)) {
    const vals = stateValues[st];
    if (vals.length === 0) continue;
    result.push({
      state: st,
      avgPrice: round(mean(vals)),
      addressCount: vals.length,
    });
  }

  // Sort by avgPrice descending
  result.sort((a, b) => (b.avgPrice || 0) - (a.avgPrice || 0));

  return result;
}

// ---------------------------------------------------------------------------
// Spark Table Data (state-level summary with sparklines & volatility)
// ---------------------------------------------------------------------------

function computeSparkTableData(snapshotsByDate, mergedDates, addresses) {
  // Build addressId -> state lookup
  const addrStateMap = {};
  for (const addr of addresses) {
    addrStateMap[addr.addressId] = addr.state || 'Unknown';
  }

  // Group addresses by state
  const stateAddrs = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!stateAddrs[st]) stateAddrs[st] = [];
    stateAddrs[st].push(addr);
  }

  const result = [];

  for (const [st, addrs] of Object.entries(stateAddrs)) {
    const addrIds = addrs.map(a => a.addressId);
    const addressCount = addrs.length;

    // Current avg/min/max pest365 from latest address data
    const currentPrices = addrs.map(a => parsePrice(a.pest365)).filter(v => v != null);
    const avgPrice = round(mean(currentPrices));
    const minPrice = currentPrices.length > 0 ? round(Math.min(...currentPrices)) : null;
    const maxPrice = currentPrices.length > 0 ? round(Math.max(...currentPrices)) : null;

    // Sparkline: avg pest365 per date for addresses in this state
    const sparkline = [];
    const sparkValues = []; // flat list for volatility calc

    for (const date of mergedDates) {
      const snap = snapshotsByDate[date];
      const vals = [];
      for (const addrId of addrIds) {
        const val = snap[addrId]?.pest365;
        if (val != null) vals.push(val);
      }
      const avg = mean(vals);
      sparkline.push(avg != null ? round(avg) : null);
      if (avg != null) sparkValues.push(avg);
    }

    // changePercent: overall % change from first to last date avg
    const firstVal = sparkline.find(v => v != null);
    const lastVal = [...sparkline].reverse().find(v => v != null);
    let changePercent = null;
    if (firstVal != null && lastVal != null && firstVal !== 0) {
      changePercent = round(((lastVal - firstVal) / firstVal) * 100);
    }

    // Volatility: CV% of avg pest365 across dates (stdDev / mean * 100)
    let volatility = null;
    if (sparkValues.length >= 2) {
      const m = mean(sparkValues);
      const sd = stdDev(sparkValues);
      if (m != null && m !== 0) {
        volatility = round((sd / m) * 100);
      }
    }

    result.push({
      state: st,
      addressCount,
      avgPrice,
      changePercent,
      sparkline,
      minPrice,
      maxPrice,
      volatility,
    });
  }

  // Sort by addressCount descending
  result.sort((a, b) => b.addressCount - a.addressCount);

  return result;
}

// ---------------------------------------------------------------------------
// Area Stack Data (price tier distribution over time)
// ---------------------------------------------------------------------------

function computeAreaStackData(snapshotsByDate, mergedDates) {
  const tiers = ['Budget (<$40)', 'Standard ($40-$50)', 'Premium ($50-$60)', 'Ultra (>$60)'];
  const colors = ['#16a34a', '#2563eb', '#f59e0b', '#dc2626'];

  const dates = [];
  const series = {};
  for (const tier of tiers) {
    series[tier] = [];
  }
  const totals = [];

  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const addrIds = Object.keys(snap);

    // Collect valid pest365 values
    const values = [];
    for (const addrId of addrIds) {
      const val = snap[addrId]?.pest365;
      if (val != null) values.push(val);
    }

    // Skip dates with fewer than 5 valid addresses
    if (values.length < 5) continue;

    dates.push(date);
    totals.push(values.length);

    let budget = 0, standard = 0, premium = 0, ultra = 0;
    for (const v of values) {
      if (v < 40) budget++;
      else if (v < 50) standard++;
      else if (v < 60) premium++;
      else ultra++;
    }

    series['Budget (<$40)'].push(budget);
    series['Standard ($40-$50)'].push(standard);
    series['Premium ($50-$60)'].push(premium);
    series['Ultra (>$60)'].push(ultra);
  }

  return { dates, tiers, colors, series, totals };
}

// ---------------------------------------------------------------------------
// Scatter Matrix Data (pairwise field scatter with correlations)
// ---------------------------------------------------------------------------

function computeScatterMatrixData(addresses) {
  const fields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  const labels = {
    pest365: 'P365',
    pest365Plus: 'P365+',
    oneTime: 'OneTime',
    initialServiceVisit: 'ISV',
  };

  // Build points: only addresses that have all 4 fields as valid numbers
  const points = [];
  for (const addr of addresses) {
    const vals = {};
    let allValid = true;
    for (const f of fields) {
      const v = parsePrice(addr[f]);
      if (v == null) { allValid = false; break; }
      vals[f] = v;
    }
    if (!allValid) continue;

    points.push({
      addressId: addr.addressId,
      label: `${addr.city || ''}, ${addr.state || ''}`.replace(/^, |, $/g, '').trim(),
      state: addr.state || 'Unknown',
      pest365: vals.pest365,
      pest365Plus: vals.pest365Plus,
      oneTime: vals.oneTime,
      initialServiceVisit: vals.initialServiceVisit,
    });
  }

  // Compute Pearson correlation for each pair of fields
  const correlations = {};
  for (let i = 0; i < fields.length; i++) {
    for (let j = i + 1; j < fields.length; j++) {
      const f1 = fields[i];
      const f2 = fields[j];
      const xs = points.map(p => p[f1]);
      const ys = points.map(p => p[f2]);
      const r = pearsonCorrelation(xs, ys);
      correlations[`${f1}_${f2}`] = r != null ? round(r, 4) : null;
    }
  }

  return { fields, labels, points, correlations };
}

// ---------------------------------------------------------------------------
// Price Range Data (distribution stats for pest365)
// ---------------------------------------------------------------------------

function computePriceRangeData(addresses) {
  const values = [];
  for (const addr of addresses) {
    const price = parsePrice(addr.pest365);
    if (price != null) values.push(price);
  }

  if (values.length === 0) return null;

  values.sort((a, b) => a - b);

  const count = values.length;
  const avg = mean(values);
  const sd = stdDev(values);
  const minVal = values[0];
  const maxVal = values[count - 1];

  // Median: middle value of sorted array
  const median = count % 2 === 1
    ? values[Math.floor(count / 2)]
    : (values[count / 2 - 1] + values[count / 2]) / 2;

  // Percentiles using nearest-rank method
  const p25 = values[Math.floor(count * 0.25)];
  const p75 = values[Math.floor(count * 0.75)];
  const iqr = p75 - p25;

  return {
    values,
    stats: {
      count,
      mean: round(avg),
      median: round(median),
      min: round(minVal),
      max: round(maxVal),
      stdDev: round(sd),
      p25: round(p25),
      p75: round(p75),
      iqr: round(iqr),
    },
  };
}

// ---------------------------------------------------------------------------
// Z-Score Data (per-address z-scores over time)
// ---------------------------------------------------------------------------

function computeZScoreData(snapshotsByDate, mergedDates, addresses) {
  // Build addressId -> address info lookup
  const addrMap = {};
  for (const addr of addresses) {
    addrMap[addr.addressId] = addr;
  }

  // Filter dates that have at least 5 addresses with pest365 data
  const validDates = [];
  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    let count = 0;
    for (const addrId of Object.keys(snap)) {
      if (snap[addrId]?.pest365 != null) count++;
    }
    if (count >= 5) validDates.push(date);
  }

  if (validDates.length < 1) return null;

  // For each valid date, compute mean and stdDev of all pest365 values
  const dateMean = {};
  const dateStdDev = {};
  for (const date of validDates) {
    const snap = snapshotsByDate[date];
    const values = [];
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val != null) values.push(val);
    }
    dateMean[date] = mean(values);
    dateStdDev[date] = stdDev(values);
  }

  // For each address, compute z-score on each valid date
  const addressResults = [];
  for (const addr of addresses) {
    const zScores = [];
    let datesWithData = 0;
    for (const date of validDates) {
      const snap = snapshotsByDate[date];
      const val = snap[addr.addressId]?.pest365;
      if (val != null && dateStdDev[date] > 0) {
        const z = round((val - dateMean[date]) / dateStdDev[date], 4);
        zScores.push(z);
        datesWithData++;
      } else {
        zScores.push(null);
      }
    }

    // Only include addresses that have data on at least 3 dates
    if (datesWithData < 3) continue;

    // Check if outlier on latest date (last non-null z-score)
    let isOutlier = false;
    for (let i = zScores.length - 1; i >= 0; i--) {
      if (zScores[i] != null) {
        isOutlier = Math.abs(zScores[i]) > 2;
        break;
      }
    }

    addressResults.push({
      addressId: addr.addressId,
      label: `${addr.city || 'Unknown'}, ${addr.state || 'Unknown'}`,
      state: addr.state || 'Unknown',
      zScores,
      isOutlier,
    });
  }

  return {
    dates: validDates,
    addresses: addressResults,
    thresholds: { warning: 1.5, critical: 2.0 },
  };
}

// ---------------------------------------------------------------------------
// Chord Data (state-to-state price similarity matrix)
// ---------------------------------------------------------------------------

function computeChordData(snapshotsByDate, mergedDates, addresses) {
  const CHORD_COLORS = [
    '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f',
    '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac',
  ];

  // Build addressId -> state lookup
  const addrStateMap = {};
  for (const addr of addresses) {
    addrStateMap[addr.addressId] = addr.state || 'Unknown';
  }

  // Collect unique states
  const statesSet = new Set();
  for (const addr of addresses) {
    statesSet.add(addr.state || 'Unknown');
  }
  const allStates = [...statesSet].sort();

  // Build per-state pest365 time series (avg per date, same as computeNetworkData)
  const stateSeries = {};
  for (const st of allStates) {
    stateSeries[st] = [];
  }

  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const addrIds = Object.keys(snap);
    if (addrIds.length < 3) continue;

    // Group pest365 by state
    const byState = {};
    for (const st of allStates) {
      byState[st] = [];
    }
    for (const addrId of addrIds) {
      const val = snap[addrId]?.pest365;
      const st = addrStateMap[addrId] || 'Unknown';
      if (val != null && byState[st]) {
        byState[st].push(val);
      }
    }

    for (const st of allStates) {
      if (byState[st].length > 0) {
        stateSeries[st].push(round(mean(byState[st])));
      } else {
        stateSeries[st].push(null);
      }
    }
  }

  // Filter states with at least 3 data points
  const states = allStates.filter(st => {
    const valid = stateSeries[st].filter(v => v != null);
    return valid.length >= 3;
  });

  if (states.length < 2) return null;

  // Build 2D symmetric similarity matrix
  const n = states.length;
  const matrix = Array.from({ length: n }, () => Array(n).fill(0));

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const corr = pearsonCorrelation(stateSeries[states[i]], stateSeries[states[j]]);
      const similarity = corr != null ? Math.max(0, round(corr * 100)) : 0;
      matrix[i][j] = similarity;
      matrix[j][i] = similarity;
    }
    // Diagonal = 0
    matrix[i][i] = 0;
  }

  // Assign colors from the palette (cycling if more states than colors)
  const colors = states.map((_, idx) => CHORD_COLORS[idx % CHORD_COLORS.length]);

  return {
    states,
    matrix,
    colors,
  };
}

// ---------------------------------------------------------------------------
// State Ranking Data (rank states by mean pest365 over time)
// ---------------------------------------------------------------------------

function computeStateRankingData(addresses, snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0) return null;

  const PALETTE = ['#2563eb', '#dc2626', '#16a34a', '#f59e0b', '#7c3aed', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16', '#06b6d4', '#e11d48'];

  // Build addressId -> state mapping
  const addrStateMap = {};
  const stateAddrCount = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    addrStateMap[addr.addressId] = st;
    stateAddrCount[st] = (stateAddrCount[st] || 0) + 1;
  }

  // Only include states with at least 2 addresses
  const validStates = new Set(
    Object.entries(stateAddrCount).filter(([, count]) => count >= 2).map(([st]) => st)
  );

  if (validStates.size === 0) return null;

  // For each date, compute mean pest365 per state, then rank
  const stateRanksByDate = {}; // state -> [rank at each date]
  for (const st of validStates) {
    stateRanksByDate[st] = [];
  }

  for (const date of dates) {
    const snap = snapshotsByDate[date];
    // Collect pest365 values grouped by state
    const statePrices = {};
    for (const st of validStates) {
      statePrices[st] = [];
    }
    for (const [addrId, data] of Object.entries(snap)) {
      const st = addrStateMap[addrId];
      if (st && validStates.has(st) && data.pest365 != null && !isNaN(data.pest365)) {
        statePrices[st].push(data.pest365);
      }
    }

    // Compute mean per state for this date
    const stateMeans = [];
    for (const st of validStates) {
      const vals = statePrices[st];
      const avg = vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
      stateMeans.push({ state: st, mean: avg });
    }

    // Rank by mean pest365 descending (1 = most expensive)
    const ranked = stateMeans
      .filter(s => s.mean != null)
      .sort((a, b) => b.mean - a.mean);

    const rankMap = {};
    ranked.forEach((entry, idx) => {
      rankMap[entry.state] = idx + 1;
    });

    for (const st of validStates) {
      stateRanksByDate[st].push(rankMap[st] != null ? rankMap[st] : null);
    }
  }

  // Build states array
  const statesArr = [];
  let colorIdx = 0;
  for (const st of validStates) {
    const ranks = stateRanksByDate[st];
    const validRanks = ranks.filter(r => r != null);
    const firstRank = validRanks.length > 0 ? validRanks[0] : null;
    const lastRank = validRanks.length > 0 ? validRanks[validRanks.length - 1] : null;
    statesArr.push({
      state: st,
      ranks,
      color: PALETTE[colorIdx % PALETTE.length],
      currentRank: lastRank,
      rankChange: firstRank != null && lastRank != null ? firstRank - lastRank : 0,
    });
    colorIdx++;
  }

  // Sort by currentRank ascending
  statesArr.sort((a, b) => (a.currentRank || 999) - (b.currentRank || 999));

  return { dates, states: statesArr };
}

// ---------------------------------------------------------------------------
// Distribution Shift Data (KDE of pest365 first vs last snapshot)
// ---------------------------------------------------------------------------

function computeDistributionShiftData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 2) return null;

  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];

  // Collect pest365 values from each snapshot
  function collectPrices(snap) {
    const prices = [];
    for (const data of Object.values(snap)) {
      const p = data.pest365;
      if (p != null && !isNaN(p)) prices.push(p);
    }
    return prices;
  }

  const firstPrices = collectPrices(snapshotsByDate[firstDate]);
  const lastPrices = collectPrices(snapshotsByDate[lastDate]);

  if (firstPrices.length === 0 || lastPrices.length === 0) return null;

  // Compute stats for an array of values
  function computeStats(values) {
    const sorted = [...values].sort((a, b) => a - b);
    const n = sorted.length;
    const avg = values.reduce((s, v) => s + v, 0) / n;
    const med = n % 2 === 1
      ? sorted[Math.floor(n / 2)]
      : (sorted[n / 2 - 1] + sorted[n / 2]) / 2;
    const variance = values.reduce((s, v) => s + (v - avg) ** 2, 0) / n;
    const sd = Math.sqrt(variance);
    return {
      mean: round(avg),
      median: round(med),
      stdDev: round(sd),
      min: round(sorted[0]),
      max: round(sorted[n - 1]),
    };
  }

  // Kernel Density Estimation using Epanechnikov kernel
  function computeKDE(values) {
    const sorted = [...values].sort((a, b) => a - b);
    const n = sorted.length;
    const minVal = sorted[0];
    const maxVal = sorted[n - 1];
    const range = maxVal - minVal;

    // Bandwidth: Silverman's rule fallback, otherwise range/10
    const sd = Math.sqrt(values.reduce((s, v) => s + (v - values.reduce((a, b) => a + b, 0) / n) ** 2, 0) / n);
    let bandwidth = range > 0 ? range / 10 : 1;
    const silverman = 1.06 * sd * Math.pow(n, -0.2);
    if (silverman > 0 && silverman < bandwidth) {
      bandwidth = silverman;
    }
    if (bandwidth <= 0) bandwidth = 1;

    const padding = range * 0.15;
    const lo = minVal - padding;
    const hi = maxVal + padding;
    const step = (hi - lo) / 99;
    const points = [];

    for (let i = 0; i < 100; i++) {
      const x = lo + i * step;
      let density = 0;
      for (const xi of values) {
        const u = (x - xi) / bandwidth;
        // Epanechnikov kernel: 0.75 * (1 - u^2) for |u| <= 1
        if (Math.abs(u) <= 1) {
          density += 0.75 * (1 - u * u);
        }
      }
      density /= (n * bandwidth);
      points.push({ price: round(x), density: round(density, 6) });
    }

    return points;
  }

  const firstDistribution = computeKDE(firstPrices);
  const lastDistribution = computeKDE(lastPrices);
  const firstStats = computeStats(firstPrices);
  const lastStats = computeStats(lastPrices);

  return {
    firstDate,
    lastDate,
    firstDistribution,
    lastDistribution,
    firstStats,
    lastStats,
    shift: {
      meanShift: round(lastStats.mean - firstStats.mean),
      medianShift: round(lastStats.median - firstStats.median),
      spreadChange: round(lastStats.stdDev - firstStats.stdDev),
    },
  };
}

// ---------------------------------------------------------------------------
// Target Data (state deviation from national average pest365)
// ---------------------------------------------------------------------------

function computeTargetData(addresses, snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0) return null;

  const latestDate = dates[dates.length - 1];
  const snap = snapshotsByDate[latestDate];

  // Build addressId -> state mapping
  const addrStateMap = {};
  for (const addr of addresses) {
    addrStateMap[addr.addressId] = addr.state || 'Unknown';
  }

  // Collect all pest365 values and group by state
  const allPrices = [];
  const statePrices = {};

  for (const [addrId, data] of Object.entries(snap)) {
    const p = data.pest365;
    if (p == null || isNaN(p)) continue;
    allPrices.push(p);
    const st = addrStateMap[addrId] || 'Unknown';
    if (!statePrices[st]) statePrices[st] = [];
    statePrices[st].push(p);
  }

  if (allPrices.length === 0) return null;

  const benchmark = round(allPrices.reduce((s, v) => s + v, 0) / allPrices.length);

  // Build per-state entries — filter out states with fewer than 1 address with valid pest365
  const statesArr = [];
  for (const [state, prices] of Object.entries(statePrices)) {
    if (prices.length < 1) continue;
    const currentPrice = round(prices.reduce((s, v) => s + v, 0) / prices.length);
    const deviation = round(currentPrice - benchmark);
    const deviationPercent = round((deviation / benchmark) * 100);
    let status;
    if (Math.abs(deviationPercent) <= 1) {
      status = 'at_target';
    } else if (deviationPercent > 1) {
      status = 'above';
    } else {
      status = 'below';
    }
    statesArr.push({ state, currentPrice, benchmark, deviation, deviationPercent, status });
  }

  // Sort by deviation descending (most above benchmark first)
  statesArr.sort((a, b) => b.deviation - a.deviation);

  const withinTarget = statesArr.filter(s => s.status === 'at_target').length;
  const totalStates = statesArr.length;
  const overallDeviation = totalStates > 0
    ? round(statesArr.reduce((s, st) => s + Math.abs(st.deviation), 0) / totalStates)
    : 0;

  return { states: statesArr, overallDeviation, withinTarget, totalStates };
}

// ---------------------------------------------------------------------------
// Empty result skeleton (returned when no data)
// ---------------------------------------------------------------------------

function emptyResult() {
  return {
    priceIndex: { timeline: [], currentIndex: 100, baselineDate: null },
    kpis: {
      totalAddresses: 0, totalDataPoints: 0, trackingSinceDays: 0, avgPriceChange: 0,
      addressesWithIncrease: 0, addressesWithDecrease: 0, addressesUnchanged: 0,
      daysSinceLastChange: null, activePromos: [],
      mostExpensiveState: null, cheapestState: null,
    },
    stateAnalysis: [],
    priceArchitecture: {
      pest365PlusSpread: { mean: null, min: null, max: null, stdDev: null },
      pest365PlusRatio: { mean: null, min: null, max: null },
      subscriptionToOneTimeRatio: { mean: null },
      isvToAnnualRatio: { mean: null },
      timeline: [],
    },
    changeEvents: [],
    heatmapData: { dates: [], addresses: [], addressIds: [], matrix: [] },
    priceTiers: { oneTime: [], mosquitoTickControl: [], pest365: [] },
    correlations: { matrix: {}, keyInsight: '' },
    serviceAvailability: {
      mosquitoTickControl: { availableCount: 0, unavailableCount: 0, byState: [], changes: [] },
    },
    anomalies: [],
    insights: [],
    weeklyTrends: { dates: [], avgPest365: [], avgPest365Plus: [], avgOneTime: [], avgISV: [], avgMTC: [] },
    distributions: { pest365ByDate: {}, current: { pest365: [], pest365Plus: [], oneTime: [], initialServiceVisit: [], mosquitoTickControl: [] } },
    rankData: { dates: [], ranks: {} },
    geoData: [],
    calendarData: [],
    candlestickData: [],
    dispersionData: { dates: [], series: {} },
    ridgelineData: { dates: [], distributions: {} },
    ragStatus: [],
    psychologicalPricingData: [],
    priceWarIndicators: null,
    smallMultiplesData: [],
    sankeyData: { periods: [], tiers: [], flows: [] },
    forecastData: null,
    radarData: [],
    percentileBands: null,
    topMovers: null,
    waterfallData: [],
    boxPlotData: [],
    convergenceData: null,
    donutData: null,
    healthScore: null,
    fieldComparisonData: [],
    slopeData: { slopes: [], firstDate: '', lastDate: '' },
    scatterData: [],
    treemapData: [],
    parallelData: [],
    dumbbellData: [],
    horizonData: { dates: [], fields: [] },
    volatilityMatrix: null,
    lollipopData: null,
    beeswarmData: [],
    bandwidthData: null,
    streamData: null,
    velocityData: null,
    marimekkoData: null,
    cumulativeReturnData: null,
    dotMatrixData: null,
    bulletData: [],
    corridorData: null,
    networkData: null,
    ganttData: null,
    priceGapData: null,
    radialBarData: [],
    priceIndexComparison: null,
    histogramData: null,
    polarAreaData: [],
    sparkTableData: [],
    areaStackData: null,
    scatterMatrixData: null,
    movingAverageData: null,
    treemapPricingData: null,
    sunburstData: null,
    priceRangeData: null,
    zScoreData: null,
    chordData: null,
    heatmapGridData: null,
    competitivePositionData: [],
    miniDashboardData: [],
    violinData: [],
    momentumData: null,
    seasonalData: null,
    elasticityData: [],
    rollingVolatilityData: null,
    regimeData: null,
    anomalyTimelineData: null,
    pyramidData: [],
    decileData: null,
    retentionData: null,
    crossCorrelationData: null,
    quartileFlowData: null,
    varianceDecomposition: null,
    cohortData: null,
    spreadTimelineData: null,
    stabilityGaugeData: null,
    geographicClusterData: null,
    priceChangeDistribution: null,
    stateRankingData: null,
    distributionShiftData: null,
    targetData: null,
    indexGrowthData: null,
    addressOutlierData: null,
    volatilityHeatmapData: null,
    accelerationData: null,
    competitiveLandscapeData: null,
    funnelData: null,
    elasticityHeatmapData: null,
    trendRibbonsData: null,
    statePriceProfileData: null,
    priceMemoryData: null,
    clusterDendrogramData: null,
    pricePaceData: null,
    stickinessData: null,
    surpriseData: null,
    cumulativeAnomalyData: null,
    changepointData: null,
    ratioStabilityData: null,
    mosquitoBoundaryData: null,
    bucketMigrationData: null,
    anomalyScorecardData: null,
    rankStabilityData: null,
    deviationBenchmarkData: null,
    cusumData: null,
    shewhartData: null,
    ewmaData: null,
    entropyData: null,
    benfordData: null,
    complexityData: null,
    lorenzCurveData: null,
    qqPlotData: null,
    herfindahlData: null,
    markovTransitionData: null,
    monteCarloFanData: null,
    bayesianUpdateData: null,
    grangerCausalityData: null,
    leadLagData: null,
    impulseResponseData: null,
    recurrencePlotData: null,
    runsTestData: null,
    spectralDensityData: null,
    waveletScalogramData: null,
    phasePortraitData: null,
    lyapunovExponentData: null,
    stateSyncData: null,
    spectralCoherenceData: null,
    regressionDiscontinuityData: null,
    quantileRegressionData: null,
    residualsData: null,
    valueAtRiskData: null,
    pcaBiplotData: null,
    tsneClusterData: null,
    factorLoadingsData: null,
    rsiData: null,
    performanceAttributionData: null,
    addressSimilarityData: null,
    signalNoiseData: null,
    eventStudyData: null,
    counterfactualData: null,
    arimaForecastData: null,
    ensembleForecastData: null,
    priceHalfLifeData: null,
    priceContagionData: null,
    regimeTransitionData: null,
    informationRatioData: null,
    pricePathDivergenceData: null,
  };
}

// ---------------------------------------------------------------------------
// Snapshot Builder
// ---------------------------------------------------------------------------

/**
 * For each date, build a map of addressId -> { pest365, pest365Plus, ... }
 * using the latest entry on that date. This gives us a clean matrix for
 * time-series computations.
 */
function buildSnapshots(sortedHistories, allDates) {
  // snapshots[date][addressId] = { pest365: number|null, ... }
  const snapshots = {};
  for (const date of allDates) {
    snapshots[date] = {};
  }

  for (const [addrId, entries] of Object.entries(sortedHistories)) {
    // Group entries by date, keeping last per date
    const byDate = {};
    for (const e of entries) {
      const d = toDateStr(getTimestamp(e));
      if (d) byDate[d] = e; // last write wins (entries are sorted chronologically)
    }
    for (const [d, e] of Object.entries(byDate)) {
      if (snapshots[d]) {
        snapshots[d][addrId] = {};
        for (const f of PRICE_FIELDS) {
          snapshots[d][addrId][f] = getHistoryPrice(e, f);
        }
        // Also store raw for not_available detection
        snapshots[d][addrId]._raw = {};
        for (const f of PRICE_FIELDS) {
          snapshots[d][addrId]._raw[f] = getHistoryRaw(e, f);
        }
      }
    }
  }

  return snapshots;
}

/**
 * Merge consecutive dates that are ≤1 day apart into a single observation.
 *
 * Observed patterns in real data — all should be merged:
 * - Midnight split: one batch crosses midnight → disjoint addresses on 2 dates
 * - Rolling price update: Competitor rolls out prices over ~2 days → same addresses,
 *   some changed on day 1, rest on day 2
 * - Duplicate batch: re-scrape next day with identical prices
 *
 * The LATER date's prices always win (they represent the settled/final state).
 * For midnight splits this adds new addresses; for rolling updates it overwrites
 * with the fully-settled prices.
 */
function mergeAdjacentDates(allDates, snapshotsByDate) {
  if (allDates.length < 2) return { dates: allDates, snapshots: snapshotsByDate };

  const merged = [allDates[0]];
  const snaps = {};
  snaps[allDates[0]] = { ...snapshotsByDate[allDates[0]] };

  for (let i = 1; i < allDates.length; i++) {
    const prev = merged[merged.length - 1];
    const curr = allDates[i];
    const diffMs = new Date(curr) - new Date(prev);
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    if (diffDays <= 1) {
      // Merge into previous date — later prices overwrite earlier ones
      for (const [addrId, data] of Object.entries(snapshotsByDate[curr])) {
        snaps[prev][addrId] = data;
      }
    } else {
      merged.push(curr);
      snaps[curr] = { ...snapshotsByDate[curr] };
    }
  }

  return { dates: merged, snapshots: snaps };
}

// ---------------------------------------------------------------------------
// 1. CERTUS Price Index (Fisher Ideal)
// ---------------------------------------------------------------------------

function computePriceIndex(snapshotsByDate, allDates, addresses) {
  if (allDates.length === 0) {
    return { timeline: [], currentIndex: 100, baselineDate: null };
  }

  const baselineDate = allDates[0];
  const baseSnap = snapshotsByDate[baselineDate];
  const addrIds = Object.keys(baseSnap);

  if (addrIds.length === 0) {
    return { timeline: [], currentIndex: 100, baselineDate };
  }

  // Baseline average prices per field (across all addresses present in baseline)
  const baseAvg = {};
  for (const f of PRICE_FIELDS) {
    const vals = addrIds.map(id => baseSnap[id]?.[f]).filter(v => v != null);
    baseAvg[f] = vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
  }

  const timeline = [];

  for (const date of allDates) {
    const snap = snapshotsByDate[date];
    const dateAddrIds = Object.keys(snap);
    if (dateAddrIds.length === 0) continue;

    // Current average prices per field
    const currAvg = {};
    for (const f of PRICE_FIELDS) {
      const vals = dateAddrIds.map(id => snap[id]?.[f]).filter(v => v != null);
      currAvg[f] = vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    }

    // Laspeyres: sum(currentPrice * baseWeight) / sum(basePrice * baseWeight)
    // With equal weights, simplifies to: avg(current_i / base_i) across fields
    // Paasche: using current-period weights (also equal here)
    // We use the ratio-of-averages approach for clarity.
    let laspeyresNum = 0, laspeyresDen = 0;
    let paascheNum = 0, paascheDen = 0;
    let fieldsUsed = 0;

    for (const f of PRICE_FIELDS) {
      if (baseAvg[f] != null && baseAvg[f] > 0 && currAvg[f] != null) {
        // Equal weights (1 per field)
        laspeyresNum += currAvg[f]; // current price * base quantity (=1)
        laspeyresDen += baseAvg[f]; // base price * base quantity (=1)
        paascheNum += currAvg[f];   // current price * current quantity (=1)
        paascheDen += baseAvg[f];   // base price * current quantity (=1)
        fieldsUsed++;
      }
    }

    if (fieldsUsed === 0 || laspeyresDen === 0 || paascheDen === 0) {
      timeline.push({ date, fisherIndex: 100, laspeyresIndex: 100, paascheIndex: 100 });
      continue;
    }

    const laspeyresIndex = (laspeyresNum / laspeyresDen) * 100;
    const paascheIndex = (paascheNum / paascheDen) * 100;
    const fisherIndex = Math.sqrt(laspeyresIndex * paascheIndex);

    timeline.push({
      date,
      fisherIndex: round(fisherIndex),
      laspeyresIndex: round(laspeyresIndex),
      paascheIndex: round(paascheIndex),
    });
  }

  const currentIndex = timeline.length > 0 ? timeline[timeline.length - 1].fisherIndex : 100;

  return { timeline, currentIndex, baselineDate };
}

// ---------------------------------------------------------------------------
// 2. KPI Summary
// ---------------------------------------------------------------------------

function computeKPIs(addresses, sortedHistories, allDates, snapshotsByDate) {
  const totalAddresses = addresses.length;

  // Total data points across all history
  let totalDataPoints = 0;
  for (const entries of Object.values(sortedHistories)) {
    totalDataPoints += entries.length;
  }

  // Tracking duration
  let trackingSinceDays = 0;
  if (allDates.length >= 2) {
    const first = new Date(allDates[0]);
    const last = new Date(allDates[allDates.length - 1]);
    trackingSinceDays = Math.round((last - first) / (1000 * 60 * 60 * 24));
  }

  // Per-address cumulative change (pest365 as primary metric)
  let addressesWithIncrease = 0;
  let addressesWithDecrease = 0;
  let addressesUnchanged = 0;
  const cumulativeChanges = [];

  for (const addr of addresses) {
    const hist = sortedHistories[addr.addressId];
    if (!hist || hist.length < 2) {
      addressesUnchanged++;
      continue;
    }
    const first = getHistoryPrice(hist[0], 'pest365');
    const last = getHistoryPrice(hist[hist.length - 1], 'pest365');
    if (first == null || last == null || first === 0) {
      addressesUnchanged++;
      continue;
    }
    const pctChange = ((last - first) / first) * 100;
    cumulativeChanges.push(pctChange);
    if (pctChange > 0.01) addressesWithIncrease++;
    else if (pctChange < -0.01) addressesWithDecrease++;
    else addressesUnchanged++;
  }

  const avgPriceChange = cumulativeChanges.length > 0
    ? round(cumulativeChanges.reduce((s, v) => s + v, 0) / cumulativeChanges.length)
    : 0;

  // Days since last change: find the most recent date where any address changed pest365
  let daysSinceLastChange = null;
  if (allDates.length >= 2) {
    const now = new Date();
    for (let i = allDates.length - 1; i >= 1; i--) {
      const curSnap = snapshotsByDate[allDates[i]];
      const prevSnap = snapshotsByDate[allDates[i - 1]];
      let anyChange = false;
      for (const addrId of Object.keys(curSnap)) {
        if (prevSnap[addrId]) {
          for (const f of PRICE_FIELDS) {
            const c = curSnap[addrId][f];
            const p = prevSnap[addrId][f];
            if (c != null && p != null && Math.abs(c - p) > 0.001) {
              anyChange = true;
              break;
            }
          }
        }
        if (anyChange) break;
      }
      if (anyChange) {
        daysSinceLastChange = Math.round((now - new Date(allDates[i])) / (1000 * 60 * 60 * 24));
        break;
      }
    }
  }

  // Active promos: statistical detection across all price fields
  const activePromos = [];
  const detectedPromos = detectPromoPrices(addresses);
  for (const [field, promo] of detectedPromos) {
    activePromos.push({
      field,
      normalPrice: promo.normalAvg,
      promoPrice: promo.promoPrice,
      affectedCount: promo.count,
    });
  }

  // Most/least expensive state by avg pest365
  const stateAvgs = {};
  for (const addr of addresses) {
    const st = addr.state;
    if (!st) continue;
    const p = parsePrice(addr.pest365);
    if (p == null) continue;
    if (!stateAvgs[st]) stateAvgs[st] = [];
    stateAvgs[st].push(p);
  }
  let mostExpensiveState = null;
  let cheapestState = null;
  let maxAvg = -Infinity;
  let minAvg = Infinity;
  for (const [state, vals] of Object.entries(stateAvgs)) {
    const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
    if (avg > maxAvg) { maxAvg = avg; mostExpensiveState = { state, avgPest365: round(avg) }; }
    if (avg < minAvg) { minAvg = avg; cheapestState = { state, avgPest365: round(avg) }; }
  }

  // Overall Portfolio Average (Pest365)
  const allPrices = addresses.map(a => {
    const p = a.pest365 || (a.pricing && a.pricing.pest365);
    return typeof p === 'number' ? p : parseFloat(String(p).replace(/[$,]/g, ''));
  }).filter(p => !isNaN(p) && p != null);
  
  const overallAvgPrice = allPrices.length > 0 ? round(allPrices.reduce((s, v) => s + v, 0) / allPrices.length) : 0;

  return {
    totalAddresses,
    totalDataPoints,
    trackingSinceDays,
    avgPriceChange,
    overallAvgPrice,
    addressesWithIncrease,
    addressesWithDecrease,
    addressesUnchanged,
    daysSinceLastChange,
    activePromos,
    mostExpensiveState,
    cheapestState,
  };
}

// ---------------------------------------------------------------------------
// 3. State-Level Analysis
// ---------------------------------------------------------------------------

function computeStateAnalysis(addresses, sortedHistories) {
  const byState = {};

  for (const addr of addresses) {
    const st = addr.state;
    if (!st) continue;
    if (!byState[st]) {
      byState[st] = { 
        state: st, 
        pest365: [], 
        pest365Plus: [], 
        oneTime: [], 
        isv: [], 
        mtc: [], 
        mtcAvailableCount: 0, 
        total: 0,
        baselinePest365: [] 
      };
    }
    const g = byState[st];
    g.total++;

    const p365 = parsePrice(addr.pest365);
    const p365p = parsePrice(addr.pest365Plus);
    const ot = parsePrice(addr.oneTime);
    const isv = parsePrice(addr.initialServiceVisit);
    const mtc = parsePrice(addr.mosquitoTickControl);

    if (p365 != null) g.pest365.push(p365);
    if (p365p != null) g.pest365Plus.push(p365p);
    if (ot != null) g.oneTime.push(ot);
    if (isv != null) g.isv.push(isv);
    if (mtc != null) {
      g.mtc.push(mtc);
      g.mtcAvailableCount++;
    }

    // Capture baseline from history if available
    if (sortedHistories && sortedHistories[addr.addressId]) {
      const hist = sortedHistories[addr.addressId];
      if (hist.length > 0) {
        const bp = getHistoryPrice(hist[0], 'pest365');
        if (bp != null) g.baselinePest365.push(bp);
      }
    }
  }

  const result = Object.values(byState).map(g => {
    const avgCurr = mean(g.pest365);
    const avgBase = mean(g.baselinePest365);
    let changePct = 0;
    if (avgBase != null && avgBase > 0 && avgCurr != null) {
      changePct = ((avgCurr - avgBase) / avgBase) * 100;
    }

    return {
      state: g.state,
      addressCount: g.total,
      avgPest365: round(avgCurr),
      avgPest365Plus: round(mean(g.pest365Plus)),
      avgOneTime: round(mean(g.oneTime)),
      avgISV: round(mean(g.isv)),
      avgMTC: round(mean(g.mtc)),
      mtcAvailable: g.mtcAvailableCount > 0,
      baselineAvgPest365: round(avgBase),
      changePercent: round(changePct)
    };
  });

  // Sort by avgPest365 descending
  result.sort((a, b) => (b.avgPest365 || 0) - (a.avgPest365 || 0));

  return result;
}

// ---------------------------------------------------------------------------
// 4. Price Architecture
// ---------------------------------------------------------------------------

function computePriceArchitecture(addresses, snapshotsByDate, allDates) {
  // Current snapshot spreads and ratios
  const spreads = [];
  const ratios = [];
  const subToOneTime = [];
  const isvToAnnual = [];

  for (const addr of addresses) {
    const p365 = parsePrice(addr.pest365);
    const p365p = parsePrice(addr.pest365Plus);
    const ot = parsePrice(addr.oneTime);
    const isv = parsePrice(addr.initialServiceVisit);

    if (p365 != null && p365p != null) {
      spreads.push(p365p - p365);
      if (p365 > 0) ratios.push(p365p / p365);
    }
    if (p365 != null && ot != null && ot > 0) {
      subToOneTime.push((p365 * 12) / ot);
    }
    if (p365 != null && isv != null && p365 > 0) {
      isvToAnnual.push(isv / (p365 * 12));
    }
  }

  const pest365PlusSpread = {
    mean: round(mean(spreads)),
    min: round(safeMin(spreads)),
    max: round(safeMax(spreads)),
    stdDev: round(stdDev(spreads)),
  };

  const pest365PlusRatio = {
    mean: round(mean(ratios), 4),
    min: round(safeMin(ratios), 4),
    max: round(safeMax(ratios), 4),
  };

  const subscriptionToOneTimeRatio = { mean: round(mean(subToOneTime), 4) };
  const isvToAnnualRatio = { mean: round(mean(isvToAnnual), 4) };

  // Timeline: track spread and ratio over time
  const timeline = [];
  for (const date of allDates) {
    const snap = snapshotsByDate[date];
    const dateSpreads = [];
    const dateRatios = [];
    for (const addrId of Object.keys(snap)) {
      const p365 = snap[addrId]?.pest365;
      const p365p = snap[addrId]?.pest365Plus;
      if (p365 != null && p365p != null) {
        dateSpreads.push(p365p - p365);
        if (p365 > 0) dateRatios.push(p365p / p365);
      }
    }
    if (dateSpreads.length > 0) {
      timeline.push({
        date,
        avgSpread: round(mean(dateSpreads)),
        avgRatio: round(mean(dateRatios), 4),
      });
    }
  }

  return { pest365PlusSpread, pest365PlusRatio, subscriptionToOneTimeRatio, isvToAnnualRatio, timeline };
}

// ---------------------------------------------------------------------------
// 5. Price Change Events (Change Detection)
// ---------------------------------------------------------------------------

function computeChangeEvents(sortedHistories, allDates, snapshotsByDate, addresses) {
  if (allDates.length < 2) return [];

  const events = [];
  const totalAddresses = addresses.length;

  for (let i = 1; i < allDates.length; i++) {
    const date = allDates[i];
    const prevDate = allDates[i - 1];
    const curSnap = snapshotsByDate[date];
    const prevSnap = snapshotsByDate[prevDate];

    // Track changes per field
    const fieldChanges = {};
    for (const f of PRICE_FIELDS) {
      fieldChanges[f] = { count: 0, magnitudes: [], directions: [] };
    }

    let totalAffected = new Set();

    for (const addrId of Object.keys(curSnap)) {
      if (!prevSnap[addrId]) continue;
      for (const f of PRICE_FIELDS) {
        const curr = curSnap[addrId][f];
        const prev = prevSnap[addrId][f];
        if (curr != null && prev != null && prev !== 0 && Math.abs(curr - prev) > 0.001) {
          const pctChange = ((curr - prev) / prev) * 100;
          fieldChanges[f].count++;
          fieldChanges[f].magnitudes.push(pctChange);
          fieldChanges[f].directions.push(pctChange > 0 ? 'up' : 'down');
          totalAffected.add(addrId);
        }

        // Check for MTC availability changes
        if (f === 'mosquitoTickControl') {
          const rawCurr = curSnap[addrId]._raw?.[f];
          const rawPrev = prevSnap[addrId]._raw?.[f];
          const currNA = isNotAvailable(rawCurr);
          const prevNA = isNotAvailable(rawPrev);
          if (currNA !== prevNA) {
            totalAffected.add(addrId);
            // MTC availability change events handled below
          }
        }
      }
    }

    if (totalAffected.size === 0) continue;

    // Determine event type for each changed field
    for (const f of PRICE_FIELDS) {
      const fc = fieldChanges[f];
      if (fc.count === 0) continue;

      const avgMag = mean(fc.magnitudes);
      const affectedRatio = fc.count / totalAddresses;

      // Detect coordinated events
      if (affectedRatio >= COORDINATED_THRESHOLD) {
        const direction = avgMag > 0 ? 'increase' : 'decrease';
        events.push({
          date,
          type: 'rate_hike',
          description: `Coordinated ${avgMag > 0 ? '+' : ''}${round(avgMag)}% ${direction} in ${f} across ${fc.count} addresses`,
          affectedAddresses: fc.count,
          affectedFields: [f],
          avgMagnitude: round(avgMag),
        });
      }

      // Statistical promo detection: convergence to / divergence from a common low price
      // Collect new values and old values for changed addresses in this field
      const toValues = {}; // price → [addrIds that moved TO this price]
      const fromValues = {}; // price → [addrIds that moved FROM this price]
      for (const addrId of Object.keys(curSnap)) {
        if (!prevSnap[addrId]) continue;
        const curr = curSnap[addrId][f];
        const prev = prevSnap[addrId][f];
        if (curr != null && prev != null && Math.abs(curr - prev) > 0.01) {
          const toKey = curr.toFixed(2);
          if (!toValues[toKey]) toValues[toKey] = { price: curr, addrs: [] };
          toValues[toKey].addrs.push(addrId);
          const fromKey = prev.toFixed(2);
          if (!fromValues[fromKey]) fromValues[fromKey] = { price: prev, addrs: [] };
          fromValues[fromKey].addrs.push(addrId);
        }
      }

      // Promo start: many addresses converging to the same below-average price
      for (const info of Object.values(toValues)) {
        if (info.addrs.length < PROMO_MIN_COUNT) continue;
        if (info.addrs.length / Math.max(fc.count, 1) < PROMO_CONCENTRATION) continue;
        const fromPrices = info.addrs.map(id => prevSnap[id]?.[f]).filter(v => v != null);
        const fromAvg = mean(fromPrices);
        if (fromAvg != null && fromAvg > 0 && info.price <= fromAvg * PROMO_DISCOUNT) {
          events.push({
            date,
            type: 'promo_start',
            description: `$${info.price} ${fieldLabel(f)} promotion started for ${info.addrs.length} addresses (was ~$${round(fromAvg)})`,
            affectedAddresses: info.addrs.length,
            affectedFields: [f],
            promoPrice: info.price,
            avgMagnitude: round(avgMag),
          });
        }
      }

      // Promo end: many addresses diverging from the same below-average price
      for (const info of Object.values(fromValues)) {
        if (info.addrs.length < PROMO_MIN_COUNT) continue;
        if (info.addrs.length / Math.max(fc.count, 1) < PROMO_CONCENTRATION) continue;
        const toPrices = info.addrs.map(id => curSnap[id]?.[f]).filter(v => v != null);
        const toAvg = mean(toPrices);
        if (toAvg != null && toAvg > 0 && info.price <= toAvg * PROMO_DISCOUNT) {
          events.push({
            date,
            type: 'promo_end',
            description: `$${info.price} ${fieldLabel(f)} promotion ended for ${info.addrs.length} addresses (now ~$${round(toAvg)})`,
            affectedAddresses: info.addrs.length,
            affectedFields: [f],
            promoPrice: info.price,
            avgMagnitude: round(avgMag),
          });
        }
      }
    }

    // MTC service availability changes
    let mtcGained = 0, mtcLost = 0;
    for (const addrId of Object.keys(curSnap)) {
      if (!prevSnap[addrId]) continue;
      const rawCurr = curSnap[addrId]._raw?.mosquitoTickControl;
      const rawPrev = prevSnap[addrId]._raw?.mosquitoTickControl;
      const currNA = isNotAvailable(rawCurr);
      const prevNA = isNotAvailable(rawPrev);
      if (prevNA && !currNA && curSnap[addrId].mosquitoTickControl != null) mtcGained++;
      if (!prevNA && currNA) mtcLost++;
    }
    if (mtcGained > 0) {
      events.push({
        date,
        type: 'service_change',
        description: `Mosquito & Tick Control became available for ${mtcGained} addresses`,
        affectedAddresses: mtcGained,
        affectedFields: ['mosquitoTickControl'],
        avgMagnitude: 0,
      });
    }
    if (mtcLost > 0) {
      events.push({
        date,
        type: 'service_change',
        description: `Mosquito & Tick Control became unavailable for ${mtcLost} addresses`,
        affectedAddresses: mtcLost,
        affectedFields: ['mosquitoTickControl'],
        avgMagnitude: 0,
      });
    }

    // Individual (non-coordinated) changes — only if not already covered by coordinated
    for (const f of PRICE_FIELDS) {
      const fc = fieldChanges[f];
      const affectedRatio = fc.count / totalAddresses;
      if (fc.count > 0 && fc.count <= 5 && affectedRatio < COORDINATED_THRESHOLD) {
        events.push({
          date,
          type: 'individual',
          description: `${fc.count} address${fc.count > 1 ? 'es' : ''} changed ${f} (avg ${round(mean(fc.magnitudes))}%)`,
          affectedAddresses: fc.count,
          affectedFields: [f],
          avgMagnitude: round(mean(fc.magnitudes)),
        });
      }
    }
  }

  // Sort events by date descending (most recent first)
  events.sort((a, b) => b.date.localeCompare(a.date));

  return events;
}

// ---------------------------------------------------------------------------
// 6. Heatmap Data
// ---------------------------------------------------------------------------

function computeHeatmapData(addresses, sortedHistories, allDates, snapshotsByDate) {
  if (allDates.length < 2) {
    return { dates: allDates, addresses: [], addressIds: [], matrix: [] };
  }

  // Sort addresses by state then city then address
  const sortedAddrs = [...addresses].sort((a, b) => {
    const stCmp = (a.state || '').localeCompare(b.state || '');
    if (stCmp !== 0) return stCmp;
    const ctCmp = (a.city || '').localeCompare(b.city || '');
    if (ctCmp !== 0) return ctCmp;
    return (a.address || '').localeCompare(b.address || '');
  });

  // address field already contains city, state, zip — no need to append them
  const addrLabels = sortedAddrs.map(a => a.address || `${a.city}, ${a.state}`);
  const addrIds = sortedAddrs.map(a => a.addressId);

  // matrix[addrIndex][dateIndex] = max % change across ALL price fields
  // Carry forward last known price per field so changes appear on the correct date.
  // Using max-abs across fields ensures the heatmap highlights any price change,
  // not just pest365 changes.
  const heatmapFields = PRICE_FIELDS;

  const matrix = [];
  for (let ai = 0; ai < sortedAddrs.length; ai++) {
    const addrId = sortedAddrs[ai].addressId;

    // Build carry-forward timelines for all fields
    const timelines = {};
    for (const f of heatmapFields) {
      const values = [];
      let lastKnown = null;
      for (let di = 0; di < allDates.length; di++) {
        const val = snapshotsByDate[allDates[di]][addrId]?.[f];
        if (val != null) lastKnown = val;
        values.push(lastKnown);
      }
      timelines[f] = values;
    }

    // For each date, compute the largest absolute % change across all fields
    const row = [];
    for (let di = 0; di < allDates.length; di++) {
      if (di === 0) {
        row.push(null);
        continue;
      }
      let maxChange = null;
      for (const f of heatmapFields) {
        const curr = timelines[f][di];
        const prev = timelines[f][di - 1];
        if (curr != null && prev != null && prev !== 0) {
          const pctChange = round(((curr - prev) / prev) * 100);
          if (pctChange !== 0) {
            if (maxChange == null || Math.abs(pctChange) > Math.abs(maxChange)) {
              maxChange = pctChange;
            }
          }
        }
      }
      row.push(maxChange);
    }
    matrix.push(row);
  }

  const states = sortedAddrs.map(a => a.state || '');
  const cities = sortedAddrs.map(a => a.city || '');

  return { dates: allDates, addresses: addrLabels, addressIds: addrIds, states, cities, matrix };
}

// ---------------------------------------------------------------------------
// 7. Price Tiers / Clusters
// ---------------------------------------------------------------------------

function computePriceTiers(addresses) {
  const buildTiers = (field) => {
    const counts = {};
    for (const addr of addresses) {
      const v = parsePrice(addr[field]);
      if (v == null) {
        // Check for "not_available" specifically for MTC
        if (field === 'mosquitoTickControl' && isNotAvailable(addr[field])) {
          const key = 'not_available';
          counts[key] = (counts[key] || 0) + 1;
        }
        continue;
      }
      // Round to 2 decimal places for grouping
      const key = round(v).toString();
      counts[key] = (counts[key] || 0) + 1;
    }

    const total = Object.values(counts).reduce((s, c) => s + c, 0);
    return Object.entries(counts)
      .map(([value, count]) => ({
        value: value === 'not_available' ? value : parseFloat(value),
        count,
        pctOfTotal: total > 0 ? round((count / total) * 100, 1) : 0,
      }))
      .sort((a, b) => {
        // "not_available" sorts last
        if (a.value === 'not_available') return 1;
        if (b.value === 'not_available') return -1;
        return a.value - b.value;
      });
  };

  return {
    oneTime: buildTiers('oneTime'),
    mosquitoTickControl: buildTiers('mosquitoTickControl'),
    pest365: buildTiers('pest365'),
  };
}

// ---------------------------------------------------------------------------
// 8. Correlations
// ---------------------------------------------------------------------------

function computeCorrelations(addresses) {
  // Build arrays of parsed prices per field
  const arrays = {};
  for (const f of PRICE_FIELDS) {
    arrays[f] = addresses.map(a => parsePrice(a[f]));
  }

  // Compute all pairwise correlations
  const matrix = {};
  let maxCorr = -Infinity;
  let maxPair = '';

  for (let i = 0; i < PRICE_FIELDS.length; i++) {
    for (let j = i + 1; j < PRICE_FIELDS.length; j++) {
      const f1 = PRICE_FIELDS[i];
      const f2 = PRICE_FIELDS[j];
      const key = `${f1}_${f2}`;
      const r = pearsonCorrelation(arrays[f1], arrays[f2]);
      matrix[key] = r != null ? round(r, 4) : null;

      if (r != null && Math.abs(r) > maxCorr) {
        maxCorr = Math.abs(r);
        maxPair = key;
      }
    }
  }

  // Generate key insight
  let keyInsight = '';
  if (maxPair && maxCorr > 0.9) {
    const [f1, f2] = maxPair.split('_');
    const r = matrix[maxPair];
    keyInsight = `${f1} and ${f2} are nearly perfectly correlated (r=${r}), suggesting a formulaic pricing relationship.`;
  } else if (maxPair) {
    const [f1, f2] = maxPair.split('_');
    const r = matrix[maxPair];
    keyInsight = `Strongest correlation: ${f1} and ${f2} (r=${r}).`;
  }

  return { matrix, keyInsight };
}

// ---------------------------------------------------------------------------
// 9. Service Availability
// ---------------------------------------------------------------------------

function computeServiceAvailability(addresses, sortedHistories) {
  let availableCount = 0;
  let unavailableCount = 0;
  const byState = {};
  const changes = [];

  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!byState[st]) byState[st] = { state: st, available: 0, total: 0 };
    byState[st].total++;

    const mtcVal = parsePrice(addr.mosquitoTickControl);
    const mtcNA = isNotAvailable(addr.mosquitoTickControl);

    if (mtcNA || mtcVal == null) {
      unavailableCount++;
    } else {
      availableCount++;
      byState[st].available++;
    }
  }

  // Detect historical changes in MTC availability
  for (const addr of addresses) {
    const hist = sortedHistories[addr.addressId];
    if (!hist || hist.length < 2) continue;

    for (let i = 1; i < hist.length; i++) {
      const prevRaw = getHistoryRaw(hist[i - 1], 'mosquitoTickControl');
      const currRaw = getHistoryRaw(hist[i], 'mosquitoTickControl');
      const prevNA = isNotAvailable(prevRaw) || prevRaw == null;
      const currNA = isNotAvailable(currRaw) || currRaw == null;

      if (prevNA !== currNA) {
        changes.push({
          date: toDateStr(getTimestamp(hist[i])),
          addressId: addr.addressId,
          address: `${addr.address}, ${addr.city}, ${addr.state}`,
          from: prevNA ? 'not_available' : parsePrice(prevRaw),
          to: currNA ? 'not_available' : parsePrice(currRaw),
        });
      }
    }
  }

  // Sort changes by date
  changes.sort((a, b) => a.date.localeCompare(b.date));

  const byStateArr = Object.values(byState).sort((a, b) => b.available - a.available);

  return {
    mosquitoTickControl: {
      availableCount,
      unavailableCount,
      byState: byStateArr,
      changes,
    },
  };
}

// ---------------------------------------------------------------------------
// 10. Anomalies
// ---------------------------------------------------------------------------

function computeAnomalies(addresses) {
  const anomalies = [];
  const currentPromos = detectPromoPrices(addresses);

  // Compute mean and stdDev for each field
  const stats = {};
  for (const f of PRICE_FIELDS) {
    const vals = addresses.map(a => parsePrice(a[f])).filter(v => v != null);
    stats[f] = { mean: mean(vals), stdDev: stdDev(vals) };
  }

  for (const addr of addresses) {
    for (const f of PRICE_FIELDS) {
      const val = parsePrice(addr[f]);
      const rawVal = addr[f];

      // Null / empty value
      if (val == null && !isNotAvailable(rawVal)) {
        // Only flag non-MTC fields as data_quality issues
        if (f !== 'mosquitoTickControl') {
          anomalies.push({
            addressId: addr.addressId,
            address: `${addr.address}, ${addr.city}, ${addr.state}`,
            field: f,
            value: rawVal,
            zScore: null,
            type: 'data_quality',
          });
        }
        continue;
      }

      if (val == null) continue;

      // Z-score outlier detection
      const s = stats[f];
      if (s.stdDev > 0 && s.mean != null) {
        const z = Math.abs((val - s.mean) / s.stdDev);
        if (z > OUTLIER_Z_THRESHOLD) {
          anomalies.push({
            addressId: addr.addressId,
            address: `${addr.address}, ${addr.city}, ${addr.state}`,
            field: f,
            value: val,
            zScore: round(z),
            type: 'outlier',
          });
        }
      }

      // Promo detection: statistical (any field)
      if (currentPromos.has(f) && Math.abs(val - currentPromos.get(f).promoPrice) < 0.01) {
        anomalies.push({
          addressId: addr.addressId,
          address: `${addr.address}, ${addr.city}, ${addr.state}`,
          field: f,
          value: val,
          zScore: null,
          type: 'promo',
        });
      }
    }
  }

  // Sort: outliers first (by z-score desc), then promos, then data_quality
  const typeOrder = { outlier: 0, promo: 1, data_quality: 2 };
  anomalies.sort((a, b) => {
    const tCmp = (typeOrder[a.type] || 9) - (typeOrder[b.type] || 9);
    if (tCmp !== 0) return tCmp;
    return (b.zScore || 0) - (a.zScore || 0);
  });

  return anomalies;
}

// ---------------------------------------------------------------------------
// 11. Natural Language Insights
// ---------------------------------------------------------------------------

function generateInsights(kpis, stateAnalysis, priceArchitecture, changeEvents, correlations, serviceAvailability, priceIndex, addresses) {
  const insights = [];

  // Coordinated price changes
  const coordinated = changeEvents.filter(e => e.type === 'rate_hike');
  if (coordinated.length > 0) {
    const latest = coordinated[0]; // already sorted by date desc
    const direction = latest.avgMagnitude > 0 ? 'increase' : 'decrease';
    insights.push(
      `Competitor implemented a coordinated ${latest.avgMagnitude > 0 ? '+' : ''}${latest.avgMagnitude}% price ${direction} on ${latest.date}, affecting ${latest.affectedAddresses} tracked addresses.`
    );
  }

  // Most expensive state
  if (kpis.mostExpensiveState && kpis.cheapestState && stateAnalysis.length > 1) {
    const natAvg = mean(addresses.map(a => parsePrice(a.pest365)).filter(v => v != null));
    if (natAvg != null && natAvg > 0) {
      const pctAbove = round(((kpis.mostExpensiveState.avgPest365 - natAvg) / natAvg) * 100, 1);
      insights.push(
        `${kpis.mostExpensiveState.state} is the most expensive market, averaging $${kpis.mostExpensiveState.avgPest365}/mo for Pest365 — ${pctAbove}% above the national average.`
      );
    }
  }

  // Cheapest state
  if (kpis.cheapestState && stateAnalysis.length > 1) {
    insights.push(
      `${kpis.cheapestState.state} is the most affordable market at $${kpis.cheapestState.avgPest365}/mo for Pest365.`
    );
  }

  // Promotional pricing insights (any field, statistically detected)
  const promoStartEvents = changeEvents.filter(e => e.type === 'promo_start');
  const promoEndEvents = changeEvents.filter(e => e.type === 'promo_end');
  if (promoStartEvents.length > 0) {
    // Group by field to find start/end pairs
    const seenFields = new Set();
    for (const start of promoStartEvents) {
      const field = start.affectedFields[0];
      if (seenFields.has(field)) continue;
      seenFields.add(field);
      const end = promoEndEvents.find(e => e.affectedFields[0] === field);
      if (end) {
        insights.push(
          `A $${start.promoPrice} ${fieldLabel(field)} promotion ran from ${start.date} to ${end.date}, affecting ${start.affectedAddresses} addresses.`
        );
      }
    }
  }
  // Currently active promos (from KPIs)
  if (kpis.activePromos.length > 0) {
    for (const promo of kpis.activePromos) {
      insights.push(
        `A $${promo.promoPrice} ${fieldLabel(promo.field)} promotion is currently active for ${promo.affectedCount} addresses (normal price ~$${promo.normalPrice}).`
      );
    }
  }

  // Pricing architecture insight (pest365Plus spread)
  if (priceArchitecture.pest365PlusSpread.mean != null && priceArchitecture.pest365PlusSpread.stdDev != null) {
    const spread = priceArchitecture.pest365PlusSpread.mean;
    const r = correlations.matrix.pest365_pest365Plus;
    if (r != null && r > 0.95 && priceArchitecture.pest365PlusSpread.stdDev < 1) {
      insights.push(
        `pest365Plus is priced at exactly pest365 + $${spread} (r=${r}), confirming a formulaic pricing relationship.`
      );
    } else if (r != null) {
      insights.push(
        `pest365Plus trades at a $${spread}/mo premium over pest365 on average (correlation r=${r}).`
      );
    }
  }

  // MTC availability
  const mtc = serviceAvailability.mosquitoTickControl;
  if (mtc.unavailableCount > 0) {
    const pct = round((mtc.unavailableCount / (mtc.availableCount + mtc.unavailableCount)) * 100, 1);
    const unavailStates = mtc.byState
      .filter(s => s.available === 0 && s.total > 0)
      .map(s => s.state);
    if (unavailStates.length > 0) {
      insights.push(
        `Mosquito & Tick Control is unavailable in ${unavailStates.join(', ')} (${pct}% of addresses), consistent with low-risk desert/arid climates.`
      );
    }
  }

  // Price index trend
  if (priceIndex.timeline.length >= 2) {
    const latest = priceIndex.timeline[priceIndex.timeline.length - 1];
    const direction = latest.fisherIndex > 100 ? 'above' : latest.fisherIndex < 100 ? 'below' : 'at';
    insights.push(
      `The CERTUS Price Index stands at ${latest.fisherIndex}, ${Math.abs(round(latest.fisherIndex - 100, 1))}% ${direction} the baseline established on ${priceIndex.baselineDate}.`
    );
  }

  // Tracking summary
  if (kpis.trackingSinceDays > 0) {
    insights.push(
      `Tracking ${kpis.totalAddresses} addresses across ${stateAnalysis.length} states over ${kpis.trackingSinceDays} days, with ${kpis.totalDataPoints} total pricing observations collected.`
    );
  }

  return insights;
}

// ---------------------------------------------------------------------------
// 12. Weekly Trend Data
// ---------------------------------------------------------------------------

function computeWeeklyTrends(snapshotsByDate, allDates) {
  const dates = [];
  const avgPest365 = [];
  const avgPest365Plus = [];
  const avgOneTime = [];
  const avgISV = [];
  const avgMTC = [];

  for (const date of allDates) {
    const snap = snapshotsByDate[date];
    const addrIds = Object.keys(snap);
    if (addrIds.length === 0) continue;

    const p365Vals = addrIds.map(id => snap[id]?.pest365).filter(v => v != null);
    const p365pVals = addrIds.map(id => snap[id]?.pest365Plus).filter(v => v != null);
    const otVals = addrIds.map(id => snap[id]?.oneTime).filter(v => v != null);
    const isvVals = addrIds.map(id => snap[id]?.initialServiceVisit).filter(v => v != null);
    const mtcVals = addrIds.map(id => snap[id]?.mosquitoTickControl).filter(v => v != null);

    dates.push(date);
    avgPest365.push(round(mean(p365Vals)));
    avgPest365Plus.push(round(mean(p365pVals)));
    avgOneTime.push(round(mean(otVals)));
    avgISV.push(round(mean(isvVals)));
    avgMTC.push(round(mean(mtcVals)));
  }

  return { dates, avgPest365, avgPest365Plus, avgOneTime, avgISV, avgMTC };
}

// ---------------------------------------------------------------------------
// 13. Distribution Data
// ---------------------------------------------------------------------------

function computeDistributions(addresses, snapshotsByDate, allDates) {
  // pest365 values by date
  const pest365ByDate = {};
  for (const date of allDates) {
    const snap = snapshotsByDate[date];
    const vals = Object.keys(snap)
      .map(id => snap[id]?.pest365)
      .filter(v => v != null);
    if (vals.length > 0) {
      pest365ByDate[date] = vals;
    }
  }

  // Current snapshot distributions per field
  const current = {};
  for (const f of PRICE_FIELDS) {
    current[f] = addresses
      .map(a => parsePrice(a[f]))
      .filter(v => v != null);
  }

  // Raw MTC values (including nulls for "not_available") — needed for availability pie
  current.mosquitoTickControlAll = addresses.map(a => {
    if (isNotAvailable(a.mosquitoTickControl)) return null;
    return parsePrice(a.mosquitoTickControl);
  });

  return { pest365ByDate, current };
}

// ---------------------------------------------------------------------------
// 14. Calendar Heatmap Data
// ---------------------------------------------------------------------------

function computeCalendarData(snapshotsByDate, allDates, addresses) {
  if (allDates.length < 2) return [];

  const result = [];
  for (let i = 1; i < allDates.length; i++) {
    const date = allDates[i];
    const prevDate = allDates[i - 1];
    const curSnap = snapshotsByDate[date];
    const prevSnap = snapshotsByDate[prevDate];

    let changeCount = 0;
    let totalCount = 0;
    for (const addrId of Object.keys(curSnap)) {
      if (!prevSnap[addrId]) continue;
      totalCount++;
      for (const f of PRICE_FIELDS) {
        const curr = curSnap[addrId][f];
        const prev = prevSnap[addrId][f];
        if (curr != null && prev != null && Math.abs(curr - prev) > 0.001) {
          changeCount++;
          break; // count each address at most once
        }
      }
    }

    result.push({
      date,
      changeCount,
      totalAddresses: totalCount || addresses.length,
    });
  }

  // Also add the first date with 0 changes (baseline)
  if (allDates.length > 0) {
    result.unshift({ date: allDates[0], changeCount: 0, totalAddresses: addresses.length });
  }

  return result;
}

// ---------------------------------------------------------------------------
// 15. Candlestick Data
// ---------------------------------------------------------------------------

function computeCandlestickData(snapshotsByDate, allDates) {
  if (allDates.length === 0) return [];

  const result = [];
  let prevAvg = null;

  for (const date of allDates) {
    const snap = snapshotsByDate[date];
    const vals = Object.keys(snap)
      .map(id => snap[id]?.pest365)
      .filter(v => v != null);

    if (vals.length === 0) continue;

    const avg = round(mean(vals));
    const high = Math.max(...vals);
    const low = Math.min(...vals);

    result.push({
      date,
      open: prevAvg != null ? prevAvg : avg,
      close: avg,
      high: round(high),
      low: round(low),
      avgPrice: avg,
      addressCount: vals.length,
    });

    prevAvg = avg;
  }

  return result;
}

// ---------------------------------------------------------------------------
// 16. Price Dispersion Data (Coefficient of Variation over time)
// ---------------------------------------------------------------------------

function computeDispersionData(snapshotsByDate, allDates) {
  const dates = [];
  const series = {};
  for (const f of PRICE_FIELDS) {
    series[f] = [];
  }

  for (const date of allDates) {
    const snap = snapshotsByDate[date];
    const addrIds = Object.keys(snap);
    if (addrIds.length < 3) continue; // need enough data for meaningful CV

    dates.push(date);
    for (const f of PRICE_FIELDS) {
      const vals = addrIds.map(id => snap[id]?.[f]).filter(v => v != null);
      if (vals.length < 3) {
        series[f].push(null);
      } else {
        const m = mean(vals);
        const sd = stdDev(vals);
        series[f].push(m > 0 ? round((sd / m) * 100, 2) : null);
      }
    }
  }

  return { dates, series };
}

// ---------------------------------------------------------------------------
// 17. Ridgeline Data (distributions per date for density plots)
// ---------------------------------------------------------------------------

function computeRidgelineData(snapshotsByDate, allDates) {
  const dates = [];
  const distributions = {};

  for (const date of allDates) {
    const snap = snapshotsByDate[date];
    const vals = Object.keys(snap)
      .map(id => snap[id]?.pest365)
      .filter(v => v != null);
    if (vals.length >= 3) {
      dates.push(date);
      distributions[date] = vals;
    }
  }

  return { dates, distributions };
}

// ---------------------------------------------------------------------------
// 18. Rank Data (for bump charts)
// ---------------------------------------------------------------------------

function computeRankData(snapshotsByDate, allDates, addresses) {
  if (allDates.length === 0) {
    return { dates: allDates, ranks: {} };
  }

  // For each address, compute its rank (by pest365, descending) at each date.
  // Carry forward last known price so addresses keep their rank on dates
  // they weren't scraped — prevents gaps/disconnects in the bump chart.
  const ranks = {};
  for (const addr of addresses) {
    ranks[addr.addressId] = [];
  }

  const lastKnown = {};

  for (const date of allDates) {
    const snap = snapshotsByDate[date];

    const entries = [];
    for (const addr of addresses) {
      const val = snap[addr.addressId]?.pest365;
      if (val != null) {
        lastKnown[addr.addressId] = val;
      }
      const effectiveVal = val != null ? val : (lastKnown[addr.addressId] ?? null);
      entries.push({ addressId: addr.addressId, value: effectiveVal });
    }

    // Sort by value descending (most expensive = rank 1)
    const withValue = entries.filter(e => e.value != null);
    const withoutValue = entries.filter(e => e.value == null);

    withValue.sort((a, b) => b.value - a.value);

    const rankMap = {};
    withValue.forEach((e, idx) => {
      rankMap[e.addressId] = idx + 1;
    });
    for (const e of withoutValue) {
      rankMap[e.addressId] = null;
    }

    for (const addr of addresses) {
      ranks[addr.addressId].push(rankMap[addr.addressId]);
    }
  }

  return { dates: allDates, ranks };
}

// ---------------------------------------------------------------------------
// 15. Geographic Data
// ---------------------------------------------------------------------------

const STATE_CENTROIDS = {
  FL: { lat: 27.9944, lng: -81.7603 },
  CA: { lat: 36.7783, lng: -119.4179 },
  GA: { lat: 32.1656, lng: -83.5085 },
  WA: { lat: 47.7511, lng: -120.7401 },
  NV: { lat: 38.8026, lng: -116.4194 },
  AZ: { lat: 34.0489, lng: -111.0937 },
  OR: { lat: 43.8041, lng: -120.5542 },
  ID: { lat: 44.0682, lng: -114.7420 },
};

function computeGeoData(addresses, sortedHistories) {
  // Small deterministic jitter to spread markers within the same state
  const jitter = (idx) => (idx % 7 - 3) * 0.3;

  return addresses.map((addr, i) => {
    const p365 = parsePrice(addr.pest365);
    const p365p = parsePrice(addr.pest365Plus);

    // Compute cumulative change from first to current pest365
    let cumulativeChange = null;
    const hist = sortedHistories[addr.addressId];
    if (hist && hist.length >= 2) {
      const first = getHistoryPrice(hist[0], 'pest365');
      const last = getHistoryPrice(hist[hist.length - 1], 'pest365');
      if (first != null && last != null && first > 0) {
        cumulativeChange = round(((last - first) / first) * 100);
      }
    }

    const base = STATE_CENTROIDS[addr.state] || { lat: 39.8283, lng: -98.5795 };

    return {
      addressId: addr.addressId,
      address: addr.address,
      city: addr.city,
      state: addr.state,
      zipCode: addr.zipCode,
      lat: base.lat + jitter(i),
      lng: base.lng + jitter(i + 3),
      currentPest365: p365,
      currentPest365Plus: p365p,
      cumulativeChange,
    };
  });
}

// ---------------------------------------------------------------------------
// Price Index Comparison (rebased to 100)
// ---------------------------------------------------------------------------

function computePriceIndexComparison(snapshotsByDate, allDates) {
  const fields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  const labels = {
    pest365: 'Pest365',
    pest365Plus: 'Pest365+',
    oneTime: 'One-Time',
    initialServiceVisit: 'Initial Service Visit',
  };
  const colors = {
    pest365: '#2563eb',
    pest365Plus: '#7c3aed',
    oneTime: '#059669',
    initialServiceVisit: '#d97706',
  };

  if (allDates.length === 0) return null;

  // Compute average per field per date
  const avgByField = {};
  for (const f of fields) {
    avgByField[f] = [];
  }

  const dates = [];
  for (const date of allDates) {
    const snap = snapshotsByDate[date];
    const addrIds = Object.keys(snap);
    if (addrIds.length === 0) continue;

    dates.push(date);
    for (const f of fields) {
      const vals = addrIds.map(id => snap[id]?.[f]).filter(v => v != null);
      avgByField[f].push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
    }
  }

  if (dates.length === 0) return null;

  // Rebase each series to 100 on the first date
  const series = {};
  for (const f of fields) {
    const baseVal = avgByField[f][0];
    if (baseVal == null || baseVal === 0) continue; // can't rebase — skip field
    series[f] = avgByField[f].map(v => v != null ? round((v / baseVal) * 100, 2) : null);
  }

  return { dates, series, labels, colors };
}

// ---------------------------------------------------------------------------
// Histogram Data (pest365 distributions by state)
// ---------------------------------------------------------------------------

function computeHistogramData(addresses) {
  const D3_TABLEAU10 = ['#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f',
                         '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac'];

  // Group addresses by state
  const byState = {};
  for (const addr of addresses) {
    const st = addr.state;
    if (!st) continue;
    if (!byState[st]) byState[st] = [];
    const val = parsePrice(addr.pest365);
    if (val != null) byState[st].push(val);
  }

  // Only include states with at least 1 valid pest365 value
  const validStates = Object.entries(byState)
    .filter(([, vals]) => vals.length >= 1)
    .sort((a, b) => b[1].length - a[1].length); // sort by address count descending

  const states = validStates.map(([st]) => st);
  const distributions = {};
  const colors = {};

  validStates.forEach(([st, vals], idx) => {
    distributions[st] = vals;
    colors[st] = D3_TABLEAU10[idx % D3_TABLEAU10.length];
  });

  return { field: 'pest365', states, distributions, colors };
}

// ---------------------------------------------------------------------------
// Polar Area Data (per-state price volatility)
// ---------------------------------------------------------------------------

function computePolarAreaData(snapshotsByDate, allDates, addresses) {
  const D3_TABLEAU10 = ['#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f',
                         '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac'];

  if (allDates.length < 2) return [];

  // Build a map of addressId -> state
  const stateByAddr = {};
  for (const addr of addresses) {
    if (addr.state) stateByAddr[addr.addressId] = addr.state;
  }

  // Build per-state pest365 time series (avg per date)
  const stateTimeSeries = {}; // { state: [avgOnDate0, avgOnDate1, ...] }

  for (const date of allDates) {
    const snap = snapshotsByDate[date];
    // Collect pest365 values grouped by state for this date
    const stateVals = {};
    for (const [addrId, data] of Object.entries(snap)) {
      const st = stateByAddr[addrId];
      if (!st) continue;
      const val = data?.pest365;
      if (val != null) {
        if (!stateVals[st]) stateVals[st] = [];
        stateVals[st].push(val);
      }
    }

    // Compute avg per state and append to time series
    for (const [st, vals] of Object.entries(stateVals)) {
      if (!stateTimeSeries[st]) stateTimeSeries[st] = [];
      stateTimeSeries[st].push(vals.reduce((s, v) => s + v, 0) / vals.length);
    }
  }

  // Compute coefficient of variation (CV%) for each state
  const result = [];
  const stateKeys = Object.keys(stateTimeSeries);

  stateKeys.forEach((st, idx) => {
    const series = stateTimeSeries[st];
    if (series.length < 2) return; // need at least 2 data points

    const m = mean(series);
    const sd = stdDev(series);
    if (m == null || m === 0) return;

    const cv = (sd / m) * 100;

    // Count addresses in this state
    const addressCount = addresses.filter(a => a.state === st).length;

    result.push({
      state: st,
      volatility: round(cv, 2),
      avgPrice: round(m),
      addressCount,
      color: D3_TABLEAU10[idx % D3_TABLEAU10.length],
    });
  });

  // Sort by volatility descending
  result.sort((a, b) => b.volatility - a.volatility);

  return result;
}

// ---------------------------------------------------------------------------
// Moving Average Data (3-period short MA, 6-period long MA, crossovers)
// ---------------------------------------------------------------------------

function computeMovingAverageData(snapshotsByDate, mergedDates) {
  if (mergedDates.length === 0) return null;

  const dates = [];
  const raw = [];

  for (const date of mergedDates) {
    const snap = snapshotsByDate[date];
    const addrIds = Object.keys(snap);
    if (addrIds.length === 0) continue;

    const p365Vals = addrIds.map(id => snap[id]?.pest365).filter(v => v != null);
    const avg = p365Vals.length > 0 ? round(mean(p365Vals)) : null;

    dates.push(date);
    raw.push(avg);
  }

  if (raw.length === 0) return null;

  // Compute simple moving averages
  const shortPeriod = 3;
  const longPeriod = 6;

  const shortMA = raw.map((_, i) => {
    if (i < shortPeriod - 1) return null;
    const window = raw.slice(i - shortPeriod + 1, i + 1).filter(v => v != null);
    return window.length === shortPeriod ? round(mean(window)) : null;
  });

  const longMA = raw.map((_, i) => {
    if (i < longPeriod - 1) return null;
    const window = raw.slice(i - longPeriod + 1, i + 1).filter(v => v != null);
    return window.length === longPeriod ? round(mean(window)) : null;
  });

  // Detect crossovers
  const crossovers = [];
  for (let i = 1; i < shortMA.length; i++) {
    if (shortMA[i] == null || longMA[i] == null || shortMA[i - 1] == null || longMA[i - 1] == null) continue;

    if (shortMA[i - 1] <= longMA[i - 1] && shortMA[i] > longMA[i]) {
      crossovers.push({ dateIndex: i, type: 'bullish', date: dates[i] });
    } else if (shortMA[i - 1] >= longMA[i - 1] && shortMA[i] < longMA[i]) {
      crossovers.push({ dateIndex: i, type: 'bearish', date: dates[i] });
    }
  }

  return { dates, raw, shortMA, longMA, crossovers };
}

// ---------------------------------------------------------------------------
// Treemap Pricing Data (pest365 by address, sorted by price)
// ---------------------------------------------------------------------------

function computeTreemapPricingData(addresses, sortedHistories) {
  const items = [];
  let totalPrice = 0;

  for (const addr of addresses) {
    const pest365 = parsePrice(addr.pest365);
    if (pest365 == null) continue;

    // Calculate real changePercent from history if available
    let changePct = 0;
    const history = sortedHistories[addr.addressId];
    if (history && history.length >= 2) {
      const first = parsePrice(history[0].pricing?.pest365 || history[0].pest365);
      const last = parsePrice(history[history.length - 1].pricing?.pest365 || history[history.length - 1].pest365);
      if (first && last && first > 0) {
        changePct = ((last - first) / first) * 100;
      }
    } else if (addr.changePercent != null) {
      changePct = addr.changePercent;
    }

    items.push({
      addressId: addr.addressId,
      label: (addr.city || '') + ', ' + (addr.state || ''),
      state: addr.state || 'Unknown',
      price: pest365,
      changePercent: changePct,
    });
    totalPrice += pest365;
  }

  // Sort by price descending
  items.sort((a, b) => b.price - a.price);

  return { addresses: items, totalPrice: round(totalPrice) };
}

// ---------------------------------------------------------------------------
// Sunburst Data (hierarchical: All States → state → city → address)
// ---------------------------------------------------------------------------

function computeSunburstData(addresses, sortedHistories) {
  // Group addresses by state, then by city
  const stateMap = {};

  for (const addr of addresses) {
    const pest365 = parsePrice(addr.pest365);
    if (pest365 == null) continue;

    const state = addr.state || 'Unknown';
    const city = addr.city || 'Unknown';
    const street = addr.street || (addr.address ? addr.address.split(',')[0].trim() : 'Unknown');

    // Calculate real changePercent from history if available
    let changePct = 0;
    const history = sortedHistories[addr.addressId];
    if (history && history.length >= 2) {
      const first = parsePrice(history[0].pricing?.pest365 || history[0].pest365);
      const last = parsePrice(history[history.length - 1].pricing?.pest365 || history[history.length - 1].pest365);
      if (first && last && first > 0) {
        changePct = ((last - first) / first) * 100;
      }
    }

    if (!stateMap[state]) stateMap[state] = {};
    if (!stateMap[state][city]) stateMap[state][city] = [];

    stateMap[state][city].push({
      name: street,
      price: pest365,
      addressId: addr.addressId,
      changePercent: changePct,
    });
  }

  // Build hierarchy sorted alphabetically
  const stateKeys = Object.keys(stateMap).sort();
  const children = stateKeys.map(state => {
    const cityMap = stateMap[state];
    const cityKeys = Object.keys(cityMap).sort();
    const cityChildren = cityKeys.map(city => ({
      name: city,
      children: cityMap[city],
    }));
    return {
      name: state,
      children: cityChildren,
    };
  });

  return { name: 'All States', children };
}

// ---------------------------------------------------------------------------
// Heatmap Grid Data (state × field averages matrix)
// ---------------------------------------------------------------------------

function computeHeatmapGridData(addresses) {
  const fields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit', 'mosquitoTickControl'];
  const fieldLabels = {
    pest365: 'Pest365',
    pest365Plus: 'Pest365+',
    oneTime: 'One-Time',
    initialServiceVisit: 'ISV',
    mosquitoTickControl: 'MTC',
  };

  // Group addresses by state
  const byState = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!byState[st]) byState[st] = [];
    byState[st].push(addr);
  }

  // Compute average of each field per state
  const values = {};
  for (const [state, addrs] of Object.entries(byState)) {
    values[state] = {};
    for (const field of fields) {
      const vals = addrs
        .map(a => parsePrice(a[field]))
        .filter(v => v != null);
      values[state][field] = vals.length > 0 ? round(mean(vals)) : null;
    }
  }

  // Compute ranges (min/max across all states) for each field
  const ranges = {};
  for (const field of fields) {
    const allVals = Object.values(values)
      .map(sv => sv[field])
      .filter(v => v != null);
    ranges[field] = {
      min: allVals.length > 0 ? round(Math.min(...allVals)) : null,
      max: allVals.length > 0 ? round(Math.max(...allVals)) : null,
    };
  }

  // Sort states by sum of available field averages descending
  const states = Object.keys(values).sort((a, b) => {
    const sumA = fields.reduce((s, f) => s + (values[a][f] != null ? values[a][f] : 0), 0);
    const sumB = fields.reduce((s, f) => s + (values[b][f] != null ? values[b][f] : 0), 0);
    return sumB - sumA;
  });

  return { states, fields, fieldLabels, values, ranges };
}

// ---------------------------------------------------------------------------
// Competitive Position Data (state-level pest365 summary with change & volatility)
// ---------------------------------------------------------------------------

function computeCompetitivePositionData(snapshotsByDate, mergedDates, addresses) {
  // Group addresses by state
  const stateAddrs = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!stateAddrs[st]) stateAddrs[st] = [];
    stateAddrs[st].push(addr);
  }

  const result = [];

  for (const [st, addrs] of Object.entries(stateAddrs)) {
    const addrIds = addrs.map(a => a.addressId);
    const addressCount = addrs.length;

    // Current avg pest365 from latest address data
    const currentPrices = addrs.map(a => parsePrice(a.pest365)).filter(v => v != null);
    const avgPrice = round(mean(currentPrices));

    // Compute per-date averages for change and volatility
    const dateAvgs = [];
    for (const date of mergedDates) {
      const snap = snapshotsByDate[date];
      const vals = [];
      for (const addrId of addrIds) {
        const val = snap[addrId]?.pest365;
        if (val != null) vals.push(val);
      }
      const avg = mean(vals);
      dateAvgs.push(avg != null ? avg : null);
    }

    // changePercent: % change from first to last date avg
    const firstVal = dateAvgs.find(v => v != null);
    const lastVal = [...dateAvgs].reverse().find(v => v != null);
    let changePercent = 0;
    if (firstVal != null && lastVal != null && firstVal !== 0 && dateAvgs.filter(v => v != null).length >= 2) {
      changePercent = round(((lastVal - firstVal) / firstVal) * 100);
    }

    // Volatility: CV% of avg pest365 across dates
    const validAvgs = dateAvgs.filter(v => v != null);
    let volatility = null;
    if (validAvgs.length >= 2) {
      const m = mean(validAvgs);
      const sd = stdDev(validAvgs);
      if (m != null && m !== 0) {
        volatility = round((sd / m) * 100);
      }
    }

    result.push({
      state: st,
      avgPrice,
      changePercent,
      addressCount,
      volatility,
    });
  }

  // Sort by avgPrice descending
  result.sort((a, b) => (b.avgPrice || 0) - (a.avgPrice || 0));

  return result;
}

// ---------------------------------------------------------------------------
// Violin Data (per-state distribution of pest365 across all snapshots)
// ---------------------------------------------------------------------------

function computeViolinData(addresses, snapshots) {
  // Group addresses by state
  const addrState = {};
  for (const addr of addresses) {
    addrState[addr.addressId] = addr.state || 'Unknown';
  }

  // Collect all pest365 values per state across ALL snapshots
  const stateValues = {};
  const dates = Object.keys(snapshots).sort();
  for (const date of dates) {
    const snap = snapshots[date];
    for (const [addrId, priceData] of Object.entries(snap)) {
      const st = addrState[addrId];
      if (!st) continue;
      const val = priceData.pest365;
      if (val != null && !isNaN(val)) {
        if (!stateValues[st]) stateValues[st] = [];
        stateValues[st].push(val);
      }
    }
  }

  const result = [];

  for (const [state, values] of Object.entries(stateValues)) {
    if (values.length < 3) continue;

    const sorted = [...values].sort((a, b) => a - b);
    const n = sorted.length;

    const getPercentile = (arr, p) => {
      const idx = (p / 100) * (arr.length - 1);
      const lo = Math.floor(idx);
      const hi = Math.ceil(idx);
      if (lo === hi) return arr[lo];
      return arr[lo] + (arr[hi] - arr[lo]) * (idx - lo);
    };

    const median = round(getPercentile(sorted, 50));
    const q1 = round(getPercentile(sorted, 25));
    const q3 = round(getPercentile(sorted, 75));
    const avg = round(mean(values));

    result.push({
      state,
      values: sorted.map(v => round(v)),
      median,
      q1,
      q3,
      mean: avg,
      count: n,
      min: round(sorted[0]),
      max: round(sorted[n - 1]),
    });
  }

  // Sort by median descending
  result.sort((a, b) => (b.median || 0) - (a.median || 0));

  return result;
}

// ---------------------------------------------------------------------------
// Momentum Data (momentum, RSI, MACD of mean pest365 over time)
// ---------------------------------------------------------------------------

function computeMomentumData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length === 0) return null;

  // Compute mean pest365 per date
  const meanPrices = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val != null && !isNaN(val)) vals.push(val);
    }
    const avg = mean(vals);
    meanPrices.push(avg != null ? avg : null);
  }

  // Momentum: percent change from 3 periods ago
  const momentum = [];
  for (let i = 0; i < meanPrices.length; i++) {
    if (i < 3 || meanPrices[i] == null || meanPrices[i - 3] == null || meanPrices[i - 3] === 0) {
      momentum.push(0);
    } else {
      momentum.push(round((meanPrices[i] - meanPrices[i - 3]) / meanPrices[i - 3] * 100));
    }
  }

  // RSI (14-period) using exponential moving average
  const rsiPeriod = 14;
  const rsi = [];
  const gains = [];
  const losses = [];
  for (let i = 0; i < meanPrices.length; i++) {
    if (i === 0 || meanPrices[i] == null || meanPrices[i - 1] == null) {
      gains.push(0);
      losses.push(0);
    } else {
      const diff = meanPrices[i] - meanPrices[i - 1];
      gains.push(diff > 0 ? diff : 0);
      losses.push(diff < 0 ? -diff : 0);
    }
  }

  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 0; i < meanPrices.length; i++) {
    if (i < rsiPeriod) {
      // Accumulate initial SMA
      avgGain += gains[i];
      avgLoss += losses[i];
      if (i === rsiPeriod - 1) {
        avgGain /= rsiPeriod;
        avgLoss /= rsiPeriod;
        const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
        rsi.push(round(Math.min(100, Math.max(0, 100 - 100 / (1 + rs)))));
      } else {
        rsi.push(50); // neutral before enough data
      }
    } else {
      // EMA smoothing
      avgGain = (avgGain * (rsiPeriod - 1) + gains[i]) / rsiPeriod;
      avgLoss = (avgLoss * (rsiPeriod - 1) + losses[i]) / rsiPeriod;
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      rsi.push(round(Math.min(100, Math.max(0, 100 - 100 / (1 + rs)))));
    }
  }

  // EMA helper
  const computeEMA = (data, period) => {
    const ema = [];
    const k = 2 / (period + 1);
    for (let i = 0; i < data.length; i++) {
      if (data[i] == null) {
        ema.push(ema.length > 0 ? ema[ema.length - 1] : null);
        continue;
      }
      if (ema.length === 0 || ema[ema.length - 1] == null) {
        ema.push(data[i]);
      } else {
        ema.push(data[i] * k + ema[ema.length - 1] * (1 - k));
      }
    }
    return ema;
  };

  // MACD: EMA(12) - EMA(26) of mean prices
  const ema12 = computeEMA(meanPrices, 12);
  const ema26 = computeEMA(meanPrices, 26);
  const macdLine = [];
  for (let i = 0; i < meanPrices.length; i++) {
    if (ema12[i] != null && ema26[i] != null) {
      macdLine.push(round(ema12[i] - ema26[i], 4));
    } else {
      macdLine.push(0);
    }
  }

  // Signal: EMA(9) of MACD line
  const signal = computeEMA(macdLine, 9).map(v => round(v, 4));
  const histogram = macdLine.map((v, i) => round((v || 0) - (signal[i] || 0), 4));

  return {
    dates,
    momentum,
    rsi,
    macd: {
      line: macdLine,
      signal,
      histogram,
    },
    overbought: 70,
    oversold: 30,
  };
}

// ---------------------------------------------------------------------------
// Seasonal Decomposition Data (trend, seasonal, residual of mean pest365)
// ---------------------------------------------------------------------------

function computeSeasonalData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length === 0) return null;

  const n = dates.length;

  // Observed: mean pest365 per date
  const observed = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val != null && !isNaN(val)) vals.push(val);
    }
    const avg = mean(vals);
    observed.push(avg != null ? round(avg) : null);
  }

  // Trend: centered moving average
  let windowSize = Math.min(7, Math.floor(n / 2) * 2 - 1);
  if (windowSize < 3) windowSize = 3;
  // Ensure odd window
  if (windowSize % 2 === 0) windowSize -= 1;
  if (windowSize < 1) windowSize = 1;

  const halfW = Math.floor(windowSize / 2);
  const trend = new Array(n).fill(null);

  for (let i = halfW; i < n - halfW; i++) {
    const windowVals = [];
    for (let j = i - halfW; j <= i + halfW; j++) {
      if (observed[j] != null) windowVals.push(observed[j]);
    }
    trend[i] = windowVals.length > 0 ? round(mean(windowVals)) : null;
  }

  // Pad edges with nearest available trend value
  // Left edge
  let firstTrend = null;
  for (let i = 0; i < n; i++) {
    if (trend[i] != null) { firstTrend = trend[i]; break; }
  }
  for (let i = 0; i < n; i++) {
    if (trend[i] != null) break;
    trend[i] = firstTrend;
  }
  // Right edge
  let lastTrend = null;
  for (let i = n - 1; i >= 0; i--) {
    if (trend[i] != null) { lastTrend = trend[i]; break; }
  }
  for (let i = n - 1; i >= 0; i--) {
    if (trend[i] != null) break;
    trend[i] = lastTrend;
  }

  // Seasonal: average deviation for each position in cycle
  const period = Math.min(7, n);
  const seasonalAvg = new Array(period).fill(null);
  const seasonalCounts = new Array(period).fill(0);
  const seasonalSums = new Array(period).fill(0);

  for (let i = 0; i < n; i++) {
    if (observed[i] != null && trend[i] != null) {
      const pos = i % period;
      seasonalSums[pos] += observed[i] - trend[i];
      seasonalCounts[pos] += 1;
    }
  }
  for (let p = 0; p < period; p++) {
    seasonalAvg[p] = seasonalCounts[p] > 0 ? seasonalSums[p] / seasonalCounts[p] : 0;
  }

  // Build seasonal and residual arrays
  const seasonal = [];
  const residual = [];
  for (let i = 0; i < n; i++) {
    const s = round(seasonalAvg[i % period]);
    seasonal.push(s);
    if (observed[i] != null && trend[i] != null) {
      residual.push(round(observed[i] - trend[i] - s));
    } else {
      residual.push(null);
    }
  }

  return {
    dates,
    observed,
    trend,
    seasonal,
    residual,
    fieldLabel: 'pest365',
  };
}

// ---------------------------------------------------------------------------
// Mini Dashboard Data (per-field summary with sparklines)
// ---------------------------------------------------------------------------

function computeMiniDashboardData(snapshotsByDate, mergedDates, addresses) {
  const fields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit', 'mosquitoTickControl'];
  const labels = {
    pest365: 'Pest365',
    pest365Plus: 'Pest365+',
    oneTime: 'One-Time',
    initialServiceVisit: 'ISV',
    mosquitoTickControl: 'MTC',
  };
  const colors = {
    pest365: '#2563eb',
    pest365Plus: '#7c3aed',
    oneTime: '#059669',
    initialServiceVisit: '#d97706',
    mosquitoTickControl: '#dc2626',
  };

  const result = [];

  for (const field of fields) {
    // Current values across all addresses
    const currentValues = addresses
      .map(a => parsePrice(a[field]))
      .filter(v => v != null);

    const currentAvg = round(mean(currentValues));
    const min = currentValues.length > 0 ? round(Math.min(...currentValues)) : null;
    const max = currentValues.length > 0 ? round(Math.max(...currentValues)) : null;

    // addressCount: number of addresses with a valid value
    // For MTC, exclude 'not_available' (parsePrice already returns null for those)
    const addressCount = currentValues.length;

    // Sparkline: average of the field per date
    const sparkline = [];
    for (const date of mergedDates) {
      const snap = snapshotsByDate[date];
      const vals = [];
      for (const addrId of Object.keys(snap)) {
        const val = snap[addrId]?.[field];
        if (val != null) vals.push(val);
      }
      const avg = mean(vals);
      sparkline.push(avg != null ? round(avg) : null);
    }

    // changePercent: % change from first to last date's average
    const firstVal = sparkline.find(v => v != null);
    const lastVal = [...sparkline].reverse().find(v => v != null);
    let changePercent = null;
    if (firstVal != null && lastVal != null && firstVal !== 0 && sparkline.filter(v => v != null).length >= 2) {
      changePercent = round(((lastVal - firstVal) / firstVal) * 100);
    }

    result.push({
      field,
      label: labels[field],
      currentAvg,
      changePercent,
      sparkline,
      min,
      max,
      addressCount,
      color: colors[field],
    });
  }

  return result;
}

// ---------------------------------------------------------------------------
// Elasticity Data (price sensitivity / dispersion per state)
// ---------------------------------------------------------------------------

function computeElasticityData(addresses, snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length === 0) return [];

  // Map addressId -> state
  const addrState = {};
  for (const addr of addresses) {
    addrState[addr.addressId] = addr.state || 'Unknown';
  }

  // Collect all pest365 values per state across all snapshots,
  // plus per-address time series for change frequency calculation
  const stateValues = {};      // state -> [all pest365 values]
  const stateAddresses = {};   // state -> Set of addressIds
  const addrTimeSeries = {};   // addressId -> [{ date, pest365 }]

  for (const date of dates) {
    const snap = snapshots[date];
    for (const [addrId, priceData] of Object.entries(snap)) {
      const st = addrState[addrId];
      if (!st) continue;
      const val = priceData.pest365;
      if (val != null && !isNaN(val)) {
        if (!stateValues[st]) stateValues[st] = [];
        stateValues[st].push(val);
        if (!stateAddresses[st]) stateAddresses[st] = new Set();
        stateAddresses[st].add(addrId);
        if (!addrTimeSeries[addrId]) addrTimeSeries[addrId] = [];
        addrTimeSeries[addrId].push({ date, pest365: val });
      }
    }
  }

  const result = [];

  for (const [state, values] of Object.entries(stateValues)) {
    const addrIds = stateAddresses[state];
    const addressCount = addrIds ? addrIds.size : 0;
    if (addressCount < 2) continue;

    const avgPrice = mean(values);
    if (avgPrice == null || avgPrice === 0) continue;

    const sd = stdDev(values);
    const elasticity = round(sd / avgPrice, 4);
    const priceRange = round(Math.max(...values) - Math.min(...values));

    // Change frequency: count date-over-date changes for addresses in this state
    let totalChanges = 0;
    let totalPossibleChanges = 0;
    if (addrIds && dates.length > 1) {
      for (const addrId of addrIds) {
        const series = addrTimeSeries[addrId];
        if (!series || series.length < 2) continue;
        // Sort by date
        series.sort((a, b) => a.date.localeCompare(b.date));
        for (let i = 1; i < series.length; i++) {
          totalPossibleChanges++;
          if (series[i].pest365 !== series[i - 1].pest365) {
            totalChanges++;
          }
        }
      }
    }
    const changeFrequency = totalPossibleChanges > 0
      ? round(Math.max(0, Math.min(1, totalChanges / totalPossibleChanges)), 4)
      : 0;

    result.push({
      state,
      elasticity,
      avgPrice: round(avgPrice),
      priceRange,
      changeFrequency,
      addressCount,
    });
  }

  // Sort by elasticity descending
  result.sort((a, b) => (b.elasticity || 0) - (a.elasticity || 0));

  return result;
}

// ---------------------------------------------------------------------------
// Rolling Volatility Data (std dev of prices over time, with rolling window)
// ---------------------------------------------------------------------------

function computeRollingVolatilityData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length === 0) return null;

  const n = dates.length;

  // Collect per-date values for each field
  const perDate = {
    pest365: [],
    pest365Plus: [],
    oneTime: [],
  };

  for (const date of dates) {
    const snap = snapshots[date];
    const fieldVals = { pest365: [], pest365Plus: [], oneTime: [] };
    for (const addrId of Object.keys(snap)) {
      for (const field of ['pest365', 'pest365Plus', 'oneTime']) {
        const val = snap[addrId]?.[field];
        if (val != null && !isNaN(val)) {
          fieldVals[field].push(val);
        }
      }
    }
    for (const field of ['pest365', 'pest365Plus', 'oneTime']) {
      perDate[field].push(stdDev(fieldVals[field]));
    }
  }

  // Apply rolling window for smoothing
  const windowSize = Math.min(5, n);

  const applyRolling = (arr) => {
    if (n < 3 || windowSize < 2) return arr.map(v => round(v, 4));
    const halfW = Math.floor(windowSize / 2);
    const result = [];
    for (let i = 0; i < n; i++) {
      const start = Math.max(0, i - halfW);
      const end = Math.min(n - 1, i + halfW);
      const windowVals = [];
      for (let j = start; j <= end; j++) {
        if (arr[j] != null && !isNaN(arr[j])) windowVals.push(arr[j]);
      }
      result.push(windowVals.length > 0 ? round(mean(windowVals), 4) : 0);
    }
    return result;
  };

  const overall = applyRolling(perDate.pest365);
  const byField = {
    pest365: applyRolling(perDate.pest365),
    pest365Plus: applyRolling(perDate.pest365Plus),
    oneTime: applyRolling(perDate.oneTime),
  };

  // Dynamic threshold: mean of overall + 1.5 * stddev of overall
  const overallMean = mean(overall);
  const overallStd = stdDev(overall);
  const threshold = round(
    (overallMean != null ? overallMean : 0) + 1.5 * (overallStd || 0),
    4
  );

  return {
    dates,
    overall,
    byField,
    threshold,
  };
}

// ---------------------------------------------------------------------------
// Regime Data (regime detection and change point identification)
// ---------------------------------------------------------------------------

function computeRegimeData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length === 0) return null;

  const n = dates.length;

  // Compute mean pest365 per date across all addresses
  const prices = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val != null && !isNaN(val)) vals.push(val);
    }
    const avg = mean(vals);
    prices.push(avg != null ? round(avg, 4) : null);
  }

  // Change point detection
  const changePoints = [];
  let consecutiveDir = 0; // positive = up streak, negative = down streak

  for (let i = 1; i < n; i++) {
    if (prices[i] == null || prices[i - 1] == null || prices[i - 1] === 0) {
      consecutiveDir = 0;
      continue;
    }

    const pctChange = (prices[i] - prices[i - 1]) / prices[i - 1];
    const absPctChange = Math.abs(pctChange);
    const direction = pctChange >= 0 ? 'up' : 'down';

    // Track consecutive direction
    if (pctChange > 0) {
      consecutiveDir = consecutiveDir > 0 ? consecutiveDir + 1 : 1;
    } else if (pctChange < 0) {
      consecutiveDir = consecutiveDir < 0 ? consecutiveDir - 1 : -1;
    } else {
      consecutiveDir = 0;
    }

    // A change point is where absolute % change > 0.5%
    // OR direction reverses after 2+ consecutive same-direction moves
    const isSignificantChange = absPctChange > 0.005;
    const isDirectionReversal = (
      (direction === 'up' && consecutiveDir === 1 && i >= 3) ||
      (direction === 'down' && consecutiveDir === -1 && i >= 3)
    );
    // Check for reversal: previous streak was 2+ in opposite direction
    let hadPriorStreak = false;
    if (i >= 2 && prices[i - 2] != null && prices[i - 1] != null && prices[i - 2] !== 0) {
      const prevChange = (prices[i - 1] - prices[i - 2]) / prices[i - 2];
      if ((prevChange > 0 && pctChange < 0) || (prevChange < 0 && pctChange > 0)) {
        // Check if there were 2+ consecutive moves before
        if (i >= 3 && prices[i - 3] != null && prices[i - 2] !== 0) {
          const prevPrevChange = (prices[i - 2] - prices[i - 3]) / prices[i - 3];
          if ((prevPrevChange > 0 && prevChange > 0) || (prevPrevChange < 0 && prevChange < 0)) {
            hadPriorStreak = true;
          }
        }
      }
    }

    if (isSignificantChange || hadPriorStreak) {
      changePoints.push({
        idx: i,
        date: dates[i],
        magnitude: round(pctChange * 100, 4),
        direction,
      });
    }
  }

  // Regime segmentation: split at change points
  const regimes = [];
  const segmentBoundaries = [0];
  for (const cp of changePoints) {
    segmentBoundaries.push(cp.idx);
  }
  segmentBoundaries.push(n);

  for (let s = 0; s < segmentBoundaries.length - 1; s++) {
    const startIdx = segmentBoundaries[s];
    const endIdx = segmentBoundaries[s + 1] - 1;
    if (startIdx > endIdx) continue;

    // Collect prices in this regime
    const regimePrices = [];
    for (let i = startIdx; i <= endIdx; i++) {
      if (prices[i] != null) regimePrices.push(prices[i]);
    }
    if (regimePrices.length === 0) continue;

    const avgPrice = round(mean(regimePrices));
    const regimeMean = mean(regimePrices);
    const volatility = round(regimeMean != null && regimeMean !== 0
      ? stdDev(regimePrices) / regimeMean
      : 0, 4);

    // Determine trend: average price change within the regime
    let avgPriceChange = 0;
    if (regimePrices.length >= 2 && regimePrices[0] !== 0) {
      avgPriceChange = (regimePrices[regimePrices.length - 1] - regimePrices[0]) /
        regimePrices[0] / Math.max(1, regimePrices.length - 1);
    }

    let label, color;
    if (avgPriceChange > 0.001) {
      label = 'Rising';
      color = '#dcfce7';
    } else if (avgPriceChange < -0.001) {
      label = 'Declining';
      color = '#fee2e2';
    } else if (volatility > 0.02) {
      label = 'Volatile';
      color = '#fef3c7';
    } else {
      label = 'Stable';
      color = '#dbeafe';
    }

    regimes.push({
      startIdx,
      endIdx,
      label,
      color,
      avgPrice,
      volatility,
    });
  }

  // If no change points were detected, create a single stable regime
  if (regimes.length === 0 && n > 0) {
    const allValid = prices.filter(v => v != null);
    const avgPrice = round(mean(allValid));
    const regimeMean = mean(allValid);
    const volatility = round(regimeMean != null && regimeMean !== 0
      ? stdDev(allValid) / regimeMean
      : 0, 4);

    regimes.push({
      startIdx: 0,
      endIdx: n - 1,
      label: 'Stable',
      color: '#dbeafe',
      avgPrice,
      volatility,
    });
  }

  return {
    dates,
    prices: prices.map(v => v != null ? round(v) : null),
    regimes,
    changePoints,
  };
}

// ---------------------------------------------------------------------------
// Anomaly Timeline Data
// ---------------------------------------------------------------------------

function computeAnomalyTimelineData(addresses, snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0) {
    return { dates: [], priceLine: [], anomalies: [] };
  }

  // Build address lookup for labels
  const addrMap = {};
  for (const addr of addresses) {
    addrMap[addr.addressId] = `${addr.city || ''}, ${addr.state || ''}`;
  }

  // All address IDs that appear in any snapshot
  const allAddrIds = new Set();
  for (const date of dates) {
    for (const id of Object.keys(snapshotsByDate[date])) {
      allAddrIds.add(id);
    }
  }

  const priceLine = [];
  // key: "addrId|date" -> anomaly object (for dedup)
  const anomalyMap = {};

  // Track previous prices per address for temporal change detection
  const prevPriceByAddr = {};

  for (const date of dates) {
    const snap = snapshotsByDate[date];
    const vals = [];
    const addrPrices = {};

    for (const addrId of allAddrIds) {
      const p = snap[addrId]?.pest365;
      if (p != null) {
        vals.push(p);
        addrPrices[addrId] = p;
      }
    }

    const dateMean = mean(vals);
    priceLine.push(dateMean != null ? round(dateMean) : null);

    if (vals.length < 2) {
      // Update prev prices
      for (const [addrId, price] of Object.entries(addrPrices)) {
        prevPriceByAddr[addrId] = price;
      }
      continue;
    }

    const dateStdDev = stdDev(vals);

    // Cross-sectional anomaly detection (z-score based)
    for (const addrId of allAddrIds) {
      const key = `${addrId}|${date}`;
      const price = snap[addrId]?.pest365;

      // Check for missing data: address exists in other dates but not this one
      if (price == null) {
        // Only flag as missing if this address has data in at least one other date
        let hasOtherData = false;
        for (const d of dates) {
          if (d !== date && snapshotsByDate[d][addrId]?.pest365 != null) {
            hasOtherData = true;
            break;
          }
        }
        if (hasOtherData) {
          const existing = anomalyMap[key];
          if (!existing || severityRank('medium') > severityRank(existing.severity)) {
            anomalyMap[key] = {
              date,
              price: null,
              severity: 'medium',
              type: 'missing',
              address: addrMap[addrId] || addrId,
              field: 'pest365',
              zScore: null,
              deviation: null,
            };
          }
        }
        continue;
      }

      // Z-score anomaly
      if (dateStdDev > 0 && dateMean != null) {
        const zScore = (price - dateMean) / dateStdDev;
        const absZ = Math.abs(zScore);
        let severity = null;
        if (absZ > 3) severity = 'critical';
        else if (absZ > 2) severity = 'high';
        else if (absZ > 1.5) severity = 'medium';

        if (severity) {
          let type = 'spike';
          if (price < dateMean - 1.5 * dateStdDev) type = 'drop';

          const existing = anomalyMap[key];
          if (!existing || severityRank(severity) > severityRank(existing.severity)) {
            anomalyMap[key] = {
              date,
              price: round(price),
              severity,
              type,
              address: addrMap[addrId] || addrId,
              field: 'pest365',
              zScore: round(zScore, 4),
              deviation: round(Math.abs(price - dateMean)),
            };
          }
        }
      }

      // Temporal anomaly: compare to own previous price
      const prevPrice = prevPriceByAddr[addrId];
      if (prevPrice != null && prevPrice !== 0) {
        const pctChange = Math.abs((price - prevPrice) / prevPrice) * 100;
        if (pctChange > 5) {
          let severity = null;
          if (pctChange > 10) severity = 'critical';
          else if (pctChange > 7) severity = 'high';
          else severity = 'medium';

          const type = price > prevPrice ? 'spike' : 'drop';
          const existing = anomalyMap[key];
          if (!existing || severityRank(severity) > severityRank(existing.severity)) {
            anomalyMap[key] = {
              date,
              price: round(price),
              severity,
              type,
              address: addrMap[addrId] || addrId,
              field: 'pest365',
              zScore: anomalyMap[key]?.zScore ?? null,
              deviation: round(Math.abs(price - (dateMean || 0))),
            };
          }
        }
      }
    }

    // Update prev prices for next iteration
    for (const addrId of allAddrIds) {
      const p = snap[addrId]?.pest365;
      if (p != null) {
        prevPriceByAddr[addrId] = p;
      }
    }
  }

  // Collect and sort anomalies by date
  const anomalies = Object.values(anomalyMap).sort(
    (a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)
  );

  return { dates, priceLine, anomalies };
}

/** Helper for anomaly severity ranking (higher = more severe). */
function severityRank(s) {
  if (s === 'critical') return 3;
  if (s === 'high') return 2;
  if (s === 'medium') return 1;
  return 0;
}

// ---------------------------------------------------------------------------
// Pyramid Data (state-level price averages)
// ---------------------------------------------------------------------------

function computePyramidData(addresses, snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0) return [];

  // Use the latest snapshot
  const latestDate = dates[dates.length - 1];
  const snap = snapshotsByDate[latestDate];

  // Build addressId -> state map
  const addrStateMap = {};
  for (const addr of addresses) {
    addrStateMap[addr.addressId] = addr.state || 'Unknown';
  }

  // Group prices by state
  const stateData = {};
  for (const [addrId, prices] of Object.entries(snap)) {
    const state = addrStateMap[addrId] || 'Unknown';
    if (!stateData[state]) {
      stateData[state] = { pest365: [], pest365Plus: [], oneTime: [], count: 0 };
    }
    stateData[state].count++;
    const p365 = prices?.pest365;
    const p365p = prices?.pest365Plus;
    const ot = prices?.oneTime;
    if (p365 != null && !isNaN(p365)) stateData[state].pest365.push(p365);
    if (p365p != null && !isNaN(p365p)) stateData[state].pest365Plus.push(p365p);
    if (ot != null && !isNaN(ot)) stateData[state].oneTime.push(ot);
  }

  const result = [];
  for (const [state, data] of Object.entries(stateData)) {
    const pest365Avg = mean(data.pest365);
    // Filter out states with zero valid pest365 prices
    if (pest365Avg == null) continue;

    result.push({
      state,
      pest365Avg: round(pest365Avg),
      pest365PlusAvg: round(mean(data.pest365Plus)),
      oneTimeAvg: round(mean(data.oneTime)),
      addressCount: data.count,
    });
  }

  // Sort by pest365Avg descending
  result.sort((a, b) => (b.pest365Avg || 0) - (a.pest365Avg || 0));

  return result;
}

// ---------------------------------------------------------------------------
// Decile Data (price distribution deciles over time)
// ---------------------------------------------------------------------------

function computeDecileData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0) {
    return { dates: [], deciles: [], currentDistribution: [] };
  }

  const deciles = [];

  for (const date of dates) {
    const snap = snapshotsByDate[date];
    const vals = Object.keys(snap)
      .map(id => snap[id]?.pest365)
      .filter(v => v != null && !isNaN(v))
      .sort((a, b) => a - b);

    if (vals.length < 2) continue;

    deciles.push({
      date,
      d10: round(percentile(vals, 10)),
      d20: round(percentile(vals, 20)),
      d30: round(percentile(vals, 30)),
      d40: round(percentile(vals, 40)),
      d50: round(percentile(vals, 50)),
      d60: round(percentile(vals, 60)),
      d70: round(percentile(vals, 70)),
      d80: round(percentile(vals, 80)),
      d90: round(percentile(vals, 90)),
    });
  }

  // Current distribution: use the latest snapshot
  const latestDate = dates[dates.length - 1];
  const latestSnap = snapshotsByDate[latestDate];
  const latestVals = Object.keys(latestSnap)
    .map(id => latestSnap[id]?.pest365)
    .filter(v => v != null && !isNaN(v))
    .sort((a, b) => a - b);

  const currentDistribution = [];
  if (latestVals.length >= 10) {
    for (let i = 0; i < 10; i++) {
      const lower = i * 10;
      const upper = (i + 1) * 10;
      const startIdx = Math.floor(latestVals.length * lower / 100);
      const endIdx = Math.min(Math.ceil(latestVals.length * upper / 100), latestVals.length);
      const groupVals = latestVals.slice(startIdx, endIdx);

      if (groupVals.length > 0) {
        currentDistribution.push({
          decile: `D${i + 1} (${lower}-${upper}%)`,
          count: groupVals.length,
          minPrice: round(groupVals[0]),
          maxPrice: round(groupVals[groupVals.length - 1]),
          avgPrice: round(mean(groupVals)),
        });
      }
    }
  } else if (latestVals.length > 0) {
    // Not enough data for 10 groups — put everything in one bucket
    currentDistribution.push({
      decile: 'D1 (0-100%)',
      count: latestVals.length,
      minPrice: round(latestVals[0]),
      maxPrice: round(latestVals[latestVals.length - 1]),
      avgPrice: round(mean(latestVals)),
    });
  }

  return {
    dates: deciles.map(d => d.date),
    deciles,
    currentDistribution,
  };
}

// ---------------------------------------------------------------------------
// Retention Data (price retention cohort analysis)
// ---------------------------------------------------------------------------

function computeRetentionData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0) {
    return { periods: [], retentionByField: { pest365: [], pest365Plus: [], oneTime: [] }, overallRetention: [], cohortSize: 0 };
  }

  const FIELDS = ['pest365', 'pest365Plus', 'oneTime'];
  const baselineDate = dates[0];
  const baselineSnap = snapshotsByDate[baselineDate];

  // Find addresses with valid numeric values for all 3 fields in the baseline
  const cohortAddresses = [];
  const baselineValues = {}; // { addressId: { pest365, pest365Plus, oneTime } }
  for (const [addrId, prices] of Object.entries(baselineSnap)) {
    const vals = {};
    let allValid = true;
    for (const f of FIELDS) {
      const v = prices?.[f];
      if (v == null || isNaN(v)) { allValid = false; break; }
      vals[f] = v;
    }
    if (allValid) {
      cohortAddresses.push(addrId);
      baselineValues[addrId] = vals;
    }
  }

  const cohortSize = cohortAddresses.length;
  if (cohortSize === 0) {
    return { periods: [], retentionByField: { pest365: [], pest365Plus: [], oneTime: [] }, overallRetention: [], cohortSize: 0 };
  }

  const periods = [];
  const retentionByField = { pest365: [], pest365Plus: [], oneTime: [] };
  const overallRetention = [];

  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];
    const snap = snapshotsByDate[date];
    periods.push(date);

    if (i === 0) {
      // Period 0: 100% retention for all
      for (const f of FIELDS) {
        retentionByField[f].push(100);
      }
      overallRetention.push(100);
      continue;
    }

    const fieldCounts = { pest365: 0, pest365Plus: 0, oneTime: 0 };
    let overallCount = 0;

    for (const addrId of cohortAddresses) {
      const currentPrices = snap[addrId];
      if (!currentPrices) continue; // address not present in this snapshot

      let allMatch = true;
      for (const f of FIELDS) {
        const currentVal = currentPrices[f];
        if (currentVal != null && !isNaN(currentVal) && currentVal === baselineValues[addrId][f]) {
          fieldCounts[f]++;
        } else {
          allMatch = false;
        }
      }
      if (allMatch && currentPrices.pest365 != null) {
        overallCount++;
      }
    }

    for (const f of FIELDS) {
      retentionByField[f].push(round(fieldCounts[f] / cohortSize * 100));
    }
    overallRetention.push(round(overallCount / cohortSize * 100));
  }

  return { periods, retentionByField, overallRetention, cohortSize };
}

// ---------------------------------------------------------------------------
// Cross-Correlation Data (field-pair Pearson correlations over time)
// ---------------------------------------------------------------------------

function computeCrossCorrelationData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  const FIELDS = ['pest365', 'pest365Plus', 'oneTime'];
  const PAIR_DEFS = [
    { fieldA: 'pest365', fieldB: 'pest365Plus', label: 'pest365 vs pest365Plus' },
    { fieldA: 'pest365', fieldB: 'oneTime', label: 'pest365 vs oneTime' },
    { fieldA: 'pest365Plus', fieldB: 'oneTime', label: 'pest365Plus vs oneTime' },
  ];

  if (dates.length < 2) {
    return {
      pairs: PAIR_DEFS.map(p => ({ fieldA: p.fieldA, fieldB: p.fieldB, correlation: 0, lag: 0, timeSeries: [] })),
      summary: { strongestPair: null, weakestPair: null, avgCorrelation: 0 },
    };
  }

  // Build mean time series for each field
  const meanSeries = {};
  for (const f of FIELDS) {
    meanSeries[f] = [];
  }

  for (const date of dates) {
    const snap = snapshotsByDate[date];
    const fieldValues = { pest365: [], pest365Plus: [], oneTime: [] };

    for (const prices of Object.values(snap)) {
      for (const f of FIELDS) {
        const v = prices?.[f];
        if (v != null && !isNaN(v)) {
          fieldValues[f].push(v);
        }
      }
    }

    for (const f of FIELDS) {
      meanSeries[f].push(mean(fieldValues[f]));
    }
  }

  // Compute Pearson correlation for each pair
  const pairs = [];
  for (const pDef of PAIR_DEFS) {
    const seriesA = meanSeries[pDef.fieldA];
    const seriesB = meanSeries[pDef.fieldB];

    // Build time series for the pair
    const timeSeries = dates.map((date, i) => ({
      date,
      valueA: seriesA[i] != null ? round(seriesA[i]) : null,
      valueB: seriesB[i] != null ? round(seriesB[i]) : null,
    }));

    // Compute Pearson correlation
    const validPairs = [];
    for (let i = 0; i < seriesA.length; i++) {
      if (seriesA[i] != null && seriesB[i] != null && !isNaN(seriesA[i]) && !isNaN(seriesB[i])) {
        validPairs.push([seriesA[i], seriesB[i]]);
      }
    }

    let correlation = 0;
    if (validPairs.length >= 2) {
      const n = validPairs.length;
      const meanA = validPairs.reduce((s, p) => s + p[0], 0) / n;
      const meanB = validPairs.reduce((s, p) => s + p[1], 0) / n;
      let num = 0, denA = 0, denB = 0;
      for (const [a, b] of validPairs) {
        const da = a - meanA;
        const db = b - meanB;
        num += da * db;
        denA += da * da;
        denB += db * db;
      }
      const den = Math.sqrt(denA * denB);
      correlation = den === 0 ? 0 : round(num / den, 4);
    }

    pairs.push({
      fieldA: pDef.fieldA,
      fieldB: pDef.fieldB,
      correlation,
      lag: 0,
      timeSeries,
    });
  }

  // Summary
  const absPairs = pairs.map(p => ({ label: PAIR_DEFS.find(d => d.fieldA === p.fieldA && d.fieldB === p.fieldB).label, absCorr: Math.abs(p.correlation) }));
  absPairs.sort((a, b) => b.absCorr - a.absCorr);
  const avgCorrelation = round(absPairs.reduce((s, p) => s + p.absCorr, 0) / absPairs.length, 4);

  return {
    pairs,
    summary: {
      strongestPair: absPairs[0]?.label || null,
      weakestPair: absPairs[absPairs.length - 1]?.label || null,
      avgCorrelation,
    },
  };
}

// ---------------------------------------------------------------------------
// Quartile Flow Data (address movement between pest365 quartiles)
// ---------------------------------------------------------------------------

function computeQuartileFlowData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0) {
    return { periods: [], flows: [], totalAddresses: 0 };
  }

  // Helper: assign quartile labels to sorted addresses by pest365
  function assignQuartiles(snap) {
    const entries = [];
    for (const [addrId, prices] of Object.entries(snap)) {
      const v = prices?.pest365;
      if (v != null && !isNaN(v)) {
        entries.push({ addrId, value: v });
      }
    }
    entries.sort((a, b) => a.value - b.value);
    const n = entries.length;
    const result = {}; // addrId -> quartile label
    for (let i = 0; i < n; i++) {
      const pct = i / n;
      let q;
      if (pct < 0.25) q = 'Q1';
      else if (pct < 0.50) q = 'Q2';
      else if (pct < 0.75) q = 'Q3';
      else q = 'Q4';
      result[entries[i].addrId] = q;
    }
    return { assignments: result, count: n };
  }

  // Build quartile assignments per date
  const allAssignments = [];
  const periods = [];
  const allAddressIds = new Set();

  for (const date of dates) {
    const { assignments, count } = assignQuartiles(snapshotsByDate[date]);
    allAssignments.push(assignments);

    // Count per quartile
    const qCounts = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
    for (const q of Object.values(assignments)) {
      qCounts[q]++;
    }
    periods.push({ date, quartiles: qCounts });

    for (const addrId of Object.keys(assignments)) {
      allAddressIds.add(addrId);
    }
  }

  // Compute flows between adjacent periods
  const flows = [];
  const QUARTILES = ['Q1', 'Q2', 'Q3', 'Q4'];

  for (let i = 0; i < dates.length - 1; i++) {
    const fromAssign = allAssignments[i];
    const toAssign = allAssignments[i + 1];

    // Count transitions for addresses present in both
    const transitionCounts = {};
    for (const fq of QUARTILES) {
      for (const tq of QUARTILES) {
        transitionCounts[`${fq}->${tq}`] = 0;
      }
    }

    for (const addrId of Object.keys(fromAssign)) {
      if (toAssign[addrId]) {
        const key = `${fromAssign[addrId]}->${toAssign[addrId]}`;
        transitionCounts[key]++;
      }
    }

    for (const fq of QUARTILES) {
      for (const tq of QUARTILES) {
        const count = transitionCounts[`${fq}->${tq}`];
        if (count > 0) {
          flows.push({
            from: { period: i, quartile: fq },
            to: { period: i + 1, quartile: tq },
            count,
          });
        }
      }
    }
  }

  return {
    periods,
    flows,
    totalAddresses: allAddressIds.size,
  };
}

// ---------------------------------------------------------------------------
// Variance Decomposition (between-state / within-state / temporal / residual)
// ---------------------------------------------------------------------------

function computeVarianceDecomposition(addresses, snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0 || addresses.length === 0) {
    return { totalVariance: 0, components: [], byField: [] };
  }

  // Build address-to-state lookup
  const addrState = {};
  for (const addr of addresses) {
    addrState[addr.addressId] = addr.state || 'Unknown';
  }

  // Helper: population variance of an array of numbers
  function popVariance(vals) {
    if (vals.length === 0) return 0;
    const m = vals.reduce((s, v) => s + v, 0) / vals.length;
    return vals.reduce((s, v) => s + (v - m) ** 2, 0) / vals.length;
  }

  // Helper: collect all numeric values for a field across all dates and addresses
  function collectAll(field) {
    const all = [];
    for (const date of dates) {
      const snap = snapshotsByDate[date];
      for (const addrId of Object.keys(snap)) {
        const v = snap[addrId]?.[field];
        if (v != null && !isNaN(v)) all.push({ value: v, state: addrState[addrId] || 'Unknown', date });
      }
    }
    return all;
  }

  // Helper: decompose a field into between-state, within-state, temporal components
  function decompose(field) {
    const all = collectAll(field);
    if (all.length === 0) return { totalVar: 0, between: 0, within: 0, temporal: 0 };

    const totalVar = popVariance(all.map(d => d.value));

    // Between-state: variance of state means, weighted by count
    const stateGroups = {};
    for (const d of all) {
      if (!stateGroups[d.state]) stateGroups[d.state] = [];
      stateGroups[d.state].push(d.value);
    }
    const grandMean = all.reduce((s, d) => s + d.value, 0) / all.length;
    const n = all.length;
    let betweenVar = 0;
    for (const vals of Object.values(stateGroups)) {
      const stateMean = vals.reduce((s, v) => s + v, 0) / vals.length;
      betweenVar += (vals.length / n) * (stateMean - grandMean) ** 2;
    }

    // Within-state: weighted average of within-state variances
    let withinVar = 0;
    for (const vals of Object.values(stateGroups)) {
      withinVar += (vals.length / n) * popVariance(vals);
    }

    // Temporal: variance of date means
    const dateGroups = {};
    for (const d of all) {
      if (!dateGroups[d.date]) dateGroups[d.date] = [];
      dateGroups[d.date].push(d.value);
    }
    let temporalVar = 0;
    for (const vals of Object.values(dateGroups)) {
      const dateMean = vals.reduce((s, v) => s + v, 0) / vals.length;
      temporalVar += (vals.length / n) * (dateMean - grandMean) ** 2;
    }

    return { totalVar, between: betweenVar, within: withinVar, temporal: temporalVar };
  }

  // Main decomposition on pest365
  const pest365Decomp = decompose('pest365');
  const totalVariance = round(pest365Decomp.totalVar, 4);
  const residual = Math.max(0, pest365Decomp.totalVar - pest365Decomp.between - pest365Decomp.within - pest365Decomp.temporal);

  const components = [
    { source: 'Between States', variance: round(pest365Decomp.between, 4), percentage: totalVariance > 0 ? round((pest365Decomp.between / pest365Decomp.totalVar) * 100, 2) : 0, color: '#2563eb' },
    { source: 'Within State', variance: round(pest365Decomp.within, 4), percentage: totalVariance > 0 ? round((pest365Decomp.within / pest365Decomp.totalVar) * 100, 2) : 0, color: '#16a34a' },
    { source: 'Temporal', variance: round(pest365Decomp.temporal, 4), percentage: totalVariance > 0 ? round((pest365Decomp.temporal / pest365Decomp.totalVar) * 100, 2) : 0, color: '#f59e0b' },
    { source: 'Residual', variance: round(residual, 4), percentage: totalVariance > 0 ? round((residual / pest365Decomp.totalVar) * 100, 2) : 0, color: '#9ca3af' },
  ];

  // byField for pest365, pest365Plus, oneTime
  const byField = ['pest365', 'pest365Plus', 'oneTime'].map(field => {
    const d = decompose(field);
    return { field, withinVariance: round(d.within, 4), betweenVariance: round(d.between, 4) };
  });

  return { totalVariance, components, byField };
}

// ---------------------------------------------------------------------------
// Cohort Data (state-based cohort price timelines)
// ---------------------------------------------------------------------------

function computeCohortData(addresses, snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0 || addresses.length === 0) {
    return { cohorts: [], groupBy: 'State' };
  }

  const COHORT_COLORS = ['#2563eb', '#dc2626', '#16a34a', '#f59e0b', '#7c3aed', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16', '#06b6d4', '#e11d48'];

  // Group addressIds by state
  const stateAddrs = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!stateAddrs[st]) stateAddrs[st] = [];
    stateAddrs[st].push(addr.addressId);
  }

  // Build cohorts
  const cohorts = [];
  for (const [state, addrIds] of Object.entries(stateAddrs)) {
    if (addrIds.length < 2) continue; // filter out states with fewer than 2 addresses

    const cohortDates = [];
    const avgPrices = [];

    for (const date of dates) {
      const snap = snapshotsByDate[date];
      const vals = [];
      for (const id of addrIds) {
        const v = snap[id]?.pest365;
        if (v != null && !isNaN(v)) vals.push(v);
      }
      if (vals.length > 0) {
        cohortDates.push(date);
        avgPrices.push(round(vals.reduce((s, v) => s + v, 0) / vals.length));
      }
    }

    if (avgPrices.length === 0) continue;

    const currentAvg = avgPrices[avgPrices.length - 1];
    const firstAvg = avgPrices[0];
    const changePercent = firstAvg > 0 ? round(((currentAvg - firstAvg) / firstAvg) * 100) : 0;

    cohorts.push({
      name: state,
      color: '', // assigned after sorting
      dates: cohortDates,
      avgPrices,
      addressCount: addrIds.length,
      currentAvg,
      changePercent,
    });
  }

  // Sort by addressCount descending and assign colors
  cohorts.sort((a, b) => b.addressCount - a.addressCount);
  for (let i = 0; i < cohorts.length; i++) {
    cohorts[i].color = COHORT_COLORS[i % COHORT_COLORS.length];
  }

  return { cohorts, groupBy: 'State' };
}

// ---------------------------------------------------------------------------
// Spread Timeline Data (premium spread, one-time ratio, ISV spread over time)
// ---------------------------------------------------------------------------

function computeSpreadTimelineData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0) {
    return { dates: [], spreads: { premiumSpread: [], oneTimeRatio: [], isvSpread: [] }, averages: { premiumSpread: 0, oneTimeRatio: 0, isvSpread: 0 } };
  }

  const resultDates = [];
  const premiumSpread = [];
  const oneTimeRatio = [];
  const isvSpread = [];

  for (const date of dates) {
    const snap = snapshotsByDate[date];
    const addrIds = Object.keys(snap);
    if (addrIds.length === 0) continue;

    const p365Vals = [];
    const p365PlusVals = [];
    const oneTimeVals = [];
    const isvVals = [];

    for (const id of addrIds) {
      const prices = snap[id];
      if (!prices) continue;
      const p365 = prices.pest365;
      const p365Plus = prices.pest365Plus;
      const ot = prices.oneTime;
      const isv = prices.initialServiceVisit;

      if (p365 != null && !isNaN(p365)) p365Vals.push(p365);
      if (p365Plus != null && !isNaN(p365Plus)) p365PlusVals.push(p365Plus);
      if (ot != null && !isNaN(ot)) oneTimeVals.push(ot);
      if (isv != null && !isNaN(isv)) isvVals.push(isv);
    }

    const meanP365 = p365Vals.length > 0 ? p365Vals.reduce((s, v) => s + v, 0) / p365Vals.length : null;
    const meanP365Plus = p365PlusVals.length > 0 ? p365PlusVals.reduce((s, v) => s + v, 0) / p365PlusVals.length : null;
    const meanOneTime = oneTimeVals.length > 0 ? oneTimeVals.reduce((s, v) => s + v, 0) / oneTimeVals.length : null;
    const meanISV = isvVals.length > 0 ? isvVals.reduce((s, v) => s + v, 0) / isvVals.length : null;

    if (meanP365 == null) continue;

    resultDates.push(date);
    premiumSpread.push(round(meanP365Plus != null ? meanP365Plus - meanP365 : 0));
    oneTimeRatio.push(round(meanOneTime != null && meanP365 > 0 ? meanOneTime / meanP365 : 0));
    isvSpread.push(round(meanISV != null ? meanISV - meanP365 : 0));
  }

  const avgPremiumSpread = premiumSpread.length > 0 ? round(premiumSpread.reduce((s, v) => s + v, 0) / premiumSpread.length) : 0;
  const avgOneTimeRatio = oneTimeRatio.length > 0 ? round(oneTimeRatio.reduce((s, v) => s + v, 0) / oneTimeRatio.length) : 0;
  const avgIsvSpread = isvSpread.length > 0 ? round(isvSpread.reduce((s, v) => s + v, 0) / isvSpread.length) : 0;

  return {
    dates: resultDates,
    spreads: { premiumSpread, oneTimeRatio, isvSpread },
    averages: { premiumSpread: avgPremiumSpread, oneTimeRatio: avgOneTimeRatio, isvSpread: avgIsvSpread },
  };
}

// ---------------------------------------------------------------------------
// Stability Gauge Data (per-field price stability scores)
// ---------------------------------------------------------------------------

function computeStabilityGaugeData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 2) return null;

  const fields = ['pest365', 'pest365Plus', 'oneTime'];
  const labels = { pest365: 'Pest 365', pest365Plus: 'Pest 365+', oneTime: 'One-Time' };

  // Collect all address IDs that appear in any snapshot
  const addrIdSet = new Set();
  for (const date of dates) {
    for (const id of Object.keys(snapshotsByDate[date])) {
      addrIdSet.add(id);
    }
  }
  const addressCount = addrIdSet.size;
  if (addressCount === 0) return null;

  const snapshotCount = dates.length;
  const totalPossibleChanges = addressCount * (snapshotCount - 1);

  const gauges = [];

  for (const field of fields) {
    let changeCount = 0;
    const absPercentChanges = [];
    let maxChange = 0;

    for (const addrId of addrIdSet) {
      for (let i = 1; i < dates.length; i++) {
        const prevSnap = snapshotsByDate[dates[i - 1]];
        const currSnap = snapshotsByDate[dates[i]];
        const prevPrice = prevSnap[addrId] ? parsePrice(prevSnap[addrId][field]) : null;
        const currPrice = currSnap[addrId] ? parsePrice(currSnap[addrId][field]) : null;

        if (prevPrice != null && currPrice != null && prevPrice > 0) {
          const pctChange = Math.abs((currPrice - prevPrice) / prevPrice) * 100;
          if (pctChange > 0) {
            changeCount++;
            absPercentChanges.push(pctChange);
            if (pctChange > maxChange) maxChange = pctChange;
          }
        }
      }
    }

    const avgChange = absPercentChanges.length > 0
      ? absPercentChanges.reduce((s, v) => s + v, 0) / absPercentChanges.length
      : 0;

    let score = 100 - Math.min(100, (changeCount / totalPossibleChanges) * 100 + avgChange * 10);
    score = Math.max(0, Math.min(100, score));

    gauges.push({
      field,
      score: round(score),
      label: labels[field],
      changeCount,
      avgChange: round(avgChange),
      maxChange: round(maxChange),
    });
  }

  // Overall gauge: average of three field scores
  const overallScore = round(gauges.reduce((s, g) => s + g.score, 0) / gauges.length);
  gauges.push({
    field: 'overall',
    score: overallScore,
    label: 'Overall',
    changeCount: gauges.reduce((s, g) => s + g.changeCount, 0),
    avgChange: round(gauges.filter(g => g.field !== 'overall').reduce((s, g) => s + g.avgChange, 0) / 3),
    maxChange: round(Math.max(...gauges.filter(g => g.field !== 'overall').map(g => g.maxChange))),
  });

  let interpretation;
  if (overallScore > 90) {
    interpretation = 'Prices are highly stable with minimal variation';
  } else if (overallScore > 75) {
    interpretation = 'Prices show moderate stability with occasional adjustments';
  } else if (overallScore > 50) {
    interpretation = 'Prices are moderately volatile with regular changes';
  } else {
    interpretation = 'Prices are highly volatile with frequent significant changes';
  }

  return { gauges, overallScore, interpretation };
}

// ---------------------------------------------------------------------------
// Geographic Cluster Data (hierarchical clustering by state mean prices)
// ---------------------------------------------------------------------------

function computeGeographicClusterData(addresses, snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0 || addresses.length === 0) return null;

  // Group addresses by state
  const stateAddrs = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!stateAddrs[st]) stateAddrs[st] = [];
    stateAddrs[st].push(addr.addressId);
  }

  // Compute mean pest365 for each state across all snapshots
  const stateMeans = {};
  for (const [state, addrIds] of Object.entries(stateAddrs)) {
    const allPrices = [];
    for (const date of dates) {
      const snap = snapshotsByDate[date];
      for (const id of addrIds) {
        const v = snap[id] ? parsePrice(snap[id].pest365) : null;
        if (v != null && !isNaN(v)) allPrices.push(v);
      }
    }
    if (allPrices.length > 0) {
      stateMeans[state] = round(allPrices.reduce((s, v) => s + v, 0) / allPrices.length);
    }
  }

  const stateNames = Object.keys(stateMeans);
  if (stateNames.length === 0) return null;
  if (stateNames.length === 1) {
    const st = stateNames[0];
    return {
      tree: { name: st, distance: 0, avgPrice: stateMeans[st] },
      clusters: [{ id: 0, states: [st], avgPrice: stateMeans[st], color: '#2563eb' }],
      distanceMatrix: [[0]],
    };
  }

  // Build distance matrix
  const n = stateNames.length;
  const distanceMatrix = Array.from({ length: n }, () => Array(n).fill(0));
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const d = Math.abs(stateMeans[stateNames[i]] - stateMeans[stateNames[j]]);
      distanceMatrix[i][j] = round(d);
      distanceMatrix[j][i] = round(d);
    }
  }

  // Agglomerative hierarchical clustering (single-linkage)
  // Each cluster tracks its tree node and member indices
  let clusters = stateNames.map((st, i) => ({
    indices: [i],
    node: { name: st, distance: 0, avgPrice: stateMeans[st] },
  }));

  let clusterCounter = 0;

  while (clusters.length > 1) {
    // Find two closest clusters (single-linkage: min distance between any members)
    let minDist = Infinity;
    let mergeA = 0;
    let mergeB = 1;

    for (let i = 0; i < clusters.length; i++) {
      for (let j = i + 1; j < clusters.length; j++) {
        for (const ai of clusters[i].indices) {
          for (const bj of clusters[j].indices) {
            if (distanceMatrix[ai][bj] < minDist) {
              minDist = distanceMatrix[ai][bj];
              mergeA = i;
              mergeB = j;
            }
          }
        }
      }
    }

    const newNode = {
      name: `cluster_${clusterCounter++}`,
      children: [clusters[mergeA].node, clusters[mergeB].node],
      distance: round(minDist),
    };

    const mergedIndices = [...clusters[mergeA].indices, ...clusters[mergeB].indices];
    const newCluster = { indices: mergedIndices, node: newNode };

    // Remove merged clusters and add new one
    const newClusters = [];
    for (let i = 0; i < clusters.length; i++) {
      if (i !== mergeA && i !== mergeB) newClusters.push(clusters[i]);
    }
    newClusters.push(newCluster);
    clusters = newClusters;
  }

  const tree = { name: 'root', children: clusters[0].node.children || [clusters[0].node], distance: clusters[0].node.distance };

  // Cut the tree at threshold to form flat clusters
  const maxDist = clusters[0].node.distance || 1;
  const threshold = maxDist * 0.4;

  const CLUSTER_COLORS = ['#2563eb', '#dc2626', '#16a34a', '#f59e0b', '#7c3aed', '#ec4899', '#14b8a6', '#f97316'];

  function collectLeaves(node) {
    if (!node.children || node.children.length === 0) return [node.name];
    const leaves = [];
    for (const child of node.children) {
      leaves.push(...collectLeaves(child));
    }
    return leaves;
  }

  function cutTree(node, thresh) {
    if (!node.children || node.children.length === 0) return [[node.name]];
    if (node.distance <= thresh) return [collectLeaves(node)];
    const result = [];
    for (const child of node.children) {
      result.push(...cutTree(child, thresh));
    }
    return result;
  }

  const flatClusters = cutTree(clusters[0].node, threshold);
  const clusterResults = flatClusters.map((states, idx) => {
    const prices = states.filter(st => stateMeans[st] != null).map(st => stateMeans[st]);
    const avgPrice = prices.length > 0 ? round(prices.reduce((s, v) => s + v, 0) / prices.length) : 0;
    return {
      id: idx,
      states,
      avgPrice,
      color: CLUSTER_COLORS[idx % CLUSTER_COLORS.length],
    };
  });

  return { tree, clusters: clusterResults, distanceMatrix };
}

// ---------------------------------------------------------------------------
// Price Change Distribution (histogram of all pct changes between snapshots)
// ---------------------------------------------------------------------------

function computePriceChangeDistribution(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 2) return null;

  // Collect all percent changes
  const changes = [];
  const addrIdSet = new Set();
  for (const date of dates) {
    for (const id of Object.keys(snapshotsByDate[date])) {
      addrIdSet.add(id);
    }
  }

  for (const addrId of addrIdSet) {
    for (let i = 1; i < dates.length; i++) {
      const prevSnap = snapshotsByDate[dates[i - 1]];
      const currSnap = snapshotsByDate[dates[i]];
      const oldPrice = prevSnap[addrId] ? parsePrice(prevSnap[addrId].pest365) : null;
      const newPrice = currSnap[addrId] ? parsePrice(currSnap[addrId].pest365) : null;

      if (oldPrice != null && newPrice != null && !isNaN(oldPrice) && !isNaN(newPrice) && oldPrice > 0) {
        changes.push(((newPrice - oldPrice) / oldPrice) * 100);
      }
    }
  }

  if (changes.length === 0) return null;

  // Stats
  const totalChanges = changes.length;
  const sortedChanges = [...changes].sort((a, b) => a - b);
  const changeMean = changes.reduce((s, v) => s + v, 0) / totalChanges;
  const changeMedian = totalChanges % 2 === 1
    ? sortedChanges[Math.floor(totalChanges / 2)]
    : (sortedChanges[totalChanges / 2 - 1] + sortedChanges[totalChanges / 2]) / 2;
  const variance = changes.reduce((s, v) => s + (v - changeMean) ** 2, 0) / totalChanges;
  const changeStdDev = Math.sqrt(variance);
  const skewness = changeStdDev > 0 ? (changeMean - changeMedian) / changeStdDev : 0;

  let positiveCount = 0;
  let negativeCount = 0;
  let zeroCount = 0;
  for (const c of changes) {
    if (c > 0.001) positiveCount++;
    else if (c < -0.001) negativeCount++;
    else zeroCount++;
  }

  // Histogram bins
  const minChange = sortedChanges[0];
  const maxChange = sortedChanges[sortedChanges.length - 1];
  const range = maxChange - minChange;
  const binCount = Math.min(20, Math.max(1, Math.ceil(Math.sqrt(totalChanges))));
  const binWidth = range > 0 ? range / binCount : 1;

  const bins = [];
  for (let b = 0; b < binCount; b++) {
    const x0 = minChange + b * binWidth;
    const x1 = b === binCount - 1 ? maxChange : minChange + (b + 1) * binWidth;
    let count = 0;
    for (const c of changes) {
      if (b === binCount - 1) {
        if (c >= x0 && c <= x1) count++;
      } else {
        if (c >= x0 && c < x1) count++;
      }
    }
    bins.push({
      x0: round(x0, 4),
      x1: round(x1, 4),
      count,
      percentage: round((count / totalChanges) * 100),
    });
  }

  return {
    changes: changes.map(c => round(c, 4)),
    bins,
    stats: {
      mean: round(changeMean, 4),
      median: round(changeMedian, 4),
      stdDev: round(changeStdDev, 4),
      skewness: round(skewness, 4),
      totalChanges,
      positiveCount,
      negativeCount,
      zeroCount,
    },
  };
}

// ---------------------------------------------------------------------------
// Index Growth Data (state-level price index rebased to 100)
// ---------------------------------------------------------------------------

function computeIndexGrowthData(addresses, snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length === 0 || addresses.length === 0) return null;

  // Map addressId -> state
  const addrState = {};
  for (const addr of addresses) {
    addrState[addr.addressId] = addr.state || 'Unknown';
  }

  // Group addresses by state (require >= 2 per state)
  const stateAddrIds = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!stateAddrIds[st]) stateAddrIds[st] = [];
    stateAddrIds[st].push(addr.addressId);
  }

  // Filter to states with >= 2 addresses
  const qualifiedStates = Object.entries(stateAddrIds)
    .filter(([, ids]) => ids.length >= 2)
    .map(([st]) => st);

  if (qualifiedStates.length === 0) return null;

  // For each date, compute mean pest365 per state and nationally
  const stateMeansByDate = {}; // state -> [meanPerDate]
  const nationalMeans = [];    // [meanPerDate]

  for (const date of dates) {
    const snap = snapshots[date];
    const allPrices = [];

    for (const state of qualifiedStates) {
      const prices = [];
      for (const addrId of stateAddrIds[state]) {
        if (snap[addrId]) {
          const v = snap[addrId].pest365;
          if (v != null && !isNaN(v)) prices.push(v);
        }
      }
      if (!stateMeansByDate[state]) stateMeansByDate[state] = [];
      const m = prices.length > 0 ? prices.reduce((s, v) => s + v, 0) / prices.length : null;
      stateMeansByDate[state].push(m);
      if (prices.length > 0) allPrices.push(...prices);
    }

    const natMean = allPrices.length > 0 ? allPrices.reduce((s, v) => s + v, 0) / allPrices.length : null;
    nationalMeans.push(natMean);
  }

  // Rebase to 100 at first date
  const INDEX_COLORS = ['#2563eb', '#dc2626', '#16a34a', '#f59e0b', '#7c3aed', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16', '#06b6d4', '#e11d48'];

  const states = [];
  for (const state of qualifiedStates) {
    const means = stateMeansByDate[state];
    const base = means[0];
    if (base == null || base === 0) continue;
    const indexValues = means.map(m => m != null ? round((m / base) * 100, 4) : null);
    const lastIdx = indexValues.filter(v => v != null);
    const last = lastIdx.length > 0 ? lastIdx[lastIdx.length - 1] : 100;
    const totalGrowth = round(((last - 100) / 100) * 100, 4);
    states.push({ state, indexValues, totalGrowth });
  }

  // Sort by totalGrowth descending
  states.sort((a, b) => (b.totalGrowth || 0) - (a.totalGrowth || 0));

  // Assign colors
  for (let i = 0; i < states.length; i++) {
    states[i].color = INDEX_COLORS[i % INDEX_COLORS.length];
  }

  // National index
  const natBase = nationalMeans[0];
  const nationalIndex = (natBase != null && natBase !== 0)
    ? nationalMeans.map(m => m != null ? round((m / natBase) * 100, 4) : null)
    : nationalMeans.map(() => null);

  return {
    dates,
    states,
    nationalIndex,
  };
}

// ---------------------------------------------------------------------------
// Address Outlier Data (z-score based outlier detection per state)
// ---------------------------------------------------------------------------

function computeAddressOutlierData(addresses, snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length === 0 || addresses.length === 0) return null;

  // Use the latest snapshot
  const latestDate = dates[dates.length - 1];
  const latestSnap = snapshots[latestDate];

  // Build address lookup
  const addrMap = {};
  for (const addr of addresses) {
    addrMap[addr.addressId] = addr;
  }

  // Collect valid pest365 values grouped by state
  const stateData = {}; // state -> [{ addressId, price }]
  for (const [addrId, priceData] of Object.entries(latestSnap)) {
    const addr = addrMap[addrId];
    if (!addr) continue;
    const price = priceData.pest365;
    if (price == null || isNaN(price)) continue;
    const st = addr.state || 'Unknown';
    if (!stateData[st]) stateData[st] = [];
    stateData[st].push({ addressId: addrId, price });
  }

  // Compute mean and stdDev per state
  const stateStats = {};
  for (const [state, entries] of Object.entries(stateData)) {
    const prices = entries.map(e => e.price);
    const m = prices.reduce((s, v) => s + v, 0) / prices.length;
    const sd = prices.length >= 2
      ? Math.sqrt(prices.reduce((s, v) => s + (v - m) ** 2, 0) / prices.length)
      : 0;
    stateStats[state] = { mean: m, stdDev: sd };
  }

  // Build per-address results
  const result = [];
  for (const [state, entries] of Object.entries(stateData)) {
    const stats = stateStats[state];
    for (const entry of entries) {
      const addr = addrMap[entry.addressId];
      const deviation = entry.price - stats.mean;
      const deviationPercent = stats.mean !== 0 ? (deviation / stats.mean) * 100 : 0;
      const zScore = stats.stdDev > 0 ? (entry.price - stats.mean) / stats.stdDev : 0;
      const isOutlier = Math.abs(zScore) > 1.5;
      const city = addr.city || '';
      const label = `${city}, ${state}`;

      result.push({
        addressId: entry.addressId,
        label,
        state,
        price: round(entry.price),
        stateMean: round(stats.mean),
        deviation: round(deviation),
        deviationPercent: round(deviationPercent),
        isOutlier,
        zScore: round(zScore, 4),
      });
    }
  }

  const outlierCount = result.filter(r => r.isOutlier).length;

  return {
    addresses: result,
    thresholds: { warning: 1.5, critical: 2.5 },
    outlierCount,
    totalCount: result.length,
  };
}

// ---------------------------------------------------------------------------
// Volatility Heatmap Data (state x date change magnitudes)
// ---------------------------------------------------------------------------

function computeVolatilityHeatmapData(addresses, snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length === 0 || addresses.length === 0) return null;

  // Map addressId -> state
  const addrState = {};
  for (const addr of addresses) {
    addrState[addr.addressId] = addr.state || 'Unknown';
  }

  // Group address IDs by state
  const stateAddrIds = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!stateAddrIds[st]) stateAddrIds[st] = [];
    stateAddrIds[st].push(addr.addressId);
  }

  const stateNames = Object.keys(stateAddrIds).sort();
  if (stateNames.length === 0) return null;

  // For each state + date, compute mean pest365
  const stateDateMean = {}; // state -> { date -> mean }
  for (const state of stateNames) {
    stateDateMean[state] = {};
    for (const date of dates) {
      const snap = snapshots[date];
      const prices = [];
      for (const addrId of stateAddrIds[state]) {
        if (snap[addrId]) {
          const v = snap[addrId].pest365;
          if (v != null && !isNaN(v)) prices.push(v);
        }
      }
      stateDateMean[state][date] = prices.length > 0
        ? prices.reduce((s, v) => s + v, 0) / prices.length
        : null;
    }
  }

  // Build cells
  const cells = [];
  let maxValue = 0;

  for (const state of stateNames) {
    for (let i = 0; i < dates.length; i++) {
      const date = dates[i];
      if (i === 0) {
        cells.push({ state, date, value: 0, changePercent: 0 });
        continue;
      }
      const prevDate = dates[i - 1];
      const meanOld = stateDateMean[state][prevDate];
      const meanNew = stateDateMean[state][date];

      if (meanOld != null && meanNew != null && meanOld !== 0) {
        const changePercent = round(((meanNew - meanOld) / meanOld) * 100, 4);
        const value = round(Math.abs(changePercent), 4);
        cells.push({ state, date, value, changePercent });
        if (value > maxValue) maxValue = value;
      } else {
        cells.push({ state, date, value: 0, changePercent: 0 });
      }
    }
  }

  return {
    states: stateNames,
    dates,
    cells,
    maxValue: round(maxValue, 4),
  };
}

// ---------------------------------------------------------------------------
// Acceleration Data (price velocity & acceleration with phase detection)
// ---------------------------------------------------------------------------

function computeAccelerationData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0) return null;

  // Compute mean pest365 per date across all addresses
  const price = [];
  for (const date of dates) {
    const snap = snapshotsByDate[date];
    const values = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) values.push(v);
    }
    price.push(values.length > 0 ? round(values.reduce((s, x) => s + x, 0) / values.length, 4) : null);
  }

  // Velocity: first derivative (price[i] - price[i-1])
  const velocity = [];
  for (let i = 0; i < price.length; i++) {
    if (i === 0 || price[i] == null || price[i - 1] == null) {
      velocity.push(0);
    } else {
      velocity.push(round(price[i] - price[i - 1], 4));
    }
  }

  // Acceleration: second derivative (velocity[i] - velocity[i-1])
  const acceleration = [];
  for (let i = 0; i < velocity.length; i++) {
    if (i < 2) {
      acceleration.push(0);
    } else {
      acceleration.push(round(velocity[i] - velocity[i - 1], 4));
    }
  }

  // Phase detection: group consecutive dates with the same phase label
  function phaseColor(lbl) {
    if (lbl === 'Accelerating Up') return '#dcfce7';
    if (lbl === 'Decelerating') return '#fee2e2';
    return '#dbeafe';
  }

  const phases = [];
  let currentPhase = null;
  let phaseStart = 0;

  for (let i = 0; i < acceleration.length; i++) {
    let label;
    if (acceleration[i] > 0.01) {
      label = 'Accelerating Up';
    } else if (acceleration[i] < -0.01) {
      label = 'Decelerating';
    } else {
      label = 'Stable';
    }

    if (label !== currentPhase) {
      if (currentPhase != null) {
        phases.push({ startIdx: phaseStart, endIdx: i - 1, label: currentPhase, color: phaseColor(currentPhase) });
      }
      currentPhase = label;
      phaseStart = i;
    }
  }
  // Close the last phase
  if (currentPhase != null) {
    phases.push({ startIdx: phaseStart, endIdx: acceleration.length - 1, label: currentPhase, color: phaseColor(currentPhase) });
  }

  return { dates, velocity, acceleration, price, phases };
}

// ---------------------------------------------------------------------------
// Competitive Landscape Data (state-level pricing comparison)
// ---------------------------------------------------------------------------

function computeCompetitiveLandscapeData(addresses, snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0 || addresses.length === 0) return null;

  const latestDate = dates[dates.length - 1];
  const latestSnap = snapshotsByDate[latestDate];

  // Map addressId -> state
  const addrState = {};
  for (const addr of addresses) {
    addrState[addr.addressId] = addr.state || 'Unknown';
  }

  // Group address IDs by state
  const stateAddrIds = {};
  for (const addr of addresses) {
    const st = addr.state || 'Unknown';
    if (!stateAddrIds[st]) stateAddrIds[st] = [];
    stateAddrIds[st].push(addr.addressId);
  }

  const COLORS = ['#2563eb', '#dc2626', '#16a34a', '#f59e0b', '#7c3aed', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16', '#06b6d4', '#e11d48'];

  const states = [];
  let colorIdx = 0;

  for (const [state, addrIds] of Object.entries(stateAddrIds)) {
    if (addrIds.length < 2) continue;

    // avgPrice from latest snapshot
    const latestPrices = [];
    for (const addrId of addrIds) {
      if (latestSnap[addrId]) {
        const v = latestSnap[addrId].pest365;
        if (v != null && !isNaN(v)) latestPrices.push(v);
      }
    }
    if (latestPrices.length === 0) continue;

    const avgPrice = round(latestPrices.reduce((s, v) => s + v, 0) / latestPrices.length);

    // Collect all pest365 values across all snapshots for volatility
    const allValues = [];
    for (const date of dates) {
      const snap = snapshotsByDate[date];
      for (const addrId of addrIds) {
        if (snap[addrId]) {
          const v = snap[addrId].pest365;
          if (v != null && !isNaN(v)) allValues.push(v);
        }
      }
    }

    // Coefficient of variation
    const m = allValues.reduce((s, v) => s + v, 0) / allValues.length;
    const sd = allValues.length >= 2
      ? Math.sqrt(allValues.reduce((s, v) => s + (v - m) ** 2, 0) / allValues.length)
      : 0;
    const volatility = m !== 0 ? round(sd / m, 4) : 0;
    const stability = round(100 - Math.min(100, volatility * 500));

    states.push({
      state,
      avgPrice,
      stability,
      addressCount: addrIds.length,
      volatility,
      color: COLORS[colorIdx % COLORS.length],
    });
    colorIdx++;
  }

  if (states.length === 0) return null;

  // Median price and stability
  const sortedPrices = states.map(s => s.avgPrice).sort((a, b) => a - b);
  const sortedStability = states.map(s => s.stability).sort((a, b) => a - b);

  const medianPrice = round(sortedPrices.length % 2 === 1
    ? sortedPrices[Math.floor(sortedPrices.length / 2)]
    : (sortedPrices[sortedPrices.length / 2 - 1] + sortedPrices[sortedPrices.length / 2]) / 2);

  const medianStability = round(sortedStability.length % 2 === 1
    ? sortedStability[Math.floor(sortedStability.length / 2)]
    : (sortedStability[sortedStability.length / 2 - 1] + sortedStability[sortedStability.length / 2]) / 2);

  return { states, medianPrice, medianStability };
}

// ---------------------------------------------------------------------------
// Funnel Data (price field tier comparison)
// ---------------------------------------------------------------------------

function computeFunnelData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0) return null;

  const latestDate = dates[dates.length - 1];
  const latestSnap = snapshotsByDate[latestDate];

  const fieldConfig = [
    { field: 'pest365', label: 'Pest 365', color: '#2563eb' },
    { field: 'pest365Plus', label: 'Pest 365+', color: '#7c3aed' },
    { field: 'initialServiceVisit', label: 'Initial Service Visit', color: '#16a34a' },
    { field: 'oneTime', label: 'One-Time', color: '#f59e0b' },
  ];

  const tiers = [];

  for (const cfg of fieldConfig) {
    const values = [];
    for (const addrId of Object.keys(latestSnap)) {
      const v = latestSnap[addrId]?.[cfg.field];
      if (v != null && !isNaN(v) && typeof v === 'number') values.push(v);
    }
    if (values.length === 0) continue;

    values.sort((a, b) => a - b);
    const avg = round(values.reduce((s, v) => s + v, 0) / values.length);
    const med = values.length % 2 === 1
      ? values[Math.floor(values.length / 2)]
      : (values[values.length / 2 - 1] + values[values.length / 2]) / 2;

    tiers.push({
      field: cfg.field,
      label: cfg.label,
      avgPrice: avg,
      medianPrice: round(med),
      minPrice: round(values[0]),
      maxPrice: round(values[values.length - 1]),
      spread: round(values[values.length - 1] - values[0]),
      color: cfg.color,
      addressCount: values.length,
    });
  }

  // Sort by avgPrice ascending
  tiers.sort((a, b) => a.avgPrice - b.avgPrice);

  // Compute ratios between adjacent tiers
  const ratios = [];
  for (let i = 0; i < tiers.length - 1; i++) {
    const lower = tiers[i];
    const higher = tiers[i + 1];
    if (lower.avgPrice > 0) {
      const ratio = round(higher.avgPrice / lower.avgPrice, 2);
      ratios.push({
        from: lower.label,
        to: higher.label,
        ratio,
        label: `${ratio.toFixed(2)}x`,
      });
    }
  }

  return { tiers, ratios };
}

function computeElasticityHeatmapData(snapshotsByDate) {
  // For each state, compute cross-field elasticity between price field pairs
  // Fields: pest365, pest365Plus, oneTime, initialServiceVisit
  // Elasticity = correlation of changes between two fields across dates

  const fields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  const fieldLabels = { pest365: 'Pest365', pest365Plus: 'Pest365+', oneTime: 'One-Time', initialServiceVisit: 'ISV' };
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 3) return null;

  // Group by state
  const stateAddresses = {};
  for (const date of dates) {
    for (const snap of snapshotsByDate[date]) {
      const st = snap.state;
      if (!st) continue;
      if (!stateAddresses[st]) stateAddresses[st] = {};
      const aid = snap.addressId;
      if (!stateAddresses[st][aid]) stateAddresses[st][aid] = {};
      stateAddresses[st][aid][date] = snap;
    }
  }

  const states = Object.keys(stateAddresses).sort();
  const cells = [];

  // Generate field pairs
  const pairs = [];
  for (let i = 0; i < fields.length; i++) {
    for (let j = i + 1; j < fields.length; j++) {
      pairs.push([fields[i], fields[j]]);
    }
  }

  for (const state of states) {
    const addrs = stateAddresses[state];
    for (const [f1, f2] of pairs) {
      // Collect paired changes
      const changes1 = [];
      const changes2 = [];
      for (const aid of Object.keys(addrs)) {
        const timeline = addrs[aid];
        const sortedDates = Object.keys(timeline).sort();
        for (let d = 1; d < sortedDates.length; d++) {
          const prev = timeline[sortedDates[d - 1]];
          const curr = timeline[sortedDates[d]];
          const v1p = typeof prev[f1] === 'number' ? prev[f1] : null;
          const v1c = typeof curr[f1] === 'number' ? curr[f1] : null;
          const v2p = typeof prev[f2] === 'number' ? prev[f2] : null;
          const v2c = typeof curr[f2] === 'number' ? curr[f2] : null;
          if (v1p != null && v1c != null && v2p != null && v2c != null && v1p !== 0) {
            changes1.push((v1c - v1p) / v1p);
            changes2.push((v2c - v2p) / (v2p || 1));
          }
        }
      }
      if (changes1.length < 2) continue;
      // Compute Pearson correlation as elasticity proxy
      const n = changes1.length;
      const mean1 = changes1.reduce((s, v) => s + v, 0) / n;
      const mean2 = changes2.reduce((s, v) => s + v, 0) / n;
      let num = 0, den1 = 0, den2 = 0;
      for (let k = 0; k < n; k++) {
        const d1 = changes1[k] - mean1;
        const d2 = changes2[k] - mean2;
        num += d1 * d2;
        den1 += d1 * d1;
        den2 += d2 * d2;
      }
      const denom = Math.sqrt(den1 * den2);
      const elasticity = denom > 0 ? num / denom : 0;
      cells.push({ field1: f1, field2: f2, state, elasticity: Math.round(elasticity * 100) / 100, sampleSize: n });
    }
  }

  if (cells.length === 0) return null;
  return { fields, states, cells, fieldLabels };
}

function computeTrendRibbonsData(snapshotsByDate) {
  // Compute mean pest365 with ±1σ and ±2σ bands per date, plus trend direction
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 3) return null;

  const mean = [];
  const sigma1Upper = [];
  const sigma1Lower = [];
  const sigma2Upper = [];
  const sigma2Lower = [];
  const trendDirection = [];

  for (let i = 0; i < dates.length; i++) {
    const snaps = snapshotsByDate[dates[i]];
    const vals = snaps.map(s => s.pest365).filter(v => typeof v === 'number' && v > 0);
    if (vals.length === 0) {
      mean.push(null);
      sigma1Upper.push(null);
      sigma1Lower.push(null);
      sigma2Upper.push(null);
      sigma2Lower.push(null);
      trendDirection.push('stable');
      continue;
    }
    const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
    const variance = vals.reduce((s, v) => s + (v - avg) ** 2, 0) / vals.length;
    const sigma = Math.sqrt(variance);
    mean.push(Math.round(avg * 100) / 100);
    sigma1Upper.push(Math.round((avg + sigma) * 100) / 100);
    sigma1Lower.push(Math.round((avg - sigma) * 100) / 100);
    sigma2Upper.push(Math.round((avg + 2 * sigma) * 100) / 100);
    sigma2Lower.push(Math.round((avg - 2 * sigma) * 100) / 100);

    // Trend direction based on 3-date moving window
    if (i >= 2 && mean[i] != null && mean[i - 1] != null && mean[i - 2] != null) {
      const slope = (mean[i] - mean[i - 2]) / 2;
      if (slope > 0.05) trendDirection.push('up');
      else if (slope < -0.05) trendDirection.push('down');
      else trendDirection.push('stable');
    } else {
      trendDirection.push('stable');
    }
  }

  // Filter out nulls
  const validIndices = mean.map((v, i) => v != null ? i : -1).filter(i => i >= 0);
  if (validIndices.length < 3) return null;

  return {
    dates: validIndices.map(i => dates[i]),
    mean: validIndices.map(i => mean[i]),
    sigma1Upper: validIndices.map(i => sigma1Upper[i]),
    sigma1Lower: validIndices.map(i => sigma1Lower[i]),
    sigma2Upper: validIndices.map(i => sigma2Upper[i]),
    sigma2Lower: validIndices.map(i => sigma2Lower[i]),
    trendDirection: validIndices.map(i => trendDirection[i]),
    field: 'pest365',
  };
}

function computeStatePriceProfileData(addresses, snapshotsByDate) {
  // Build multi-metric radar profile per state
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 2) return null;

  const latestDate = dates[dates.length - 1];
  const latestSnaps = snapshotsByDate[latestDate];

  // Group by state
  const byState = {};
  for (const addr of addresses) {
    const st = addr.state;
    if (!st) continue;
    if (!byState[st]) byState[st] = { addresses: [], snaps: [] };
    byState[st].addresses.push(addr);
  }
  for (const snap of latestSnaps) {
    if (snap.state && byState[snap.state]) {
      byState[snap.state].snaps.push(snap);
    }
  }

  const stateKeys = Object.keys(byState).filter(st => byState[st].snaps.length >= 2).sort();
  if (stateKeys.length === 0) return null;

  const COLORS = ['#2563eb', '#dc2626', '#16a34a', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#e11d68'];

  // Compute raw metrics per state
  const rawStates = stateKeys.map((st, idx) => {
    const snaps = byState[st].snaps;
    const prices = snaps.map(s => s.pest365).filter(v => typeof v === 'number' && v > 0);
    const avgPrice = prices.length > 0 ? prices.reduce((s, v) => s + v, 0) / prices.length : 0;
    const meanP = avgPrice;
    const variance = prices.length > 1 ? prices.reduce((s, v) => s + (v - meanP) ** 2, 0) / prices.length : 0;
    const volatility = meanP > 0 ? Math.sqrt(variance) / meanP * 100 : 0; // CV as percentage

    // Change rate: average percent change from first to last date for addresses in this state
    const changes = [];
    for (const snap of snaps) {
      const aid = snap.addressId;
      // Find this address in first date
      const firstSnaps = snapshotsByDate[dates[0]] || [];
      const firstSnap = firstSnaps.find(s => s.addressId === aid);
      if (firstSnap && typeof firstSnap.pest365 === 'number' && firstSnap.pest365 > 0 && typeof snap.pest365 === 'number') {
        changes.push(((snap.pest365 - firstSnap.pest365) / firstSnap.pest365) * 100);
      }
    }
    const changePercent = changes.length > 0 ? changes.reduce((s, v) => s + v, 0) / changes.length : 0;

    // Spread
    const spread = prices.length > 0 ? Math.max(...prices) - Math.min(...prices) : 0;

    // Service coverage (mosquitoTickControl)
    const mtcAvailable = snaps.filter(s => typeof s.mosquitoTickControl === 'number' && s.mosquitoTickControl > 0).length;
    const coveragePercent = snaps.length > 0 ? (mtcAvailable / snaps.length) * 100 : 0;

    return {
      state: st,
      addressCount: snaps.length,
      color: COLORS[idx % COLORS.length],
      rawMetrics: {
        avgPrice: Math.round(avgPrice * 100) / 100,
        volatility: Math.round(volatility * 100) / 100,
        changePercent: Math.round(changePercent * 100) / 100,
        spread: Math.round(spread * 100) / 100,
        coveragePercent: Math.round(coveragePercent),
      },
    };
  });

  // Normalize metrics to 0-1
  const maxPrice = Math.max(...rawStates.map(s => s.rawMetrics.avgPrice), 1);
  const maxVol = Math.max(...rawStates.map(s => s.rawMetrics.volatility), 1);
  const maxChange = Math.max(...rawStates.map(s => Math.abs(s.rawMetrics.changePercent)), 1);
  const maxSpread = Math.max(...rawStates.map(s => s.rawMetrics.spread), 1);

  const states = rawStates.map(s => ({
    ...s,
    metrics: {
      priceLevel: s.rawMetrics.avgPrice / maxPrice,
      volatility: s.rawMetrics.volatility / maxVol,
      changeRate: Math.abs(s.rawMetrics.changePercent) / maxChange,
      spread: s.rawMetrics.spread / maxSpread,
      serviceCoverage: s.rawMetrics.coveragePercent / 100,
    },
  }));

  return { states };
}

function computePriceMemoryData(snapshotsByDate) {
  // Compute survival curves: how long each price level persists before changing
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 4) return null;

  const fields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];

  // Build per-address timeline for each field
  const addressTimelines = {};
  for (const date of dates) {
    for (const snap of snapshotsByDate[date]) {
      const aid = snap.addressId;
      if (!addressTimelines[aid]) addressTimelines[aid] = [];
      addressTimelines[aid].push({ date, ...snap });
    }
  }

  // For each field, compute hold durations (how many consecutive periods a price stays the same)
  const byField = {};
  const allDurations = [];

  for (const field of fields) {
    const holdDurations = [];
    for (const aid of Object.keys(addressTimelines)) {
      const timeline = addressTimelines[aid].sort((a, b) => a.date.localeCompare(b.date));
      let currentPrice = null;
      let holdStart = 0;
      for (let i = 0; i < timeline.length; i++) {
        const val = timeline[i][field];
        if (typeof val !== 'number') continue;
        if (currentPrice === null) {
          currentPrice = val;
          holdStart = i;
        } else if (Math.abs(val - currentPrice) > 0.01) {
          const duration = i - holdStart;
          if (duration > 0) holdDurations.push(duration);
          currentPrice = val;
          holdStart = i;
        }
      }
      // Add final hold period (censored)
      if (currentPrice !== null) {
        const duration = timeline.length - holdStart;
        if (duration > 0) holdDurations.push(duration);
      }
    }

    if (holdDurations.length === 0) continue;

    // Build survival curve
    const maxDur = Math.max(...holdDurations);
    const survDurations = [];
    const survRates = [];
    const total = holdDurations.length;
    for (let d = 1; d <= maxDur; d++) {
      const surviving = holdDurations.filter(h => h >= d).length;
      survDurations.push(d);
      survRates.push(Math.round((surviving / total) * 1000) / 1000);
    }

    // Median: first duration where survival <= 0.5
    let median = maxDur;
    for (let i = 0; i < survRates.length; i++) {
      if (survRates[i] <= 0.5) { median = survDurations[i]; break; }
    }

    byField[field] = { durations: survDurations, survivalRate: survRates, median };
    holdDurations.forEach(d => allDurations.push(d));
  }

  if (Object.keys(byField).length === 0) return null;

  // Overall survival
  const maxAll = Math.max(...allDurations);
  const totalAll = allDurations.length;
  const durations = [];
  const survivalRate = [];
  for (let d = 1; d <= maxAll; d++) {
    durations.push(d);
    survivalRate.push(Math.round((allDurations.filter(h => h >= d).length / totalAll) * 1000) / 1000);
  }
  let medianDuration = maxAll;
  for (let i = 0; i < survivalRate.length; i++) {
    if (survivalRate[i] <= 0.5) { medianDuration = durations[i]; break; }
  }

  return { durations, survivalRate, medianDuration, byField };
}

function computeClusterDendrogramData(addresses, snapshotsByDate) {
  // Build a hierarchical clustering dendrogram of states by pricing similarity
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 2) return null;

  const latestDate = dates[dates.length - 1];
  const latestSnaps = snapshotsByDate[latestDate];

  // Compute state-level feature vectors: [avgPest365, avgPest365Plus, avgOneTime, avgISV]
  const byState = {};
  for (const snap of latestSnaps) {
    const st = snap.state;
    if (!st) continue;
    if (!byState[st]) byState[st] = { prices: [], state: st };
    byState[st].prices.push(snap);
  }

  const stateKeys = Object.keys(byState).filter(st => byState[st].prices.length >= 2).sort();
  if (stateKeys.length < 3) return null;

  const COLORS = ['#2563eb', '#dc2626', '#16a34a', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#e11d68'];
  const stateColors = {};
  stateKeys.forEach((st, i) => { stateColors[st] = COLORS[i % COLORS.length]; });

  const fields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  const vectors = {};
  const stateInfo = {};
  for (const st of stateKeys) {
    const snaps = byState[st].prices;
    const vec = fields.map(f => {
      const vals = snaps.map(s => s[f]).filter(v => typeof v === 'number' && v > 0);
      return vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
    });
    vectors[st] = vec;
    const p365 = vec[0];
    stateInfo[st] = { avgPrice: Math.round(p365 * 100) / 100, addressCount: snaps.length };
  }

  // Euclidean distance
  function dist(a, b) {
    let sum = 0;
    for (let i = 0; i < a.length; i++) sum += (a[i] - b[i]) ** 2;
    return Math.sqrt(sum);
  }

  // Agglomerative clustering (average linkage)
  let clusters = stateKeys.map(st => ({
    name: st,
    distance: 0,
    avgPrice: stateInfo[st].avgPrice,
    addressCount: stateInfo[st].addressCount,
    members: [st],
  }));

  while (clusters.length > 1) {
    let minDist = Infinity;
    let mi = 0, mj = 1;
    for (let i = 0; i < clusters.length; i++) {
      for (let j = i + 1; j < clusters.length; j++) {
        // Average linkage
        let totalDist = 0, count = 0;
        for (const a of clusters[i].members) {
          for (const b of clusters[j].members) {
            totalDist += dist(vectors[a], vectors[b]);
            count++;
          }
        }
        const avgDist = totalDist / count;
        if (avgDist < minDist) {
          minDist = avgDist;
          mi = i;
          mj = j;
        }
      }
    }
    const merged = {
      name: `cluster_${clusters.length}`,
      distance: Math.round(minDist * 100) / 100,
      children: [clusters[mi], clusters[mj]],
      members: [...clusters[mi].members, ...clusters[mj].members],
    };
    clusters = clusters.filter((_, idx) => idx !== mi && idx !== mj);
    clusters.push(merged);
  }

  const tree = clusters[0];
  // Cutoff at ~40% of max distance
  const maxDist = tree.distance || 1;
  const cutoff = Math.round(maxDist * 0.4 * 100) / 100;

  return { tree, cutoff, stateColors };
}

function computePricePaceData(snapshotsByDate) {
  // Compute cumulative price change count over time per state
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 3) return null;

  const COLORS = ['#2563eb', '#dc2626', '#16a34a', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#e11d68'];

  // Group addresses by state and track pest365 changes
  const stateAddresses = {};
  for (const date of dates) {
    for (const snap of snapshotsByDate[date]) {
      const st = snap.state;
      if (!st) continue;
      if (!stateAddresses[st]) stateAddresses[st] = {};
      const aid = snap.addressId;
      if (!stateAddresses[st][aid]) stateAddresses[st][aid] = {};
      stateAddresses[st][aid][date] = typeof snap.pest365 === 'number' ? snap.pest365 : null;
    }
  }

  const stateKeys = Object.keys(stateAddresses).sort();
  if (stateKeys.length === 0) return null;

  const statesData = stateKeys.map((st, idx) => {
    const addrs = stateAddresses[st];
    const cumulative = [];
    let totalChanges = 0;

    for (let d = 0; d < dates.length; d++) {
      if (d === 0) {
        cumulative.push(0);
        continue;
      }
      let changesThisPeriod = 0;
      for (const aid of Object.keys(addrs)) {
        const prev = addrs[aid][dates[d - 1]];
        const curr = addrs[aid][dates[d]];
        if (prev != null && curr != null && Math.abs(curr - prev) > 0.01) {
          changesThisPeriod++;
        }
      }
      totalChanges += changesThisPeriod;
      cumulative.push(totalChanges);
    }

    return { state: st, color: COLORS[idx % COLORS.length], cumulative, totalChanges };
  });

  // Filter out states with zero changes
  const activeStates = statesData.filter(s => s.totalChanges > 0);
  if (activeStates.length === 0) return null;

  return { dates, states: activeStates };
}

function computeStickinessData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 3) return null;

  const fields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  const stickinessRate = [];
  const byField = {};
  fields.forEach(f => { byField[f] = []; });

  for (let i = 1; i < dates.length; i++) {
    const prevSnaps = snapshotsByDate[dates[i - 1]];
    const currSnaps = snapshotsByDate[dates[i]];
    const prevMap = {};
    for (const s of prevSnaps) prevMap[s.addressId] = s;

    let totalPairs = 0;
    let totalUnchanged = 0;
    const fieldUnchanged = {};
    const fieldTotal = {};
    fields.forEach(f => { fieldUnchanged[f] = 0; fieldTotal[f] = 0; });

    for (const curr of currSnaps) {
      const prev = prevMap[curr.addressId];
      if (!prev) continue;
      let allSame = true;
      for (const f of fields) {
        const pv = typeof prev[f] === 'number' ? prev[f] : null;
        const cv = typeof curr[f] === 'number' ? curr[f] : null;
        if (pv != null && cv != null) {
          fieldTotal[f]++;
          if (Math.abs(pv - cv) < 0.01) {
            fieldUnchanged[f]++;
          } else {
            allSame = false;
          }
        }
      }
      totalPairs++;
      if (allSame) totalUnchanged++;
    }

    stickinessRate.push(totalPairs > 0 ? Math.round((totalUnchanged / totalPairs) * 1000) / 1000 : 1);
    for (const f of fields) {
      byField[f].push(fieldTotal[f] > 0 ? Math.round((fieldUnchanged[f] / fieldTotal[f]) * 1000) / 1000 : 1);
    }
  }

  const outputDates = dates.slice(1);
  const overall = stickinessRate.length > 0 ? stickinessRate[stickinessRate.length - 1] : 1;

  // Trend: compare last 3 to first 3
  let trend = 'stable';
  if (stickinessRate.length >= 6) {
    const first3 = stickinessRate.slice(0, 3).reduce((s, v) => s + v, 0) / 3;
    const last3 = stickinessRate.slice(-3).reduce((s, v) => s + v, 0) / 3;
    if (last3 - first3 > 0.03) trend = 'increasing';
    else if (first3 - last3 > 0.03) trend = 'decreasing';
  }

  return { dates: outputDates, stickinessRate, byField, overall, trend };
}

function computeSurpriseData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 4) return null;

  const surpriseIndex = [];
  const outputDates = [];
  const allSurprises = [];

  for (let i = 2; i < dates.length; i++) {
    // Expected = linear extrapolation from i-2 and i-1
    const snaps2 = snapshotsByDate[dates[i - 2]];
    const snaps1 = snapshotsByDate[dates[i - 1]];
    const snapsCurr = snapshotsByDate[dates[i]];

    const prevMap2 = {};
    for (const s of snaps2) prevMap2[s.addressId] = s;
    const prevMap1 = {};
    for (const s of snaps1) prevMap1[s.addressId] = s;

    let totalSurprise = 0;
    let count = 0;
    const dateSurprises = [];

    for (const curr of snapsCurr) {
      const p2 = prevMap2[curr.addressId];
      const p1 = prevMap1[curr.addressId];
      if (!p2 || !p1) continue;

      const field = 'pest365';
      const v2 = typeof p2[field] === 'number' ? p2[field] : null;
      const v1 = typeof p1[field] === 'number' ? p1[field] : null;
      const vc = typeof curr[field] === 'number' ? curr[field] : null;
      if (v2 == null || v1 == null || vc == null || v1 === 0) continue;

      const expected = v1 + (v1 - v2); // linear extrapolation
      const surprise = Math.abs(vc - expected) / v1;
      totalSurprise += surprise;
      count++;

      if (surprise > 0.02) {
        dateSurprises.push({
          date: dates[i],
          addressId: curr.addressId,
          label: `${curr.city || ''}, ${curr.state || ''}`.trim().replace(/^,\s*/, ''),
          surprise: Math.round(surprise * 1000) / 1000,
          field,
          expected: Math.round(expected * 100) / 100,
          actual: Math.round(vc * 100) / 100,
        });
      }
    }

    const avgSurprise = count > 0 ? Math.round((totalSurprise / count) * 100) * 10 / 1000 : 0;
    surpriseIndex.push(avgSurprise);
    outputDates.push(dates[i]);
    allSurprises.push(...dateSurprises);
  }

  if (outputDates.length === 0) return null;

  // Top surprises
  allSurprises.sort((a, b) => b.surprise - a.surprise);
  const topSurprises = allSurprises.slice(0, 10);
  const threshold = 2.0;

  return { dates: outputDates, surpriseIndex, threshold, topSurprises };
}

function computeCumulativeAnomalyData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 3) return null;

  // Compute per-date anomaly counts by type
  const rate = [];
  const byType = { outlier: [], missing: [], spike: [] };

  // First pass: compute global stats for Z-score
  const allVals = {};
  const fields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  for (const f of fields) allVals[f] = [];
  for (const date of dates) {
    for (const snap of snapshotsByDate[date]) {
      for (const f of fields) {
        if (typeof snap[f] === 'number' && snap[f] > 0) allVals[f].push(snap[f]);
      }
    }
  }
  const stats = {};
  for (const f of fields) {
    const vals = allVals[f];
    if (vals.length < 2) continue;
    const mean = vals.reduce((s, v) => s + v, 0) / vals.length;
    const std = Math.sqrt(vals.reduce((s, v) => s + (v - mean) ** 2, 0) / vals.length);
    stats[f] = { mean, std };
  }

  // Build prev map for spike detection
  for (let i = 0; i < dates.length; i++) {
    const snaps = snapshotsByDate[dates[i]];
    const prevSnaps = i > 0 ? snapshotsByDate[dates[i - 1]] : [];
    const prevMap = {};
    for (const s of prevSnaps) prevMap[s.addressId] = s;

    let outliers = 0, missing = 0, spikes = 0;

    for (const snap of snaps) {
      for (const f of fields) {
        const val = snap[f];
        // Missing
        if (val == null || (typeof val !== 'number' && val !== 'not_available')) {
          missing++;
          continue;
        }
        if (typeof val !== 'number') continue;

        // Outlier (Z > 3)
        if (stats[f] && stats[f].std > 0) {
          const z = Math.abs(val - stats[f].mean) / stats[f].std;
          if (z > 3) outliers++;
        }

        // Spike (>10% change from prev)
        const prev = prevMap[snap.addressId];
        if (prev && typeof prev[f] === 'number' && prev[f] > 0) {
          const pctChange = Math.abs(val - prev[f]) / prev[f];
          if (pctChange > 0.10) spikes++;
        }
      }
    }

    rate.push(outliers + missing + spikes);
    byType.outlier.push(outliers);
    byType.missing.push(missing);
    byType.spike.push(spikes);
  }

  // Cumulative
  const cumulativeCount = [];
  let cumul = 0;
  for (const r of rate) {
    cumul += r;
    cumulativeCount.push(cumul);
  }

  // Trend slope (simple linear regression on rate)
  const n = rate.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
  for (let i = 0; i < n; i++) {
    sumX += i; sumY += rate[i]; sumXY += i * rate[i]; sumXX += i * i;
  }
  const trendSlope = n > 1 ? Math.round(((n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX)) * 1000) / 1000 : 0;

  return { dates, cumulativeCount, rate, byType, trendSlope };
}

function computeChangepointData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 5) return null;

  // Compute mean pest365 per date
  const values = dates.map(date => {
    const snaps = snapshotsByDate[date];
    const vals = snaps.map(s => s.pest365).filter(v => typeof v === 'number' && v > 0);
    return vals.length > 0 ? Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 100) / 100 : null;
  });

  // Filter nulls
  const validIndices = values.map((v, i) => v != null ? i : -1).filter(i => i >= 0);
  if (validIndices.length < 5) return null;

  const vDates = validIndices.map(i => dates[i]);
  const vValues = validIndices.map(i => values[i]);

  // Simple changepoint detection: find points where segmented means minimize total variance
  // Binary segmentation approach
  function segmentCost(vals, start, end) {
    if (end <= start) return 0;
    const seg = vals.slice(start, end);
    const mean = seg.reduce((s, v) => s + v, 0) / seg.length;
    return seg.reduce((s, v) => s + (v - mean) ** 2, 0);
  }

  const n = vValues.length;
  const totalCost = segmentCost(vValues, 0, n);
  const changepoints = [];

  // Find changepoints where split reduces cost significantly
  function findBestSplit(vals, startIdx, endIdx, minSegLen) {
    if (endIdx - startIdx < 2 * minSegLen) return null;
    const baseCost = segmentCost(vals, startIdx, endIdx);
    let bestGain = 0;
    let bestK = -1;
    for (let k = startIdx + minSegLen; k <= endIdx - minSegLen; k++) {
      const leftCost = segmentCost(vals, startIdx, k);
      const rightCost = segmentCost(vals, k, endIdx);
      const gain = baseCost - leftCost - rightCost;
      if (gain > bestGain) {
        bestGain = gain;
        bestK = k;
      }
    }
    // Significance threshold: gain must be > 5% of base cost
    if (bestK >= 0 && bestGain > baseCost * 0.15) {
      return { index: bestK, gain: bestGain };
    }
    return null;
  }

  // Recursive binary segmentation (max 4 changepoints)
  function detectChangepoints(vals, startIdx, endIdx, depth) {
    if (depth > 3) return;
    const split = findBestSplit(vals, startIdx, endIdx, 2);
    if (!split) return;

    const beforeMean = vals.slice(startIdx, split.index).reduce((s, v) => s + v, 0) / (split.index - startIdx);
    const afterMean = vals.slice(split.index, endIdx).reduce((s, v) => s + v, 0) / (endIdx - split.index);
    const magnitude = Math.round((afterMean - beforeMean) * 100) / 100;
    const confidence = Math.min(0.99, Math.round((split.gain / segmentCost(vals, startIdx, endIdx)) * 100) / 100);

    changepoints.push({
      date: vDates[split.index],
      index: split.index,
      beforeMean: Math.round(beforeMean * 100) / 100,
      afterMean: Math.round(afterMean * 100) / 100,
      magnitude,
      confidence,
    });

    detectChangepoints(vals, startIdx, split.index, depth + 1);
    detectChangepoints(vals, split.index, endIdx, depth + 1);
  }

  detectChangepoints(vValues, 0, n, 0);
  changepoints.sort((a, b) => a.index - b.index);

  return { dates: vDates, values: vValues, changepoints, field: 'pest365' };
}

function computeRatioStabilityData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 3) return null;

  const ratioConfigs = [
    { name: 'pest365Plus / pest365', num: 'pest365Plus', den: 'pest365' },
    { name: 'oneTime / pest365', num: 'oneTime', den: 'pest365' },
    { name: 'initialServiceVisit / pest365', num: 'initialServiceVisit', den: 'pest365' },
  ];

  const ratios = {};

  for (const config of ratioConfigs) {
    const values = [];
    for (const date of dates) {
      const snaps = snapshotsByDate[date];
      const ratioVals = [];
      for (const snap of snaps) {
        const num = typeof snap[config.num] === 'number' ? snap[config.num] : null;
        const den = typeof snap[config.den] === 'number' && snap[config.den] > 0 ? snap[config.den] : null;
        if (num != null && den != null) {
          ratioVals.push(num / den);
        }
      }
      if (ratioVals.length > 0) {
        values.push(Math.round((ratioVals.reduce((s, v) => s + v, 0) / ratioVals.length) * 1000) / 1000);
      } else {
        values.push(null);
      }
    }

    const validValues = values.filter(v => v != null);
    if (validValues.length < 3) continue;

    const mean = Math.round((validValues.reduce((s, v) => s + v, 0) / validValues.length) * 1000) / 1000;
    const std = Math.sqrt(validValues.reduce((s, v) => s + (v - mean) ** 2, 0) / validValues.length);
    const cv = mean > 0 ? Math.round((std / mean) * 10000) / 10000 : 0;
    const isStable = cv < 0.02;

    ratios[config.name] = { values, mean, cv, isStable };
  }

  if (Object.keys(ratios).length === 0) return null;
  return { dates, ratios };
}

function computeMosquitoBoundaryData(addresses, snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0) return null;

  const latestDate = dates[dates.length - 1];
  const latestSnaps = snapshotsByDate[latestDate];

  const stateData = {};
  for (const snap of latestSnaps) {
    const st = snap.state;
    if (!st) continue;
    if (!stateData[st]) stateData[st] = { available: [], notAvailable: [] };
    const mtc = snap.mosquitoTickControl;
    const city = snap.city || 'Unknown';
    if (mtc === 'not_available' || mtc === 'N/A') {
      stateData[st].notAvailable.push(city);
    } else if (typeof mtc === 'number' && mtc > 0) {
      stateData[st].available.push(city);
    } else {
      stateData[st].notAvailable.push(city);
    }
  }

  const available = [];
  const notAvailable = [];
  const edgeCases = [];

  for (const st of Object.keys(stateData).sort()) {
    const avail = stateData[st].available;
    const notAvail = stateData[st].notAvailable;
    const total = avail.length + notAvail.length;
    const pct = total > 0 ? Math.round((avail.length / total) * 1000) / 10 : 0;

    if (avail.length > 0 && notAvail.length > 0) {
      edgeCases.push({ state: st, mixedAvailability: true, availableCount: avail.length, totalCount: total, pctAvailable: pct });
    }

    // Dedupe cities and count
    const availCities = {};
    avail.forEach(c => { availCities[c] = (availCities[c] || 0) + 1; });
    const notAvailCities = {};
    notAvail.forEach(c => { notAvailCities[c] = (notAvailCities[c] || 0) + 1; });

    if (avail.length > 0) {
      available.push({ state: st, city: Object.keys(availCities).join(', '), addressCount: avail.length });
    }
    if (notAvail.length > 0) {
      notAvailable.push({ state: st, city: Object.keys(notAvailCities).join(', '), addressCount: notAvail.length });
    }
  }

  const totalAvailable = available.reduce((s, a) => s + a.addressCount, 0);
  const totalNotAvailable = notAvailable.reduce((s, a) => s + a.addressCount, 0);
  const overallPct = (totalAvailable + totalNotAvailable) > 0
    ? Math.round((totalAvailable / (totalAvailable + totalNotAvailable)) * 1000) / 10
    : 0;

  return { available, notAvailable, edgeCases, summary: { totalAvailable, totalNotAvailable, overallPct } };
}

function computeBucketMigrationData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 2) return null;

  const fromDate = dates[dates.length - 2];
  const toDate = dates[dates.length - 1];
  const fromSnaps = snapshotsByDate[fromDate];
  const toSnaps = snapshotsByDate[toDate];

  // Create buckets based on data range
  const allPrices = [];
  for (const s of fromSnaps) if (typeof s.pest365 === 'number' && s.pest365 > 0) allPrices.push(s.pest365);
  for (const s of toSnaps) if (typeof s.pest365 === 'number' && s.pest365 > 0) allPrices.push(s.pest365);
  if (allPrices.length < 5) return null;

  const minP = Math.floor(Math.min(...allPrices) / 5) * 5;
  const maxP = Math.ceil(Math.max(...allPrices) / 5) * 5;
  const buckets = [];
  for (let b = minP; b < maxP; b += 5) {
    buckets.push(`$${b}-${b + 5}`);
  }
  if (buckets.length < 2 || buckets.length > 15) return null;

  function getBucketIndex(price) {
    const idx = Math.floor((price - minP) / 5);
    return Math.min(Math.max(idx, 0), buckets.length - 1);
  }

  const n = buckets.length;
  const migrations = Array.from({ length: n }, () => Array(n).fill(0));

  const fromMap = {};
  for (const s of fromSnaps) {
    if (typeof s.pest365 === 'number' && s.pest365 > 0) fromMap[s.addressId] = s.pest365;
  }

  for (const s of toSnaps) {
    if (typeof s.pest365 !== 'number' || s.pest365 <= 0) continue;
    const fromPrice = fromMap[s.addressId];
    if (fromPrice == null) continue;
    const fi = getBucketIndex(fromPrice);
    const ti = getBucketIndex(s.pest365);
    migrations[fi][ti]++;
  }

  // Net flows
  const netFlows = buckets.map((bucket, i) => {
    let netIn = 0;
    for (let j = 0; j < n; j++) {
      if (j !== i) {
        netIn += migrations[j][i] - migrations[i][j];
      }
    }
    return { bucket, netIn };
  });

  return { buckets, fromDate, toDate, migrations, netFlows };
}

function computeAnomalyScorecardData(addresses, snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 3) return null;

  const latestDate = dates[dates.length - 1];
  const latestSnaps = snapshotsByDate[latestDate];

  // Global stats for Z-scores
  const allP365 = latestSnaps.map(s => s.pest365).filter(v => typeof v === 'number' && v > 0);
  if (allP365.length < 5) return null;
  const globalMean = allP365.reduce((s, v) => s + v, 0) / allP365.length;
  const globalStd = Math.sqrt(allP365.reduce((s, v) => s + (v - globalMean) ** 2, 0) / allP365.length);

  // State means
  const stateMeans = {};
  const byState = {};
  for (const snap of latestSnaps) {
    if (!snap.state || typeof snap.pest365 !== 'number') continue;
    if (!byState[snap.state]) byState[snap.state] = [];
    byState[snap.state].push(snap.pest365);
  }
  for (const st of Object.keys(byState)) {
    stateMeans[st] = byState[st].reduce((s, v) => s + v, 0) / byState[st].length;
  }

  // Build per-address timelines for volatility
  const addressTimelines = {};
  for (const date of dates) {
    for (const snap of snapshotsByDate[date]) {
      if (!addressTimelines[snap.addressId]) addressTimelines[snap.addressId] = [];
      if (typeof snap.pest365 === 'number' && snap.pest365 > 0) {
        addressTimelines[snap.addressId].push(snap.pest365);
      }
    }
  }

  const scorecardAddresses = [];
  for (const snap of latestSnaps) {
    const price = snap.pest365;
    if (typeof price !== 'number' || price <= 0) continue;

    const addr = addresses.find(a => a.addressId === snap.addressId);
    const label = addr ? `${addr.city || ''}, ${addr.state || ''}`.trim().replace(/^,\s*/, '') : snap.addressId;

    // Z-score rank
    const zScore = globalStd > 0 ? Math.abs(price - globalMean) / globalStd : 0;
    const zScoreRank = Math.min(zScore / 3, 1); // normalize to 0-1

    // Volatility rank
    const timeline = addressTimelines[snap.addressId] || [];
    let volatility = 0;
    if (timeline.length >= 2) {
      const mean = timeline.reduce((s, v) => s + v, 0) / timeline.length;
      const std = Math.sqrt(timeline.reduce((s, v) => s + (v - mean) ** 2, 0) / timeline.length);
      volatility = mean > 0 ? std / mean : 0;
    }
    const volatilityRank = Math.min(volatility * 10, 1);

    // Deviation from state mean
    const stateMean = stateMeans[snap.state] || globalMean;
    const deviation = stateMean > 0 ? Math.abs(price - stateMean) / stateMean : 0;
    const deviationRank = Math.min(deviation * 5, 1);

    // Missing data score
    const expectedDates = dates.length;
    const actualDates = timeline.length;
    const missingDataScore = 1 - (actualDates / expectedDates);

    // Composite score (0-10)
    const compositeScore = Math.round((zScoreRank * 3 + volatilityRank * 3 + deviationRank * 2.5 + missingDataScore * 1.5) * 100) / 100;

    let alertLevel = 'normal';
    if (compositeScore > 6) alertLevel = 'alert';
    else if (compositeScore > 3.5) alertLevel = 'watch';

    scorecardAddresses.push({
      id: snap.addressId,
      label,
      state: snap.state || '',
      compositeScore,
      components: {
        zScoreRank: Math.round(zScoreRank * 100) / 100,
        volatilityRank: Math.round(volatilityRank * 100) / 100,
        deviationRank: Math.round(deviationRank * 100) / 100,
        missingDataScore: Math.round(missingDataScore * 100) / 100,
      },
      alertLevel,
    });
  }

  scorecardAddresses.sort((a, b) => b.compositeScore - a.compositeScore);
  return { addresses: scorecardAddresses };
}

function computeRankStabilityData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 4) return null;

  // Build per-date rankings
  const rankTraces = {};
  const addressLabels = {};

  for (const date of dates) {
    const snaps = snapshotsByDate[date]
      .filter(s => typeof s.pest365 === 'number' && s.pest365 > 0)
      .sort((a, b) => b.pest365 - a.pest365);

    snaps.forEach((snap, idx) => {
      if (!rankTraces[snap.addressId]) rankTraces[snap.addressId] = [];
      rankTraces[snap.addressId].push(idx + 1);
      if (!addressLabels[snap.addressId]) {
        addressLabels[snap.addressId] = `${snap.city || ''}, ${snap.state || ''}`.trim().replace(/^,\s*/, '');
      }
    });
  }

  const addressList = [];
  for (const aid of Object.keys(rankTraces)) {
    const ranks = rankTraces[aid];
    if (ranks.length < 3) continue;

    const avgRank = Math.round((ranks.reduce((s, v) => s + v, 0) / ranks.length) * 10) / 10;
    const mean = avgRank;
    const rankStdDev = Math.round(Math.sqrt(ranks.reduce((s, v) => s + (v - mean) ** 2, 0) / ranks.length) * 10) / 10;

    let maxRankJump = 0;
    for (let i = 1; i < ranks.length; i++) {
      maxRankJump = Math.max(maxRankJump, Math.abs(ranks[i] - ranks[i - 1]));
    }

    const maxPossibleStd = ranks.length > 0 ? Math.max(...ranks) / 2 : 1;
    const stabilityScore = Math.round(Math.max(0, Math.min(100, 100 - (rankStdDev / (maxPossibleStd || 1)) * 100)));

    addressList.push({ id: aid, label: addressLabels[aid], avgRank, rankStdDev, maxRankJump, stabilityScore });
  }

  if (addressList.length < 5) return null;
  return { addresses: addressList, rankTraces, dates };
}

function computeDeviationBenchmarkData(addresses, snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 2) return null;

  const latestDate = dates[dates.length - 1];
  const latestSnaps = snapshotsByDate[latestDate];

  // National mean
  const allPrices = latestSnaps.map(s => s.pest365).filter(v => typeof v === 'number' && v > 0);
  if (allPrices.length < 5) return null;
  const nationalMean = allPrices.reduce((s, v) => s + v, 0) / allPrices.length;

  // State means
  const byState = {};
  for (const snap of latestSnaps) {
    if (!snap.state || typeof snap.pest365 !== 'number') continue;
    if (!byState[snap.state]) byState[snap.state] = [];
    byState[snap.state].push(snap.pest365);
  }
  const stateMeans = {};
  for (const st of Object.keys(byState)) {
    stateMeans[st] = byState[st].reduce((s, v) => s + v, 0) / byState[st].length;
  }

  // Historical means per address
  const addressHistory = {};
  for (const date of dates) {
    for (const snap of snapshotsByDate[date]) {
      if (typeof snap.pest365 !== 'number' || snap.pest365 <= 0) continue;
      if (!addressHistory[snap.addressId]) addressHistory[snap.addressId] = [];
      addressHistory[snap.addressId].push(snap.pest365);
    }
  }

  const result = [];
  for (const snap of latestSnaps) {
    const price = snap.pest365;
    if (typeof price !== 'number' || price <= 0) continue;

    const addr = addresses.find(a => a.addressId === snap.addressId);
    const label = addr ? `${addr.city || ''}, ${addr.state || ''}`.trim().replace(/^,\s*/, '') : snap.addressId;

    const fromNational = nationalMean > 0 ? Math.round(((price - nationalMean) / nationalMean) * 10000) / 100 : 0;
    const stMean = stateMeans[snap.state] || nationalMean;
    const fromState = stMean > 0 ? Math.round(((price - stMean) / stMean) * 10000) / 100 : 0;
    const hist = addressHistory[snap.addressId] || [];
    const histMean = hist.length > 0 ? hist.reduce((s, v) => s + v, 0) / hist.length : price;
    const fromHistorical = histMean > 0 ? Math.round(((price - histMean) / histMean) * 10000) / 100 : 0;

    const alerts = [];
    if (Math.abs(fromNational) > 5) alerts.push(fromNational > 0 ? 'Above national average' : 'Below national average');
    if (Math.abs(fromState) > 5) alerts.push(fromState > 0 ? 'Above state average' : 'Below state average');
    if (Math.abs(fromHistorical) > 5) alerts.push(fromHistorical > 0 ? 'Above historical average' : 'Below historical average');

    result.push({
      label, state: snap.state || '',
      deviations: { fromNational, fromState, fromHistorical },
      currentPrice: Math.round(price * 100) / 100,
      alerts,
    });
  }

  result.sort((a, b) => {
    const maxA = Math.max(Math.abs(a.deviations.fromNational), Math.abs(a.deviations.fromState), Math.abs(a.deviations.fromHistorical));
    const maxB = Math.max(Math.abs(b.deviations.fromNational), Math.abs(b.deviations.fromState), Math.abs(b.deviations.fromHistorical));
    return maxB - maxA;
  });

  return { addresses: result };
}

function computeCUSUMData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 5) return null;

  const fields = ['pest365', 'pest365Plus'];
  const result = { dates, fields: {} };

  for (const field of fields) {
    const means = dates.map(d => {
      const vals = snapshotsByDate[d].map(s => s[field]).filter(v => typeof v === 'number' && v > 0);
      return vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    });
    const valid = means.filter(v => v != null);
    if (valid.length < 5) continue;

    const target = valid.reduce((s, v) => s + v, 0) / valid.length;
    const std = Math.sqrt(valid.reduce((s, v) => s + (v - target) ** 2, 0) / valid.length);
    const k = std * 0.5; // allowance (half sigma)
    const h = std * 4;   // decision interval

    const cusum_pos = [];
    const cusum_neg = [];
    let cp = 0, cn = 0;

    for (const m of means) {
      if (m == null) { cusum_pos.push(0); cusum_neg.push(0); continue; }
      cp = Math.max(0, cp + (m - target) - k);
      cn = Math.min(0, cn + (m - target) + k);
      cusum_pos.push(Math.round(cp * 100) / 100);
      cusum_neg.push(Math.round(cn * 100) / 100);
    }

    result.fields[field] = { cusum_pos, cusum_neg, ucl: Math.round(h * 100) / 100, lcl: Math.round(-h * 100) / 100, target: Math.round(target * 100) / 100 };
  }

  return Object.keys(result.fields).length > 0 ? result : null;
}

function computeShewhartData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 5) return null;

  const xbar = [];
  const range = [];

  for (const date of dates) {
    const vals = snapshotsByDate[date].map(s => s.pest365).filter(v => typeof v === 'number' && v > 0);
    if (vals.length < 2) { xbar.push(null); range.push(null); continue; }
    const mean = vals.reduce((s, v) => s + v, 0) / vals.length;
    xbar.push(Math.round(mean * 100) / 100);
    range.push(Math.round((Math.max(...vals) - Math.min(...vals)) * 100) / 100);
  }

  const validXbar = xbar.filter(v => v != null);
  const validRange = range.filter(v => v != null);
  if (validXbar.length < 5) return null;

  const centerline = Math.round((validXbar.reduce((s, v) => s + v, 0) / validXbar.length) * 100) / 100;
  const avgRange = validRange.reduce((s, v) => s + v, 0) / validRange.length;
  const std = Math.sqrt(validXbar.reduce((s, v) => s + (v - centerline) ** 2, 0) / validXbar.length);

  const ucl_x = Math.round((centerline + 3 * std) * 100) / 100;
  const lcl_x = Math.round((centerline - 3 * std) * 100) / 100;
  const sigma1_upper = Math.round((centerline + std) * 100) / 100;
  const sigma1_lower = Math.round((centerline - std) * 100) / 100;
  const sigma2_upper = Math.round((centerline + 2 * std) * 100) / 100;
  const sigma2_lower = Math.round((centerline - 2 * std) * 100) / 100;
  const ucl_r = Math.round((avgRange * 2.114) * 100) / 100; // D4 for typical subgroup

  const outOfControl = xbar.map(v => v != null && (v > ucl_x || v < lcl_x));

  return { dates, xbar, range, ucl_x, lcl_x, centerline, ucl_r, sigma1_upper, sigma1_lower, sigma2_upper, sigma2_lower, outOfControl };
}

function computeEWMAData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 5) return null;

  const fields = ['pest365', 'pest365Plus'];
  const lambda = 0.2;
  const result = { dates, fields: {} };

  for (const field of fields) {
    const means = dates.map(d => {
      const vals = snapshotsByDate[d].map(s => s[field]).filter(v => typeof v === 'number' && v > 0);
      return vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    });
    const valid = means.filter(v => v != null);
    if (valid.length < 5) continue;

    const target = valid.reduce((s, v) => s + v, 0) / valid.length;
    const variance = valid.reduce((s, v) => s + (v - target) ** 2, 0) / valid.length;
    const sigma = Math.sqrt(variance);
    const L = 2.7; // control limit width

    const ewma = [];
    const ucl = [];
    const lcl = [];
    let z = target;

    for (let i = 0; i < means.length; i++) {
      const val = means[i] != null ? means[i] : z;
      z = lambda * val + (1 - lambda) * z;
      const factor = Math.sqrt((lambda / (2 - lambda)) * (1 - Math.pow(1 - lambda, 2 * (i + 1))));
      const cl = L * sigma * factor;
      ewma.push(Math.round(z * 100) / 100);
      ucl.push(Math.round((target + cl) * 100) / 100);
      lcl.push(Math.round((target - cl) * 100) / 100);
    }

    result.fields[field] = { ewma, ucl, lcl, lambda, target: Math.round(target * 100) / 100 };
  }

  return Object.keys(result.fields).length > 0 ? result : null;
}

function computeEntropyData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 3) return null;

  const fields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  const result = { dates, fields: {} };

  for (const field of fields) {
    const entropy = [];
    for (const date of dates) {
      const vals = snapshotsByDate[date].map(s => s[field]).filter(v => typeof v === 'number' && v > 0);
      if (vals.length < 3) { entropy.push(null); continue; }

      // Bin values into price buckets for entropy calculation
      const min = Math.min(...vals);
      const max = Math.max(...vals);
      const binWidth = (max - min) / Math.max(Math.ceil(Math.sqrt(vals.length)), 3) || 1;
      const bins = {};
      for (const v of vals) {
        const binIdx = Math.floor((v - min) / binWidth);
        bins[binIdx] = (bins[binIdx] || 0) + 1;
      }

      // Shannon entropy
      const n = vals.length;
      let h = 0;
      for (const count of Object.values(bins)) {
        const p = count / n;
        if (p > 0) h -= p * Math.log2(p);
      }
      entropy.push(Math.round(h * 1000) / 1000);
    }

    const validEntropy = entropy.filter(v => v != null);
    if (validEntropy.length < 3) continue;
    const maxEntropy = Math.round(Math.log2(Math.max(Math.ceil(Math.sqrt(50)), 3)) * 1000) / 1000;
    result.fields[field] = { entropy, maxEntropy };
  }

  return Object.keys(result.fields).length > 0 ? result : null;
}

function computeBenfordData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 2) return null;

  const fields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  const digits = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const expected = digits.map(d => Math.round(Math.log10(1 + 1 / d) * 1000) / 1000);
  const result = { fields: {} };

  for (const field of fields) {
    // Collect all prices across all dates
    const allPrices = [];
    for (const date of dates) {
      for (const snap of snapshotsByDate[date]) {
        const v = snap[field];
        if (typeof v === 'number' && v > 0) allPrices.push(v);
      }
    }
    if (allPrices.length < 20) continue;

    // Count leading digits
    const digitCounts = Array(9).fill(0);
    for (const price of allPrices) {
      const leading = parseInt(String(price).replace(/[^1-9]/, '').charAt(0));
      if (leading >= 1 && leading <= 9) digitCounts[leading - 1]++;
    }

    const total = digitCounts.reduce((s, v) => s + v, 0);
    if (total < 20) continue;
    const observed = digitCounts.map(c => Math.round((c / total) * 1000) / 1000);

    // Chi-square test
    let chiSquare = 0;
    for (let i = 0; i < 9; i++) {
      const exp = expected[i] * total;
      chiSquare += Math.pow(digitCounts[i] - exp, 2) / exp;
    }
    chiSquare = Math.round(chiSquare * 100) / 100;

    // Approximate p-value (chi-square with 8 df)
    // Using simplified approximation
    const pValue = chiSquare < 15.51 ? Math.round(Math.max(0.05, 1 - chiSquare / 30) * 100) / 100 : Math.round(Math.max(0.001, Math.exp(-chiSquare / 5)) * 1000) / 1000;
    const conforms = pValue > 0.05;

    result.fields[field] = { observed, expected, digits, chiSquare, pValue, conforms };
  }

  return Object.keys(result.fields).length > 0 ? result : null;
}

function computeComplexityData(snapshotsByDate) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length < 5) return null;

  const fields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  const result = { fields: {}, overall: 0 };
  const allComplexities = [];

  for (const field of fields) {
    // Build a single time series of mean values
    const series = dates.map(d => {
      const vals = snapshotsByDate[d].map(s => s[field]).filter(v => typeof v === 'number' && v > 0);
      return vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    }).filter(v => v != null);

    if (series.length < 5) continue;

    // Rolling approximate entropy (window of 5)
    const windowSize = Math.min(5, Math.floor(series.length / 2));
    const complexity = [];
    const rollingDates = [];

    for (let i = windowSize; i <= series.length; i++) {
      const window = series.slice(i - windowSize, i);
      const mean = window.reduce((s, v) => s + v, 0) / window.length;
      const std = Math.sqrt(window.reduce((s, v) => s + (v - mean) ** 2, 0) / window.length);

      // Normalized complexity: coefficient of variation + change frequency
      const cv = mean > 0 ? std / mean : 0;
      let changes = 0;
      for (let j = 1; j < window.length; j++) {
        if (Math.abs(window[j] - window[j - 1]) > 0.01) changes++;
      }
      const changeRate = changes / (window.length - 1);
      const cpx = Math.min(1, (cv * 5 + changeRate) / 2);
      complexity.push(Math.round(cpx * 1000) / 1000);
      if (i <= dates.length) rollingDates.push(dates[i - 1]);
    }

    if (complexity.length === 0) continue;

    const avgCpx = complexity.reduce((s, v) => s + v, 0) / complexity.length;
    let interpretation = 'Highly predictable';
    if (avgCpx > 0.6) interpretation = 'Erratic';
    else if (avgCpx > 0.3) interpretation = 'Moderately complex';
    else if (avgCpx > 0.15) interpretation = 'Moderately predictable';

    result.fields[field] = { dates: rollingDates, complexity, interpretation };
    allComplexities.push(avgCpx);
  }

  if (Object.keys(result.fields).length === 0) return null;
  result.overall = allComplexities.length > 0
    ? Math.round((allComplexities.reduce((s, v) => s + v, 0) / allComplexities.length) * 100) / 100
    : 0;

  return result;
}

// ---------------------------------------------------------------------------
// Lorenz Curve & Gini Coefficient
// ---------------------------------------------------------------------------

function computeLorenzCurveData(addresses) {
  const fields = PRICE_FIELDS;
  const result = { fields: [] };

  for (const field of fields) {
    const values = [];
    for (const addr of addresses) {
      const raw = addr[field];
      if (isNotAvailable(raw)) continue;
      const v = parsePrice(raw);
      if (v != null && v > 0) values.push(v);
    }
    if (values.length < 2) continue;

    values.sort((a, b) => a - b);
    const total = values.reduce((s, v) => s + v, 0);
    const n = values.length;
    const points = [{ x: 0, y: 0 }];
    let cumSum = 0;
    for (let i = 0; i < n; i++) {
      cumSum += values[i];
      points.push({ x: round((i + 1) / n, 4), y: round(cumSum / total, 4) });
    }

    // Gini coefficient via trapezoidal approximation
    let areaUnderCurve = 0;
    for (let i = 1; i < points.length; i++) {
      areaUnderCurve += (points[i].x - points[i - 1].x) * (points[i].y + points[i - 1].y) / 2;
    }
    const gini = round((0.5 - areaUnderCurve) / 0.5, 4);

    result.fields.push({ field, points, gini });
  }

  return result.fields.length > 0 ? result : null;
}

// ---------------------------------------------------------------------------
// Q-Q Plot Data
// ---------------------------------------------------------------------------

function normalQuantile(p) {
  if (p <= 0) return -4;
  if (p >= 1) return 4;
  if (p < 0.5) return -normalQuantile(1 - p);
  const t = Math.sqrt(-2 * Math.log(1 - p));
  const c0 = 2.515517, c1 = 0.802853, c2 = 0.010328;
  const d1 = 1.432788, d2 = 0.189269, d3 = 0.001308;
  return t - (c0 + c1 * t + c2 * t * t) / (1 + d1 * t + d2 * t * t + d3 * t * t * t);
}

function computeQQPlotData(addresses) {
  const fields = PRICE_FIELDS;
  const result = { fields: [] };

  for (const field of fields) {
    const values = [];
    for (const addr of addresses) {
      const raw = addr[field];
      if (isNotAvailable(raw)) continue;
      const v = parsePrice(raw);
      if (v != null) values.push(v);
    }
    if (values.length < 3) continue;

    values.sort((a, b) => a - b);
    const n = values.length;
    const avg = values.reduce((s, v) => s + v, 0) / n;
    const variance = values.reduce((s, v) => s + (v - avg) ** 2, 0) / n;
    const sd = Math.sqrt(variance);

    // Skewness
    const skewness = sd > 0
      ? values.reduce((s, v) => s + ((v - avg) / sd) ** 3, 0) / n
      : 0;

    // Kurtosis (excess)
    const kurtosis = sd > 0
      ? values.reduce((s, v) => s + ((v - avg) / sd) ** 4, 0) / n - 3
      : 0;

    const points = [];
    for (let i = 0; i < n; i++) {
      const p = (i + 0.5) / n; // continuity-corrected rank
      const theoretical = normalQuantile(p);
      points.push({ theoretical: round(theoretical, 4), observed: round(values[i], 2) });
    }

    result.fields.push({
      field,
      points,
      mean: round(avg, 2),
      stdDev: round(sd, 2),
      skewness: round(skewness, 4),
      kurtosis: round(kurtosis, 4),
    });
  }

  return result.fields.length > 0 ? result : null;
}

// ---------------------------------------------------------------------------
// Herfindahl-Hirschman Index (HHI) by State
// ---------------------------------------------------------------------------

function computeHerfindahlData(snapshots, addresses) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length === 0) return null;

  // Map addressId -> state
  const addrState = {};
  for (const addr of addresses) {
    addrState[addr.addressId] = (addr.state || '').toUpperCase();
  }

  const hhiTimeline = [];
  const byField = {};
  for (const f of PRICE_FIELDS) byField[f] = [];

  for (const date of dates) {
    const snap = snapshots[date];

    for (const f of PRICE_FIELDS) {
      // Group by state
      const stateSum = {};
      let totalSum = 0;
      for (const addrId of Object.keys(snap)) {
        const val = snap[addrId]?.[f];
        if (val == null || isNaN(val)) continue;
        const st = addrState[addrId] || 'UNKNOWN';
        stateSum[st] = (stateSum[st] || 0) + val;
        totalSum += val;
      }

      if (totalSum === 0) {
        byField[f].push(0);
        if (f === 'pest365') hhiTimeline.push(0);
        continue;
      }

      let hhi = 0;
      for (const st of Object.keys(stateSum)) {
        const sharePercent = (stateSum[st] / totalSum) * 100;
        hhi += sharePercent * sharePercent;
      }
      hhi = round(hhi, 2);
      byField[f].push(hhi);
      if (f === 'pest365') hhiTimeline.push(hhi);
    }
  }

  // Top contributors from the last snapshot
  const topContributors = [];
  if (dates.length > 0) {
    const lastDate = dates[dates.length - 1];
    const snap = snapshots[lastDate];
    const stateSum = {};
    let totalSum = 0;
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val == null || isNaN(val)) continue;
      const st = addrState[addrId] || 'UNKNOWN';
      stateSum[st] = (stateSum[st] || 0) + val;
      totalSum += val;
    }
    if (totalSum > 0) {
      for (const st of Object.keys(stateSum)) {
        topContributors.push({ state: st, share: round((stateSum[st] / totalSum) * 100, 2) });
      }
      topContributors.sort((a, b) => b.share - a.share);
    }
  }

  return {
    dates,
    hhi: hhiTimeline,
    thresholds: { low: 1500, moderate: 2500 },
    byField,
    topContributors,
  };
}

// ---------------------------------------------------------------------------
// Markov Transition Matrix
// ---------------------------------------------------------------------------

function computeMarkovTransitionData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 3) return null;

  const states = ['Decreased', 'Stable', 'Increased'];
  const stateIndex = { Decreased: 0, Stable: 1, Increased: 2 };

  // Classify each address at each date pair into a state
  function classifyChange(prev, curr) {
    if (prev == null || curr == null || prev === 0) return null;
    const pctChange = ((curr - prev) / prev) * 100;
    if (pctChange < -0.5) return 'Decreased';
    if (pctChange > 0.5) return 'Increased';
    return 'Stable';
  }

  // Build state sequences per address
  const addressStates = {}; // addrId -> [state0, state1, ...]
  for (let i = 1; i < dates.length; i++) {
    const prevSnap = snapshots[dates[i - 1]];
    const currSnap = snapshots[dates[i]];
    for (const addrId of Object.keys(currSnap)) {
      const prev = prevSnap[addrId]?.pest365;
      const curr = currSnap[addrId]?.pest365;
      const state = classifyChange(prev, curr);
      if (state == null) continue;
      if (!addressStates[addrId]) addressStates[addrId] = [];
      addressStates[addrId].push(state);
    }
  }

  // Count transitions
  const counts = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  for (const addrId of Object.keys(addressStates)) {
    const seq = addressStates[addrId];
    for (let i = 1; i < seq.length; i++) {
      const from = stateIndex[seq[i - 1]];
      const to = stateIndex[seq[i]];
      counts[from][to]++;
    }
  }

  // Convert to probabilities
  const matrix = counts.map(row => {
    const rowSum = row.reduce((s, v) => s + v, 0);
    if (rowSum === 0) return [0, 0, 0];
    return row.map(v => round(v / rowSum, 4));
  });

  // Stationary distribution via repeated matrix multiplication
  let dist = [1 / 3, 1 / 3, 1 / 3];
  for (let iter = 0; iter < 100; iter++) {
    const next = [0, 0, 0];
    for (let j = 0; j < 3; j++) {
      for (let i = 0; i < 3; i++) {
        next[j] += dist[i] * matrix[i][j];
      }
    }
    dist = next;
  }
  const stationaryDistribution = dist.map(v => round(v, 4));

  return {
    states,
    matrix,
    counts,
    stationaryDistribution,
    field: 'pest365',
  };
}

// ---------------------------------------------------------------------------
// Monte Carlo Fan Chart
// ---------------------------------------------------------------------------

function computeMonteCarloFanData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 3) return null;

  // Compute mean pest365 per date
  const meanPrices = [];
  const validDates = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val != null && !isNaN(val)) vals.push(val);
    }
    const avg = mean(vals);
    if (avg != null) {
      meanPrices.push(avg);
      validDates.push(date);
    }
  }

  if (meanPrices.length < 3) return null;

  // Compute log returns
  const logReturns = [];
  for (let i = 1; i < meanPrices.length; i++) {
    if (meanPrices[i] > 0 && meanPrices[i - 1] > 0) {
      logReturns.push(Math.log(meanPrices[i] / meanPrices[i - 1]));
    }
  }

  if (logReturns.length < 2) return null;

  const mu = logReturns.reduce((s, v) => s + v, 0) / logReturns.length;
  const sigma = Math.sqrt(
    logReturns.reduce((s, v) => s + (v - mu) ** 2, 0) / logReturns.length
  );

  // Box-Muller transform for random normal
  function randomNormal() {
    let u1, u2;
    do { u1 = Math.random(); } while (u1 === 0);
    u2 = Math.random();
    return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  }

  const lastPrice = meanPrices[meanPrices.length - 1];
  const nSimulations = 1000;
  const nSteps = 6;

  // Generate future dates (14-day increments)
  const lastDate = new Date(validDates[validDates.length - 1]);
  const futureDates = [];
  for (let s = 1; s <= nSteps; s++) {
    const d = new Date(lastDate);
    d.setDate(d.getDate() + 14 * s);
    futureDates.push(d.toISOString().slice(0, 10));
  }

  // Simulate paths
  const allPaths = []; // nSimulations x nSteps
  for (let sim = 0; sim < nSimulations; sim++) {
    const path = [];
    let price = lastPrice;
    for (let s = 0; s < nSteps; s++) {
      const z = randomNormal();
      price = price * Math.exp((mu - 0.5 * sigma * sigma) + sigma * z);
      path.push(price);
    }
    allPaths.push(path);
  }

  // Compute percentile bands at each step
  const medianArr = [], p10Arr = [], p25Arr = [], p75Arr = [], p90Arr = [];
  for (let s = 0; s < nSteps; s++) {
    const vals = allPaths.map(path => path[s]).sort((a, b) => a - b);
    p10Arr.push(round(percentile(vals, 10), 2));
    p25Arr.push(round(percentile(vals, 25), 2));
    medianArr.push(round(percentile(vals, 50), 2));
    p75Arr.push(round(percentile(vals, 75), 2));
    p90Arr.push(round(percentile(vals, 90), 2));
  }

  // Save 5 sample paths
  const samplePaths = [];
  for (let i = 0; i < Math.min(5, nSimulations); i++) {
    samplePaths.push(allPaths[i].map(v => round(v, 2)));
  }

  return {
    historical: {
      dates: validDates,
      values: meanPrices.map(v => round(v, 2)),
    },
    forecast: {
      dates: futureDates,
      median: medianArr,
      p10: p10Arr,
      p25: p25Arr,
      p75: p75Arr,
      p90: p90Arr,
      samplePaths,
    },
    simulations: nSimulations,
    field: 'pest365',
  };
}

// ---------------------------------------------------------------------------
// Bayesian Update Visualization
// ---------------------------------------------------------------------------

function computeBayesianUpdateData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 2) return null;

  // Collect pest365 values per snapshot
  function getValues(date) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const val = snap[addrId]?.pest365;
      if (val != null && !isNaN(val)) vals.push(val);
    }
    return vals;
  }

  // Prior: all snapshots except the last
  const priorValues = [];
  for (let i = 0; i < dates.length - 1; i++) {
    priorValues.push(...getValues(dates[i]));
  }
  if (priorValues.length < 2) return null;

  // Likelihood: last snapshot only
  const likelihoodValues = getValues(dates[dates.length - 1]);
  if (likelihoodValues.length < 2) return null;

  const priorMean = priorValues.reduce((s, v) => s + v, 0) / priorValues.length;
  const priorVar = priorValues.reduce((s, v) => s + (v - priorMean) ** 2, 0) / priorValues.length;
  const priorStd = Math.sqrt(priorVar);

  const likeMean = likelihoodValues.reduce((s, v) => s + v, 0) / likelihoodValues.length;
  const likeVar = likelihoodValues.reduce((s, v) => s + (v - likeMean) ** 2, 0) / likelihoodValues.length;
  const likeStd = Math.sqrt(likeVar);

  if (priorVar === 0 || likeVar === 0) return null;

  // Posterior (normal-normal conjugate)
  const posteriorVar = 1 / (1 / priorVar + 1 / likeVar);
  const posteriorMean = posteriorVar * (priorMean / priorVar + likeMean / likeVar);
  const posteriorStd = Math.sqrt(posteriorVar);

  // Normal PDF
  function normalPDF(x, mu, sigma) {
    return (1 / (sigma * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * ((x - mu) / sigma) ** 2);
  }

  // Generate density points
  function generateDensity(mu, sigma, label) {
    const points = [];
    const lo = mu - 4 * sigma;
    const hi = mu + 4 * sigma;
    const step = (hi - lo) / 99;
    for (let i = 0; i < 100; i++) {
      const x = lo + i * step;
      points.push({ x: round(x, 2), y: round(normalPDF(x, mu, sigma), 6) });
    }
    return { label, points, mean: round(mu, 2), stdDev: round(sigma, 2) };
  }

  const prior = generateDensity(priorMean, priorStd, 'Prior');
  const likelihood = generateDensity(likeMean, likeStd, 'Likelihood');
  const posterior = generateDensity(posteriorMean, posteriorStd, 'Posterior');

  // Shrinkage: fraction posterior moved from prior toward likelihood
  const totalDist = Math.abs(likeMean - priorMean);
  const shrinkage = totalDist > 0
    ? round(Math.abs(posteriorMean - priorMean) / totalDist, 4)
    : 0;

  return {
    field: 'pest365',
    prior,
    likelihood,
    posterior,
    credibleInterval: {
      low: round(posteriorMean - 1.96 * posteriorStd, 2),
      high: round(posteriorMean + 1.96 * posteriorStd, 2),
      level: 0.95,
    },
    shrinkage,
  };
}

// ---------------------------------------------------------------------------
// Granger Causality Data
// ---------------------------------------------------------------------------

/**
 * Simple OLS regression via normal equations: beta = (X'X)^{-1} X'y
 * X: array of arrays (rows = observations, cols = features incl. intercept)
 * y: array of response values
 * Returns { coefficients, rss }
 */
function simpleOLS(X, y) {
  const n = X.length;
  const k = X[0].length;
  // X'X
  const XtX = Array.from({ length: k }, () => new Array(k).fill(0));
  for (let i = 0; i < n; i++) {
    for (let r = 0; r < k; r++) {
      for (let c = 0; c < k; c++) {
        XtX[r][c] += X[i][r] * X[i][c];
      }
    }
  }
  // X'y
  const Xty = new Array(k).fill(0);
  for (let i = 0; i < n; i++) {
    for (let r = 0; r < k; r++) {
      Xty[r] += X[i][r] * y[i];
    }
  }
  // Invert XtX via Gauss-Jordan elimination
  const aug = XtX.map((row, i) => {
    const identity = new Array(k).fill(0);
    identity[i] = 1;
    return [...row, ...identity];
  });
  for (let col = 0; col < k; col++) {
    // Partial pivoting
    let maxRow = col;
    for (let row = col + 1; row < k; row++) {
      if (Math.abs(aug[row][col]) > Math.abs(aug[maxRow][col])) maxRow = row;
    }
    [aug[col], aug[maxRow]] = [aug[maxRow], aug[col]];
    const pivot = aug[col][col];
    if (Math.abs(pivot) < 1e-12) {
      // Singular matrix — return null
      return { coefficients: new Array(k).fill(0), rss: Infinity };
    }
    for (let j = 0; j < 2 * k; j++) aug[col][j] /= pivot;
    for (let row = 0; row < k; row++) {
      if (row === col) continue;
      const factor = aug[row][col];
      for (let j = 0; j < 2 * k; j++) aug[row][j] -= factor * aug[col][j];
    }
  }
  // Extract inverse
  const inv = aug.map(row => row.slice(k));
  // beta = inv * Xty
  const beta = new Array(k).fill(0);
  for (let r = 0; r < k; r++) {
    for (let c = 0; c < k; c++) {
      beta[r] += inv[r][c] * Xty[c];
    }
  }
  // RSS
  let rss = 0;
  for (let i = 0; i < n; i++) {
    let predicted = 0;
    for (let j = 0; j < k; j++) predicted += X[i][j] * beta[j];
    rss += (y[i] - predicted) ** 2;
  }
  return { coefficients: beta, rss };
}

function computeGrangerCausalityData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  const FIELDS = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  const LAGS = 2;

  if (dates.length < LAGS + 3) return null;

  // Build mean time series for each field
  const series = {};
  for (const f of FIELDS) {
    series[f] = [];
  }
  for (const date of dates) {
    const snap = snapshots[date];
    for (const f of FIELDS) {
      const vals = [];
      for (const addrId of Object.keys(snap)) {
        const v = snap[addrId]?.[f];
        if (v != null && !isNaN(v)) vals.push(v);
      }
      series[f].push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
    }
  }

  // Replace nulls with last known value (forward fill)
  for (const f of FIELDS) {
    for (let i = 1; i < series[f].length; i++) {
      if (series[f][i] == null) series[f][i] = series[f][i - 1];
    }
    // Backward fill remaining
    for (let i = series[f].length - 2; i >= 0; i--) {
      if (series[f][i] == null) series[f][i] = series[f][i + 1];
    }
  }

  // Check that we have enough valid data
  for (const f of FIELDS) {
    if (series[f].every(v => v == null)) return null;
  }

  const n = dates.length;
  const matrix = Array.from({ length: FIELDS.length }, () => new Array(FIELDS.length).fill(null));
  const fStatistics = Array.from({ length: FIELDS.length }, () => new Array(FIELDS.length).fill(null));

  for (let xi = 0; xi < FIELDS.length; xi++) {
    for (let yi = 0; yi < FIELDS.length; yi++) {
      if (xi === yi) continue;
      const xSeries = series[FIELDS[xi]];
      const ySeries = series[FIELDS[yi]];

      // Build regression data starting from index LAGS
      const yVals = [];
      const xRestricted = []; // intercept + own lags
      const xUnrestricted = []; // intercept + own lags + other lags

      for (let t = LAGS; t < n; t++) {
        if (ySeries[t] == null) continue;
        let skip = false;
        for (let lag = 1; lag <= LAGS; lag++) {
          if (ySeries[t - lag] == null || xSeries[t - lag] == null) { skip = true; break; }
        }
        if (skip) continue;

        yVals.push(ySeries[t]);
        const restricted = [1]; // intercept
        const unrestricted = [1]; // intercept
        for (let lag = 1; lag <= LAGS; lag++) {
          restricted.push(ySeries[t - lag]);
          unrestricted.push(ySeries[t - lag]);
        }
        for (let lag = 1; lag <= LAGS; lag++) {
          unrestricted.push(xSeries[t - lag]);
        }
        xRestricted.push(restricted);
        xUnrestricted.push(unrestricted);
      }

      if (yVals.length < LAGS * 2 + 2) {
        matrix[xi][yi] = 1;
        fStatistics[xi][yi] = 0;
        continue;
      }

      const restricted = simpleOLS(xRestricted, yVals);
      const unrestricted = simpleOLS(xUnrestricted, yVals);

      const numRestrictions = LAGS;
      const numParams = 1 + LAGS * 2; // intercept + own lags + other lags
      const dfResidual = yVals.length - numParams;

      if (dfResidual <= 0 || unrestricted.rss <= 0 || !isFinite(restricted.rss) || !isFinite(unrestricted.rss)) {
        matrix[xi][yi] = 1;
        fStatistics[xi][yi] = 0;
        continue;
      }

      const fStat = ((restricted.rss - unrestricted.rss) / numRestrictions) / (unrestricted.rss / dfResidual);
      // Approximate p-value: p ≈ exp(-0.5 * F)
      const pValue = Math.exp(-0.5 * Math.max(0, fStat));

      fStatistics[xi][yi] = round(fStat, 4);
      matrix[xi][yi] = round(pValue, 4);
    }
  }

  return {
    fields: FIELDS,
    matrix,
    fStatistics,
    significanceLevel: 0.05,
    lags: LAGS,
  };
}

// ---------------------------------------------------------------------------
// Lead-Lag Data
// ---------------------------------------------------------------------------

function computeLeadLagData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  const FIELDS = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  const MAX_LAG = 3;

  if (dates.length < 3) return null;

  // Build mean time series
  const series = {};
  for (const f of FIELDS) {
    series[f] = [];
  }
  for (const date of dates) {
    const snap = snapshots[date];
    for (const f of FIELDS) {
      const vals = [];
      for (const addrId of Object.keys(snap)) {
        const v = snap[addrId]?.[f];
        if (v != null && !isNaN(v)) vals.push(v);
      }
      series[f].push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
    }
  }

  // Forward/backward fill nulls
  for (const f of FIELDS) {
    for (let i = 1; i < series[f].length; i++) {
      if (series[f][i] == null) series[f][i] = series[f][i - 1];
    }
    for (let i = series[f].length - 2; i >= 0; i--) {
      if (series[f][i] == null) series[f][i] = series[f][i + 1];
    }
  }

  // Compute first differences
  const diffs = {};
  for (const f of FIELDS) {
    diffs[f] = [];
    for (let i = 1; i < series[f].length; i++) {
      if (series[f][i] != null && series[f][i - 1] != null) {
        diffs[f].push(series[f][i] - series[f][i - 1]);
      } else {
        diffs[f].push(0);
      }
    }
  }

  // Pearson correlation helper
  function pearsonCorr(a, b) {
    if (a.length < 2 || b.length < 2) return 0;
    const n = Math.min(a.length, b.length);
    const ma = a.slice(0, n).reduce((s, v) => s + v, 0) / n;
    const mb = b.slice(0, n).reduce((s, v) => s + v, 0) / n;
    let num = 0, dA = 0, dB = 0;
    for (let i = 0; i < n; i++) {
      const da = a[i] - ma;
      const db = b[i] - mb;
      num += da * db;
      dA += da * da;
      dB += db * db;
    }
    const den = Math.sqrt(dA * dB);
    return den === 0 ? 0 : num / den;
  }

  const pairs = [];
  for (let fi = 0; fi < FIELDS.length; fi++) {
    for (let fj = fi + 1; fj < FIELDS.length; fj++) {
      const from = FIELDS[fi];
      const to = FIELDS[fj];
      const lags = [];
      const correlations = [];

      for (let lag = -MAX_LAG; lag <= MAX_LAG; lag++) {
        lags.push(lag);
        const d1 = diffs[from];
        const d2 = diffs[to];
        // Correlate d1(t) with d2(t + lag)
        const a = [];
        const b = [];
        for (let t = 0; t < d1.length; t++) {
          const t2 = t + lag;
          if (t2 >= 0 && t2 < d2.length) {
            a.push(d1[t]);
            b.push(d2[t2]);
          }
        }
        correlations.push(round(pearsonCorr(a, b), 4));
      }

      // Find peak lag
      let peakIdx = 0;
      let peakAbs = 0;
      for (let i = 0; i < correlations.length; i++) {
        if (Math.abs(correlations[i]) > peakAbs) {
          peakAbs = Math.abs(correlations[i]);
          peakIdx = i;
        }
      }
      const peakLag = lags[peakIdx];
      const peakCorrelation = correlations[peakIdx];

      let interpretation;
      if (peakLag < 0) interpretation = `${from} leads ${to} by ${Math.abs(peakLag)} period(s)`;
      else if (peakLag > 0) interpretation = `${to} leads ${from} by ${peakLag} period(s)`;
      else interpretation = `${from} and ${to} move synchronously`;

      pairs.push({ from, to, lags, correlations, peakLag, peakCorrelation, interpretation });
    }
  }

  return { pairs };
}

// ---------------------------------------------------------------------------
// Impulse Response Data
// ---------------------------------------------------------------------------

function computeImpulseResponseData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  const IMPULSE_FIELD = 'pest365';
  const RESPONSE_FIELDS = ['pest365Plus', 'oneTime', 'initialServiceVisit'];
  const PERIODS = 7;

  if (dates.length < 4) return null;

  // Build mean time series
  const series = {};
  const allFields = [IMPULSE_FIELD, ...RESPONSE_FIELDS];
  for (const f of allFields) {
    series[f] = [];
  }
  for (const date of dates) {
    const snap = snapshots[date];
    for (const f of allFields) {
      const vals = [];
      for (const addrId of Object.keys(snap)) {
        const v = snap[addrId]?.[f];
        if (v != null && !isNaN(v)) vals.push(v);
      }
      series[f].push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
    }
  }

  // Forward/backward fill
  for (const f of allFields) {
    for (let i = 1; i < series[f].length; i++) {
      if (series[f][i] == null) series[f][i] = series[f][i - 1];
    }
    for (let i = series[f].length - 2; i >= 0; i--) {
      if (series[f][i] == null) series[f][i] = series[f][i + 1];
    }
  }

  // Compute first differences
  const diffs = {};
  for (const f of allFields) {
    diffs[f] = [];
    for (let i = 1; i < series[f].length; i++) {
      if (series[f][i] != null && series[f][i - 1] != null) {
        diffs[f].push(series[f][i] - series[f][i - 1]);
      } else {
        diffs[f].push(0);
      }
    }
  }

  // Compute std dev of impulse field changes
  const impulseChanges = diffs[IMPULSE_FIELD];
  const impMean = impulseChanges.reduce((s, v) => s + v, 0) / impulseChanges.length;
  const impVar = impulseChanges.reduce((s, v) => s + (v - impMean) ** 2, 0) / impulseChanges.length;
  const impStd = Math.sqrt(impVar) || 1;

  const responses = [];
  for (const responseField of RESPONSE_FIELDS) {
    const yChanges = diffs[responseField];
    const xChanges = diffs[IMPULSE_FIELD];

    // Estimate VAR(1): Y(t) = a + b*Y(t-1) + c*X(t-1)
    const yVals = [];
    const xMat = [];
    for (let t = 1; t < yChanges.length; t++) {
      yVals.push(yChanges[t]);
      xMat.push([1, yChanges[t - 1], xChanges[t - 1]]);
    }

    let b = 0, c = 0;
    let stderr = 0;
    if (yVals.length >= 4) {
      const ols = simpleOLS(xMat, yVals);
      b = ols.coefficients[1] || 0;
      c = ols.coefficients[2] || 0;
      stderr = yVals.length > 3 ? Math.sqrt(ols.rss / (yVals.length - 3)) : 0;
    }

    // Simulate impulse response
    const periods = Array.from({ length: PERIODS }, (_, i) => i);
    const response = new Array(PERIODS).fill(0);
    const upperBound = new Array(PERIODS).fill(0);
    const lowerBound = new Array(PERIODS).fill(0);
    const cumulative = new Array(PERIODS).fill(0);

    // Initial shock: 1 std dev in impulse field
    let xShock = impStd; // impulse variable state
    let yState = c * impStd; // initial response to the shock
    response[0] = round(yState, 4);
    cumulative[0] = round(yState, 4);
    upperBound[0] = round(yState + 1.96 * stderr, 4);
    lowerBound[0] = round(yState - 1.96 * stderr, 4);

    for (let p = 1; p < PERIODS; p++) {
      // Propagate: Y(t) = b*Y(t-1) + c*X(t-1), X decays as b_self * X(t-1)
      // For simplicity, assume impulse field also has AR(1) behavior
      xShock *= 0.5; // decay
      yState = b * yState + c * xShock;
      response[p] = round(yState, 4);
      cumulative[p] = round(cumulative[p - 1] + yState, 4);
      const bandWidth = 1.96 * stderr * Math.sqrt(p + 1);
      upperBound[p] = round(yState + bandWidth, 4);
      lowerBound[p] = round(yState - bandWidth, 4);
    }

    // Half life: first period where |response| < 0.5 * |initial response|
    const initialAbs = Math.abs(response[0]);
    let halfLife = null;
    for (let p = 1; p < PERIODS; p++) {
      if (Math.abs(response[p]) < 0.5 * initialAbs) {
        halfLife = p;
        break;
      }
    }

    responses.push({ responseField, periods, response, upperBound, lowerBound, cumulative, halfLife });
  }

  return { impulseField: IMPULSE_FIELD, responses };
}

// ---------------------------------------------------------------------------
// Recurrence Plot Data
// ---------------------------------------------------------------------------

function computeRecurrencePlotData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 3) return null;

  // Extract average pest365 per snapshot date
  const prices = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) vals.push(v);
    }
    prices.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
  }

  // Filter out null entries
  const validDates = [];
  const validPrices = [];
  for (let i = 0; i < dates.length; i++) {
    if (prices[i] != null) {
      validDates.push(dates[i]);
      validPrices.push(prices[i]);
    }
  }

  const N = validPrices.length;
  if (N < 3) return null;

  const minP = Math.min(...validPrices);
  const maxP = Math.max(...validPrices);
  const range = maxP - minP;
  const threshold = range > 0 ? 0.02 * range : 0.01;

  // Build NxN binary matrix
  const matrix = [];
  let onesCount = 0;
  for (let i = 0; i < N; i++) {
    const row = [];
    for (let j = 0; j < N; j++) {
      const val = Math.abs(validPrices[i] - validPrices[j]) <= threshold ? 1 : 0;
      row.push(val);
      onesCount += val;
    }
    matrix.push(row);
  }

  const recurrenceRate = round(onesCount / (N * N), 4);

  // Compute determinism: fraction of recurrence points on diagonal lines of length >= 2
  let diagonalPoints = 0;
  const lineLengths = [];

  // Scan all diagonals (above and including main diagonal)
  for (let offset = -(N - 1); offset < N; offset++) {
    let lineLen = 0;
    const iStart = offset >= 0 ? 0 : -offset;
    const jStart = offset >= 0 ? offset : 0;
    for (let k = 0; iStart + k < N && jStart + k < N; k++) {
      const i = iStart + k;
      const j = jStart + k;
      if (i === j) { // skip main diagonal (always 1)
        if (lineLen >= 2) {
          lineLengths.push(lineLen);
          diagonalPoints += lineLen;
        }
        lineLen = 0;
        continue;
      }
      if (matrix[i][j] === 1) {
        lineLen++;
      } else {
        if (lineLen >= 2) {
          lineLengths.push(lineLen);
          diagonalPoints += lineLen;
        }
        lineLen = 0;
      }
    }
    if (lineLen >= 2) {
      lineLengths.push(lineLen);
      diagonalPoints += lineLen;
    }
  }

  // Total off-diagonal recurrence points
  const offDiagOnes = onesCount - N; // subtract main diagonal (always 1)
  const determinism = offDiagOnes > 0 ? round(diagonalPoints / offDiagOnes, 4) : 0;

  // Shannon entropy of diagonal line length distribution
  let entropy = 0;
  if (lineLengths.length > 0) {
    const totalLines = lineLengths.length;
    const lengthCounts = {};
    for (const l of lineLengths) {
      lengthCounts[l] = (lengthCounts[l] || 0) + 1;
    }
    for (const count of Object.values(lengthCounts)) {
      const p = count / totalLines;
      if (p > 0) entropy -= p * Math.log2(p);
    }
  }

  return {
    dates: validDates,
    matrix,
    threshold: round(threshold, 4),
    field: 'pest365',
    recurrenceRate,
    determinism,
    entropy: round(entropy, 4),
  };
}

// ---------------------------------------------------------------------------
// Runs Test Data (Wald-Wolfowitz)
// ---------------------------------------------------------------------------

function computeRunsTestData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  const FIELDS = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit', 'mosquitoTickControl'];

  if (dates.length < 4) return null;

  // Normal CDF approximation: Phi(x) = 1 / (1 + exp(-1.7155277 * x * (1 + 0.0348 * x * x)))
  function normalCDF(x) {
    return 1 / (1 + Math.exp(-1.7155277 * x * (1 + 0.0348 * x * x)));
  }

  const fields = [];
  for (const field of FIELDS) {
    // Build average price series
    const avgPrices = [];
    for (const date of dates) {
      const snap = snapshots[date];
      const vals = [];
      for (const addrId of Object.keys(snap)) {
        const v = snap[addrId]?.[field];
        if (v != null && !isNaN(v)) vals.push(v);
      }
      avgPrices.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
    }

    // Compute changes between consecutive snapshots
    const changes = [];
    const changeDates = [];
    for (let i = 1; i < avgPrices.length; i++) {
      if (avgPrices[i] != null && avgPrices[i - 1] != null) {
        changes.push(avgPrices[i] - avgPrices[i - 1]);
        changeDates.push({ startDate: dates[i - 1], endDate: dates[i] });
      }
    }

    if (changes.length < 3) {
      fields.push({
        field,
        runs: [],
        totalRuns: 0,
        expectedRuns: 0,
        zScore: 0,
        pValue: 1,
        isRandom: true,
        longestRun: null,
        avgRunLength: 0,
      });
      continue;
    }

    // Classify changes as 'up' or 'down'
    const directions = changes.map(c => c > 0 ? 'up' : 'down');

    // Count runs and build runs array
    const runs = [];
    let currentType = directions[0];
    let currentLength = 1;
    let currentStart = changeDates[0].startDate;

    for (let i = 1; i < directions.length; i++) {
      if (directions[i] === currentType) {
        currentLength++;
      } else {
        runs.push({ type: currentType, length: currentLength, startDate: currentStart, endDate: changeDates[i - 1].endDate });
        currentType = directions[i];
        currentLength = 1;
        currentStart = changeDates[i].startDate;
      }
    }
    runs.push({ type: currentType, length: currentLength, startDate: currentStart, endDate: changeDates[changeDates.length - 1].endDate });

    const totalRuns = runs.length;
    const nUp = directions.filter(d => d === 'up').length;
    const nDown = directions.filter(d => d === 'down').length;
    const nTotal = nUp + nDown;

    // Expected runs and variance
    let expectedRuns = 0;
    let variance = 0;
    let zScore = 0;
    let pValue = 1;

    if (nUp > 0 && nDown > 0 && nTotal > 1) {
      expectedRuns = 1 + (2 * nUp * nDown) / nTotal;
      variance = (2 * nUp * nDown * (2 * nUp * nDown - nUp - nDown)) / (nTotal * nTotal * (nTotal - 1));
      if (variance > 0) {
        zScore = (totalRuns - expectedRuns) / Math.sqrt(variance);
        pValue = 2 * (1 - normalCDF(Math.abs(zScore)));
      }
    }

    // Find longest run
    let longestRun = runs[0] || null;
    for (const r of runs) {
      if (r.length > (longestRun?.length || 0)) longestRun = r;
    }

    const avgRunLength = runs.length > 0
      ? round(runs.reduce((s, r) => s + r.length, 0) / runs.length, 2)
      : 0;

    fields.push({
      field,
      runs,
      totalRuns,
      expectedRuns: round(expectedRuns, 2),
      zScore: round(zScore, 4),
      pValue: round(pValue, 4),
      isRandom: pValue > 0.05,
      longestRun: longestRun ? { type: longestRun.type, length: longestRun.length } : null,
      avgRunLength,
    });
  }

  return { fields };
}

// ---------------------------------------------------------------------------
// Spectral Density Data (DFT)
// ---------------------------------------------------------------------------

function computeSpectralDensityData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  const FIELDS = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];

  if (dates.length < 4) return null;

  const fields = [];
  for (const field of FIELDS) {
    // Build average price time series
    const prices = [];
    for (const date of dates) {
      const snap = snapshots[date];
      const vals = [];
      for (const addrId of Object.keys(snap)) {
        const v = snap[addrId]?.[field];
        if (v != null && !isNaN(v)) vals.push(v);
      }
      prices.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
    }

    // Forward/backward fill nulls
    for (let i = 1; i < prices.length; i++) {
      if (prices[i] == null) prices[i] = prices[i - 1];
    }
    for (let i = prices.length - 2; i >= 0; i--) {
      if (prices[i] == null) prices[i] = prices[i + 1];
    }

    if (prices.every(v => v == null)) {
      fields.push({ field, frequencies: [], power: [], periods: [], dominantFrequency: null, dominantPeriod: null, dominantPower: null });
      continue;
    }

    const N = prices.length;
    // Remove mean (center the data)
    const avg = prices.reduce((s, v) => s + v, 0) / N;
    const centered = prices.map(v => v - avg);

    // DFT
    const frequencies = [];
    const power = [];
    const periods = [];
    const halfN = Math.floor(N / 2);

    for (let k = 1; k <= halfN; k++) {
      let realPart = 0;
      let imagPart = 0;
      for (let n = 0; n < N; n++) {
        const angle = (2 * Math.PI * k * n) / N;
        realPart += centered[n] * Math.cos(angle);
        imagPart += centered[n] * Math.sin(angle);
      }
      const p = (realPart * realPart + imagPart * imagPart) / N;
      frequencies.push(round(k / N, 4));
      power.push(round(p, 4));
      periods.push(round(N / k, 2));
    }

    // Find dominant frequency (highest power)
    let maxPower = -1;
    let maxIdx = 0;
    for (let i = 0; i < power.length; i++) {
      if (power[i] > maxPower) {
        maxPower = power[i];
        maxIdx = i;
      }
    }

    fields.push({
      field,
      frequencies,
      power,
      periods,
      dominantFrequency: frequencies[maxIdx] || null,
      dominantPeriod: periods[maxIdx] || null,
      dominantPower: power[maxIdx] || null,
    });
  }

  return { fields };
}

// ---------------------------------------------------------------------------
// ARIMA(1,1,1) Forecast Data
// ---------------------------------------------------------------------------

function computeARIMAForecastData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 6) return null;

  const forecastFields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  const fields = [];

  for (const field of forecastFields) {
    // Build mean price time series
    const series = [];
    const historical = [];
    for (const date of dates) {
      const snap = snapshots[date];
      const vals = [];
      for (const addrId of Object.keys(snap)) {
        const v = snap[addrId]?.[field];
        if (v != null && !isNaN(v)) vals.push(v);
      }
      const avg = vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
      series.push(avg);
      historical.push({ date, value: round(avg, 4) });
    }

    // Forward/backward fill nulls
    for (let i = 1; i < series.length; i++) {
      if (series[i] == null) series[i] = series[i - 1];
    }
    for (let i = series.length - 2; i >= 0; i--) {
      if (series[i] == null) series[i] = series[i + 1];
    }
    if (series.every(v => v == null)) continue;

    const n = series.length;

    // Difference the series (d=1)
    const diff = [];
    for (let i = 1; i < n; i++) {
      diff.push(series[i] - series[i - 1]);
    }
    if (diff.length < 3) continue;

    const dLen = diff.length;
    const diffMean = diff.reduce((s, v) => s + v, 0) / dLen;

    // Estimate AR(1) coefficient phi via autocorrelation at lag 1
    let num = 0, den = 0;
    for (let i = 1; i < dLen; i++) {
      num += (diff[i] - diffMean) * (diff[i - 1] - diffMean);
    }
    for (let i = 0; i < dLen; i++) {
      den += (diff[i] - diffMean) * (diff[i] - diffMean);
    }
    const phi = den !== 0 ? num / den : 0;

    // Compute residuals from AR(1)
    const residuals = [];
    for (let i = 1; i < dLen; i++) {
      const predicted = diffMean + phi * (diff[i - 1] - diffMean);
      residuals.push(diff[i] - predicted);
    }

    // Estimate MA(1) coefficient theta via residual autocorrelation
    const rLen = residuals.length;
    if (rLen < 2) continue;
    const resMean = residuals.reduce((s, v) => s + v, 0) / rLen;
    let resNum = 0, resDen = 0;
    for (let i = 1; i < rLen; i++) {
      resNum += (residuals[i] - resMean) * (residuals[i - 1] - resMean);
    }
    for (let i = 0; i < rLen; i++) {
      resDen += (residuals[i] - resMean) * (residuals[i] - resMean);
    }
    const theta = resDen !== 0 ? -(resNum / resDen) : 0;

    // Sigma = std dev of residuals
    const resVar = resDen / rLen;
    const sigma = Math.sqrt(resVar);

    // Generate 5-step ahead forecast with confidence intervals
    const lastLevel = series[n - 1];
    const lastDiff = diff[dLen - 1];
    const lastResidual = residuals[rLen - 1];
    const forecast = [];

    let prevDiff = lastDiff;
    let prevResidual = lastResidual;
    let cumLevel = lastLevel;

    for (let step = 1; step <= 5; step++) {
      const nextDiff = diffMean + phi * (prevDiff - diffMean) + theta * prevResidual;
      cumLevel += nextDiff;
      const se = sigma * Math.sqrt(step);
      forecast.push({
        step,
        value: round(cumLevel, 4),
        lower80: round(cumLevel - 1.282 * se, 4),
        upper80: round(cumLevel + 1.282 * se, 4),
        lower95: round(cumLevel - 1.96 * se, 4),
        upper95: round(cumLevel + 1.96 * se, 4),
      });
      prevDiff = nextDiff;
      prevResidual = 0; // future residuals unknown, set to 0
    }

    // AIC approximation: n*ln(sigma²) + 2*k
    const k = 3; // phi, theta, sigma
    const aic = sigma > 0 ? dLen * Math.log(resVar) + 2 * k : null;

    fields.push({
      field,
      historical,
      forecast,
      phi: round(phi, 4),
      theta: round(theta, 4),
      sigma: round(sigma, 4),
      aic: round(aic, 2),
    });
  }

  return fields.length > 0 ? { fields } : null;
}

// ---------------------------------------------------------------------------
// Ensemble Forecast Data
// ---------------------------------------------------------------------------

function computeEnsembleForecastData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 4) return null;

  const forecastFields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  const fields = [];

  for (const field of forecastFields) {
    // Build mean price series
    const series = [];
    const historical = [];
    for (const date of dates) {
      const snap = snapshots[date];
      const vals = [];
      for (const addrId of Object.keys(snap)) {
        const v = snap[addrId]?.[field];
        if (v != null && !isNaN(v)) vals.push(v);
      }
      const avg = vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
      series.push(avg);
      historical.push({ date, value: round(avg, 4) });
    }

    // Forward/backward fill
    for (let i = 1; i < series.length; i++) {
      if (series[i] == null) series[i] = series[i - 1];
    }
    for (let i = series.length - 2; i >= 0; i--) {
      if (series[i] == null) series[i] = series[i + 1];
    }
    if (series.every(v => v == null)) continue;

    const n = series.length;
    const steps = 5;

    // Method 1: Linear trend via OLS
    const X = [];
    const y = [];
    for (let i = 0; i < n; i++) {
      X.push([1, i]);
      y.push(series[i]);
    }
    const ols = simpleOLS(X, y);
    const linearIntercept = ols.coefficients[0];
    const linearSlope = ols.coefficients[1];
    const linearForecast = [];
    for (let s = 1; s <= steps; s++) {
      linearForecast.push({ step: s, value: round(linearIntercept + linearSlope * (n - 1 + s), 4) });
    }

    // Method 2: EMA with alpha=0.3, extend flat
    const alpha = 0.3;
    let ema = series[0];
    for (let i = 1; i < n; i++) {
      ema = alpha * series[i] + (1 - alpha) * ema;
    }
    const emaForecast = [];
    for (let s = 1; s <= steps; s++) {
      emaForecast.push({ step: s, value: round(ema, 4) });
    }

    // Method 3: Mean reversion at rate 0.2 per step
    const longRunMean = series.reduce((s, v) => s + v, 0) / n;
    let mrLevel = series[n - 1];
    const mrRate = 0.2;
    const mrForecast = [];
    for (let s = 1; s <= steps; s++) {
      mrLevel = mrLevel + mrRate * (longRunMean - mrLevel);
      mrForecast.push({ step: s, value: round(mrLevel, 4) });
    }

    // Method 4: Random walk (last value repeated)
    const rwForecast = [];
    for (let s = 1; s <= steps; s++) {
      rwForecast.push({ step: s, value: round(series[n - 1], 4) });
    }

    // Ensemble = weighted average
    const weights = { linear: 0.25, ema: 0.35, meanReversion: 0.25, randomWalk: 0.15 };
    const ensemble = [];
    for (let s = 0; s < steps; s++) {
      const lv = linearForecast[s].value;
      const ev = emaForecast[s].value;
      const mv = mrForecast[s].value;
      const rv = rwForecast[s].value;
      const ensVal = weights.linear * lv + weights.ema * ev + weights.meanReversion * mv + weights.randomWalk * rv;
      const spread = Math.max(lv, ev, mv, rv) - Math.min(lv, ev, mv, rv);
      ensemble.push({ step: s + 1, value: round(ensVal, 4), spread: round(spread, 4) });
    }

    fields.push({
      field,
      historical,
      methods: {
        linear: linearForecast,
        ema: emaForecast,
        meanReversion: mrForecast,
        randomWalk: rwForecast,
      },
      ensemble,
      weights,
    });
  }

  return fields.length > 0 ? { fields } : null;
}

// ---------------------------------------------------------------------------
// Price Half-Life (Ornstein-Uhlenbeck) Data
// ---------------------------------------------------------------------------

function computePriceHalfLifeData(snapshots, addresses) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 4) return null;

  const forecastFields = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];
  const fields = [];

  // Compute average days between scrapes
  let totalDayGap = 0;
  let dayGapCount = 0;
  for (let i = 1; i < dates.length; i++) {
    const d0 = new Date(dates[i - 1]);
    const d1 = new Date(dates[i]);
    const gap = (d1 - d0) / (1000 * 60 * 60 * 24);
    if (gap > 0) {
      totalDayGap += gap;
      dayGapCount++;
    }
  }
  const avgDaysBetween = dayGapCount > 0 ? totalDayGap / dayGapCount : 1;

  // Map addressId -> state
  const addrState = {};
  if (addresses) {
    for (const addr of addresses) {
      addrState[addr.addressId] = (addr.state || '').toUpperCase();
    }
  }

  for (const field of forecastFields) {
    // Build mean price series
    const series = [];
    for (const date of dates) {
      const snap = snapshots[date];
      const vals = [];
      for (const addrId of Object.keys(snap)) {
        const v = snap[addrId]?.[field];
        if (v != null && !isNaN(v)) vals.push(v);
      }
      series.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
    }

    // Forward/backward fill
    for (let i = 1; i < series.length; i++) {
      if (series[i] == null) series[i] = series[i - 1];
    }
    for (let i = series.length - 2; i >= 0; i--) {
      if (series[i] == null) series[i] = series[i + 1];
    }
    if (series.every(v => v == null)) continue;

    const n = series.length;
    const mu = series.reduce((s, v) => s + v, 0) / n;

    // AR(1) regression: price_t = alpha + beta * price_{t-1}
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    let count = 0;
    for (let i = 1; i < n; i++) {
      sumX += series[i - 1];
      sumY += series[i];
      sumXY += series[i - 1] * series[i];
      sumXX += series[i - 1] * series[i - 1];
      count++;
    }
    if (count < 2) continue;

    const betaDen = count * sumXX - sumX * sumX;
    const beta = betaDen !== 0 ? (count * sumXY - sumX * sumY) / betaDen : 1;
    const alphaVal = (sumY - beta * sumX) / count;

    // Compute residuals and R²
    const residuals = [];
    let ssRes = 0, ssTot = 0;
    const yMean = sumY / count;
    for (let i = 1; i < n; i++) {
      const predicted = alphaVal + beta * series[i - 1];
      const res = series[i] - predicted;
      residuals.push(res);
      ssRes += res * res;
      ssTot += (series[i] - yMean) * (series[i] - yMean);
    }
    const rSquared = ssTot !== 0 ? 1 - ssRes / ssTot : 0;
    const sigma = residuals.length > 0 ? Math.sqrt(ssRes / residuals.length) : 0;

    // theta = -ln(beta), halfLife = ln(2) / theta
    const clampedBeta = Math.max(beta, 0.001); // avoid log of 0 or negative
    const theta = -Math.log(clampedBeta);
    const halfLife = theta > 0 ? Math.LN2 / theta : null;
    const halfLifeDays = halfLife != null ? halfLife * avgDaysBetween : null;

    // Theoretical decay curve
    const decayCurve = [];
    for (let t = 0; t <= 10; t++) {
      decayCurve.push({ t, value: round(Math.exp(-theta * t), 4) });
    }

    // Actual deviations from mean (normalized)
    const actualDeviations = [];
    const maxDev = Math.max(...series.map(v => Math.abs(v - mu)));
    for (let i = 0; i < n; i++) {
      actualDeviations.push({
        t: i,
        deviation: round(maxDev !== 0 ? (series[i] - mu) / maxDev : 0, 4),
      });
    }

    fields.push({
      field,
      halfLife: round(halfLife, 4),
      halfLifeDays: round(halfLifeDays, 2),
      theta: round(theta, 4),
      mu: round(mu, 4),
      sigma: round(sigma, 4),
      rSquared: round(rSquared, 4),
      decayCurve,
      actualDeviations,
    });
  }

  // Compute half-life by state
  const byState = [];
  if (addresses && addresses.length > 0) {
    const stateGroups = {};
    for (const addr of addresses) {
      const st = (addr.state || 'UNKNOWN').toUpperCase();
      if (!stateGroups[st]) stateGroups[st] = [];
      stateGroups[st].push(addr.addressId);
    }

    for (const [state, addrIds] of Object.entries(stateGroups)) {
      // Build state-level pest365 series
      const stateSeries = [];
      for (const date of dates) {
        const snap = snapshots[date];
        const vals = [];
        for (const addrId of addrIds) {
          const v = snap[addrId]?.pest365;
          if (v != null && !isNaN(v)) vals.push(v);
        }
        stateSeries.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
      }

      // Fill nulls
      for (let i = 1; i < stateSeries.length; i++) {
        if (stateSeries[i] == null) stateSeries[i] = stateSeries[i - 1];
      }
      for (let i = stateSeries.length - 2; i >= 0; i--) {
        if (stateSeries[i] == null) stateSeries[i] = stateSeries[i + 1];
      }
      if (stateSeries.every(v => v == null)) continue;

      const sn = stateSeries.length;
      let sx = 0, sy = 0, sxy = 0, sxx = 0, sc = 0;
      for (let i = 1; i < sn; i++) {
        sx += stateSeries[i - 1];
        sy += stateSeries[i];
        sxy += stateSeries[i - 1] * stateSeries[i];
        sxx += stateSeries[i - 1] * stateSeries[i - 1];
        sc++;
      }
      if (sc < 2) continue;
      const sBetaDen = sc * sxx - sx * sx;
      const sBeta = sBetaDen !== 0 ? (sc * sxy - sx * sy) / sBetaDen : 1;
      const sClampedBeta = Math.max(sBeta, 0.001);
      const sTheta = -Math.log(sClampedBeta);
      const sHalfLife = sTheta > 0 ? Math.LN2 / sTheta : null;
      const sHalfLifeDays = sHalfLife != null ? sHalfLife * avgDaysBetween : null;

      byState.push({
        state,
        halfLife: round(sHalfLife, 4),
        halfLifeDays: round(sHalfLifeDays, 2),
        addressCount: addrIds.length,
      });
    }
  }

  return fields.length > 0 ? { fields, byState } : null;
}

// ---------------------------------------------------------------------------
// Price Contagion Data (Cross-state price change propagation)
// ---------------------------------------------------------------------------

function computePriceContagionData(snapshots, addresses) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 4 || !addresses || addresses.length === 0) return null;

  // Group addresses by state
  const stateAddrs = {};
  for (const addr of addresses) {
    const st = (addr.state || 'UNKNOWN').toUpperCase();
    if (!stateAddrs[st]) stateAddrs[st] = [];
    stateAddrs[st].push(addr.addressId);
  }

  const states = Object.keys(stateAddrs).sort();
  if (states.length < 2) return null;

  // Compute mean pest365 change series per state
  const stateChangeSeries = {};
  for (const state of states) {
    const addrIds = stateAddrs[state];
    const meanSeries = [];
    for (const date of dates) {
      const snap = snapshots[date];
      const vals = [];
      for (const addrId of addrIds) {
        const v = snap[addrId]?.pest365;
        if (v != null && !isNaN(v)) vals.push(v);
      }
      meanSeries.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
    }
    // Fill nulls
    for (let i = 1; i < meanSeries.length; i++) {
      if (meanSeries[i] == null) meanSeries[i] = meanSeries[i - 1];
    }
    for (let i = meanSeries.length - 2; i >= 0; i--) {
      if (meanSeries[i] == null) meanSeries[i] = meanSeries[i + 1];
    }
    // Differences
    const changes = [];
    for (let i = 1; i < meanSeries.length; i++) {
      changes.push(meanSeries[i] != null && meanSeries[i - 1] != null ? meanSeries[i] - meanSeries[i - 1] : 0);
    }
    stateChangeSeries[state] = changes;
  }

  const nChanges = dates.length - 1;
  const maxLag = Math.min(3, Math.floor(nChanges / 2));

  // Cross-correlation function
  function crossCorr(a, b, lag) {
    const n = Math.min(a.length, b.length);
    if (lag >= n || lag < 0) return 0;
    const aMean = a.reduce((s, v) => s + v, 0) / a.length;
    const bMean = b.reduce((s, v) => s + v, 0) / b.length;
    let num = 0, denA = 0, denB = 0;
    for (let i = lag; i < n; i++) {
      num += (a[i] - aMean) * (b[i - lag] - bMean);
    }
    for (let i = 0; i < n; i++) {
      denA += (a[i] - aMean) * (a[i] - aMean);
      denB += (b[i] - bMean) * (b[i] - bMean);
    }
    const den = Math.sqrt(denA * denB);
    return den !== 0 ? num / den : 0;
  }

  // Build propagation and strength matrices
  const nStates = states.length;
  const propagationMatrix = Array.from({ length: nStates }, () => new Array(nStates).fill(0));
  const strengthMatrix = Array.from({ length: nStates }, () => new Array(nStates).fill(0));
  const events = [];

  const outgoing = new Array(nStates).fill(0);
  const incoming = new Array(nStates).fill(0);

  for (let i = 0; i < nStates; i++) {
    for (let j = 0; j < nStates; j++) {
      if (i === j) continue;
      const a = stateChangeSeries[states[i]];
      const b = stateChangeSeries[states[j]];
      let bestLag = 0;
      let bestCorr = 0;
      for (let lag = 0; lag <= maxLag; lag++) {
        const c = crossCorr(a, b, lag);
        if (Math.abs(c) > Math.abs(bestCorr)) {
          bestCorr = c;
          bestLag = lag;
        }
      }
      propagationMatrix[i][j] = bestLag;
      strengthMatrix[i][j] = round(bestCorr, 4);

      if (Math.abs(bestCorr) > 0.3 && bestLag > 0) {
        outgoing[j]++;
        incoming[i]++;
        events.push({
          sourceState: states[j],
          targetState: states[i],
          lag: bestLag,
          strength: round(bestCorr, 4),
          direction: bestCorr > 0 ? 'positive' : 'negative',
          date: dates[Math.min(bestLag, dates.length - 1)],
        });
      }
    }
  }

  // Find most contagious and susceptible
  let maxOut = -1, mostContagious = states[0];
  let maxIn = -1, mostSusceptible = states[0];
  for (let i = 0; i < nStates; i++) {
    if (outgoing[i] > maxOut) { maxOut = outgoing[i]; mostContagious = states[i]; }
    if (incoming[i] > maxIn) { maxIn = incoming[i]; mostSusceptible = states[i]; }
  }

  return {
    states,
    propagationMatrix,
    strengthMatrix,
    events,
    mostContagious,
    mostSusceptible,
  };
}

// ---------------------------------------------------------------------------
// Regime Transition Diagram Data
// ---------------------------------------------------------------------------

function computeRegimeTransitionDiagramData(snapshots) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 4) return null;

  // Build mean pest365 series
  const series = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) vals.push(v);
    }
    series.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
  }

  // Fill nulls
  for (let i = 1; i < series.length; i++) {
    if (series[i] == null) series[i] = series[i - 1];
  }
  for (let i = series.length - 2; i >= 0; i--) {
    if (series[i] == null) series[i] = series[i + 1];
  }
  if (series.every(v => v == null)) return null;

  const n = series.length;
  const overallMean = series.reduce((s, v) => s + v, 0) / n;

  // Compute changes
  const changes = [];
  for (let i = 1; i < n; i++) {
    changes.push(series[i] - series[i - 1]);
  }

  // Overall variance
  const changeMean = changes.reduce((s, v) => s + v, 0) / changes.length;
  const overallVar = changes.reduce((s, v) => s + (v - changeMean) * (v - changeMean), 0) / changes.length;

  // Regime labels
  const regimeLabels = ['stable', 'trending_up', 'trending_down', 'volatile'];
  const regimeColors = { stable: '#16a34a', trending_up: '#dc2626', trending_down: '#2563eb', volatile: '#f59e0b' };
  const regimeIndex = { stable: 0, trending_up: 1, trending_down: 2, volatile: 3 };

  // Classify each period
  const timeline = [];
  const regimeSequence = [];
  const threshold = 0.005 * Math.abs(overallMean); // 0.5% of mean

  for (let i = 1; i < n; i++) {
    const change = changes[i - 1];

    // Check volatility: variance of recent 3 periods
    let isVolatile = false;
    if (i >= 3) {
      const recent = changes.slice(Math.max(0, i - 3), i);
      const recentMean = recent.reduce((s, v) => s + v, 0) / recent.length;
      const recentVar = recent.reduce((s, v) => s + (v - recentMean) * (v - recentMean), 0) / recent.length;
      isVolatile = overallVar > 0 && recentVar > 2 * overallVar;
    }

    let regime;
    if (isVolatile) {
      regime = 'volatile';
    } else if (Math.abs(change) < threshold) {
      regime = 'stable';
    } else if (change > 0) {
      regime = 'trending_up';
    } else {
      regime = 'trending_down';
    }

    timeline.push({ date: dates[i], regime });
    regimeSequence.push(regime);
  }

  // Build 4x4 transition matrix
  const transitionCounts = Array.from({ length: 4 }, () => new Array(4).fill(0));
  for (let i = 1; i < regimeSequence.length; i++) {
    const from = regimeIndex[regimeSequence[i - 1]];
    const to = regimeIndex[regimeSequence[i]];
    transitionCounts[from][to]++;
  }

  // Normalize to probabilities
  const transitionMatrix = transitionCounts.map(row => {
    const sum = row.reduce((s, v) => s + v, 0);
    return sum > 0 ? row.map(v => round(v / sum, 4)) : row.map(() => 0);
  });

  // Compute stationary distribution via repeated matrix multiplication
  let dist = [0.25, 0.25, 0.25, 0.25];
  for (let iter = 0; iter < 100; iter++) {
    const next = [0, 0, 0, 0];
    for (let j = 0; j < 4; j++) {
      for (let i = 0; i < 4; i++) {
        next[j] += dist[i] * transitionMatrix[i][j];
      }
    }
    dist = next;
  }
  const stationaryDistribution = dist.map(v => round(v, 4));

  // Current regime
  const currentRegime = regimeSequence.length > 0 ? regimeSequence[regimeSequence.length - 1] : 'stable';

  // Compute regime stats
  const regimeCounts = { stable: 0, trending_up: 0, trending_down: 0, volatile: 0 };
  const regimeDurations = { stable: [], trending_up: [], trending_down: [], volatile: [] };
  let currentRun = 1;
  for (let i = 0; i < regimeSequence.length; i++) {
    regimeCounts[regimeSequence[i]]++;
    if (i > 0 && regimeSequence[i] === regimeSequence[i - 1]) {
      currentRun++;
    } else {
      if (i > 0) {
        regimeDurations[regimeSequence[i - 1]].push(currentRun);
      }
      currentRun = 1;
    }
  }
  // Push last run
  if (regimeSequence.length > 0) {
    regimeDurations[regimeSequence[regimeSequence.length - 1]].push(currentRun);
  }

  const totalPeriods = regimeSequence.length;
  const regimes = regimeLabels.map((label, id) => ({
    id,
    label,
    color: regimeColors[label],
    avgDuration: regimeDurations[label].length > 0
      ? round(regimeDurations[label].reduce((s, v) => s + v, 0) / regimeDurations[label].length, 2)
      : 0,
    frequency: totalPeriods > 0 ? round(regimeCounts[label] / totalPeriods, 4) : 0,
  }));

  return {
    regimes,
    transitionMatrix,
    timeline,
    currentRegime,
    stationaryDistribution,
  };
}

// ---------------------------------------------------------------------------
// Information Ratio Data (risk-adjusted performance vs national benchmark)
// ---------------------------------------------------------------------------

function computeInformationRatioData(snapshots, addresses) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 3 || !addresses || addresses.length === 0) return null;

  // Group addresses by state
  const stateAddrs = {};
  for (const addr of addresses) {
    const st = (addr.state || 'UNKNOWN').toUpperCase();
    if (!stateAddrs[st]) stateAddrs[st] = [];
    stateAddrs[st].push(addr.addressId);
  }

  // Compute national average pest365 series
  const nationalSeries = [];
  for (const date of dates) {
    const snap = snapshots[date];
    const vals = [];
    for (const addrId of Object.keys(snap)) {
      const v = snap[addrId]?.pest365;
      if (v != null && !isNaN(v)) vals.push(v);
    }
    nationalSeries.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
  }

  // Fill nulls
  for (let i = 1; i < nationalSeries.length; i++) {
    if (nationalSeries[i] == null) nationalSeries[i] = nationalSeries[i - 1];
  }
  for (let i = nationalSeries.length - 2; i >= 0; i--) {
    if (nationalSeries[i] == null) nationalSeries[i] = nationalSeries[i + 1];
  }
  if (nationalSeries.every(v => v == null)) return null;

  // National benchmark changes
  const benchmarkChanges = [];
  for (let i = 1; i < nationalSeries.length; i++) {
    benchmarkChanges.push(nationalSeries[i] - nationalSeries[i - 1]);
  }

  const benchmarkAvg = nationalSeries.reduce((s, v) => s + v, 0) / nationalSeries.length;

  const statesResult = [];

  for (const [state, addrIds] of Object.entries(stateAddrs)) {
    // Compute state-level pest365 series
    const stateSeries = [];
    for (const date of dates) {
      const snap = snapshots[date];
      const vals = [];
      for (const addrId of addrIds) {
        const v = snap[addrId]?.pest365;
        if (v != null && !isNaN(v)) vals.push(v);
      }
      stateSeries.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
    }

    // Fill nulls
    for (let i = 1; i < stateSeries.length; i++) {
      if (stateSeries[i] == null) stateSeries[i] = stateSeries[i - 1];
    }
    for (let i = stateSeries.length - 2; i >= 0; i--) {
      if (stateSeries[i] == null) stateSeries[i] = stateSeries[i + 1];
    }
    if (stateSeries.every(v => v == null)) continue;

    // State changes
    const stateChanges = [];
    for (let i = 1; i < stateSeries.length; i++) {
      stateChanges.push(stateSeries[i] - stateSeries[i - 1]);
    }

    // Excess returns
    const excessReturns = [];
    for (let i = 0; i < Math.min(stateChanges.length, benchmarkChanges.length); i++) {
      excessReturns.push(stateChanges[i] - benchmarkChanges[i]);
    }

    if (excessReturns.length === 0) continue;

    const meanExcess = excessReturns.reduce((s, v) => s + v, 0) / excessReturns.length;
    const trackingError = stdDev(excessReturns);
    const ir = trackingError > 0 ? meanExcess / trackingError : 0;

    const stateAvgPrice = stateSeries.reduce((s, v) => s + v, 0) / stateSeries.length;
    const firstPrice = stateSeries[0];
    const lastPrice = stateSeries[stateSeries.length - 1];
    const priceChangePct = firstPrice !== 0 ? ((lastPrice - firstPrice) / firstPrice) * 100 : 0;

    statesResult.push({
      state,
      informationRatio: round(ir, 4),
      excessReturn: round(meanExcess, 4),
      trackingError: round(trackingError, 4),
      avgPrice: round(stateAvgPrice, 2),
      benchmarkAvg: round(benchmarkAvg, 2),
      priceChangePct: round(priceChangePct, 2),
      addressCount: addrIds.length,
    });
  }

  // Rolling IR using expanding window
  const rollingIR = [];
  for (let w = 2; w <= benchmarkChanges.length; w++) {
    // All-state excess returns up to window w
    const allExcess = [];
    for (const [state, addrIds] of Object.entries(stateAddrs)) {
      const stateSeries = [];
      for (let d = 0; d <= w; d++) {
        const snap = snapshots[dates[d]];
        const vals = [];
        for (const addrId of addrIds) {
          const v = snap[addrId]?.pest365;
          if (v != null && !isNaN(v)) vals.push(v);
        }
        stateSeries.push(vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null);
      }
      for (let i = 1; i < stateSeries.length; i++) {
        if (stateSeries[i] == null) stateSeries[i] = stateSeries[i - 1];
      }
      for (let i = stateSeries.length - 2; i >= 0; i--) {
        if (stateSeries[i] == null) stateSeries[i] = stateSeries[i + 1];
      }
      for (let i = 1; i < stateSeries.length; i++) {
        const sc = stateSeries[i] - stateSeries[i - 1];
        const bc = nationalSeries[i] - nationalSeries[i - 1];
        allExcess.push(sc - bc);
      }
    }
    if (allExcess.length > 1) {
      const mEx = allExcess.reduce((s, v) => s + v, 0) / allExcess.length;
      const te = stdDev(allExcess);
      rollingIR.push({
        date: dates[w],
        ir: round(te > 0 ? mEx / te : 0, 4),
      });
    }
  }

  return {
    states: statesResult,
    benchmark: { type: 'national_average', value: round(benchmarkAvg, 2), label: 'National Average' },
    field: 'pest365',
    rollingIR,
  };
}

// ---------------------------------------------------------------------------
// Price Path Divergence Data
// ---------------------------------------------------------------------------

function computePricePathDivergenceData(snapshots, addresses, historyByAddress) {
  const dates = Object.keys(snapshots).sort();
  if (dates.length < 3 || !addresses || addresses.length === 0) return null;

  const field = 'pest365';
  const paths = [];
  const allNormByDate = {}; // date -> array of normalized values

  for (const addr of addresses) {
    const addrId = addr.addressId;
    const label = `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''}`.trim();
    const state = (addr.state || '').toUpperCase();

    // Build price series for this address from snapshots
    const normalizedPath = [];
    let initialPrice = null;
    let hasData = false;

    for (const date of dates) {
      const snap = snapshots[date];
      const v = snap[addrId]?.[field];
      if (v != null && !isNaN(v)) {
        if (initialPrice == null) {
          initialPrice = v;
        }
        if (initialPrice > 0) {
          const norm = v / initialPrice;
          normalizedPath.push({ date, value: round(norm, 4) });
          if (!allNormByDate[date]) allNormByDate[date] = [];
          allNormByDate[date].push(norm);
          hasData = true;
        }
      }
    }

    if (hasData && normalizedPath.length >= 2) {
      paths.push({ addressId: addrId, label, state, normalizedPath });
    }
  }

  if (paths.length < 2) return null;

  // Compute divergence index per date
  const divergenceIndex = [];
  let maxDivergence = 0;

  for (const date of dates) {
    const norms = allNormByDate[date];
    if (norms && norms.length >= 2) {
      const avg = norms.reduce((s, v) => s + v, 0) / norms.length;
      const variance = norms.reduce((s, v) => s + (v - avg) * (v - avg), 0) / norms.length;
      const div = Math.sqrt(variance);
      divergenceIndex.push({ date, value: round(div, 4) });
      if (div > maxDivergence) maxDivergence = div;
    }
  }

  // Find most divergent address (largest absolute deviation from 1.0 at end)
  let mostDivergentAddress = null;
  let mostDivergentLabel = null;
  let maxEndDeviation = 0;

  const finalValues = [];
  for (const path of paths) {
    const lastVal = path.normalizedPath[path.normalizedPath.length - 1]?.value;
    if (lastVal != null) {
      finalValues.push(lastVal);
      const dev = Math.abs(lastVal - 1.0);
      if (dev > maxEndDeviation) {
        maxEndDeviation = dev;
        mostDivergentAddress = path.addressId;
        mostDivergentLabel = path.label;
      }
    }
  }

  const avgFinalValue = finalValues.length > 0 ? finalValues.reduce((s, v) => s + v, 0) / finalValues.length : 1.0;
  const spreadAtEnd = finalValues.length > 1 ? Math.max(...finalValues) - Math.min(...finalValues) : 0;

  return {
    paths,
    divergenceIndex,
    maxDivergence: round(maxDivergence, 4),
    mostDivergentAddress,
    mostDivergentLabel,
    avgFinalValue: round(avgFinalValue, 4),
    spreadAtEnd: round(spreadAtEnd, 4),
    field,
  };
}

/**
 * Linear interpolation percentile.
 * For percentile p (0-100), index = (n-1) * p/100, interpolate between floor and ceil.
 */
function percentile(sortedArr, p) {
  if (sortedArr.length === 0) return null;
  if (sortedArr.length === 1) return sortedArr[0];
  const idx = (sortedArr.length - 1) * p / 100;
  const lower = Math.floor(idx);
  const upper = Math.ceil(idx);
  if (lower === upper) return sortedArr[lower];
  const frac = idx - lower;
  return sortedArr[lower] * (1 - frac) + sortedArr[upper] * frac;
}

// ---------------------------------------------------------------------------
// Default export
// ---------------------------------------------------------------------------

export default computeAnalytics;

// ---------------------------------------------------------------------------
// Psychological Pricing Density
// ---------------------------------------------------------------------------

function computePsychologicalPricing(snapshotsByDate, addresses) {
  const dates = Object.keys(snapshotsByDate).sort();
  if (dates.length === 0) return [];
  const latestDate = dates[dates.length - 1];
  const snap = snapshotsByDate[latestDate];
  
  const endings = {};
  for (const [addrId, data] of Object.entries(snap)) {
    ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'].forEach(field => {
      const price = data[field];
      if (price != null && !isNaN(price)) {
        const cents = Math.round((price % 1) * 100);
        const centsStr = cents.toString().padStart(2, '0');
        if (centsStr !== 'NaN') {
            const key = '.' + centsStr;
            endings[key] = (endings[key] || 0) + 1;
        }
      }
    });
  }
  
  const result = Object.keys(endings).map(k => ({ ending: k, count: endings[k] }));
  result.sort((a, b) => b.count - a.count);
  return result.slice(0, 15);
}
